// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";
import { getAnalytics } from "firebase/analytics";
import {
  initializeAuth,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithCustomToken,
  signInWithEmailAndPassword as firebaseSignInWithEmailAndPassword,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  verifyPasswordResetCode as firebaseVerifyPasswordResetCode,
  confirmPasswordReset as firebaseConfirmPasswordReset,
  getAdditionalUserInfo,
} from "firebase/auth";
import {
  getFirestore,
  doc,
  setDoc,
  addDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  getCountFromServer,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  writeBatch,
  increment,
  runTransaction,
  arrayUnion,
  arrayRemove,
  collectionGroup,
  deleteField,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getFunctions, httpsCallable, connectFunctionsEmulator } from "firebase/functions";
import { getMessaging, getToken, onMessage, isSupported as isMessagingSupported } from "firebase/messaging";

// Import date utilities for Israel timezone handling
import {
  serializeForRedux,
  formatLocalDateString,
  getIsraelDateString,
  toIsraelDate,
  getWeekId as dateUtilsGetWeekId,
  getWeekStart as dateUtilsGetWeekStart,
  getWeekEnd as dateUtilsGetWeekEnd,
  generateUniqueAppointmentId,
} from "./utils/dateUtils";
import {
  resolveRoomSlotPure,
  resolveRoomDay as resolveRoomDayPure,
} from "./utils/roomAvailability";
// Shared food contract — the same validator the seed import and the NiV
// promotion step use, so a food can only ever enter the catalog in one shape
import {
  normalizeFood,
  buildSearchName as buildFoodSearchName,
} from "./utils/foodsNormalize.mjs";
// Body-metric math for measurements (BMI / BMR / RMR / body fat)
import {
  calcBmi,
  calcBmr,
  calcRmr,
  calcBodyFat,
  calcAgeFromBirthDate,
  calcMenuNutrition,
  calcBlockNutrition,
  calcSubBlockNutrition,
  calcIngredientGroupNutrition,
  calcIngredientNutrition,
  METRIC_OVERRIDE_FIELDS,
  sanitizeMetricOverrides,
} from "./utils/nutritionCalc";
import { nutritionMenuTemplates } from "./data/systemContent/nutritionMenuTemplates";
import { getHolidayForDateStr } from "./utils/israeliHolidays";
import { buildCoupleDenormalizedFields } from "./utils/coupleUtils";
import { phoneMatchesQuery } from "./utils/phoneUtils";
import { normalizeCalendarStartHour } from "./utils/calendarHours";
import { toLocalDateStr as absenceDateStr, toIsraelHHMM, findBlockingAbsence } from "./utils/absenceUtils";
import {
  normalizeLeadInput,
  LEAD_SYSTEM_STATUSES,
  LEAD_ARCHIVED_STATUSES,
  LEAD_SCHEMA_VERSION,
} from "./utils/leadContract";

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: "AIzaSyCw8oVtphlWvEs3Yjm1CfUA1Jzd9iMvsCo",
  authDomain: "clinikit-61f1f.firebaseapp.com",
  projectId: "clinikit-61f1f",
  storageBucket: "clinikit-61f1f.firebasestorage.app",
  messagingSenderId: "35158995981",
  appId: "1:35158995981:web:d26974ab22fbc86987d144",
  measurementId: "G-3CX9VDLVK9",
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// App Check — attests requests come from the genuine web app. Required for the
// enforceAppCheck:true public booking callables (sendBookingOtp et al.) so raw
// script/curl calls can't drain the SMS quota. Site key registered in the
// Firebase console (App Check → reCAPTCHA v3). Missing key only warns so the app
// still boots; the booking flow will fail until the key is set.
const APPCHECK_SITE_KEY = process.env.REACT_APP_APPCHECK_SITE_KEY;
// navigator.webdriver is true inside the puppeteer prerender (scripts/prerender.js).
// App Check's ReCaptchaV3Provider injects grecaptcha DOM (#fire_app_check_[DEFAULT],
// badge iframe, script tags) into the page; if that gets baked into the prerendered
// snapshot, every real visitor's App Check init then finds a non-empty container and
// grecaptcha throws "reCAPTCHA placeholder element must be empty" on load.
const IS_PRERENDER = typeof navigator !== "undefined" && navigator.webdriver === true;
if (APPCHECK_SITE_KEY && !IS_PRERENDER) {
  // Debug token for localhost so App Check works in dev (register it in console).
  if (
    process.env.NODE_ENV !== "production" &&
    typeof window !== "undefined" &&
    process.env.REACT_APP_APPCHECK_DEBUG_TOKEN
  ) {
    // eslint-disable-next-line no-restricted-globals
    self.FIREBASE_APPCHECK_DEBUG_TOKEN =
      process.env.REACT_APP_APPCHECK_DEBUG_TOKEN;
  }
  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(APPCHECK_SITE_KEY),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (error) {
    console.error("[CK-1099] Failed to initialize App Check:", {
      errorCode: "CK-1099",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
  }
} else {
  console.warn(
    "[CK-1099] App Check site key missing (REACT_APP_APPCHECK_SITE_KEY) — public booking will fail once enforcement is live.",
  );
}

const analytics = getAnalytics(app);
const auth = initializeAuth(app, {
  persistence: [indexedDBLocalPersistence, browserLocalPersistence],
});
const db = getFirestore(app);
const storage = getStorage(app);
// Use europe-west1 region to match Cloud Functions deployment
const functions = getFunctions(app, "europe-west1");
// Local dev only: route callables to `firebase emulators:start --only functions`
// (Firestore/Auth/Storage stay on the real project). Opt-in via .env.local;
// CRA strips the branch in production builds where the var is unset.
if (process.env.NODE_ENV === "development" && process.env.REACT_APP_USE_FUNCTIONS_EMULATOR === "1") {
  connectFunctionsEmulator(functions, "127.0.0.1", 5001);
  console.warn("[CliniKit] Cloud Functions → local emulator (REACT_APP_USE_FUNCTIONS_EMULATOR=1)");
}

// Set language to Hebrew for SMS
auth.languageCode = "he";

// Helper function to setup reCAPTCHA
export const setupRecaptcha = (containerId) => {
  if (!window.recaptchaVerifier) {
    // RecaptchaVerifier throws "reCAPTCHA placeholder element must be empty" if
    // the container has leftover widget DOM (stale prerendered HTML, or a
    // previous verifier that was nulled without clear()).
    const container = document.getElementById(containerId);
    if (container) {
      container.innerHTML = "";
    }
    window.recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
      size: "invisible",
      callback: () => {
        // reCAPTCHA solved
      },
      "expired-callback": () => {
        // Reset reCAPTCHA — clear() removes the widget DOM so the next
        // setupRecaptcha call gets an empty container.
        try {
          window.recaptchaVerifier?.clear();
        } catch (e) {
          // Ignore errors during cleanup
        }
        window.recaptchaVerifier = null;
      },
    });
  }
  return window.recaptchaVerifier;
};

// Helper function to send verification code
export const sendVerificationCode = async (phoneNumber) => {
  const appVerifier = window.recaptchaVerifier;
  const confirmationResult = await signInWithPhoneNumber(
    auth,
    phoneNumber,
    appVerifier
  );
  window.confirmationResult = confirmationResult;
  return confirmationResult;
};

// Helper function to verify code
export const verifyCode = async (code) => {
  const confirmationResult = window.confirmationResult;
  const result = await confirmationResult.confirm(code);
  return result.user;
};

// Same as verifyCode but also reports whether Firebase Auth just CREATED this
// account. Phone auth is always sign-in-or-sign-up: if the Auth user was deleted
// (e.g. from the console) but the therapist doc still exists, confirm() silently
// creates a fresh account with a new uid. Callers use isNewUser to clean that up.
export const verifyCodeWithInfo = async (code) => {
  const confirmationResult = window.confirmationResult;
  const result = await confirmationResult.confirm(code);
  const info = getAdditionalUserInfo(result);
  return { user: result.user, isNewUser: info?.isNewUser === true };
};

// Helper function to send email sign-in link (passwordless)
export const sendEmailSignInLink = async (email) => {
  const actionCodeSettings = {
    url: `${window.location.origin}/auth/email-callback`,
    handleCodeInApp: true,
  };
  await sendSignInLinkToEmail(auth, email, actionCodeSettings);
  window.localStorage.setItem("emailForSignIn", email);
};

// Helper function to complete email link sign-in
// Verifies the link entirely server-side to avoid creating duplicate Firebase Auth accounts.
// The oobCode is extracted from the URL and sent to the Cloud Function which verifies it
// via the Firebase REST API, finds the original therapist account, and returns a custom token.
export const completeEmailLinkSignIn = async (email, url) => {
  if (!isSignInWithEmailLink(auth, url)) {
    throw new Error("הקישור אינו תקין או שפג תוקפו.");
  }

  const urlObj = new URL(url);
  const oobCode = urlObj.searchParams.get("oobCode");

  if (!oobCode) {
    throw new Error("הקישור אינו תקין.");
  }

  const completeEmailLoginFunc = httpsCallable(functions, "completeEmailLogin");
  const result = await completeEmailLoginFunc({
    email: email.trim().toLowerCase(),
    oobCode,
  });

  window.localStorage.removeItem("emailForSignIn");

  const finalResult = await signInWithCustomToken(auth, result.data.customToken);
  return finalResult.user;
};

// Helper function to check if current URL is an email sign-in link
export const checkIsEmailSignInLink = (url) => {
  return isSignInWithEmailLink(auth, url);
};

// Helper function to sign in with email and password
export const signInWithEmailPassword = async (email, password) => {
  const userCredential = await firebaseSignInWithEmailAndPassword(
    auth,
    email.trim().toLowerCase(),
    password
  );
  return userCredential.user;
};

// Helper function to send password reset email
export const resetPassword = async (email) => {
  await firebaseSendPasswordResetEmail(auth, email.trim().toLowerCase());
};

// Verify a password reset code and return the associated email
export const verifyPasswordResetCodeWrapper = async (oobCode) => {
  return await firebaseVerifyPasswordResetCode(auth, oobCode);
};

// Confirm a password reset with the code and new password
export const confirmPasswordResetWrapper = async (oobCode, newPassword) => {
  await firebaseConfirmPasswordReset(auth, oobCode, newPassword);
};

// Helper function to link email (and optionally set password) on the current authenticated user's Firebase Auth account
export const linkEmailToAuthUser = async (email, password) => {
  const linkEmailFunc = httpsCallable(functions, "linkEmailToUser");
  const payload = { email: email.trim().toLowerCase() };
  if (password) {
    payload.password = password;
  }
  const result = await linkEmailFunc(payload);
  return result.data;
};

// Helper function to check if a therapist exists by email
// Returns true ONLY if the server explicitly confirms existence
export const checkTherapistExistsByEmail = async (email) => {
  const checkEmailExistsFunc = httpsCallable(functions, "checkEmailExists");
  const result = await checkEmailExistsFunc({ email });
  return result?.data?.exists === true;
};

// Helper function to check if a therapist exists by phone number
// Uses Cloud Function to bypass Firestore rules (user not authenticated yet during login)
export const checkTherapistExistsByPhone = async (phoneNumber) => {
  try {
    const checkPhoneExistsFunc = httpsCallable(functions, "checkPhoneExists");
    const result = await checkPhoneExistsFunc({ phone: phoneNumber });
    return result.data.exists;
  } catch (error) {
    console.error("[CK-1002] Error checking phone exists:", {
      errorCode: "CK-1002",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    // On error, allow the flow to continue (better UX than blocking)
    // The actual auth will fail anyway if user doesn't exist
    return true;
  }
};

// Check whether a phone is on the admin-managed registration whitelist.
// Used by the signup UI to gate OTP send. The hard security boundary is in
// firestore.rules (clinic/therapist create requires whitelist exists()).
export const checkPhoneAllowedForRegistration = async (phoneNumber) => {
  try {
    const fn = httpsCallable(functions, "checkPhoneAllowedForRegistration");
    const result = await fn({ phone: phoneNumber });
    return result.data.allowed === true;
  } catch (error) {
    console.error("[CK-1053] Error checking registration whitelist:", {
      errorCode: "CK-1053",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    // On callable failure, treat as not-allowed so we don't accidentally
    // open a window for non-whitelisted signups (rules will block anyway).
    return false;
  }
};

// ── Custom OTP fallback (used when Firebase Phone Auth SMS is blocked) ─────────
// Primary auth stays Firebase Phone Auth; these are only invoked when Firebase
// returns auth/too-many-requests or the internal error-code:-39. The server sends
// the OTP over ActiveTrail and (on verify) returns a custom token so the user
// still lands in Firebase Auth. mode: "register" | "login".

// Request a custom-OTP SMS. Returns the callable's data, e.g. { success, error }.
export const sendCustomOtp = async (phoneNumber, mode) => {
  const fn = httpsCallable(functions, "sendCustomOtp");
  const result = await fn({ phone: phoneNumber, mode });
  return result.data;
};

// Verify a custom OTP. Returns { success, customToken?, error? }.
export const verifyCustomOtp = async (phoneNumber, code, mode) => {
  const fn = httpsCallable(functions, "verifyCustomOtp");
  const result = await fn({ phone: phoneNumber, code, mode });
  return result.data;
};

// ============================================
// TRIAL EXPIRATION MANAGEMENT
// ============================================

// Custom error class for trial expiration
export class TrialExpiredError extends Error {
  constructor(message = "תקופת הניסיון הסתיימה. פנה למשרד להמשך השימוש.") {
    super(message);
    this.name = "TrialExpiredError";
    this.code = "trial-expired";
  }
}

// Custom error class for trial write operations limit
export class TrialWriteOpsLimitError extends Error {
  constructor(message = "הגעת למגבלת הפעולות בתקופת הניסיון. שדרגו את התוכנית כדי להמשיך לעבוד במערכת.") {
    super(message);
    this.name = "TrialWriteOpsLimitError";
    this.code = "trial-write-ops-limit";
  }
}

// Custom error class for clinics deactivated by the system admin (NiV)
export class ClinicInactiveError extends Error {
  constructor(message = "הקליניקה אינה פעילה. אנא צרו קשר עם המשרד להמשך השימוש במערכת.") {
    super(message);
    this.name = "ClinicInactiveError";
    this.code = "clinic-inactive";
  }
}

const TRIAL_WRITE_OPS_LIMIT = 3000;

// Cache for clinic trial status to avoid repeated DB calls
// Key: clinicId, Value: { isExpired: boolean, checkedAt: number }
const trialStatusCache = new Map();
const TRIAL_CACHE_TTL = 60000; // 1 minute cache

/**
 * Check if a clinic's trial has expired
 * @param {string} clinicId - The clinic ID to check
 * @returns {Promise<boolean>} - True if trial is expired
 */
export const checkTrialExpired = async (clinicId) => {
  if (!clinicId) return false;

  // Check cache first
  const cached = trialStatusCache.get(clinicId);
  if (cached && Date.now() - cached.checkedAt < TRIAL_CACHE_TTL) {
    return cached.isExpired;
  }

  try {
    const clinicRef = doc(db, "clinics", clinicId);
    const clinicSnap = await getDoc(clinicRef);

    if (!clinicSnap.exists()) {
      return false;
    }

    const clinicData = clinicSnap.data();

    // If not on trial plan, not expired
    if (clinicData.plan !== "trial") {
      trialStatusCache.set(clinicId, {
        isExpired: false,
        checkedAt: Date.now(),
      });
      return false;
    }

    // Check trial end date
    const trialEndDate = clinicData.trialEndDate;
    if (!trialEndDate) {
      return false;
    }

    const endDate = trialEndDate.toDate
      ? trialEndDate.toDate()
      : new Date(trialEndDate);
    const now = new Date();
    const isExpired = now > endDate;

    // Cache the result
    trialStatusCache.set(clinicId, { isExpired, checkedAt: Date.now() });

    return isExpired;
  } catch (error) {
    console.error("[CK-8100] Error checking trial status:", {
      errorCode: "CK-8100",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    return false; // Fail open - don't block on error
  }
};

/**
 * Guard function to block write operations if trial is expired or write ops limit reached.
 * Call this at the start of any write operation.
 *
 * For trial clinics, also pre-increments the _trialWriteOps counter (fire-and-forget).
 * Pre-increment is acceptable because the 3000 limit is generous and a few wasted
 * counts from failed writes don't affect billing.
 *
 * @param {string} clinicId - The clinic ID to check
 * @throws {ClinicInactiveError} - If the clinic was deactivated by the system admin
 * @throws {TrialExpiredError} - If trial is expired
 * @throws {TrialWriteOpsLimitError} - If trial write ops limit is reached
 */
export const guardTrialWrite = async (clinicId) => {
  if (!clinicId) return;

  try {
    // Single read for both trial expiration and write ops checks
    const clinicRef = doc(db, "clinics", clinicId);
    const clinicSnap = await getDoc(clinicRef);
    if (!clinicSnap.exists()) return;

    const clinicData = clinicSnap.data();

    // Clinic deactivated by system admin — block all writes regardless of plan
    if (clinicData.inactiveByAdmin === true) {
      throw new ClinicInactiveError();
    }

    // Not on trial — no limits apply
    if (clinicData.plan !== "trial") return;

    // Check trial expiration
    const trialEndDate = clinicData.trialEndDate;
    if (trialEndDate) {
      const endDate = trialEndDate.toDate
        ? trialEndDate.toDate()
        : new Date(trialEndDate);
      if (new Date() > endDate) {
        throw new TrialExpiredError();
      }
    }

    // Check write ops limit
    const currentOps = clinicData._trialWriteOps || 0;
    if (currentOps >= TRIAL_WRITE_OPS_LIMIT) {
      throw new TrialWriteOpsLimitError();
    }

    // Pre-increment (fire-and-forget)
    incrementTrialWriteOps(clinicId);
  } catch (error) {
    if (
      error instanceof TrialExpiredError ||
      error instanceof TrialWriteOpsLimitError ||
      error instanceof ClinicInactiveError
    ) {
      throw error;
    }
    // Don't block writes if the check itself fails
    console.error("[CK-8111] Error in guardTrialWrite:", error.message);
  }
};

/**
 * Increment the trial write operations counter on the clinic document.
 * Fire-and-forget — errors are logged but never thrown.
 * Only increments if the clinic is on the trial plan (checked by caller).
 * @param {string} clinicId - The clinic ID
 */
export const incrementTrialWriteOps = async (clinicId) => {
  if (!clinicId) return;
  try {
    const clinicRef = doc(db, "clinics", clinicId);
    await updateDoc(clinicRef, {
      _trialWriteOps: increment(1),
    });
  } catch (error) {
    console.error("[CK-8110] Error incrementing trial write ops:", {
      errorCode: "CK-8110",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
  }
};

/**
 * Clear the trial status cache for a specific clinic
 * Call this when plan is updated
 * @param {string} clinicId - The clinic ID to clear
 */
export const clearTrialCache = (clinicId) => {
  if (clinicId) {
    trialStatusCache.delete(clinicId);
  } else {
    trialStatusCache.clear();
  }
};

// ============================================
// CLINIC & THERAPIST FUNCTIONS
// ============================================

// Helper function to upload logo to storage (now for clinics)
export const uploadClinicLogo = async (file, clinicId) => {
  const storageRef = ref(storage, `clinics/${clinicId}/logo/${file.name}`);
  const snapshot = await uploadBytes(storageRef, file);
  const downloadURL = await getDownloadURL(snapshot.ref);
  return downloadURL;
};

// Create a new clinic and therapist (during registration)
export const createClinicAndTherapist = async (uid, userData) => {
  const now = Timestamp.now();
  const trialEndDate = new Date();
  trialEndDate.setDate(trialEndDate.getDate() + 30); // 30-day trial period

  // Parse full name into first and last name
  const nameParts = (userData.fullName || "").trim().split(" ");
  const firstName = nameParts[0] || "";
  const lastName = nameParts.slice(1).join(" ") || "";

  // 1. Create clinic document first
  const clinicsRef = collection(db, "clinics");
  const newClinicRef = doc(clinicsRef);

  const clinicDoc = {
    id: newClinicRef.id,
    name: userData.clinicName,
    logoUrl: userData.logoUrl || null,
    type: userData.clinicType,
    customTypeName: userData.customTypeName || null,
    specializations: userData.specializations || [],
    subSpecializations: userData.subSpecializations || [],
    ageRange: userData.ageRange || [],
    ownerTherapistId: uid,
    // Hard-coded defaults for new clinics
    plan: "trial",
    // smsTokens is deprecated - SMS quota is now determined by plan's smsPerMonth limit via monthlyUsage
    // Storage limits (in MB)
    allowedStorageInMbMaxPerClinic: 1000,
    usedStorageInMb: 0,
    trialStartDate: now,
    trialEndDate: Timestamp.fromDate(trialEndDate),
    isActive: true,
    isSoloClinic: true, // Default to solo clinic, changes when adding more therapists
    therapists: [], // Array of therapist references for easy access
    // Business settings
    defaultHourlyRate: userData.defaultHourlyRate
      ? Number(userData.defaultHourlyRate)
      : null,
    defaultMeetingTime: userData.defaultMeetingTime || 45,
    businessId: userData.businessId || null,
    licenseNumber: userData.licenseNumber || null,
    clinicAddress: userData.clinicAddress || null,
    vatType: userData.vatType || null, // 'exempt' | 'licensed' | 'company'
    // Work preferences chosen at signup (same opt-in flags as Settings → התאמה אישית)
    clinicManagesCouplesAsPatients: userData.clinicManagesCouplesAsPatients === true,
    tempCustomersEnabled: userData.tempCustomersEnabled === true,
    onlineMeetingsEnabled: userData.onlineMeetingsEnabled === true,
    // First hour shown in the Day/Week calendar grid (1–20, default 05:00; editable in Settings)
    calendarStartHour: normalizeCalendarStartHour(userData.calendarStartHour),
    // Morning (Green Invoice) Integration
    isMorningPaymentSystemIntegrated: false,
    morningIntegration: null, // Will be populated when integration is set up
    // Digital Invoice (חשבונית דיגיטלית) Integration
    isDigitalInvoiceIntegrated: false,
    digitalInvoiceIntegration: null,
    // Trial write operations counter (enforced only for trial plan)
    _trialWriteOps: 0,
    // Onboarding
    onboardingCallDate: userData.onboardingCallDate || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(newClinicRef, clinicDoc);

  // 2. Create therapist document with reference to clinic
  const therapistRef = doc(db, "therapists", uid);

  const therapistDoc = {
    uid,
    fullName: userData.fullName,
    firstName,
    lastName,
    email: userData.email?.trim().toLowerCase() || '',
    phone: userData.phone,
    gender: userData.gender || null, // 'male' | 'female' | 'other'
    licenseNumber: userData.licenseNumber || null,
    clinicId: newClinicRef.id,
    // Denormalized so the onTherapistCreatedNotifyAdmin trigger has name + clinic
    // without a second read.
    clinicName: userData.clinicName || null,
    // Which OTP path completed signup: "firebase_otp" | "custom_otp".
    registrationMethod: userData.registrationMethod || "firebase_otp",
    role: "owner", // Creator is always the owner
    isAdmin: true, // Creator is always admin
    isActive: true,
    smsSentCount: 0, // Track SMS messages sent by this therapist
    registrationDate: now,
    lastLoginDate: now,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(therapistRef, therapistDoc);

  // 3. Create welcome notification for the new user
  try {
    const notificationsRef = collection(
      db,
      "clinics",
      newClinicRef.id,
      "notifications"
    );
    const welcomeNotificationRef = doc(notificationsRef);

    const welcomeNotification = {
      id: welcomeNotificationRef.id,
      clinicId: newClinicRef.id,
      therapistId: uid,
      title: "ברוך הבא ל-CliniKit!",
      message:
        "זוהי התראת ניסיון - המערכת מוכנה לשימוש. התחל בהוספת מטופלים וקביעת פגישות.",
      type: "system",
      urgency: 2, // NOTIFICATION_URGENCY.MEDIUM
      link: null,
      patientId: null,
      createdBy: "system",
      readBy: [],
      timestamp: new Date().toISOString(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(welcomeNotificationRef, welcomeNotification);
  } catch (notificationError) {
    // Don't fail registration if notification creation fails
    console.error(
      "[CK-7050] Error creating welcome notification:",
      notificationError.message
    );
  }

  // 4. Log notifications for custom sub-specializations (if any)
  try {
    const { getPredefinedSubValues, getClinicTypeLabel } = await import("./data/clinicTypesConfig");
    const predefined = getPredefinedSubValues(userData.clinicType);
    const customSubs = (userData.subSpecializations || []).filter(
      (s) => !predefined.has(s)
    );

    for (const customValue of customSubs) {
      await logCustomSubSpecializationNotification({
        clinicId: newClinicRef.id,
        clinicName: userData.clinicName,
        clinicType: userData.clinicType,
        clinicTypeLabel:
          userData.clinicType === "other"
            ? userData.customTypeName || "אחר"
            : getClinicTypeLabel(userData.clinicType),
        therapistName: userData.fullName,
        therapistEmail: userData.email || "",
        customValue,
      });
    }
  } catch (customSubError) {
    console.error(
      "[CK-7051] Error logging custom sub-specialization notifications:",
      customSubError.message
    );
  }

  // Return serializable versions (replace serverTimestamp with actual values for Redux)
  const nowIso = new Date().toISOString();
  return {
    therapist: {
      ...therapistDoc,
      createdAt: nowIso,
      updatedAt: nowIso,
    },
    clinic: {
      ...clinicDoc,
      createdAt: nowIso,
      updatedAt: nowIso,
    },
  };
};

/**
 * Writes a notification to niv_system_notifications when a therapist
 * adds a custom sub-specialization via the "אחר" feature.
 */
export const logCustomSubSpecializationNotification = async ({
  clinicId,
  clinicName,
  clinicType,
  clinicTypeLabel,
  therapistName,
  therapistEmail,
  customValue,
}) => {
  const notifRef = doc(collection(db, "niv_system_notifications"));
  await setDoc(notifRef, {
    id: notifRef.id,
    type: "custom_sub_specialization",
    clinicId,
    clinicName: clinicName || "",
    clinicType,
    clinicTypeLabel: clinicTypeLabel || clinicType,
    therapistName: therapistName || "",
    therapistEmail: therapistEmail || "",
    customValue,
    message: `המטפל/ת ${therapistName || "לא ידוע"} מקליניקת "${clinicName || "לא ידוע"}" (${clinicTypeLabel || clinicType}) הוסיף/ה תת-התמחות מותאמת: "${customValue}"`,
    createdAt: serverTimestamp(),
  });
};

// Create a new therapist under an existing clinic (admin only)
// uid: The real Firebase Auth UID from phone verification
export const createTherapistUnderClinic = async (
  clinicId,
  uid,
  therapistData
) => {
  const now = Timestamp.now();

  // Use the real UID from phone verification
  const therapistRef = doc(db, "therapists", uid);

  const therapistDoc = {
    uid: uid,
    firstName: therapistData.firstName,
    lastName: therapistData.lastName,
    email: therapistData.email,
    phone: therapistData.phone, // Already in international format from PhoneInput
    clinicId: clinicId,
    role: "therapist", // Not owner/admin
    isAdmin: false,
    isActive: true,
    smsSentCount: 0, // Track SMS messages sent by this therapist
    registrationDate: now,
    lastLoginDate: now,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(therapistRef, therapistDoc);

  // Update clinic's therapists array
  const clinicRef = doc(db, "clinics", clinicId);
  const clinicSnap = await getDoc(clinicRef);

  if (clinicSnap.exists()) {
    const clinicData = clinicSnap.data();
    const therapists = clinicData.therapists || [];

    await updateDoc(clinicRef, {
      therapists: [
        ...therapists,
        {
          id: uid,
          firstName: therapistData.firstName,
          lastName: therapistData.lastName,
          email: therapistData.email,
          isActive: true,
        },
      ],
      isSoloClinic: false, // No longer solo when adding therapists
      updatedAt: serverTimestamp(),
    });
  }

  return serializeForRedux(therapistDoc);
};

// Get therapist document
export const getTherapistDocument = async (uid) => {
  const therapistRef = doc(db, "therapists", uid);
  const therapistSnap = await getDoc(therapistRef);

  if (therapistSnap.exists()) {
    // Serialize Timestamps to ISO strings for Redux compatibility
    return serializeForRedux(therapistSnap.data());
  }
  return null;
};

// Get clinic document
export const getClinicDocument = async (clinicId) => {
  const clinicRef = doc(db, "clinics", clinicId);
  const clinicSnap = await getDoc(clinicRef);

  if (clinicSnap.exists()) {
    // Serialize Timestamps to ISO strings for Redux compatibility
    return serializeForRedux(clinicSnap.data());
  }
  return null;
};

// Subscribe to live clinic document changes (plan / trial / inactiveByAdmin etc.).
// Returns the unsubscribe function. Payload is Redux-serialized like getClinicDocument.
export const subscribeToClinicDocument = (clinicId, callback) => {
  const clinicRef = doc(db, "clinics", clinicId);
  return onSnapshot(
    clinicRef,
    (clinicSnap) => {
      if (clinicSnap.exists()) {
        callback(serializeForRedux(clinicSnap.data()));
      }
    },
    (error) => {
      console.error("[CK-8112] Error in clinic document listener:", {
        errorCode: "CK-8112",
        errorMessage: error.message,
        clinicId,
        timestamp: new Date().toISOString(),
      });
    }
  );
};

// Subscribe to live therapist document changes (role / permissions / isActive
// — multi-clinic v2 §1.6: no forced logout on permission change). Payload is
// Redux-serialized like getTherapistDocument.
export const subscribeToTherapistDocument = (uid, callback) => {
  const ref = doc(db, "therapists", uid);
  return onSnapshot(
    ref,
    (snap) => {
      if (snap.exists()) {
        callback(serializeForRedux({ id: snap.id, ...snap.data() }));
      }
    },
    (error) => {
      console.error("[CK-8546] Error in therapist document listener:", {
        errorCode: "CK-8546",
        errorMessage: error.message,
        timestamp: new Date().toISOString(),
      });
    }
  );
};

// Get therapist with clinic data (combined fetch)
export const getTherapistWithClinic = async (uid) => {
  const therapist = await getTherapistDocument(uid);
  if (!therapist) return null;

  const clinic = await getClinicDocument(therapist.clinicId);

  return {
    therapist,
    clinic,
  };
};

// Update therapist document
export const updateTherapistDocument = async (uid, data) => {
  const therapistRef = doc(db, "therapists", uid);
  await updateDoc(therapistRef, {
    ...data,
    updatedAt: serverTimestamp(),
  });
};

// ============================================
// SYSTEM MESSAGES (NiV broadcasts -> entry popups)
// ============================================

// Fetch all active system messages (any authenticated user).
// Sorted client-side by createdAt asc (oldest first) to avoid a composite index.
export const getActiveSystemMessages = async () => {
  try {
    const messagesRef = collection(db, "system_messages");
    const q = query(messagesRef, where("isActive", "==", true));
    const snapshot = await getDocs(q);

    const messages = snapshot.docs.map((d) =>
      serializeForRedux({ id: d.id, ...d.data() })
    );

    messages.sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return aTime - bTime;
    });

    return messages;
  } catch (error) {
    console.error("[CK-8930] Error fetching active system messages:", {
      errorCode: "CK-8930",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// Mark a system message as acknowledged by the current therapist.
// Uses arrayUnion so concurrent acks never clobber prior IDs.
export const acknowledgeSystemMessage = async (uid, messageId) => {
  try {
    const therapistRef = doc(db, "therapists", uid);
    await updateDoc(therapistRef, {
      acknowledgedSystemMessages: arrayUnion(messageId),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("[CK-8931] Error acknowledging system message:", {
      errorCode: "CK-8931",
      errorMessage: error.message,
      messageId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// Update clinic document
export const updateClinicDocument = async (clinicId, data) => {
  const clinicRef = doc(db, "clinics", clinicId);
  await updateDoc(clinicRef, {
    ...data,
    updatedAt: serverTimestamp(),
  });
};

// ============================================
// PATIENT CRUD FUNCTIONS (under clinics)
// ============================================

// Helper function to calculate age from birth date
const calculatePatientAge = (birthDate) => {
  if (!birthDate) return null;
  const birth = new Date(birthDate);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

// Helper function to format gender to Hebrew
const formatPatientGender = (gender) => {
  switch (gender) {
    case "male":
      return "זכר";
    case "female":
      return "נקבה";
    case "other":
      return "אחר";
    default:
      return "לא צוין";
  }
};

// Create a new patient
/** The four macro goal keys a client's daily targets consist of. */
const NUTRITION_GOAL_KEYS = ["calories", "protein", "carbohydrates", "fat"];

/**
 * Does this goals map hold at least one real daily target? Drives the
 * `nutrition.goalsSet` flag the pending queue filters on, and the
 * "לא הוגדרו יעדים" banner on the workspace.
 */
export const hasNutritionGoals = (goals) =>
  !!goals && NUTRITION_GOAL_KEYS.some((key) => Number(goals[key]) > 0);

/** Client-facing switches every new nutrition client starts with ON. */
const NUTRITION_TOGGLE_DEFAULTS = {
  selfLoggingEnabled: true,
  aiEstimateEnabled: true,
  selfMeasurementsEnabled: true,
  showGoalsToClient: true,
};

/**
 * Defaults + the derived `goalsSet` flag, applied on CREATE only. Existing
 * patient docs are never rewritten: a missing switch still reads as before.
 */
const normalizeNutritionForCreate = (nutrition) => ({
  ...NUTRITION_TOGGLE_DEFAULTS,
  ...nutrition,
  goalsSet: hasNutritionGoals(nutrition?.goals),
});

export const createPatient = async (clinicId, patientData, therapistId, { enableAiPatientSummary = false } = {}) => {
  // Validate required parameters
  if (!clinicId) {
    throw new Error("Missing clinicId - user profile may not be loaded");
  }
  if (!therapistId) {
    throw new Error("Missing therapistId - user profile may not be loaded");
  }

  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  let patientSummary = null;

  if (enableAiPatientSummary) {
    const sanitizedPatientData = {
      age: calculatePatientAge(patientData.birthDate),
      gender: formatPatientGender(patientData.gender),
      languages: patientData.languages || [],
      referralReason: patientData.referralReason || null,
      diagnoses: patientData.diagnoses || null,
      allergies: patientData.allergies || null,
      medications: patientData.medications || null,
      previousTreatments: patientData.previousTreatments || null,
      clinicalNotes: patientData.clinicalNotes || null,
      educationalFramework: patientData.educationalFramework || null,
    };

    try {
      const summaryResult = await generateInitialPatientSummary({
        clinicId,
        patientData: sanitizedPatientData,
      });

      if (summaryResult.success && summaryResult.summary) {
        patientSummary = summaryResult.summary;
      } else {
        throw new Error(
          summaryResult.error || "שגיאה ביצירת סיכום מטופל. נסה שוב."
        );
      }
    } catch (error) {
      console.error("[CK-7000] Failed to generate patient summary:", error);
      throw new Error("שגיאה ביצירת סיכום מטופל. נסה שוב.");
    }
  }

  const patientsRef = collection(db, "clinics", clinicId, "patients");
  const newPatientRef = doc(patientsRef);
  const now = Timestamp.now();

  // Couple patient: denormalize combined name + primary partner's phone/ID
  // into the legacy fields so every existing consumer (display, search, SMS,
  // public verification) keeps working unchanged.
  const isCouple = patientData.isCouple === true;
  const coupleFields = isCouple
    ? buildCoupleDenormalizedFields(patientData)
    : null;

  const patientDoc = {
    id: newPatientRef.id,

    // Therapist association (REQUIRED - patient belongs to therapist who created them)
    therapistId: therapistId,
    therapistIds: [therapistId],

    // Basic (Required)
    firstName: coupleFields ? coupleFields.firstName : patientData.firstName,
    lastName: coupleFields ? coupleFields.lastName : patientData.lastName,
    phone: coupleFields ? coupleFields.phone : patientData.phone,
    referralReason: patientData.referralReason,

    // Couple (partner data; the basic fields above are denormalized from it)
    isCouple,
    partner1: isCouple ? patientData.partner1 || null : null,
    partner2: isCouple ? patientData.partner2 || null : null,
    smsPrimaryPartner: isCouple
      ? patientData.smsPrimaryPartner === 2
        ? 2
        : 1
      : null,

    // Personal Details
    idNumber: coupleFields
      ? coupleFields.idNumber
      : patientData.idNumber || null,
    birthDate: patientData.birthDate
      ? Timestamp.fromDate(new Date(patientData.birthDate))
      : null,
    gender: patientData.gender || null,
    languages: patientData.languages || [],

    // Contact
    email: patientData.email || null,
    address: patientData.address || null,

    // Parents/Guardians
    parent1: patientData.parent1 || null,
    parent2: patientData.parent2 || null,
    familyNotes: patientData.familyNotes || null,

    // Clinical
    diagnoses: patientData.diagnoses || null,
    healthFund: patientData.healthFund || null,
    referringDoctor: patientData.referringDoctor || null,
    allergies: patientData.allergies || null,
    medications: patientData.medications || null,
    previousTreatments: patientData.previousTreatments || null,
    educationalFramework: patientData.educationalFramework || null,

    // Admin
    status: patientData.status || "active",
    // Visibility/archive flag (orthogonal to clinical `status`).
    // Missing field on legacy docs is treated as active everywhere via `isActive !== false`.
    isActive: patientData.isActive !== false,
    patientTypeIds: patientData.patientTypeIds || [], // Array of patient type IDs
    joinDate: patientData.joinDate
      ? Timestamp.fromDate(new Date(patientData.joinDate))
      : now,
    referralSource: patientData.referralSource || null,
    treatmentRate: patientData.treatmentRate || null,
    treatmentDuration: patientData.treatmentDuration || null,
    treatmentFrequency: patientData.treatmentFrequency || null,
    defaultTreatmentTypeId: patientData.defaultTreatmentTypeId || null,
    preferredSchedule: patientData.preferredSchedule || null,

    // Language used for this patient's SMS and confirmation page. Only ever
    // "en" when the clinic opted in to English-speaking patients; the write is
    // normalized here so an unexpected value can never reach the doc.
    communicationLanguage:
      patientData.communicationLanguage === "en" ? "en" : "he",

    // Emergency
    emergencyContact: patientData.emergencyContact || null,

    // Notes
    adminNotes: patientData.adminNotes || null,
    clinicalNotes: patientData.clinicalNotes || null,
    confidentialityWaiver: patientData.confidentialityWaiver || false,

    // Nutrition clinics only. Spread conditionally so the field is simply
    // ABSENT on every other clinic's patients rather than a null that later
    // reads would have to defend against. The client-facing switches default
    // ON here — not only in the wizard's local state — so a client created
    // by any path gets logging, AI breakdown, self-measurements and goals.
    ...(patientData.nutrition
      ? { nutrition: normalizeNutritionForCreate(patientData.nutrition) }
      : {}),

    // AI Patient Summary
    patientSummary: patientSummary,
    ...(patientSummary ? { patientSummaryUpdatedAt: serverTimestamp() } : {}),

    // Meta
    isDraft: patientData.isDraft || false,
    lastAppointmentDate: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(newPatientRef, patientDoc);

  // Fetch and return the properly serialized patient data
  // (serverTimestamp() gets resolved on the server, so we need to fetch to get actual values)
  return getPatient(clinicId, newPatientRef.id);
};

// Get all patients for a clinic with optional filters
// If therapistId is provided, filter by therapist (for regular therapists)
// If therapistId is null/undefined, return all patients (for admin view)
// Uses dual-query for multi-therapist support: therapistIds array-contains + legacy therapistId fallback
export const getPatients = async (clinicId, options = {}) => {
  const {
    search,
    status,
    cursor,
    pageSize = 10,
    therapistId,
    // includeInactive: when false (default), patients with `isActive === false` are
    // hidden. Filtering happens in JS after fetch so legacy docs missing the field
    // (treated as active) are not silently dropped. A page may yield <pageSize rows
    // if it contained inactive patients.
    includeInactive = false,
  } = options;

  const patientsRef = collection(db, "clinics", clinicId, "patients");

  const buildConstraints = (therapistFilter) => {
    const c = [];
    if (therapistFilter) c.push(therapistFilter);
    if (status && status !== "all") c.push(where("status", "==", status));
    c.push(where("isDraft", "==", false));
    c.push(orderBy("createdAt", "desc"));
    if (cursor) {
      c.push(startAfter(Timestamp.fromDate(new Date(cursor))));
    }
    c.push(limit(pageSize));
    return c;
  };

  let allDocs = [];

  if (therapistId) {
    // Primary query: patients with therapistIds array containing this therapist
    const arrayQuery = query(
      patientsRef,
      ...buildConstraints(where("therapistIds", "array-contains", therapistId))
    );
    const arraySnapshot = await getDocs(arrayQuery);
    const arrayDocs = arraySnapshot.docs;

    // Fallback query: legacy patients without therapistIds field (using old therapistId field)
    const legacyQuery = query(
      patientsRef,
      ...buildConstraints(where("therapistId", "==", therapistId))
    );
    const legacySnapshot = await getDocs(legacyQuery);
    const legacyDocs = legacySnapshot.docs;

    // Merge and deduplicate
    const seenIds = new Set();
    for (const d of arrayDocs) {
      seenIds.add(d.id);
      allDocs.push(d);
    }
    for (const d of legacyDocs) {
      if (!seenIds.has(d.id)) {
        allDocs.push(d);
      }
    }

    // Re-sort merged results by createdAt desc
    allDocs.sort((a, b) => {
      const aTime = a.data().createdAt?.toMillis?.() || 0;
      const bTime = b.data().createdAt?.toMillis?.() || 0;
      return bTime - aTime;
    });

    // Re-apply page limit after merge
    allDocs = allDocs.slice(0, pageSize);
  } else {
    // Admin view: all patients
    const q = query(patientsRef, ...buildConstraints(null));
    const snapshot = await getDocs(q);
    allDocs = snapshot.docs;
  }

  // Serialize all Timestamps to ISO strings for Redux compatibility
  let patients = allDocs.map((d) =>
    serializeForRedux({ id: d.id, ...d.data() })
  );

  // Get the last document's createdAt for cursor-based pagination
  const lastDocData = allDocs[allDocs.length - 1]?.data();
  const nextCursor = lastDocData?.createdAt
    ? lastDocData.createdAt.toDate().toISOString()
    : null;

  // Client-side search (Firestore doesn't support full-text search)
  if (search) {
    const searchLower = search.toLowerCase();
    patients = patients.filter(
      (p) =>
        p.firstName?.toLowerCase().includes(searchLower) ||
        p.lastName?.toLowerCase().includes(searchLower) ||
        phoneMatchesQuery(p.phone, search) ||
        // Couple patients: the non-primary partner's phone isn't in the
        // denormalized `phone` field — match partner phones too.
        (p.isCouple === true &&
          (phoneMatchesQuery(p.partner1?.phone, search) ||
            phoneMatchesQuery(p.partner2?.phone, search)))
    );
  }

  if (!includeInactive) {
    patients = patients.filter((p) => p.isActive !== false);
  }

  return {
    patients,
    nextCursor,
    hasMore: allDocs.length === pageSize,
  };
};

// Get a single patient
export const getPatient = async (clinicId, patientId) => {
  const patientRef = doc(db, "clinics", clinicId, "patients", patientId);
  const patientSnap = await getDoc(patientRef);

  if (patientSnap.exists()) {
    // Serialize all Timestamps to ISO strings for Redux compatibility
    return serializeForRedux({ id: patientSnap.id, ...patientSnap.data() });
  }
  return null;
};

// Update a patient
export const updatePatient = async (clinicId, patientId, patientData) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  const patientRef = doc(db, "clinics", clinicId, "patients", patientId);

  // Remove metadata fields that should not be updated
  // (especially createdAt which would change from Timestamp to string and break queries)
  const {
    id, // Don't update the ID
    createdAt, // Don't update creation date - keeps it as Timestamp
    updatedAt, // Don't update - will be set with serverTimestamp()
    ...updateData
  } = patientData;

  // Ensure isDraft is always a boolean (not null)
  if (updateData.isDraft === null || updateData.isDraft === undefined) {
    updateData.isDraft = false;
  }

  // Nutrition map: keep `goalsSet` in step with the goals so the "clients
  // without goals" queue clears the moment targets are saved, whichever
  // screen saved them. Only when the map itself is being written.
  if (updateData.nutrition && typeof updateData.nutrition === "object") {
    updateData.nutrition = {
      ...updateData.nutrition,
      goalsSet: hasNutritionGoals(updateData.nutrition.goals),
    };
  }

  // Couple patient: recompute the denormalized legacy fields from partner data.
  // Guarded on full partner payload so partial updates (patientSummary,
  // lastAppointmentDate, isActive, etc.) never touch couple fields.
  if (
    updateData.isCouple === true &&
    updateData.partner1 &&
    updateData.partner2
  ) {
    Object.assign(updateData, buildCoupleDenormalizedFields(updateData));
    updateData.smsPrimaryPartner = updateData.smsPrimaryPartner === 2 ? 2 : 1;
  }

  // Convert dates to Timestamps
  if (updateData.birthDate) {
    updateData.birthDate = Timestamp.fromDate(new Date(updateData.birthDate));
  }
  if (updateData.joinDate) {
    updateData.joinDate = Timestamp.fromDate(new Date(updateData.joinDate));
  }
  // Convert lastAppointmentDate if it's a string
  if (
    updateData.lastAppointmentDate &&
    typeof updateData.lastAppointmentDate === "string"
  ) {
    updateData.lastAppointmentDate = Timestamp.fromDate(
      new Date(updateData.lastAppointmentDate)
    );
  }

  // Detect a rename so denormalized `patientName` copies (appointments,
  // transactions, treatments, links…) can be refreshed server-side.
  // Compared after couple denormalization so partner edits count too.
  let nameChanged = false;
  if (updateData.firstName !== undefined || updateData.lastName !== undefined) {
    try {
      const existingSnap = await getDoc(patientRef);
      const existing = existingSnap.exists() ? existingSnap.data() : {};
      const oldName = buildPatientDisplayName(existing);
      const newName = buildPatientDisplayName({
        firstName:
          updateData.firstName !== undefined
            ? updateData.firstName
            : existing.firstName,
        lastName:
          updateData.lastName !== undefined
            ? updateData.lastName
            : existing.lastName,
      });
      nameChanged = Boolean(newName) && oldName !== newName;
    } catch (error) {
      console.error("[CK-8945] Failed to read patient before rename check:", {
        errorCode: "CK-8945",
        errorMessage: error.message,
        clinicId,
        patientId,
        timestamp: new Date().toISOString(),
      });
    }
  }

  await updateDoc(patientRef, {
    ...updateData,
    updatedAt: serverTimestamp(),
  });

  if (nameChanged) {
    // Rename already persisted — propagation failure must not fail the save.
    try {
      await propagatePatientName({ clinicId, patientId });
    } catch (error) {
      console.error("[CK-8944] Failed to propagate patient name change:", {
        errorCode: "CK-8944",
        errorMessage: error.message,
        clinicId,
        patientId,
        timestamp: new Date().toISOString(),
      });
    }
  }

  return getPatient(clinicId, patientId);
};

// Display name used for denormalized `patientName` copies (mirrors server helper)
export const buildPatientDisplayName = (patient) =>
  [patient?.firstName, patient?.lastName]
    .filter(Boolean)
    .map((s) => String(s).trim())
    .filter(Boolean)
    .join(" ");

// Fan a renamed patient's name out to appointments / transactions / treatments /
// links / punch cards (server-side, Admin SDK). Returns { success, updated }.
export const propagatePatientName = async ({ clinicId, patientId }) => {
  const fn = httpsCallable(functions, "propagatePatientName");
  const result = await fn({ clinicId, patientId });
  return result.data;
};

// Reassign a patient to a different therapist (admin only, via Cloud Function)
export const reassignPatientToTherapist = async ({ clinicId, patientId, newTherapistId, transferAppointments }) => {
  const reassignFn = httpsCallable(functions, "reassignPatientToTherapist");
  const result = await reassignFn({ clinicId, patientId, newTherapistId, transferAppointments });
  return result.data;
};

// Update patient therapist assignments (multi-therapist, admin only, via Cloud Function)
export const updatePatientTherapists = async ({ clinicId, patientId, therapistIds }) => {
  const fn = httpsCallable(functions, "updatePatientTherapists");
  const result = await fn({ clinicId, patientId, therapistIds });
  return result.data;
};

// Helper: get all therapist IDs for a patient (handles legacy patients without therapistIds)
export const getPatientTherapistIds = (patient) => {
  if (patient?.therapistIds && patient.therapistIds.length > 0) {
    return patient.therapistIds;
  }
  return patient?.therapistId ? [patient.therapistId] : [];
};

// Delete a patient
export const deletePatient = async (clinicId, patientId) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  const patientRef = doc(db, "clinics", clinicId, "patients", patientId);
  await deleteDoc(patientRef);
  return patientId;
};

// Toggle the visibility/archive flag on a patient (active <-> inactive).
// Orthogonal to clinical `status`. Used by PatientProfilePage deactivate/restore.
export const setPatientActive = async ({
  clinicId,
  patientId,
  isActive,
  therapistId,
  therapistName,
}) => {
  if (!clinicId || !patientId) {
    throw new Error("Missing clinicId or patientId");
  }

  const nextIsActive = isActive !== false;
  const action = nextIsActive ? "patient_reactivated" : "patient_deactivated";

  try {
    await guardTrialWrite(clinicId);

    const patientRef = doc(db, "clinics", clinicId, "patients", patientId);
    await updateDoc(patientRef, {
      isActive: nextIsActive,
      updatedAt: serverTimestamp(),
    });

    logActivity(clinicId, {
      therapistId: therapistId || null,
      therapistName: therapistName || null,
      action,
      entityType: "patient",
      entityId: patientId,
      success: true,
    });

    return getPatient(clinicId, patientId);
  } catch (error) {
    console.error("[CK-2020] Failed to set patient active state:", {
      errorCode: "CK-2020",
      errorMessage: error.message,
      clinicId,
      patientId,
      nextIsActive,
      timestamp: new Date().toISOString(),
    });
    logActivity(clinicId, {
      therapistId: therapistId || null,
      therapistName: therapistName || null,
      action,
      entityType: "patient",
      entityId: patientId,
      success: false,
      errorMessage: error.message,
    });
    throw error;
  }
};

// Get patient drafts
export const getPatientDrafts = async (clinicId) => {
  const patientsRef = collection(db, "clinics", clinicId, "patients");
  const q = query(patientsRef, where("isDraft", "==", true));
  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
};

// ============================================
// APPOINTMENT CRUD FUNCTIONS
// ============================================

// Create a new appointment
export const createAppointment = async (
  clinicId,
  appointmentData,
  therapistId,
  therapistName = null
) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  const appointmentsRef = collection(db, "clinics", clinicId, "appointments");
  const newAppointmentRef = doc(appointmentsRef);

  const appointmentDoc = {
    id: newAppointmentRef.id,

    // Unique appointment identifier for easy reference
    uniqueId: generateUniqueAppointmentId(),

    // Therapist association (REQUIRED - appointment belongs to therapist who created it)
    therapistId: therapistId,
    therapistName: therapistName || appointmentData.therapistName || null, // Denormalized for display

    patientId: appointmentData.patientId ?? null,
    patientName: appointmentData.patientName,

    // Temp customer (לקוח מזדמן) - one-off client without a patient record;
    // phone is kept on the appointment since there is no patient doc
    isTempCustomer: appointmentData.isTempCustomer === true,
    tempCustomerPhone: appointmentData.tempCustomerPhone || null,

    startTime: Timestamp.fromDate(new Date(appointmentData.startTime)),
    endTime: Timestamp.fromDate(new Date(appointmentData.endTime)),
    duration: appointmentData.duration || 45,
    notes: appointmentData.notes || null,

    // Payment - from patient's treatmentRate (fixed price per session)
    // If patient has no treatmentRate defined, this will be null
    paymentAmount: appointmentData.paymentAmount || null,

    // Punch card (כרטיסיה) - defaults; set by punchVisit() after creation
    isPunchCard: false,
    punchCardId: null,
    punchCardPrepaid: false,

    // Enhanced payment tracking (Finance Module)
    // paymentStatus: "pending" = awaiting payment, "paid" = fully paid, "partial" = partially paid,
    //                "skipped" = user chose to skip, "debt" = marked as debt for collection
    paymentStatus: "pending",
    actualPaidAmount: null, // Amount actually paid (may differ from paymentAmount)
    paymentDate: null, // When payment was received
    paymentMethod: null, // cash, credit, transfer, check, bit, etc.

    // Invoice integration (future)
    invoiceCreated: false,
    invoiceNumber: null,
    invoiceProvider: null, // morning, icount, greeninvoice, digital_invoice
    invoiceUrl: null,

    // WhatsApp notification statuses
    whatsappInitialSent: false,
    whatsappInitialConfirmed: null, // null = pending, true = confirmed
    whatsappInitialDeclined: null, // null = pending, true = declined
    whatsappReminderSent: false,
    whatsappReminderConfirmed: null,
    whatsappReminderDeclined: null,

    // SMS scheduling (custom send times from preview)
    initialSmsScheduledAt: appointmentData.initialSmsScheduledAt || null,
    initialSmsEnabled: appointmentData.initialSmsEnabled !== false,
    reminderSmsScheduledAt: appointmentData.reminderSmsScheduledAt || null,
    reminderSmsEnabled: appointmentData.reminderSmsEnabled !== false,
    reminderSmsGapOverride: appointmentData.reminderSmsGapOverride === true,
    initialSmsTaskName: null,

    // End-meeting (post-session) SMS — clinic-level feature; per-meeting opt-in.
    // Default follows the checkbox in the modal (which defaults to the clinic
    // setting). The Cloud Task is scheduled at completion time, not here.
    sendEndMeetingSms: appointmentData.sendEndMeetingSms === true,
    endMeetingSmsTaskName: null,
    endMeetingSmsSent: false,

    // ===== POST-COMPLETION WORKFLOW FLAGS =====
    // These are set to false on creation and managed when appointment is completed
    isTeraphistAskedToHide: false, // If true, appointment is hidden from carousel

    // Notify flags - null on creation, set to true when appointment completes (by Cloud Task)
    // When therapist completes a step, the flag becomes false
    NotifyAboutThisMeetingApproval: null,
    NotifyAboutThisMeetingSummarize: null,
    NotifyAboutThisMeetingPaymentStatus: null,

    // Value flags - track if therapist has completed each step
    isApprovedByTherapist: false,
    isTherapistSummarizedTheMeeting: false,
    isTherapistUpdatedPaymentStatus: false,

    // Receipt tracking
    isProducedReceipitForThisMeeting: false,
    RecepitsDocsRefArray: [], // Array of receipt document references

    // Treatment type
    treatmentTypeId: appointmentData.treatmentTypeId || null,
    treatmentTypeName: appointmentData.treatmentTypeName || null,

    // Room assignment (optional - for clinics with room management)
    roomId: appointmentData.roomId || null,

    // Scheduling-policy waivers (multi-clinic v2, audit only — see
    // ACTIVITY_ACTIONS.OVERRIDE_THERAPIST_CONFLICT / OVERRIDE_ABSENCE)
    overrideTherapistConflict: appointmentData.overrideTherapistConflict === true,
    therapistConflictOverride: appointmentData.therapistConflictOverride || null,
    overrideAbsence: appointmentData.overrideAbsence === true,
    absenceOverride: appointmentData.absenceOverride || null,

    // Online video meeting (100ms). videoMeetLinkId is the public join-link id
    // (clinikit.co.il/meet/{id}); the onAppointmentCreated trigger publishes
    // the server-side lookup doc. videoRoomId is filled lazily by the
    // token-issuing Cloud Function on first join.
    isOnlineMeeting: appointmentData.isOnlineMeeting === true,
    videoMeetLinkId: appointmentData.videoMeetLinkId || null,
    videoRoomId: null,

    // Google Calendar sync
    googleCalendarEventId: null,

    // Soft-delete / cancellation tracking
    isDeleted: false,
    deletedAt: null,
    deletedBy: null,
    cancelledAt: null,
    cancelledBy: null, // "therapist" | "patient" | null
    cancelledByTherapistId: null, // uid of the therapist/admin who cancelled
    cancellationReason: null, // optional free-text reason captured during the cancel flow
    cancellationFeeRecorded: false,

    // Admin-scheduling attribution — set when an admin books this appointment
    // for a *different* therapist. Read by onAppointmentCreated to push a
    // "scheduled for you" notification. null for self-scheduled appointments.
    scheduledByAdminId: appointmentData.scheduledByAdminId || null,
    scheduledByAdminName: appointmentData.scheduledByAdminName || null,
    // uid of the last person to edit this appointment (reschedule detection).
    lastActionByTherapistId: null,

    status: appointmentData.status || "scheduled", // scheduled, confirmed, cancelled, completed
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(newAppointmentRef, appointmentDoc);

  // Fetch and return the properly serialized appointment data
  // (serverTimestamp() gets resolved on the server, so we need to fetch to get actual values)
  return getAppointment(clinicId, newAppointmentRef.id);
};

// Get appointments for a date range
// If therapistId is provided, filter by therapist (for regular therapists)
// If therapistId is null/undefined, return all appointments (for admin view)
// If patientId is provided, filter by patient (for multi-therapist future appointments)
export const getAppointments = async (
  clinicId,
  startDate,
  endDate,
  therapistId = null,
  patientId = null
) => {
  const appointmentsRef = collection(db, "clinics", clinicId, "appointments");

  let constraints = [];

  // IMPORTANT: In Firestore, equality filters must come BEFORE range filters
  if (patientId) {
    constraints.push(where("patientId", "==", patientId));
  } else if (therapistId) {
    constraints.push(where("therapistId", "==", therapistId));
  }

  // Range filters on startTime
  constraints.push(where("startTime", ">=", Timestamp.fromDate(startDate)));
  constraints.push(where("startTime", "<=", Timestamp.fromDate(endDate)));

  // Order by startTime
  constraints.push(orderBy("startTime", "asc"));

  try {
    const q = query(appointmentsRef, ...constraints);
    const snapshot = await getDocs(q);

    // Serialize all Timestamps to ISO strings for Redux compatibility.
    // Soft-deleted appointments are filtered out so they don't appear anywhere
    // in the app (they remain in Firestore for archival only).
    return snapshot.docs
      .map((doc) => serializeForRedux({ id: doc.id, ...doc.data() }))
      .filter((apt) => apt.isDeleted !== true);
  } catch (error) {
    console.error("[CK-8010] Error fetching appointments:", {
      errorCode: "CK-8010",
      errorMessage: error.message,
      clinicId,
      therapistId,
      patientId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// Get a single appointment.
// Returns null if the appointment does not exist OR has been soft-deleted.
export const getAppointment = async (clinicId, appointmentId) => {
  const appointmentRef = doc(
    db,
    "clinics",
    clinicId,
    "appointments",
    appointmentId
  );
  const appointmentSnap = await getDoc(appointmentRef);

  if (appointmentSnap.exists()) {
    const data = appointmentSnap.data();
    if (data.isDeleted === true) return null;
    return serializeForRedux({ id: appointmentSnap.id, ...data });
  }
  return null;
};

/**
 * Last non-cancelled appointment of a patient — used by the appointment wizard
 * to default the next meeting's treatment type / duration from the previous one.
 * Fetches a few and filters cancelled/deleted client-side so no new composite
 * index beyond the existing patientId+startTime family is needed.
 *
 * Error Code: CK-8940
 */
export const getLastPatientAppointment = async (clinicId, patientId) => {
  try {
    const appointmentsRef = collection(db, "clinics", clinicId, "appointments");
    const q = query(
      appointmentsRef,
      where("patientId", "==", patientId),
      orderBy("startTime", "desc"),
      limit(5)
    );
    const snapshot = await getDocs(q);
    const match = snapshot.docs
      .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
      .find((apt) => apt.isDeleted !== true && apt.status !== "cancelled");
    return match ? serializeForRedux(match) : null;
  } catch (error) {
    console.error("[CK-8940] Error fetching last patient appointment:", {
      errorCode: "CK-8940",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// Update an appointment
export const updateAppointment = async (
  clinicId,
  appointmentId,
  appointmentData
) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  const appointmentRef = doc(
    db,
    "clinics",
    clinicId,
    "appointments",
    appointmentId
  );

  const updateData = { ...appointmentData };

  // Convert dates to Timestamps
  if (updateData.startTime) {
    updateData.startTime = Timestamp.fromDate(new Date(updateData.startTime));
  }
  if (updateData.endTime) {
    updateData.endTime = Timestamp.fromDate(new Date(updateData.endTime));
  }

  await updateDoc(appointmentRef, {
    ...updateData,
    updatedAt: serverTimestamp(),
  });

  return getAppointment(clinicId, appointmentId);
};

/**
 * Live subscription to a single appointment's SMS scheduling state.
 *
 * Needed because the SMS fields are written TWICE for one user action: the
 * client writes the new meeting time, and a moment later the
 * `onAppointmentUpdated` Cloud Function rewrites `initialSmsScheduledAt` /
 * `reminderSmsScheduledAt` and the Cloud Task names. A one-shot refetch right
 * after the client write still shows the pre-trigger values, which is why the
 * scheduled-send times used to update only after a page refresh.
 *
 * Only the SMS-related fields are surfaced — the rest of the appointment keeps
 * flowing through RTK Query as usual.
 *
 * @param {string} clinicId
 * @param {string} appointmentId
 * @param {(smsState: Object) => void} callback
 * @returns {() => void} unsubscribe (no-op when ids are missing)
 *
 * Error Code: CK-8937
 */
export const subscribeToAppointmentSmsState = (
  clinicId,
  appointmentId,
  callback
) => {
  if (!clinicId || !appointmentId || typeof callback !== "function") {
    return () => {};
  }

  const appointmentRef = doc(
    db,
    "clinics",
    clinicId,
    "appointments",
    appointmentId
  );

  return onSnapshot(
    appointmentRef,
    (snapshot) => {
      if (!snapshot.exists()) return;
      const data = snapshot.data();
      callback({
        initialSmsEnabled: data.initialSmsEnabled,
        initialSmsScheduledAt: data.initialSmsScheduledAt ?? null,
        initialSmsTaskName: data.initialSmsTaskName ?? null,
        reminderSmsEnabled: data.reminderSmsEnabled,
        reminderSmsScheduledAt: data.reminderSmsScheduledAt ?? null,
        reminderTaskName: data.reminderTaskName ?? null,
        whatsappInitialSent: data.whatsappInitialSent === true,
        whatsappReminderSent: data.whatsappReminderSent === true,
        whatsappInitialConfirmed: data.whatsappInitialConfirmed ?? null,
        whatsappInitialDeclined: data.whatsappInitialDeclined ?? null,
        whatsappReminderConfirmed: data.whatsappReminderConfirmed ?? null,
        whatsappReminderDeclined: data.whatsappReminderDeclined ?? null,
        // Terminal send outcome written by the Cloud Task handlers
        // (CK-9340/CK-9345) — why a scheduled message did/didn't go out.
        initialSmsOutcome: data.initialSmsOutcome ?? null,
        reminderSmsOutcome: data.reminderSmsOutcome ?? null,
        // Messaging router stamps (channel / fallback detail) — plain JSON,
        // written server-side only (whatsapp_integration.md §2.1 step 10).
        messagingInitial: data.messagingInitial ?? null,
        messagingReminder: data.messagingReminder ?? null,
        messagingReschedule: data.messagingReschedule ?? null,
        // Day-before reminder sent by the recurring cron (CK-9410) — a real
        // reminder the patient received, tracked separately from the
        // Cloud-Task path's whatsappReminderSent.
        recurring1DayReminderSent: data.recurring1DayReminderSent === true,
        // Manual "send reminder now" stamps (functions/manualReminderHelper.js,
        // CK-9355). Independent of the scheduled reminder's state above.
        manualReminderCount: data.manualReminderCount || 0,
        manualReminderLastSentAt: data.manualReminderLastSentAt ?? null,
        messagingManualReminder: data.messagingManualReminder ?? null,
      });
    },
    (error) => {
      console.error("[CK-8937] Error listening to appointment SMS state:", {
        errorCode: "CK-8937",
        errorMessage: error.message,
        clinicId,
        appointmentId,
        timestamp: new Date().toISOString(),
      });
    }
  );
};

/**
 * Cancel a queued (not yet sent) confirmation/reminder SMS for an appointment.
 *
 * The pending send is a Cloud Task, which the client cannot delete. Instead the
 * appointment-level flag is flipped off with the meeting time left untouched —
 * the `onAppointmentUpdated` trigger's late-disable path (CK-9316) recognises
 * that exact transition, deletes the queued Cloud Task and nulls the stored
 * task name. The display time is cleared here so the UI stops showing
 * "מתוזמן" immediately instead of waiting for the trigger.
 *
 * The confirmation link is deliberately left active: the therapist can still
 * copy it and send it manually.
 *
 * @param {string} clinicId
 * @param {string} appointmentId
 * @param {"initial"|"reminder"} smsType
 * @returns {Promise<{smsType: string}>}
 *
 * Error Code: CK-8936
 */
export const cancelScheduledSms = async (clinicId, appointmentId, smsType) => {
  try {
    if (smsType !== "initial" && smsType !== "reminder") {
      throw new Error("סוג הודעה לא תקין");
    }

    await guardTrialWrite(clinicId);

    const appointmentRef = doc(
      db,
      "clinics",
      clinicId,
      "appointments",
      appointmentId
    );

    const appointmentSnap = await getDoc(appointmentRef);
    if (!appointmentSnap.exists()) {
      throw new Error("הפגישה לא נמצאה");
    }
    const data = appointmentSnap.data();

    const alreadySent =
      smsType === "initial"
        ? data.whatsappInitialSent === true
        : data.whatsappReminderSent === true;
    if (alreadySent) {
      throw new Error("ההודעה כבר נשלחה ולא ניתן לבטל אותה");
    }

    const isScheduled =
      smsType === "initial"
        ? !!data.initialSmsScheduledAt || !!data.initialSmsTaskName
        : !!data.reminderSmsScheduledAt || !!data.reminderTaskName;
    if (!isScheduled) {
      throw new Error("אין הודעה מתוזמנת לביטול");
    }

    const updateData =
      smsType === "initial"
        ? { initialSmsEnabled: false, initialSmsScheduledAt: null }
        : { reminderSmsEnabled: false, reminderSmsScheduledAt: null };

    await updateDoc(appointmentRef, {
      ...updateData,
      updatedAt: serverTimestamp(),
    });

    return { smsType };
  } catch (error) {
    console.error("[CK-8936] Error cancelling scheduled SMS:", {
      errorCode: "CK-8936",
      errorMessage: error.message,
      clinicId,
      appointmentId,
      smsType,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Manual "send reminder now" for a future, not-yet-confirmed appointment.
 *
 * Everything (eligibility, permission, 15-min cooldown, quota, link choice,
 * WhatsApp-first / SMS-fallback send, stamps) runs in the
 * `sendAppointmentReminderNow` Cloud Function (functions/manualReminderHelper.js).
 * The scheduled automatic reminder is left untouched; the server stamps
 * `manualReminderCount` / `manualReminderLastSentAt` / `messagingManualReminder`.
 *
 * Re-throws with the server's Hebrew message so the UI can toast it as-is.
 *
 * @param {string} clinicId
 * @param {string} appointmentId
 * @returns {Promise<{success: boolean, channel: string, fallbackUsed: boolean, sentAt: string}>}
 *
 * Error Code: CK-8551
 */
export const sendAppointmentReminderNow = async (clinicId, appointmentId) => {
  try {
    if (!clinicId || !appointmentId) {
      throw new Error("פרטי הפגישה חסרים");
    }
    await guardTrialWrite(clinicId);
    const callable = httpsCallable(functions, "sendAppointmentReminderNow");
    const result = await callable({ clinicId, appointmentId });
    return result.data;
  } catch (error) {
    console.error("[CK-8551] Error sending manual appointment reminder:", {
      errorCode: "CK-8551",
      firebaseCode: error.code,
      errorMessage: error.message,
      clinicId,
      appointmentId,
      timestamp: new Date().toISOString(),
    });
    if (error.code === "functions/unauthenticated") {
      throw new Error("משתמש לא מחובר. אנא התחבר מחדש ונסה שוב.");
    }
    if (error.code === "functions/permission-denied") {
      throw new Error("אין לך הרשאה לשלוח תזכורת לפגישה של מטפל/ת אחר/ת");
    }
    throw error;
  }
};

/**
 * Soft-delete an appointment.
 *
 * The document stays in Firestore but is marked `isDeleted: true` so every
 * reader in the app filters it out. Hard deletion is intentionally avoided so
 * we keep an archival record of what was booked historically.
 *
 * Deletion is permitted for both future and past appointments — including
 * already-completed ones. The caller (RTK mutation) is responsible for
 * deciding what to do with the linked financial transaction (delete it,
 * cancel it, or leave it untouched). Already-soft-deleted appointments are
 * a no-op — the helper returns silently so re-deletion attempts don't fail.
 *
 * @param {string} clinicId
 * @param {string} appointmentId
 * @param {Object} [meta]
 * @param {string|null} [meta.deletedBy] - therapist id that performed the delete
 */
export const deleteAppointment = async (
  clinicId,
  appointmentId,
  { deletedBy = null } = {}
) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  const appointmentRef = doc(
    db,
    "clinics",
    clinicId,
    "appointments",
    appointmentId
  );

  const appointmentSnap = await getDoc(appointmentRef);
  if (!appointmentSnap.exists()) {
    throw new Error("הפגישה לא נמצאה");
  }
  const data = appointmentSnap.data();

  // Idempotency: a second delete attempt on the same appointment is a no-op.
  if (data.isDeleted === true) {
    return appointmentId;
  }

  await updateDoc(appointmentRef, {
    isDeleted: true,
    deletedAt: serverTimestamp(),
    deletedBy: deletedBy || null,
    updatedAt: serverTimestamp(),
  });

  return appointmentId;
};

/**
 * Cancel an appointment (keeps it in the calendar, marks as not-occurred).
 *
 * Mirrors the flow used by `PendingAppointmentsCarousel` when the therapist
 * clicks X on "האם הפגישה התרחשה". The appointment stays visible as a
 * "cancelled" event, is removed from the pending-actions carousel (approval &
 * summary Notify flags turned off), and the payment step is optionally kept
 * active if the therapist plans to collect a cancellation fee.
 *
 * @param {string} clinicId
 * @param {string} appointmentId
 * @param {Object} options
 * @param {"therapist"|"patient"} [options.cancelledBy="therapist"]
 * @param {boolean} [options.keepPaymentNotify=false] - if true, leaves
 *   NotifyAboutThisMeetingPaymentStatus active so the cancellation fee can be
 *   collected via the post-completion flow.
 * @param {string|null} [options.cancellationReason=null] - optional free-text
 *   reason supplied by the therapist. When non-empty it is persisted so it can
 *   be displayed on the cancelled banner, card tooltip, and patient diary.
 *
 * Error Code: CK-8014
 */
export const cancelAppointment = async (
  clinicId,
  appointmentId,
  {
    cancelledBy = "therapist",
    keepPaymentNotify = false,
    cancellationReason = null,
    cancelledByTherapistId = null,
  } = {}
) => {
  try {
    await guardTrialWrite(clinicId);

    const appointmentRef = doc(
      db,
      "clinics",
      clinicId,
      "appointments",
      appointmentId
    );

    const updateData = {
      status: "cancelled",
      cancelledAt: serverTimestamp(),
      cancelledBy,
      cancelledByTherapistId: cancelledByTherapistId || null,
      isApprovedByTherapist: true,
      isTherapistSummarizedTheMeeting: true,
      NotifyAboutThisMeetingApproval: false,
      NotifyAboutThisMeetingSummarize: false,
      updatedAt: serverTimestamp(),
    };

    if (!keepPaymentNotify) {
      updateData.NotifyAboutThisMeetingPaymentStatus = false;
    }

    // Only persist the reason when the therapist actually filled it in;
    // otherwise explicitly null the field so re-cancellations don't keep
    // stale text from a previous attempt.
    const trimmedReason =
      typeof cancellationReason === "string" ? cancellationReason.trim() : "";
    updateData.cancellationReason = trimmedReason ? trimmedReason : null;

    await updateDoc(appointmentRef, updateData);
    return getAppointment(clinicId, appointmentId);
  } catch (error) {
    console.error("[CK-8014] Error cancelling appointment:", {
      errorCode: "CK-8014",
      errorMessage: error.message,
      clinicId,
      appointmentId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Undo a cancellation (used by the patient re-confirm flow).
 *
 * Decides which status to restore based on the appointment's endTime:
 *   - `endTime > now`  → `status: "scheduled"` (Cloud Task will be re-created
 *     by `onAppointmentUpdated` when it sees the status change back).
 *   - `endTime <= now` → `status: "completed"` and re-arms the post-completion
 *     Notify flags so the therapist gets the pending-action card again.
 *
 * @param {string} clinicId
 * @param {string} appointmentId
 *
 * Error Code: CK-8015
 */
export const uncancelAppointment = async (clinicId, appointmentId) => {
  try {
    await guardTrialWrite(clinicId);

    const appointmentRef = doc(
      db,
      "clinics",
      clinicId,
      "appointments",
      appointmentId
    );

    const appointmentSnap = await getDoc(appointmentRef);
    if (!appointmentSnap.exists()) {
      throw new Error("הפגישה לא נמצאה");
    }
    const data = appointmentSnap.data();

    const endMs = data.endTime?.toDate
      ? data.endTime.toDate().getTime()
      : data.endTime
      ? new Date(data.endTime).getTime()
      : NaN;
    const isFuture = Number.isFinite(endMs) && endMs > Date.now();

    const updateData = {
      cancelledAt: null,
      cancelledBy: null,
      cancelledByTherapistId: null,
      cancellationReason: null,
      cancellationFeeRecorded: false,
      updatedAt: serverTimestamp(),
    };

    if (isFuture) {
      updateData.status = "scheduled";
      updateData.isApprovedByTherapist = false;
      updateData.isTherapistSummarizedTheMeeting = false;
      updateData.NotifyAboutThisMeetingApproval = null;
      updateData.NotifyAboutThisMeetingSummarize = null;
      updateData.NotifyAboutThisMeetingPaymentStatus = null;
    } else {
      updateData.status = "completed";
      updateData.isApprovedByTherapist = false;
      updateData.isTherapistSummarizedTheMeeting = false;
      updateData.isTherapistUpdatedPaymentStatus = false;
      updateData.NotifyAboutThisMeetingApproval = true;
      // Temp customers have no patient record — no treatment summary step
      updateData.NotifyAboutThisMeetingSummarize =
        data.isTempCustomer === true ? false : true;
      updateData.NotifyAboutThisMeetingPaymentStatus = true;
      updateData.isTeraphistAskedToHide = false;
    }

    await updateDoc(appointmentRef, updateData);
    return getAppointment(clinicId, appointmentId);
  } catch (error) {
    console.error("[CK-8015] Error uncancelling appointment:", {
      errorCode: "CK-8015",
      errorMessage: error.message,
      clinicId,
      appointmentId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// ============================================
// RECURRING APPOINTMENTS
// ============================================

/**
 * Recurring frequency constants
 */
export const RECURRING_FREQUENCY = {
  WEEKLY: "weekly", // Every week
  BIWEEKLY: "biweekly", // Every 2 weeks
  MONTHLY: "monthly", // Every month (same day of week)
  TWICE_WEEKLY: "twice_weekly", // Two fixed slots every week
  // Blocked-time (חסימת זמן) only — never offered in the meeting-series UI
  DAILY: "daily", // Every calendar day, incl. Fri/Sat
  MONTHLY_DATE: "monthly_date", // Same day-of-month, clamped to short months
  YEARLY: "yearly", // Same date every year
};

/**
 * Series slot identifiers. Only twice-weekly series use "secondary";
 * every other frequency has a single ("primary") slot.
 */
export const SERIES_SLOT = {
  PRIMARY: "primary",
  SECONDARY: "secondary",
};

/**
 * Calculate all dates for a recurring series (legacy - end-date based)
 * Kept for backward compatibility with existing recurring appointments.
 *
 * @param {Date} startDate - The first appointment date
 * @param {Date} endDate - The last possible date (inclusive)
 * @param {string} frequency - RECURRING_FREQUENCY value
 * @returns {Array<Date>} - Array of dates for the series
 */
export const calculateRecurringDates = (startDate, endDate, frequency) => {
  const dates = [];
  let currentDate = new Date(startDate);
  const finalDate = new Date(endDate);

  // Set to end of day for inclusive end date comparison
  finalDate.setHours(23, 59, 59, 999);

  while (currentDate <= finalDate) {
    dates.push(new Date(currentDate));

    // Move to next occurrence based on frequency
    switch (frequency) {
      case RECURRING_FREQUENCY.WEEKLY:
        currentDate.setDate(currentDate.getDate() + 7);
        break;
      case RECURRING_FREQUENCY.BIWEEKLY:
        currentDate.setDate(currentDate.getDate() + 14);
        break;
      case RECURRING_FREQUENCY.MONTHLY:
        currentDate.setMonth(currentDate.getMonth() + 1);
        break;
      default:
        currentDate.setDate(currentDate.getDate() + 7);
    }
  }

  return dates;
};

/**
 * Calculate all occurrence dates for a blocked-time (חסימת זמן) series.
 *
 * Semantics differ from meeting series: until-date based (inclusive), daily
 * includes Fri/Sat, and monthly/yearly anchor to the START DATE's day-of-month,
 * clamping to the last day of shorter months (Jan 31 → Feb 28/29 → Mar 31).
 *
 * @param {Date} startDate - First blocked occurrence (date + time)
 * @param {string} frequency - RECURRING_FREQUENCY: DAILY | WEEKLY | MONTHLY_DATE | YEARLY
 * @param {Date} untilDate - Last possible date (inclusive, end of day)
 * @returns {Array<Date>} - Occurrence dates, each carrying startDate's clock time
 */
export const calculateBlockedTimeDates = (startDate, frequency, untilDate) => {
  const MAX_OCCURRENCES = 366; // safety net; UI caps until-date at 1 year
  const dates = [];

  const start = new Date(startDate);
  const finalDate = new Date(untilDate);
  finalDate.setHours(23, 59, 59, 999);

  const anchorDay = start.getDate();
  const hours = start.getHours();
  const minutes = start.getMinutes();
  const daysInMonth = (year, monthIndex) =>
    new Date(year, monthIndex + 1, 0).getDate();

  // setFullYear/setDate + setHours (never ms arithmetic) so Israel DST
  // transitions don't drift the clock time — same rule as meeting series.
  let step = 0;
  while (dates.length < MAX_OCCURRENCES) {
    let current;
    switch (frequency) {
      case RECURRING_FREQUENCY.DAILY:
        current = new Date(start);
        current.setDate(current.getDate() + step);
        break;
      case RECURRING_FREQUENCY.WEEKLY:
        current = new Date(start);
        current.setDate(current.getDate() + step * 7);
        break;
      case RECURRING_FREQUENCY.MONTHLY_DATE: {
        const totalMonths = start.getMonth() + step;
        const year = start.getFullYear() + Math.floor(totalMonths / 12);
        const monthIndex = ((totalMonths % 12) + 12) % 12;
        current = new Date(
          year,
          monthIndex,
          Math.min(anchorDay, daysInMonth(year, monthIndex))
        );
        break;
      }
      case RECURRING_FREQUENCY.YEARLY: {
        const year = start.getFullYear() + step;
        current = new Date(
          year,
          start.getMonth(),
          Math.min(anchorDay, daysInMonth(year, start.getMonth()))
        );
        break;
      }
      default:
        return dates;
    }
    current.setHours(hours, minutes, 0, 0);
    if (current > finalDate) break;
    dates.push(current);
    step++;
  }

  return dates;
};

/**
 * Calculate the occurrences of a meeting series by count, tagging each one with
 * the weekly slot it belongs to.
 *
 * For every frequency except twice-weekly there is a single slot, so all
 * occurrences come back as "primary". Twice-weekly series weave two fixed
 * weekly slots (the base date/time and `secondSlot`) into one ordered list.
 *
 * @param {Date} startDate - Date of the first appointment to create
 * @param {string} frequency - RECURRING_FREQUENCY value
 * @param {number} totalCount - Total meetings in the series (1–100)
 * @param {number} startIndex - Which meeting number this starts at (default 1)
 * @param {Object|null} secondSlot - { dayOfWeek: 0-6, time: "HH:MM" }, twice-weekly only
 * @returns {Array<{date: Date, slot: string}>}
 */
export const calculateSeriesOccurrences = (
  startDate,
  frequency,
  totalCount,
  startIndex = 1,
  secondSlot = null
) => {
  const count = totalCount - startIndex + 1;
  if (count <= 0) return [];

  const start = new Date(startDate);

  if (
    frequency === RECURRING_FREQUENCY.TWICE_WEEKLY &&
    secondSlot &&
    secondSlot.time &&
    secondSlot.dayOfWeek !== null &&
    secondSlot.dayOfWeek !== undefined &&
    secondSlot.dayOfWeek !== ""
  ) {
    const secondDay = Number(secondSlot.dayOfWeek);
    const [secondHour, secondMinute] = secondSlot.time.split(":").map(Number);

    // Sunday of the week containing startDate, at midnight local time
    const weekStart = new Date(start);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay());
    weekStart.setHours(0, 0, 0, 0);

    // Build one occurrence anchored to weekStart. setDate + setHours (never ms
    // arithmetic) so Israel DST transitions don't drift the clock time.
    const buildOccurrence = (weekOffset, dayOfWeek, hours, minutes, slot) => {
      const date = new Date(weekStart);
      date.setDate(date.getDate() + weekOffset * 7 + dayOfWeek);
      date.setHours(hours, minutes, 0, 0);
      return { date, slot };
    };

    const candidates = [];
    // count + 1 weeks of candidates: enough even if the second slot is dropped
    // entirely as a duplicate of the base slot.
    const weeksNeeded = count + 1;
    for (let w = 0; w < weeksNeeded; w++) {
      candidates.push(
        buildOccurrence(w, start.getDay(), start.getHours(), start.getMinutes(), SERIES_SLOT.PRIMARY)
      );
      candidates.push(
        buildOccurrence(w, secondDay, secondHour, secondMinute, SERIES_SLOT.SECONDARY)
      );
    }

    const seenTimes = new Set();
    return candidates
      .filter((occ) => occ.date >= start)
      .sort((a, b) => a.date - b.date)
      .filter((occ) => {
        // Drop an exact duplicate (second slot identical to the base slot)
        const key = occ.date.getTime();
        if (seenTimes.has(key)) return false;
        seenTimes.add(key);
        return true;
      })
      .slice(0, count);
  }

  const occurrences = [];
  let currentDate = new Date(start);

  for (let i = 0; i < count; i++) {
    occurrences.push({ date: new Date(currentDate), slot: SERIES_SLOT.PRIMARY });

    switch (frequency) {
      case RECURRING_FREQUENCY.WEEKLY:
        currentDate.setDate(currentDate.getDate() + 7);
        break;
      case RECURRING_FREQUENCY.BIWEEKLY:
        currentDate.setDate(currentDate.getDate() + 14);
        break;
      case RECURRING_FREQUENCY.MONTHLY:
        currentDate.setMonth(currentDate.getMonth() + 1);
        break;
      default:
        currentDate.setDate(currentDate.getDate() + 7);
    }
  }

  return occurrences;
};

/**
 * Calculate dates for a meeting series by count (new series-based approach).
 * E.g., totalCount=12, startIndex=5 → creates 8 appointments numbered 5–12.
 *
 * Thin wrapper over calculateSeriesOccurrences for callers that only need dates.
 *
 * @param {Date} startDate - Date of the first appointment to create
 * @param {string} frequency - RECURRING_FREQUENCY value
 * @param {number} totalCount - Total meetings in the series (1–100)
 * @param {number} startIndex - Which meeting number this starts at (default 1)
 * @param {Object|null} secondSlot - { dayOfWeek: 0-6, time: "HH:MM" }, twice-weekly only
 * @returns {Array<Date>} - Array of dates for the appointments to create
 */
export const calculateSeriesDates = (
  startDate,
  frequency,
  totalCount,
  startIndex = 1,
  secondSlot = null
) =>
  calculateSeriesOccurrences(startDate, frequency, totalCount, startIndex, secondSlot).map(
    (occ) => occ.date
  );

/**
 * Check for conflicts with existing appointments for recurring series
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} therapistId - The therapist ID
 * @param {Array<Object>} appointments - Array of {startTime, endTime} objects
 * @returns {Object} - { hasConflicts: boolean, conflicts: Array }
 *
 * Error Code: CK-8340
 */
export const checkRecurringConflicts = async (
  clinicId,
  therapistId,
  appointments,
  excludeIds = [],
  excludeGroupId = null
) => {
  try {
    if (!appointments || appointments.length === 0) {
      return { hasConflicts: false, conflicts: [] };
    }
    // When an existing series is being moved, its own occurrences are already
    // in Firestore — without this they would flag themselves as conflicts.
    // The group id covers siblings that fall outside the shifted range too.
    const excludeSet = new Set(excludeIds);

    // Get the date range for the entire series
    const startDates = appointments.map((a) => new Date(a.startTime));
    const endDates = appointments.map((a) => new Date(a.endTime));
    const minDate = new Date(Math.min(...startDates));
    const maxDate = new Date(Math.max(...endDates));
    // Query by startTime only, so an existing meeting that STARTS before the
    // first new occurrence but still overlaps it (09:00–10:00 vs new 09:30)
    // was never fetched. Look back a day; the overlap predicate below decides.
    const queryFrom = new Date(minDate.getTime() - 24 * 60 * 60 * 1000);

    // Fetch all existing appointments in the range
    const appointmentsRef = collection(db, "clinics", clinicId, "appointments");
    const q = query(
      appointmentsRef,
      where("therapistId", "==", therapistId),
      where("startTime", ">=", Timestamp.fromDate(queryFrom)),
      where("startTime", "<=", Timestamp.fromDate(maxDate)),
      where("status", "!=", "cancelled")
    );

    const snapshot = await getDocs(q);
    const existingAppointments = snapshot.docs
      .map((doc) => ({
        id: doc.id,
        ...doc.data(),
        startTime: doc.data().startTime?.toDate(),
        endTime: doc.data().endTime?.toDate(),
      }))
      .filter(
        (apt) =>
          apt.isDeleted !== true &&
          !excludeSet.has(apt.id) &&
          !(excludeGroupId && apt.recurringGroupId === excludeGroupId)
      );

    // Check for overlaps
    const conflicts = [];
    for (const newAppt of appointments) {
      const newStart = new Date(newAppt.startTime);
      const newEnd = new Date(newAppt.endTime);

      for (const existing of existingAppointments) {
        if (!existing.startTime || !existing.endTime) continue;

        // Check if times overlap
        const existingStart = existing.startTime;
        const existingEnd = existing.endTime;

        // Overlap condition: new starts before existing ends AND new ends after existing starts
        if (newStart < existingEnd && newEnd > existingStart) {
          conflicts.push({
            date: newStart.toISOString(),
            // Carried through so the UI can name the offending meeting
            // ("מפגש 4 מתוך 10") instead of only showing a raw date.
            seriesIndex: newAppt.seriesIndex ?? null,
            selfConflict: false,
            existingAppointment: {
              id: existing.id,
              patientName: existing.patientName,
              startTime: existingStart.toISOString(),
              endTime: existingEnd.toISOString(),
            },
          });
          break; // One conflict per new appointment is enough
        }
      }
    }

    return {
      hasConflicts: conflicts.length > 0,
      conflicts,
    };
  } catch (error) {
    console.error("[CK-8340] Error checking recurring conflicts:", {
      errorCode: "CK-8340",
      errorMessage: error.message,
      clinicId,
      therapistId,
      appointmentsCount: appointments?.length,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Create a meeting series (סדרת מפגשים)
 *
 * Supports two modes:
 * 1. Series mode (new): { frequency, totalCount, startIndex } — count-based
 * 2. Legacy mode: { frequency, endDate } — end-date-based (backward compat)
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} therapistId - The therapist ID
 * @param {Object} appointmentData - Base appointment data
 * @param {Object} recurringConfig - { frequency, totalCount?, startIndex?, endDate? }
 * @returns {Object} - { recurringGroupId, appointments: Array }
 *
 * Error Code: CK-8341
 */
export const createRecurringAppointments = async (
  clinicId,
  therapistId,
  appointmentData,
  recurringConfig,
  therapistName = null
) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  try {
    // `allowConflicts` is the therapist's explicit "create anyway" after being
    // shown the conflict list. It only waives collisions with OTHER existing
    // appointments — a series colliding with itself is always refused, since
    // that would double-book the same patient against themselves.
    const {
      frequency,
      totalCount,
      startIndex = 1,
      endDate,
      overrides,
      secondSlot,
      allowConflicts = false,
    } = recurringConfig;
    const startDateTime = new Date(appointmentData.startTime);
    const duration = appointmentData.duration || 45;

    const seriesTotalCount = totalCount || null;
    // Twice-weekly is only supported on the count-based path; the legacy
    // end-date path has no notion of a second slot.
    const isTwiceWeekly =
      frequency === RECURRING_FREQUENCY.TWICE_WEEKLY && !!secondSlot && !!seriesTotalCount;

    let occurrences;
    if (seriesTotalCount) {
      occurrences = calculateSeriesOccurrences(
        startDateTime,
        frequency,
        seriesTotalCount,
        startIndex,
        secondSlot
      );
    } else {
      occurrences = calculateRecurringDates(startDateTime, new Date(endDate), frequency).map(
        (date) => ({ date, slot: SERIES_SLOT.PRIMARY })
      );
    }

    if (occurrences.length === 0) {
      throw new Error("לא נמצאו תאריכים לסדרה");
    }

    // Generate a unique recurring group ID
    const recurringGroupId = `RG-${Date.now().toString(36)}-${Math.random()
      .toString(36)
      .substring(2, 8)}`;

    // Prepare all appointments with their times, applying per-meeting overrides.
    // An override with skip:true drops that occurrence (room-conflict resolution).
    const appointmentsToCreate = occurrences.map(({ date, slot }, index) => {
      const override = overrides ? overrides[index] : null;
      if (override?.skip === true) return null;
      const apptStart = new Date(date);

      if (override?.date) {
        const [y, m, d] = override.date.split('-').map(Number);
        apptStart.setFullYear(y, m - 1, d);
      }
      if (override?.time) {
        const [h, min] = override.time.split(':').map(Number);
        apptStart.setHours(h, min, 0, 0);
      } else if (slot !== SERIES_SLOT.SECONDARY) {
        apptStart.setHours(startDateTime.getHours(), startDateTime.getMinutes(), 0, 0);
      }

      const apptEnd = new Date(apptStart);
      apptEnd.setMinutes(apptEnd.getMinutes() + duration);

      return {
        startTime: apptStart.toISOString(),
        endTime: apptEnd.toISOString(),
        seriesIndex: startIndex + index,
        slot: slot,
        therapistIdOverride: override?.therapistId || null,
        therapistNameOverride: override?.therapistName || null,
        treatmentTypeIdOverride: override?.treatmentTypeId || null,
        treatmentTypeNameOverride: override?.treatmentTypeName || null,
        // Per-meeting room: undefined = use the base room; empty string /
        // null = explicitly no room for this occurrence.
        roomIdOverride:
          override?.roomId !== undefined ? override.roomId || null : undefined,
      };
    }).filter(Boolean);

    if (appointmentsToCreate.length === 0) {
      throw new Error("כל הפגישות בסדרה דולגו");
    }

    // Twice-weekly series can place two slots on the same day, so the new
    // meetings must be checked against each other before hitting Firestore —
    // checkRecurringConflicts only compares them against existing appointments.
    if (isTwiceWeekly) {
      const selfConflicts = [];
      for (let i = 0; i < appointmentsToCreate.length; i++) {
        const a = appointmentsToCreate[i];
        const aStart = new Date(a.startTime);
        const aEnd = new Date(a.endTime);
        for (let j = i + 1; j < appointmentsToCreate.length; j++) {
          const b = appointmentsToCreate[j];
          const bStart = new Date(b.startTime);
          if (aStart < new Date(b.endTime) && aEnd > bStart) {
            selfConflicts.push({
              date: bStart.toISOString(),
              seriesIndex: b.seriesIndex ?? null,
              // Not waivable by `allowConflicts` — see the note at the top.
              selfConflict: true,
              existingAppointment: {
                id: null,
                patientName: appointmentData.patientName,
                startTime: a.startTime,
                endTime: a.endTime,
              },
            });
            break;
          }
        }
      }

      if (selfConflicts.length > 0) {
        return {
          success: false,
          hasConflicts: true,
          conflicts: selfConflicts,
          recurringGroupId: null,
          appointments: [],
        };
      }
    }

    // Check for conflicts before creating. Occurrences with a per-row
    // therapist override belong to THAT therapist's calendar, so group by the
    // effective therapist and check each group against its own appointments
    // (checkRecurringConflicts queries a single therapistId).
    const slotsByTherapist = new Map();
    appointmentsToCreate.forEach((a) => {
      const effectiveTherapistId = a.therapistIdOverride || therapistId;
      if (!slotsByTherapist.has(effectiveTherapistId)) slotsByTherapist.set(effectiveTherapistId, []);
      slotsByTherapist.get(effectiveTherapistId).push(a);
    });
    const mergedConflicts = [];
    for (const [groupTherapistId, groupSlots] of slotsByTherapist) {
      const groupCheck = await checkRecurringConflicts(clinicId, groupTherapistId, groupSlots);
      if (groupCheck.hasConflicts) mergedConflicts.push(...groupCheck.conflicts);
    }
    mergedConflicts.sort((a, b) => String(a.date).localeCompare(String(b.date)));
    const conflictCheck = {
      hasConflicts: mergedConflicts.length > 0,
      conflicts: mergedConflicts,
    };

    if (conflictCheck.hasConflicts && !allowConflicts) {
      return {
        success: false,
        hasConflicts: true,
        conflicts: conflictCheck.conflicts,
        recurringGroupId: null,
        appointments: [],
      };
    }

    // Absence waiver per occurrence: the wizard only checks (and the user only
    // waives) the base date, so stamp `overrideAbsence` ONLY on occurrences
    // that actually fall inside an absence of their therapist — never on the
    // whole series. Best effort: a failed lookup leaves the stamps off.
    const absenceHitByIdx = new Map();
    if (appointmentData.overrideAbsence === true && appointmentsToCreate.length > 0) {
      try {
        const firstStart = new Date(appointmentsToCreate[0].startTime);
        const lastStart = new Date(appointmentsToCreate[appointmentsToCreate.length - 1].startTime);
        const rangeAbsences = await getAbsences(clinicId, {
          from: absenceDateStr(firstStart),
          to: absenceDateStr(lastStart),
        });
        appointmentsToCreate.forEach((a, idx) => {
          const occTherapistId = a.therapistIdOverride || therapistId;
          const s = new Date(a.startTime);
          // Israel wall-clock, not browser-local (absence windows are Israel HH:MM).
          const hhmm = toIsraelHHMM(s);
          const durationMin = Math.max(1, Math.round((new Date(a.endTime) - s) / 60000));
          const hit = findBlockingAbsence(
            rangeAbsences.filter((ab) => ab.therapistId === occTherapistId),
            absenceDateStr(s),
            hhmm,
            durationMin
          );
          if (hit) absenceHitByIdx.set(idx, hit);
        });
      } catch (absenceError) {
        console.error("[CK-8552] createRecurringAppointments: per-occurrence absence lookup failed", {
          errorCode: "CK-8552",
          errorMessage: absenceError.message,
          clinicId,
          timestamp: new Date().toISOString(),
        });
      }
    }

    // Create all appointments in a batch
    const appointmentsRef = collection(db, "clinics", clinicId, "appointments");
    const createdAppointments = [];

    const effectiveTotalCount = seriesTotalCount || occurrences.length;

    // Slot metadata, stored on every doc in a twice-weekly group so the series
    // can be reasoned about later without re-deriving weekdays.
    const pad2 = (n) => String(n).padStart(2, "0");
    const slotMeta = isTwiceWeekly
      ? {
          recurringFirstDay: startDateTime.getDay(),
          recurringFirstTime: `${pad2(startDateTime.getHours())}:${pad2(
            startDateTime.getMinutes()
          )}`,
          recurringSecondDay: Number(secondSlot.dayOfWeek),
          recurringSecondTime: secondSlot.time,
        }
      : {
          recurringFirstDay: null,
          recurringFirstTime: null,
          recurringSecondDay: null,
          recurringSecondTime: null,
        };

    const now = new Date();

    for (let apptIdx = 0; apptIdx < appointmentsToCreate.length; apptIdx++) {
      const apptTime = appointmentsToCreate[apptIdx];
      const newAppointmentRef = doc(appointmentsRef);

      const apptEndDate = new Date(apptTime.endTime);
      const isPast = apptEndDate <= now;

      const appointmentDoc = {
        id: newAppointmentRef.id,
        uniqueId: generateUniqueAppointmentId(),
        therapistId: apptTime.therapistIdOverride || therapistId,
        therapistName: apptTime.therapistNameOverride || therapistName || appointmentData.therapistName || null,

        patientId: appointmentData.patientId ?? null,
        patientName: appointmentData.patientName,

        // Temp customer (לקוח מזדמן) - one-off client without a patient record
        isTempCustomer: appointmentData.isTempCustomer === true,
        tempCustomerPhone: appointmentData.tempCustomerPhone || null,

        startTime: Timestamp.fromDate(new Date(apptTime.startTime)),
        endTime: Timestamp.fromDate(new Date(apptTime.endTime)),
        duration: duration,
        notes: appointmentData.notes || null,

        // Scheduling-policy waiver stamped on every occurrence when the series
        // was created despite therapist conflicts (allowConflicts) — audit only.
        overrideTherapistConflict:
          conflictCheck.hasConflicts && appointmentData.overrideTherapistConflict === true,
        therapistConflictOverride:
          conflictCheck.hasConflicts && appointmentData.overrideTherapistConflict === true
            ? appointmentData.therapistConflictOverride || null
            : null,
        overrideAbsence: absenceHitByIdx.has(apptIdx),
        absenceOverride: absenceHitByIdx.has(apptIdx)
          ? { ...(appointmentData.absenceOverride || {}), absenceId: absenceHitByIdx.get(apptIdx).id }
          : null,

        // Payment
        paymentAmount: appointmentData.paymentAmount || null,
        paymentStatus: "pending",
        actualPaidAmount: null,
        paymentDate: null,
        paymentMethod: null,

        // Punch card (כרטיסיה) - defaults; set by punchVisit() after creation
        isPunchCard: false,
        punchCardId: null,
        punchCardPrepaid: false,

        // Invoice integration (future)
        invoiceCreated: false,
        invoiceNumber: null,
        invoiceProvider: null,
        invoiceUrl: null,

        // Series fields (סדרת מפגשים)
        isRecurring: true,
        recurringGroupId: recurringGroupId,
        recurringFrequency: frequency,
        recurringIndex: apptTime.seriesIndex,
        recurringTotalCount: effectiveTotalCount,
        recurringSlot: apptTime.slot || SERIES_SLOT.PRIMARY,
        ...slotMeta,

        // Online video meeting (100ms) — each occurrence gets its own link id
        isOnlineMeeting: appointmentData.isOnlineMeeting === true,
        videoMeetLinkId:
          appointmentData.isOnlineMeeting === true
            ? generateVideoMeetLinkId()
            : null,
        videoRoomId: null,

        // SMS
        whatsappInitialSent: false,
        whatsappInitialConfirmed: null,
        whatsappInitialDeclined: null,
        whatsappReminderSent: false,
        whatsappReminderConfirmed: null,
        whatsappReminderDeclined: null,

        // SMS scheduling (custom send times from preview)
        initialSmsScheduledAt: appointmentData.smsScheduling?.[apptIdx]?.initialSmsScheduledAt || null,
        initialSmsEnabled: appointmentData.smsScheduling?.[apptIdx]?.initialSmsEnabled !== false,
        reminderSmsScheduledAt: appointmentData.smsScheduling?.[apptIdx]?.reminderSmsScheduledAt || null,
        reminderSmsEnabled: appointmentData.smsScheduling?.[apptIdx]?.reminderSmsEnabled !== false,
        reminderSmsGapOverride: appointmentData.smsScheduling?.[apptIdx]?.reminderSmsGapOverride === true,
        initialSmsTaskName: null,

        // End-meeting (post-session) SMS — same per-meeting opt-in as single appts
        sendEndMeetingSms: appointmentData.sendEndMeetingSms === true,
        endMeetingSmsTaskName: null,
        endMeetingSmsSent: false,

        // Post-completion workflow flags
        isTeraphistAskedToHide: false,
        NotifyAboutThisMeetingApproval: null,
        NotifyAboutThisMeetingSummarize: null,
        NotifyAboutThisMeetingPaymentStatus: null,
        isApprovedByTherapist: false,
        isTherapistSummarizedTheMeeting: false,
        isTherapistUpdatedPaymentStatus: false,

        // Receipt tracking
        isProducedReceipitForThisMeeting: false,
        RecepitsDocsRefArray: [],

        // Treatment type (per-appointment override or default from form)
        treatmentTypeId: apptTime.treatmentTypeIdOverride || appointmentData.treatmentTypeId || null,
        treatmentTypeName: apptTime.treatmentTypeNameOverride || appointmentData.treatmentTypeName || null,

        // Room assignment (optional; per-meeting override wins, including an
        // explicit "no room" for a single occurrence)
        roomId:
          apptTime.roomIdOverride !== undefined
            ? apptTime.roomIdOverride
            : appointmentData.roomId || null,

        // Google Calendar sync
        googleCalendarEventId: null,

        // Soft-delete / cancellation tracking
        isDeleted: false,
        deletedAt: null,
        deletedBy: null,
        cancelledAt: null,
        cancelledBy: null,
        cancellationReason: null,
        cancellationFeeRecorded: false,

        // Auto-complete past appointments so Cloud Task skip doesn't leave them as "scheduled"
        status: isPast ? "completed" : "scheduled",
        ...(isPast ? { completedAt: Timestamp.fromDate(now), completedBy: "system" } : {}),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(newAppointmentRef, appointmentDoc);
      createdAppointments.push({
        id: newAppointmentRef.id,
        ...appointmentDoc,
        startTime: apptTime.startTime,
        endTime: apptTime.endTime,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
    }

    return {
      success: true,
      hasConflicts: false,
      conflicts: [],
      recurringGroupId,
      appointments: createdAppointments,
      totalCreated: createdAppointments.length,
    };
  } catch (error) {
    console.error("[CK-8341] Error creating recurring appointments:", {
      errorCode: "CK-8341",
      errorMessage: error.message,
      clinicId,
      therapistId,
      recurringConfig,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Create blocked-time (חסימת זמן פרטי) calendar entries — single or series.
 *
 * Blocks are appointment docs with `isBlockedTime: true` and `status: "blocked"`.
 * They are pure calendar visuals: no patient, no payment, no SMS/push, no
 * completion Cloud Task (server triggers early-return on the flag), and they
 * are excluded from finance summaries and counters.
 *
 * @param {string} clinicId
 * @param {string} therapistId
 * @param {string|null} therapistName
 * @param {Object} blockData - {
 *   startTime: ISO string, duration: minutes, blockCause, blockComments,
 *   repeatFrequency: RECURRING_FREQUENCY value | null, untilDate: ISO date | null
 * }
 * @returns {{ success, recurringGroupId, appointments, totalCreated }}
 *
 * Error Code: CK-8346
 */
export const createBlockedTimeAppointments = async (
  clinicId,
  therapistId,
  therapistName,
  blockData
) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  try {
    const startDateTime = new Date(blockData.startTime);
    const duration = blockData.duration || 60;
    const isSeries = !!blockData.repeatFrequency && !!blockData.untilDate;

    const occurrenceDates = isSeries
      ? calculateBlockedTimeDates(
          startDateTime,
          blockData.repeatFrequency,
          new Date(blockData.untilDate)
        )
      : [startDateTime];

    if (occurrenceDates.length === 0) {
      throw new Error("לא נמצאו תאריכים לחסימה");
    }

    const recurringGroupId =
      isSeries && occurrenceDates.length > 1
        ? `RG-${Date.now().toString(36)}-${Math.random()
            .toString(36)
            .substring(2, 8)}`
        : null;
    const isRecurring = !!recurringGroupId;

    const appointmentsRef = collection(db, "clinics", clinicId, "appointments");
    const createdAppointments = [];

    // Daily series can reach ~365 docs — chunked batch writes, not serial setDoc.
    const BATCH_LIMIT = 400;
    let batch = writeBatch(db);
    let batchCount = 0;

    for (let idx = 0; idx < occurrenceDates.length; idx++) {
      const occStart = occurrenceDates[idx];
      const occEnd = new Date(occStart);
      occEnd.setMinutes(occEnd.getMinutes() + duration);

      const newAppointmentRef = doc(appointmentsRef);
      const appointmentDoc = {
        id: newAppointmentRef.id,
        uniqueId: generateUniqueAppointmentId(),
        therapistId: therapistId,
        therapistName: therapistName || null,

        // Blocked time — no patient. Generic name keeps every shared calendar
        // view privacy-safe; the cause/comments render only for owner/admin.
        isBlockedTime: true,
        blockCause: blockData.blockCause || null,
        blockComments: blockData.blockComments || null,
        patientId: null,
        patientName: "זמן חסום",
        isTempCustomer: false,
        tempCustomerPhone: null,

        startTime: Timestamp.fromDate(occStart),
        endTime: Timestamp.fromDate(occEnd),
        duration: duration,
        notes: null,

        // Neutralized payment fields — excluded from finance flows
        paymentAmount: null,
        paymentStatus: "skipped",
        actualPaidAmount: null,
        paymentDate: null,
        paymentMethod: null,
        isPunchCard: false,
        punchCardId: null,
        punchCardPrepaid: false,
        invoiceCreated: false,
        invoiceNumber: null,
        invoiceProvider: null,
        invoiceUrl: null,

        // Series fields (reuses meeting-series shape so scope-aware
        // edit/delete of recurring groups works unchanged)
        isRecurring: isRecurring,
        recurringGroupId: recurringGroupId,
        recurringFrequency: isRecurring ? blockData.repeatFrequency : null,
        recurringIndex: isRecurring ? idx + 1 : null,
        recurringTotalCount: isRecurring ? occurrenceDates.length : null,
        recurringSlot: SERIES_SLOT.PRIMARY,
        recurringFirstDay: null,
        recurringFirstTime: null,
        recurringSecondDay: null,
        recurringSecondTime: null,
        recurringUntilDate:
          isRecurring && blockData.untilDate
            ? Timestamp.fromDate(new Date(blockData.untilDate))
            : null,

        // SMS/WhatsApp — fully disabled for blocks
        whatsappInitialSent: false,
        whatsappInitialConfirmed: null,
        whatsappInitialDeclined: null,
        whatsappReminderSent: false,
        whatsappReminderConfirmed: null,
        whatsappReminderDeclined: null,
        initialSmsScheduledAt: null,
        initialSmsEnabled: false,
        reminderSmsScheduledAt: null,
        reminderSmsEnabled: false,
        reminderSmsGapOverride: false,
        initialSmsTaskName: null,
        sendEndMeetingSms: false,
        endMeetingSmsTaskName: null,
        endMeetingSmsSent: false,

        // Post-completion workflow flags — blocks never complete
        isTeraphistAskedToHide: false,
        NotifyAboutThisMeetingApproval: null,
        NotifyAboutThisMeetingSummarize: null,
        NotifyAboutThisMeetingPaymentStatus: null,
        isApprovedByTherapist: false,
        isTherapistSummarizedTheMeeting: false,
        isTherapistUpdatedPaymentStatus: false,
        isProducedReceipitForThisMeeting: false,
        RecepitsDocsRefArray: [],

        treatmentTypeId: null,
        treatmentTypeName: null,
        roomId: null,
        googleCalendarEventId: null,

        isDeleted: false,
        deletedAt: null,
        deletedBy: null,
        cancelledAt: null,
        cancelledBy: null,
        cancelledByTherapistId: null,
        cancellationReason: null,
        cancellationFeeRecorded: false,
        scheduledByAdminId: null,
        scheduledByAdminName: null,
        lastActionByTherapistId: null,

        status: "blocked",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      batch.set(newAppointmentRef, appointmentDoc);
      batchCount++;
      if (batchCount >= BATCH_LIMIT) {
        await batch.commit();
        batch = writeBatch(db);
        batchCount = 0;
      }

      createdAppointments.push({
        ...appointmentDoc,
        startTime: occStart.toISOString(),
        endTime: occEnd.toISOString(),
        recurringUntilDate: blockData.untilDate || null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
    }

    if (batchCount > 0) {
      await batch.commit();
    }

    logActivity(clinicId, {
      therapistId,
      therapistName: therapistName || null,
      action: "blocked_time_created",
      entityType: "appointment",
      entityId: recurringGroupId || createdAppointments[0]?.id || null,
      details: {
        totalCreated: createdAppointments.length,
        repeatFrequency: isSeries ? blockData.repeatFrequency : null,
      },
    });

    return {
      success: true,
      hasConflicts: false,
      conflicts: [],
      recurringGroupId,
      appointments: createdAppointments,
      totalCreated: createdAppointments.length,
    };
  } catch (error) {
    console.error("[CK-8346] Error creating blocked time:", {
      errorCode: "CK-8346",
      errorMessage: error.message,
      clinicId,
      therapistId,
      timestamp: new Date().toISOString(),
    });
    logActivity(clinicId, {
      therapistId,
      therapistName: therapistName || null,
      action: "blocked_time_create_failed",
      entityType: "appointment",
      details: { errorMessage: error.message },
    });
    throw error;
  }
};

/**
 * Get all appointments in a recurring group
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} recurringGroupId - The recurring group ID
 * @returns {Array} - Array of appointments in the series
 *
 * Error Code: CK-8342
 */
export const getRecurringGroupAppointments = async (
  clinicId,
  recurringGroupId,
  currentTherapistId = null,
  isAdmin = false
) => {
  try {
    const appointmentsRef = collection(db, "clinics", clinicId, "appointments");
    const constraints = [
      where("recurringGroupId", "==", recurringGroupId),
    ];
    if (!isAdmin && currentTherapistId) {
      constraints.push(where("therapistId", "==", currentTherapistId));
    }
    constraints.push(orderBy("startTime", "asc"));

    const q = query(appointmentsRef, ...constraints);
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map((doc) => serializeForRedux({ id: doc.id, ...doc.data() }))
      .filter((apt) => apt.isDeleted !== true);
  } catch (error) {
    console.error("[CK-8342] Error fetching recurring group appointments:", {
      errorCode: "CK-8342",
      errorMessage: error.message,
      clinicId,
      recurringGroupId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Delete recurring appointments based on scope.
 *
 * Past / completed appointments in the series are skipped by default (the
 * historical record is preserved). Pass `includePast: true` to also soft-delete
 * already-ended/completed siblings — used when the therapist explicitly opts
 * in via the recurring delete dialog.
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} appointmentId - The appointment ID to start from
 * @param {string} scope - "single" | "all" | "future"
 * @param {string} currentTherapistId - The UID of the currently logged-in therapist
 * @param {boolean} isAdmin - Whether the current user is clinic admin/owner
 * @param {Object} [options]
 * @param {string|null} [options.deletedBy=null]
 * @param {boolean} [options.includePast=false] - if true, past/completed
 *   appointments in the series are also soft-deleted instead of being skipped.
 * @returns {Object} - { deletedCount, deletedIds, skippedCompletedCount }
 *
 * Error Code: CK-8343
 */
export const deleteRecurringAppointments = async (
  clinicId,
  appointmentId,
  scope = "single",
  currentTherapistId,
  isAdmin = false,
  { deletedBy = null, includePast = false } = {}
) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  try {
    const appointmentsRef = collection(db, "clinics", clinicId, "appointments");

    // First, get the target appointment to find the recurring group
    const targetDoc = await getDoc(doc(appointmentsRef, appointmentId));
    if (!targetDoc.exists()) {
      throw new Error("Appointment not found");
    }

    const targetAppointment = targetDoc.data();

    // Helper: is this doc past its endTime already?
    const isPastEnd = (data) => {
      if (!data?.endTime) return false;
      const endMs = data.endTime.toDate
        ? data.endTime.toDate().getTime()
        : new Date(data.endTime).getTime();
      return !Number.isNaN(endMs) && endMs < Date.now();
    };

    const softDeletePayload = {
      isDeleted: true,
      deletedAt: serverTimestamp(),
      deletedBy: deletedBy || null,
      updatedAt: serverTimestamp(),
    };

    // If not recurring or scope is single, just soft-delete the one appointment.
    // Past/completed appointments are now allowed — the caller decides via
    // `includePast` (single scope inherently means "this one", so no skip).
    if (!targetAppointment.isRecurring || scope === "single") {
      if (targetAppointment.isDeleted === true) {
        return { deletedCount: 0, deletedIds: [], skippedCompletedCount: 0 };
      }
      await updateDoc(doc(appointmentsRef, appointmentId), softDeletePayload);
      return { deletedCount: 1, deletedIds: [appointmentId], skippedCompletedCount: 0 };
    }

    const recurringGroupId = targetAppointment.recurringGroupId;

    let appointmentsToDelete = [];
    let skippedCompletedCount = 0;

    const buildRecurringQuery = (extraConstraints = []) => {
      const constraints = [
        where("recurringGroupId", "==", recurringGroupId),
      ];
      if (!isAdmin && currentTherapistId) {
        constraints.push(where("therapistId", "==", currentTherapistId));
      }
      constraints.push(...extraConstraints);
      return query(appointmentsRef, ...constraints);
    };

    const collectFromSnapshot = (snapshot) => {
      snapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        // Already soft-deleted — skip silently
        if (data.isDeleted === true) return;
        const isEnded = data.status === "completed" || isPastEnd(data);
        if (isEnded && !includePast) {
          skippedCompletedCount++;
        } else {
          appointmentsToDelete.push(docSnap.id);
        }
      });
    };

    if (scope === "all") {
      collectFromSnapshot(await getDocs(buildRecurringQuery()));
    } else if (scope === "future") {
      collectFromSnapshot(
        await getDocs(
          buildRecurringQuery([where("startTime", ">=", targetAppointment.startTime)])
        )
      );
    }

    // Soft-delete in batches
    const batch = writeBatch(db);
    appointmentsToDelete.forEach((id) => {
      batch.update(doc(appointmentsRef, id), softDeletePayload);
    });
    await batch.commit();

    return {
      deletedCount: appointmentsToDelete.length,
      deletedIds: appointmentsToDelete,
      skippedCompletedCount,
    };
  } catch (error) {
    console.error("[CK-8343] Error deleting recurring appointments:", {
      errorCode: "CK-8343",
      errorMessage: error.message,
      clinicId,
      appointmentId,
      scope,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Therapist double-booking check for a planned series shift.
 *
 * Only occurrences that are still in the future are checked, and the series'
 * own documents are excluded so siblings don't flag each other. Room conflicts
 * are deliberately not checked here — that would cost one query per occurrence.
 *
 * A failure here must never block the therapist's edit, so it degrades to
 * "no conflicts found".
 *
 * Error Code: CK-8345
 */
const findSeriesShiftConflicts = async (
  clinicId,
  therapistId,
  planned,
  groupIds,
  recurringGroupId = null
) => {
  try {
    if (!therapistId) return [];
    const nowMs = Date.now();
    const candidates = planned
      .filter((p) => p.start.getTime() > nowMs)
      .map((p) => ({
        startTime: p.start.toISOString(),
        endTime: p.end.toISOString(),
        seriesIndex: p.appt?.recurringIndex ?? null,
      }));
    if (candidates.length === 0) return [];

    const { conflicts } = await checkRecurringConflicts(
      clinicId,
      therapistId,
      candidates,
      groupIds,
      recurringGroupId
    );
    return conflicts || [];
  } catch (error) {
    console.error("[CK-8345] Error checking series shift conflicts:", {
      errorCode: "CK-8345",
      errorMessage: error.message,
      clinicId,
      plannedCount: planned?.length,
      timestamp: new Date().toISOString(),
    });
    return [];
  }
};

/**
 * Commit `update` writes in chunks so a long series can't exceed Firestore's
 * 500-operation batch limit.
 *
 * @param {Array<{ref: Object, data: Object}>} writes
 */
const commitUpdatesInChunks = async (writes, chunkSize = 400) => {
  for (let i = 0; i < writes.length; i += chunkSize) {
    const batch = writeBatch(db);
    for (const { ref, data } of writes.slice(i, i + chunkSize)) {
      batch.update(ref, data);
    }
    await batch.commit();
  }
};

/**
 * Update recurring appointments based on scope.
 * Supports time/day changes: when the edited appointment has new startTime/endTime,
 * future appointments are shifted by the same time-of-day delta and to the same weekday.
 *
 * Scopes:
 * - "single"        — only the edited meeting.
 * - "future"        — the edited meeting and every later occurrence move to the
 *                     new weekday/time.
 * - "all"           — same, for the whole group.
 * - "postpone_week" — the edited meeting takes the new date/time as a one-off;
 *                     every later occurrence keeps its own weekday and hour but
 *                     slides 7 days later (the series ends a week later). Only
 *                     the time shift propagates — other edited fields stay on
 *                     the edited meeting.
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} appointmentId - The appointment ID to start from
 * @param {Object} updateData - Fields to update
 * @param {string} scope - "single" | "all" | "future" | "postpone_week"
 * @param {Object} [options]
 * @param {boolean} [options.allowConflicts=false] - when false, a time shift that
 *   would double-book the therapist is refused and nothing is written.
 * @returns {Object} - { updatedCount: number }
 *
 * Error Code: CK-8344 (CK-8345 for the postpone branch)
 */
export const updateRecurringAppointments = async (
  clinicId,
  appointmentId,
  updateData,
  scope = "single",
  { allowConflicts = false } = {}
) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  try {
    const appointmentsRef = collection(db, "clinics", clinicId, "appointments");

    const targetDoc = await getDoc(doc(appointmentsRef, appointmentId));
    if (!targetDoc.exists()) {
      throw new Error("Appointment not found");
    }

    const targetAppointment = targetDoc.data();

    // Same result shape as the series branches so the RTK layer can run the
    // financial-transaction sync for single-scope edits too.
    const applySingleUpdate = async () => {
      const appointment = await updateAppointment(clinicId, appointmentId, updateData);
      return {
        appointment,
        updatedCount: 1,
        shiftedCount: 0,
        updatedIds: [appointmentId],
        updated: appointment
          ? [
              {
                id: appointment.id,
                startTime: appointment.startTime,
                status: appointment.status,
                therapistId: appointment.therapistId,
                patientId: appointment.patientId,
                patientName: appointment.patientName,
              },
            ]
          : [],
      };
    };

    if (!targetAppointment.isRecurring || scope === "single") {
      return await applySingleUpdate();
    }

    const recurringGroupId = targetAppointment.recurringGroupId;
    // The modal always echoes startTime/endTime, so their mere presence does
    // not mean the therapist moved the meeting. Only treat it as a time change
    // when the values actually differ from the edited appointment's current
    // times — otherwise a pure rate/notes edit would rewrite every
    // occurrence's start and drag individually-rescheduled meetings back onto
    // the series slot.
    const toMs = (t) => (t?.toDate ? t.toDate().getTime() : new Date(t).getTime());
    const hasTimeChange =
      (updateData.startTime || updateData.endTime) &&
      !(
        updateData.startTime &&
        updateData.endTime &&
        targetAppointment.startTime &&
        targetAppointment.endTime &&
        toMs(updateData.startTime) === toMs(targetAppointment.startTime) &&
        toMs(updateData.endTime) === toMs(targetAppointment.endTime)
      );

    const toDateValue = (t) => (t?.toDate ? t.toDate() : new Date(t));

    // "Push the rest by a week": the edited meeting is a one-off exception and
    // takes the whole update, while every later, not-yet-happened occurrence
    // keeps its own weekday and hour and slides exactly 7 days later. The
    // series therefore ends a week later than before.
    if (scope === "postpone_week") {
      // Nothing actually moved, so there is nothing to push out.
      if (!hasTimeChange) {
        return await applySingleUpdate();
      }

      const groupSnapshot = await getDocs(
        query(appointmentsRef, where("recurringGroupId", "==", recurringGroupId))
      );
      const groupDocs = groupSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      const groupIds = groupDocs.map((d) => d.id);

      // Ordering is against the edited meeting's ORIGINAL start, so moving it
      // far forward doesn't silently change which occurrences count as "later".
      const targetOriginalStartMs = toDateValue(targetAppointment.startTime).getTime();
      const nowMs = Date.now();

      // Never rewrite history: cancelled, deleted, completed and already-past
      // occurrences are left alone. A series belongs to one therapist, so the
      // owner check also keeps a therapist from shifting someone else's docs.
      const siblings = groupDocs.filter((apt) => {
        if (apt.id === appointmentId) return false;
        if (apt.isDeleted === true) return false;
        if (apt.status === "cancelled" || apt.status === "completed") return false;
        if (apt.therapistId !== targetAppointment.therapistId) return false;
        const startMs = toDateValue(apt.startTime).getTime();
        return startMs > targetOriginalStartMs && startMs > nowMs;
      });

      const newStart = new Date(updateData.startTime);
      const newEnd = new Date(updateData.endTime);

      const planned = [
        {
          id: appointmentId,
          appt: targetAppointment,
          update: {
            ...updateData,
            startTime: Timestamp.fromDate(newStart),
            endTime: Timestamp.fromDate(newEnd),
            duration: (newEnd - newStart) / 60000,
          },
          start: newStart,
          end: newEnd,
          shifted: false,
        },
        ...siblings.map((apt) => {
          const oldStart = toDateValue(apt.startTime);
          const oldEnd = toDateValue(apt.endTime);
          // setDate on a local Date (not +7×24h in ms) so the wall-clock hour
          // survives an Israeli DST switch inside the series.
          const shiftedStart = new Date(oldStart);
          shiftedStart.setDate(shiftedStart.getDate() + 7);
          const shiftedEnd = new Date(shiftedStart.getTime() + (oldEnd - oldStart));
          return {
            id: apt.id,
            appt: apt,
            update: {
              startTime: Timestamp.fromDate(shiftedStart),
              endTime: Timestamp.fromDate(shiftedEnd),
              // Only the edited meeting notifies the patient — otherwise the
              // whole series would send one reschedule SMS each.
              suppressRescheduleSms: true,
            },
            start: shiftedStart,
            end: shiftedEnd,
            shifted: true,
          };
        }),
      ];

      const conflicts = allowConflicts
        ? []
        : await findSeriesShiftConflicts(
            clinicId,
            // The edit may re-point the series to another therapist — check
            // against the calendar the occurrences will actually land on.
            updateData.therapistId || targetAppointment.therapistId,
            planned,
            groupIds,
            recurringGroupId
          );

      if (conflicts.length > 0) {
        return {
          success: false,
          hasConflicts: true,
          // External double-bookings only — the therapist may knowingly waive.
          waivable: true,
          conflicts,
          updatedCount: 0,
          shiftedCount: 0,
          updatedIds: [],
          updated: [],
        };
      }

      await commitUpdatesInChunks(
        planned.map((p) => ({
          ref: doc(appointmentsRef, p.id),
          data: { ...p.update, updatedAt: serverTimestamp() },
        }))
      );

      return {
        updatedCount: planned.length,
        shiftedCount: planned.length - 1,
        hasConflicts: false,
        previousStartTime: new Date(targetOriginalStartMs).toISOString(),
        updatedIds: planned.map((p) => p.id),
        updated: planned.map((p) => ({
          id: p.id,
          startTime: p.start.toISOString(),
          status: p.appt.status,
          therapistId: p.appt.therapistId,
          patientId: p.appt.patientId,
          patientName: p.appt.patientName,
        })),
      };
    }

    let appointmentDocs = [];
    // Every doc in the group (including cancelled/deleted ones) — excluded from
    // the conflict check so the series never flags itself.
    let groupDocIds = [];

    if (scope === "all") {
      const q = query(
        appointmentsRef,
        where("recurringGroupId", "==", recurringGroupId)
      );
      const snapshot = await getDocs(q);
      groupDocIds = snapshot.docs.map((d) => d.id);
      appointmentDocs = snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((apt) => apt.isDeleted !== true && apt.status !== "cancelled");
    } else if (scope === "future") {
      const q = query(
        appointmentsRef,
        where("recurringGroupId", "==", recurringGroupId),
        where("startTime", ">=", targetAppointment.startTime)
      );
      const snapshot = await getDocs(q);
      groupDocIds = snapshot.docs.map((d) => d.id);
      // Cancelled occurrences are out of the series for good — they must not
      // take field updates, time shifts, or payment cubes.
      appointmentDocs = snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((apt) => apt.isDeleted !== true && apt.status !== "cancelled");
    }

    const seriesUpdateData = { ...updateData };
    // Video join-link ids are PER OCCURRENCE (each has its own
    // /videoMeetLinks lookup doc). The wizard's edit payload always carries
    // the edited meeting's videoMeetLinkId; fanning it out to every sibling
    // made all occurrences share one link that resolves to the first meeting.
    // Keep it only for the edited doc; siblings keep theirs, or get a fresh id
    // when online meetings are being switched on and they have none.
    const editedVideoMeetLinkId = seriesUpdateData.videoMeetLinkId;
    delete seriesUpdateData.videoMeetLinkId;
    const videoLinkFor = (appt) => {
      if (appt.id === appointmentId) {
        return editedVideoMeetLinkId !== undefined
          ? { videoMeetLinkId: editedVideoMeetLinkId }
          : {};
      }
      if (seriesUpdateData.isOnlineMeeting === true && !appt.videoMeetLinkId) {
        return { videoMeetLinkId: generateVideoMeetLinkId() };
      }
      return {};
    };
    let shiftedCount = 0;
    // Per-occurrence snapshot (id + its OWN resulting startTime + owner) so the
    // RTK layer can sync financial transactions with correct per-meeting dates.
    let updatedOccurrences = [];

    if (hasTimeChange) {
      const newStart = new Date(updateData.startTime);
      const newEnd = new Date(updateData.endTime);
      const newHour = newStart.getHours();
      const newMinute = newStart.getMinutes();
      const newDuration = (newEnd - newStart) / 60000;
      const newDayOfWeek = newStart.getDay();

      // A twice-weekly series has two independent weekly slots. Moving one of
      // them must not drag the other onto the same weekday, so the day/time
      // shift is limited to siblings sharing the edited appointment's slot.
      const isTwiceWeekly =
        targetAppointment.recurringFrequency === RECURRING_FREQUENCY.TWICE_WEEKLY;

      // Blocked-time series with date-anchored frequencies (daily / monthly-
      // by-date / yearly) have occurrences on many weekdays — a day-of-week
      // shift would drag them all onto one weekday. Siblings keep their own
      // date and take only the new time-of-day and duration.
      const isDateAnchoredSeries =
        targetAppointment.isBlockedTime === true &&
        [
          RECURRING_FREQUENCY.DAILY,
          RECURRING_FREQUENCY.MONTHLY_DATE,
          RECURRING_FREQUENCY.YEARLY,
        ].includes(targetAppointment.recurringFrequency);
      const targetSlot = targetAppointment.recurringSlot || null;
      const targetOldStart = targetAppointment.startTime?.toDate
        ? targetAppointment.startTime.toDate()
        : new Date(targetAppointment.startTime);
      const targetOldDay = targetOldStart.getDay();

      // Slot metadata for the moved slot, kept in sync across the whole group
      const pad2 = (n) => String(n).padStart(2, "0");
      const newTimeStr = `${pad2(newHour)}:${pad2(newMinute)}`;
      const slotMetaUpdate = !isTwiceWeekly
        ? {}
        : targetSlot === SERIES_SLOT.SECONDARY
        ? { recurringSecondDay: newDayOfWeek, recurringSecondTime: newTimeStr }
        : { recurringFirstDay: newDayOfWeek, recurringFirstTime: newTimeStr };

      // Plan every write first, so a twice-weekly move that would drop one slot
      // on top of the other can be rejected before anything is committed.
      const planned = appointmentDocs.map((appt) => {
        const oldStart = appt.startTime?.toDate ? appt.startTime.toDate() : new Date(appt.startTime);
        const oldEnd = appt.endTime?.toDate ? appt.endTime.toDate() : new Date(appt.endTime);

        // Legacy docs predate recurringSlot — fall back to matching the weekday
        // the edited appointment sat on before the change.
        const shouldShift =
          !isTwiceWeekly ||
          (appt.recurringSlot && targetSlot
            ? appt.recurringSlot === targetSlot
            : oldStart.getDay() === targetOldDay);

        if (!shouldShift) {
          // The other slot keeps its own day and time, but still takes the
          // non-time edits. A duration change has to be applied against that
          // meeting's own start, otherwise half the series keeps the old length.
          const otherSlotUpdate = {
            ...seriesUpdateData,
            ...slotMetaUpdate,
            ...videoLinkFor(appt),
          };
          delete otherSlotUpdate.startTime;
          delete otherSlotUpdate.endTime;
          delete otherSlotUpdate.date;

          let finalEnd = oldEnd;
          if (newDuration && newDuration !== appt.duration) {
            finalEnd = new Date(oldStart.getTime() + newDuration * 60000);
            otherSlotUpdate.duration = newDuration;
            otherSlotUpdate.endTime = Timestamp.fromDate(finalEnd);
          } else {
            delete otherSlotUpdate.duration;
          }

          return { id: appt.id, appt, update: otherSlotUpdate, start: oldStart, end: finalEnd, shifted: false };
        }

        const shifted = new Date(oldStart);

        if (isDateAnchoredSeries) {
          // The edited occurrence takes its full new date; siblings keep
          // their own date and change only the time-of-day below.
          if (appt.id === appointmentId) {
            shifted.setFullYear(
              newStart.getFullYear(),
              newStart.getMonth(),
              newStart.getDate()
            );
          }
        } else {
          // Shift day-of-week if changed
          const oldDay = shifted.getDay();
          const dayDiff = newDayOfWeek - oldDay;
          shifted.setDate(shifted.getDate() + dayDiff);
        }

        // Set new time
        shifted.setHours(newHour, newMinute, 0, 0);
        const shiftedEnd = new Date(shifted.getTime() + newDuration * 60000);

        const perApptUpdate = {
          ...seriesUpdateData,
          ...slotMetaUpdate,
          ...videoLinkFor(appt),
        };
        perApptUpdate.startTime = Timestamp.fromDate(shifted);
        perApptUpdate.endTime = Timestamp.fromDate(shiftedEnd);
        perApptUpdate.duration = newDuration;

        return { id: appt.id, appt, update: perApptUpdate, start: shifted, end: shiftedEnd, shifted: true };
      });

      // Moving one slot onto the other's day and time would double-book the
      // patient for the rest of the series — refuse instead of writing it.
      if (isTwiceWeekly) {
        const collisions = [];
        for (const moved of planned.filter((p) => p.shifted)) {
          for (const other of planned.filter((p) => !p.shifted)) {
            if (moved.start < other.end && moved.end > other.start) {
              collisions.push({
                date: moved.start.toISOString(),
                seriesIndex: moved.appt.seriesIndex ?? null,
                // Series-internal collision — never waivable.
                selfConflict: true,
                existingAppointment: {
                  id: other.id,
                  patientName: targetAppointment.patientName,
                  startTime: other.start.toISOString(),
                  endTime: other.end.toISOString(),
                },
              });
              break;
            }
          }
        }

        if (collisions.length > 0) {
          return {
            success: false,
            hasConflicts: true,
            // Series-internal — the therapist cannot waive this one.
            waivable: false,
            conflicts: collisions,
            updatedCount: 0,
            shiftedCount: 0,
            updatedIds: [],
            updated: [],
          };
        }
      }

      // Moving the series can also land on unrelated meetings of the same
      // therapist. Those are waivable — surface them before writing.
      const externalConflicts = allowConflicts
        ? []
        : await findSeriesShiftConflicts(
            clinicId,
            // Same as the postpone branch: honour a therapist re-point.
            updateData.therapistId || targetAppointment.therapistId,
            planned.filter((p) => p.shifted),
            groupDocIds,
            recurringGroupId
          );

      if (externalConflicts.length > 0) {
        return {
          success: false,
          hasConflicts: true,
          waivable: true,
          conflicts: externalConflicts,
          updatedCount: 0,
          shiftedCount: 0,
          updatedIds: [],
          updated: [],
        };
      }

      await commitUpdatesInChunks(
        planned.map((p) => {
          if (p.shifted) shiftedCount++;
          return {
            ref: doc(appointmentsRef, p.id),
            data: {
              ...p.update,
              // Only the edited meeting notifies the patient.
              ...(p.id === appointmentId ? {} : { suppressRescheduleSms: true }),
              updatedAt: serverTimestamp(),
            },
          };
        })
      );

      updatedOccurrences = planned.map((p) => ({
        id: p.id,
        startTime: p.start.toISOString(),
        status: p.appt.status,
        therapistId: p.appt.therapistId,
        patientId: p.appt.patientId,
        patientName: p.appt.patientName,
      }));
    } else {
      delete seriesUpdateData.startTime;
      delete seriesUpdateData.endTime;
      delete seriesUpdateData.date;

      await commitUpdatesInChunks(
        appointmentDocs.map((appt) => ({
          ref: doc(appointmentsRef, appt.id),
          data: {
            ...seriesUpdateData,
            ...videoLinkFor(appt),
            updatedAt: serverTimestamp(),
          },
        }))
      );
      shiftedCount = 0;

      updatedOccurrences = appointmentDocs.map((appt) => {
        const start = appt.startTime?.toDate
          ? appt.startTime.toDate()
          : new Date(appt.startTime);
        return {
          id: appt.id,
          startTime: start.toISOString(),
          status: appt.status,
          therapistId: appt.therapistId,
          patientId: appt.patientId,
          patientName: appt.patientName,
        };
      });
    }

    return {
      updatedCount: appointmentDocs.length,
      shiftedCount,
      hasConflicts: false,
      previousStartTime: toDateValue(targetAppointment.startTime).toISOString(),
      updatedIds: appointmentDocs.map((a) => a.id),
      updated: updatedOccurrences,
    };
  } catch (error) {
    console.error("[CK-8344] Error updating recurring appointments:", {
      errorCode: "CK-8344",
      errorMessage: error.message,
      clinicId,
      appointmentId,
      scope,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get completed appointments that need therapist action (for carousel display)
 *
 * Display criteria:
 * - isTeraphistAskedToHide !== true (not hidden by therapist)
 * - status === "completed" (appointment has ended and was marked by Cloud Task)
 * - At least one NotifyAbout flag is true (has pending actions to display)
 * - From the last month
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} therapistId - The therapist ID
 * @returns {Array} - Array of appointment objects needing action
 *
 * Error Code: CK-8330
 */
/**
 * Normalize the three post-completion step flags of a completed appointment
 * IN MEMORY (no write) so the UI only ever sees a consistent pair:
 *
 *   1. Value flag true (step handled) ⇒ NotifyAbout flag forced false.
 *      Heals docs where the Cloud Task completion ran AFTER the therapist
 *      handled a step early (e.g. summary written during the session) and
 *      re-raised the NotifyAbout flag — those cards showed a "done" stage
 *      with no action and were stuck until they aged out.
 *   2. NotifyAbout flag null/undefined and step NOT handled ⇒ forced true.
 *      Legacy / failed-cloud-task self-healing (doc predates the flag,
 *      appointment created retroactively, task never ran).
 *   3. Temp customers never get a summary step.
 *
 * Mutates and returns `apt`. Mirrored server-side by
 * isPendingUpdateAppointment (functions/index.js, telemetryScoringHelper.js).
 *
 * @param {object} apt
 * @param {{ healUnset?: boolean }} [opts] - healUnset=false applies rule 1 only
 *   (callers that never self-healed null flags keep their existing behavior).
 */
const normalizePendingActionFlags = (apt, { healUnset = true } = {}) => {
  const isUnset = (v) => v === null || v === undefined;

  if (apt.isApprovedByTherapist === true) {
    apt.NotifyAboutThisMeetingApproval = false;
  } else if (healUnset && isUnset(apt.NotifyAboutThisMeetingApproval)) {
    apt.NotifyAboutThisMeetingApproval = true;
  }

  if (apt.isTempCustomer === true || apt.isTherapistSummarizedTheMeeting === true) {
    apt.NotifyAboutThisMeetingSummarize = false;
  } else if (healUnset && isUnset(apt.NotifyAboutThisMeetingSummarize)) {
    apt.NotifyAboutThisMeetingSummarize = true;
  }

  if (apt.isTherapistUpdatedPaymentStatus === true) {
    apt.NotifyAboutThisMeetingPaymentStatus = false;
  } else if (healUnset && isUnset(apt.NotifyAboutThisMeetingPaymentStatus)) {
    apt.NotifyAboutThisMeetingPaymentStatus = true;
  }

  return apt;
};

export const getPendingActionAppointments = async (clinicId, therapistId) => {
  try {
    const appointmentsRef = collection(db, "clinics", clinicId, "appointments");

    // Calculate one month ago and now
    const now = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

    // Build query constraints
    // Query completed appointments that are not hidden
    const constraints = [
      where("therapistId", "==", therapistId),
      where("status", "==", "completed"),
      where("isTeraphistAskedToHide", "==", false),
      where("endTime", ">=", Timestamp.fromDate(oneMonthAgo)),
      orderBy("endTime", "desc"),
    ];

    const q = query(appointmentsRef, ...constraints);
    const snapshot = await getDocs(q);

    // Self-healing normalization for legacy / failed-cloud-task appointments:
    // When status === "completed" but a NotifyAbout flag is still null/undefined
    // (meaning the completion workflow never set it — e.g. cloud task didn't run,
    // appointment created retroactively, or the doc predates the flag), treat it
    // as `true` so the therapist still sees the pending step — but only if the
    // step hasn't already been handled (value flag check).
    const appointments = snapshot.docs
      .map((doc) => {
        const apt = serializeForRedux({ id: doc.id, ...doc.data() });

        if (apt.status === "completed") {
          normalizePendingActionFlags(apt);
        }

        return apt;
      })
      // Drop soft-deleted appointments — they never show up in pending actions.
      .filter((apt) => apt.isDeleted !== true)
      .filter((apt) => {
        // Must have at least one pending action (NotifyAbout flag is true)
        return (
          apt.NotifyAboutThisMeetingApproval === true ||
          apt.NotifyAboutThisMeetingSummarize === true ||
          apt.NotifyAboutThisMeetingPaymentStatus === true
        );
      });

    return appointments;
  } catch (error) {
    console.error("[CK-8330] Error fetching pending action appointments:", {
      errorCode: "CK-8330",
      errorMessage: error.message,
      clinicId,
      therapistId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get payment-pending appointments from ALL therapists in a clinic.
 * Used by admin/owner when therapists don't have finance access —
 * these are appointments where therapists completed approval/summary
 * but skipped the payment step (NotifyAboutThisMeetingPaymentStatus is still true).
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} adminTherapistId - The admin's own therapist ID (to exclude own appointments, which already show in the regular query)
 * @returns {Array} - Array of appointment objects needing payment action
 *
 * Error Code: CK-8331
 */
export const getAdminPaymentPendingAppointments = async (clinicId, adminTherapistId) => {
  try {
    const appointmentsRef = collection(db, "clinics", clinicId, "appointments");

    const now = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

    const constraints = [
      where("status", "==", "completed"),
      where("isTeraphistAskedToHide", "==", false),
      where("endTime", ">=", Timestamp.fromDate(oneMonthAgo)),
      orderBy("endTime", "desc"),
    ];

    const q = query(appointmentsRef, ...constraints);
    const snapshot = await getDocs(q);

    const appointments = snapshot.docs
      .map((doc) =>
        // Only clear stale NotifyAbout flags for handled steps; this list never
        // self-healed null flags and must not start surfacing legacy docs.
        normalizePendingActionFlags(
          serializeForRedux({ id: doc.id, ...doc.data() }),
          { healUnset: false }
        )
      )
      .filter((apt) => apt.isDeleted !== true)
      .filter((apt) => {
        if (apt.therapistId === adminTherapistId) return false;
        return (
          apt.NotifyAboutThisMeetingPaymentStatus === true &&
          apt.NotifyAboutThisMeetingApproval !== true &&
          apt.NotifyAboutThisMeetingSummarize !== true
        );
      });

    return appointments;
  } catch (error) {
    console.error("[CK-8331] Error fetching admin payment-pending appointments:", {
      errorCode: "CK-8331",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Manual "self-healing" sync for appointments that should have been moved to
 * "completed" by the appointment-completion Cloud Task but are still stuck
 * in `status: "scheduled"` with `endTime` in the past.
 *
 * For each stale appointment, we:
 *   - Set `status = "completed"` (and record completion metadata)
 *   - For each of the three workflow-notify flags, set it to `true` ONLY if
 *     the matching value-flag has not been handled yet. This ensures steps
 *     the therapist already handled via some other path are not re-surfaced.
 *
 * Scope: therapist's own appointments from the last 60 days.
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} therapistId - The therapist ID to sync
 * @returns {{ updated: number, skipped: number, total: number }}
 *
 * Error Code: CK-8332
 */
export const syncStaleScheduledAppointments = async (clinicId, therapistId) => {
  try {
    const appointmentsRef = collection(db, "clinics", clinicId, "appointments");

    const now = new Date();
    const lookbackStart = new Date();
    lookbackStart.setDate(lookbackStart.getDate() - 60);

    // Reuses the existing composite index:
    //   isTeraphistAskedToHide + status + therapistId + endTime
    const constraints = [
      where("isTeraphistAskedToHide", "==", false),
      where("status", "==", "scheduled"),
      where("therapistId", "==", therapistId),
      where("endTime", ">=", Timestamp.fromDate(lookbackStart)),
      where("endTime", "<", Timestamp.fromDate(now)),
      orderBy("endTime", "desc"),
    ];

    const q = query(appointmentsRef, ...constraints);
    const snapshot = await getDocs(q);

    let updated = 0;
    let skipped = 0;

    // Process in small batches to avoid batch-size limits (500 writes per batch)
    const docs = snapshot.docs;
    const BATCH_SIZE = 400;

    for (let i = 0; i < docs.length; i += BATCH_SIZE) {
      const slice = docs.slice(i, i + BATCH_SIZE);
      const batch = writeBatch(db);

      slice.forEach((docSnap) => {
        const data = docSnap.data();

        // Extra safety: skip soft-deleted and non-scheduled rows.
        if (data.isDeleted === true) {
          skipped += 1;
          return;
        }
        if (data.status === "cancelled" || data.status === "completed") {
          skipped += 1;
          return;
        }

        const updateData = {
          status: "completed",
          completedAt: serverTimestamp(),
          completedBy: "manual_sync",
          completionTaskName: null,
          updatedAt: serverTimestamp(),
        };

        // Only surface workflow steps that weren't already handled
        if (!data.isApprovedByTherapist) {
          updateData.NotifyAboutThisMeetingApproval = true;
        } else {
          updateData.NotifyAboutThisMeetingApproval = false;
        }

        if (!data.isTherapistSummarizedTheMeeting) {
          updateData.NotifyAboutThisMeetingSummarize = true;
        } else {
          updateData.NotifyAboutThisMeetingSummarize = false;
        }

        if (!data.isTherapistUpdatedPaymentStatus) {
          updateData.NotifyAboutThisMeetingPaymentStatus = true;
        } else {
          updateData.NotifyAboutThisMeetingPaymentStatus = false;
        }

        batch.update(docSnap.ref, updateData);
        updated += 1;
      });

      await batch.commit();
    }

    return { updated, skipped, total: docs.length };
  } catch (error) {
    console.error("[CK-8332] Error syncing stale scheduled appointments:", {
      errorCode: "CK-8332",
      errorMessage: error.message,
      clinicId,
      therapistId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Ask the server to scan future scheduled appointments for this therapist
 * and create a Cloud Task (`completionTaskName`) for any appointment that
 * is missing one. This fixes the case where an appointment was created
 * before the completion-task workflow was in place (or created during a
 * window where the trigger failed), so that when its `endTime` arrives the
 * Cloud Task will transition it to "completed" automatically.
 *
 * Thin wrapper around the `backfillCompletionTasks` callable Cloud Function.
 *
 * @param {string} clinicId
 * @param {string} therapistId
 * @returns {{ scheduled: number, alreadyHadTask: number, failed: number, total: number, failures: Array }}
 *
 * Error Code: CK-8333
 */
export const backfillCompletionTasks = async (clinicId, therapistId) => {
  try {
    const fn = httpsCallable(functions, "backfillCompletionTasks");
    const result = await fn({ clinicId, therapistId });
    return result.data;
  } catch (error) {
    console.error("[CK-8333] Error calling backfillCompletionTasks:", {
      errorCode: "CK-8333",
      errorMessage: error.message,
      clinicId,
      therapistId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get recent appointments for a patient without linked treatments
 * Used for linking treatments to appointments
 * Returns appointments from the last month only
 * @param {string} clinicId - The clinic ID
 * @param {string} patientId - The patient ID
 * @param {string} therapistId - The therapist ID (optional, for filtering)
 * @param {number} limit - Max number of appointments to return (default 10)
 * @returns {Array} - Array of appointment objects without treatmentId
 */
export const getRecentPatientAppointmentsWithoutTreatment = async (
  clinicId,
  patientId,
  therapistId = null,
  limitCount = 10
) => {
  try {
    const appointmentsRef = collection(db, "clinics", clinicId, "appointments");

    let constraints = [where("patientId", "==", patientId)];

    // Filter by therapist if provided
    if (therapistId) {
      constraints.push(where("therapistId", "==", therapistId));
    }

    // Get appointments from the past month only (completed or that already happened)
    const now = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

    constraints.push(where("startTime", ">=", Timestamp.fromDate(oneMonthAgo)));
    constraints.push(where("startTime", "<=", Timestamp.fromDate(now)));

    // Order by startTime descending (most recent first)
    constraints.push(orderBy("startTime", "desc"));

    // Limit results
    constraints.push(limit(limitCount));

    const q = query(appointmentsRef, ...constraints);
    const snapshot = await getDocs(q);

    // Filter out soft-deleted, cancelled, and appointments already linked to
    // a treatment. Cancelled appointments are excluded because they didn't
    // occur — there is nothing to document a treatment summary for.
    const appointments = snapshot.docs
      .map((doc) => serializeForRedux({ id: doc.id, ...doc.data() }))
      .filter((apt) => apt.isDeleted !== true)
      .filter((apt) => apt.status !== "cancelled")
      .filter((apt) => apt.isBlockedTime !== true)
      .filter((apt) => !apt.treatmentId);

    return appointments;
  } catch (error) {
    console.error(
      "[CK-8320] Error fetching patient appointments without treatment:",
      {
        errorCode: "CK-8320",
        errorMessage: error.message,
        clinicId,
        patientId,
        therapistId,
        timestamp: new Date().toISOString(),
      }
    );
    throw error;
  }
};

// ============================================
// THERAPIST MANAGEMENT
// ============================================

// Get all therapists for a clinic
export const getClinicTherapists = async (clinicId) => {
  const therapistsRef = collection(db, "therapists");
  const q = query(therapistsRef, where("clinicId", "==", clinicId));
  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) =>
    serializeForRedux({ id: doc.id, ...doc.data() })
  );
};

// Update therapist active status
export const updateTherapistStatus = async (therapistId, isActive) => {
  const therapistRef = doc(db, "therapists", therapistId);
  await updateDoc(therapistRef, {
    isActive,
    updatedAt: serverTimestamp(),
  });

  // Also update in clinic's therapists array
  const therapistSnap = await getDoc(therapistRef);
  if (therapistSnap.exists()) {
    const therapistData = therapistSnap.data();
    const clinicRef = doc(db, "clinics", therapistData.clinicId);
    const clinicSnap = await getDoc(clinicRef);

    if (clinicSnap.exists()) {
      const clinicData = clinicSnap.data();
      const therapists = clinicData.therapists || [];
      const updatedTherapists = therapists.map((t) =>
        t.id === therapistId ? { ...t, isActive } : t
      );

      // Recompute isSoloClinic: the clinic is solo again when no ADDED
      // therapist (the owner is not in this array) remains active. Without
      // this, deactivating the last extra therapist left the clinic stuck as
      // "multi" forever (blocking solo-only features like self-booking).
      const hasActiveAddedTherapist = updatedTherapists.some(
        (t) =>
          t.id !== clinicData.ownerTherapistId && t.isActive !== false
      );

      await updateDoc(clinicRef, {
        therapists: updatedTherapists,
        isSoloClinic: !hasActiveAddedTherapist,
        updatedAt: serverTimestamp(),
      });
    }
  }

  return { success: true };
};

// ============================================
// CLINIC SETTINGS (Work Hours, Booking, etc.)
// ============================================

// Update clinic work hours
export const updateClinicWorkHours = async (clinicId, workHours) => {
  const clinicRef = doc(db, "clinics", clinicId);
  await updateDoc(clinicRef, {
    workHours,
    updatedAt: serverTimestamp(),
  });
};

// Update clinic booking settings
export const updateClinicBookingSettings = async (
  clinicId,
  bookingSettings
) => {
  const clinicRef = doc(db, "clinics", clinicId);
  await updateDoc(clinicRef, {
    bookingSettings,
    updatedAt: serverTimestamp(),
  });
};

// Update clinic WhatsApp settings
export const updateClinicWhatsappSettings = async (
  clinicId,
  whatsappSettings
) => {
  const clinicRef = doc(db, "clinics", clinicId);
  await updateDoc(clinicRef, {
    whatsappSettings,
    updatedAt: serverTimestamp(),
  });
};

// Generate a unique booking link slug
export const generateBookingSlug = async (clinicId) => {
  // Generate a short unique slug
  const slug = Math.random().toString(36).substring(2, 10);
  const clinicRef = doc(db, "clinics", clinicId);

  await updateDoc(clinicRef, {
    bookingSlug: slug,
    updatedAt: serverTimestamp(),
  });

  return slug;
};

// ============================================
// THERAPIST AVAILABILITY (Weekly Slots)
// ============================================

// Re-export date utilities for convenience
export const getWeekId = dateUtilsGetWeekId;
export const getWeekStart = dateUtilsGetWeekStart;
export const getWeekEnd = dateUtilsGetWeekEnd;

// Save weekly availability for a therapist
export const saveWeeklyAvailability = async (
  therapistId,
  weekId,
  slots,
  clinicId
) => {
  // slots is an array of:
  // { date: "2026-01-12", slots: [{start: "09:00", end: "13:00"}, ...], blocked: [{start: "12:00", end: "13:00"}, ...] }
  if (clinicId) {
    await guardTrialWrite(clinicId);
  }
  const availabilityRef = doc(
    db,
    "therapists",
    therapistId,
    "availability",
    weekId
  );

  await setDoc(availabilityRef, {
    weekId,
    slots,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return { weekId, slots };
};

// Get weekly availability for a therapist
export const getWeeklyAvailability = async (therapistId, weekId) => {
  const availabilityRef = doc(
    db,
    "therapists",
    therapistId,
    "availability",
    weekId
  );
  const snap = await getDoc(availabilityRef);

  if (snap.exists()) {
    // Serialize Timestamps to ISO strings for Redux compatibility
    return serializeForRedux(snap.data());
  }
  return null;
};

// Get availability for a date range (for calendar display)
export const getAvailabilityForRange = async (
  therapistId,
  startDate,
  endDate
) => {
  const availabilityRef = collection(
    db,
    "therapists",
    therapistId,
    "availability"
  );
  const snapshot = await getDocs(availabilityRef);

  // Use local date strings for comparison to avoid timezone issues
  const startDateStr = formatLocalDateString(startDate);
  const endDateStr = formatLocalDateString(endDate);

  const allSlots = [];
  snapshot.docs.forEach((docSnap) => {
    const data = docSnap.data();
    if (data.slots) {
      data.slots.forEach((daySlot) => {
        // Compare date strings directly (YYYY-MM-DD format)
        if (daySlot.date >= startDateStr && daySlot.date <= endDateStr) {
          allSlots.push(daySlot);
        }
      });
    }
  });

  return allSlots;
};

// Delete old availability (for auto-cleanup)
export const deleteOldAvailability = async (therapistId, beforeWeekId) => {
  const availabilityRef = collection(
    db,
    "therapists",
    therapistId,
    "availability"
  );
  const q = query(availabilityRef, where("weekId", "<", beforeWeekId));
  const snapshot = await getDocs(q);

  const deletePromises = snapshot.docs.map((docSnap) =>
    deleteDoc(doc(db, "therapists", therapistId, "availability", docSnap.id))
  );

  await Promise.all(deletePromises);
};

// Save availability template (for reuse)
export const saveAvailabilityTemplate = async (therapistId, template) => {
  // template is: { sunday: { slots: [{start, end}], blocked: [{start, end}] }, monday: {...}, ... }
  const therapistRef = doc(db, "therapists", therapistId);
  await updateDoc(therapistRef, {
    availabilityTemplate: template,
    updatedAt: serverTimestamp(),
  });
};

// Get availability template
// Normalizes the legacy flat-array shape ({ sunday: [{start,end}] }) to the
// current shape ({ sunday: { slots: [...], blocked: [...] } }) so callers
// always see one shape regardless of when the template was saved.
export const getAvailabilityTemplate = async (therapistId) => {
  const therapistRef = doc(db, "therapists", therapistId);
  const snap = await getDoc(therapistRef);

  if (snap.exists()) {
    const template = snap.data().availabilityTemplate;
    if (!template) return null;
    const normalized = {};
    Object.keys(template).forEach((dayKey) => {
      const value = template[dayKey];
      normalized[dayKey] = Array.isArray(value)
        ? { slots: value, blocked: [] }
        : {
            slots: value?.slots || [],
            blocked: value?.blocked || [],
          };
    });
    return normalized;
  }
  return null;
};

// ============================================
// SMS SETTINGS (Therapist-level)
// ============================================

/**
 * Default SMS settings for therapists
 */
export const DEFAULT_SMS_SETTINGS = {
  sendOnCreate: true,
  initialTiming: "immediate",
  initialHoursBefore: 3,
  sendReminderEnabled: true,
  reminderHoursBefore: 2,
  cancelRestrictionEnabled: false,
  cancelRestrictionHoursBefore: 24,
  includeTreatmentTypeInSms: false,
  skipShabbatAndHolidays: false,
};

/**
 * Record that a therapist logged in.
 *
 * `lastLoginDate` was historically written only at registration, so NiV's
 * "days inactive" ran on a stale field. The caller throttles this to once per
 * Israel day per therapist (see AuthProvider), so it is at most one small
 * write per user per day.
 *
 * Deliberately NOT guarded by guardTrialWrite: a trial-expired clinic must
 * still report its logins, otherwise NiV goes blind on exactly the clinics
 * that need attention most. It also must stay its own updateDoc — the
 * security rules allow this field only via an `onlyUpdatingFields` allowlist.
 *
 * Error Code: CK-8477
 */
export const recordTherapistLogin = async (therapistId) => {
  if (!therapistId) return;
  try {
    await updateDoc(doc(db, "therapists", therapistId), {
      lastLoginDate: serverTimestamp(),
      loginCount: increment(1),
    });
  } catch (error) {
    console.error("[CK-8477] Error recording therapist login:", {
      errorCode: "CK-8477",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Save SMS settings for a therapist
 * @param {string} therapistId - The therapist ID
 * @param {object} smsSettings - SMS settings object
 * Error Code: CK-8350
 */
export const saveSmsSettings = async (therapistId, smsSettings) => {
  try {
    const therapistRef = doc(db, "therapists", therapistId);
    await updateDoc(therapistRef, {
      smsSettings: {
        sendOnCreate:
          smsSettings.sendOnCreate ?? DEFAULT_SMS_SETTINGS.sendOnCreate,
        initialTiming:
          smsSettings.initialTiming ?? DEFAULT_SMS_SETTINGS.initialTiming,
        initialHoursBefore:
          smsSettings.initialHoursBefore ??
          DEFAULT_SMS_SETTINGS.initialHoursBefore,
        sendReminderEnabled:
          smsSettings.sendReminderEnabled ??
          DEFAULT_SMS_SETTINGS.sendReminderEnabled,
        reminderHoursBefore:
          smsSettings.reminderHoursBefore ??
          DEFAULT_SMS_SETTINGS.reminderHoursBefore,
        cancelRestrictionEnabled:
          smsSettings.cancelRestrictionEnabled ??
          DEFAULT_SMS_SETTINGS.cancelRestrictionEnabled,
        cancelRestrictionHoursBefore:
          smsSettings.cancelRestrictionHoursBefore ??
          DEFAULT_SMS_SETTINGS.cancelRestrictionHoursBefore,
        includeTreatmentTypeInSms:
          smsSettings.includeTreatmentTypeInSms ??
          DEFAULT_SMS_SETTINGS.includeTreatmentTypeInSms,
        skipShabbatAndHolidays:
          smsSettings.skipShabbatAndHolidays ??
          DEFAULT_SMS_SETTINGS.skipShabbatAndHolidays,
      },
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("[CK-8350] Error saving SMS settings:", {
      errorCode: "CK-8350",
      errorMessage: error.message,
      therapistId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get SMS settings for a therapist
 * @param {string} therapistId - The therapist ID
 * @returns {object} - SMS settings or defaults
 * Error Code: CK-8351
 */
export const getSmsSettings = async (therapistId) => {
  try {
    const therapistRef = doc(db, "therapists", therapistId);
    const snap = await getDoc(therapistRef);

    if (snap.exists()) {
      const data = snap.data();
      return {
        sendOnCreate:
          data.smsSettings?.sendOnCreate ?? DEFAULT_SMS_SETTINGS.sendOnCreate,
        initialTiming:
          data.smsSettings?.initialTiming ?? DEFAULT_SMS_SETTINGS.initialTiming,
        initialHoursBefore:
          data.smsSettings?.initialHoursBefore ??
          DEFAULT_SMS_SETTINGS.initialHoursBefore,
        sendReminderEnabled:
          data.smsSettings?.sendReminderEnabled ??
          DEFAULT_SMS_SETTINGS.sendReminderEnabled,
        reminderHoursBefore:
          data.smsSettings?.reminderHoursBefore ??
          DEFAULT_SMS_SETTINGS.reminderHoursBefore,
        cancelRestrictionEnabled:
          data.smsSettings?.cancelRestrictionEnabled ??
          DEFAULT_SMS_SETTINGS.cancelRestrictionEnabled,
        cancelRestrictionHoursBefore:
          data.smsSettings?.cancelRestrictionHoursBefore ??
          DEFAULT_SMS_SETTINGS.cancelRestrictionHoursBefore,
        includeTreatmentTypeInSms:
          data.smsSettings?.includeTreatmentTypeInSms ??
          DEFAULT_SMS_SETTINGS.includeTreatmentTypeInSms,
        skipShabbatAndHolidays:
          data.smsSettings?.skipShabbatAndHolidays ??
          DEFAULT_SMS_SETTINGS.skipShabbatAndHolidays,
      };
    }
    return DEFAULT_SMS_SETTINGS;
  } catch (error) {
    console.error("[CK-8351] Error getting SMS settings:", {
      errorCode: "CK-8351",
      errorMessage: error.message,
      therapistId,
      timestamp: new Date().toISOString(),
    });
    return DEFAULT_SMS_SETTINGS;
  }
};

// ============================================
// END-MEETING SMS (clinic-level post-session message)
// ============================================

/**
 * Default clinic-level end-meeting SMS settings.
 * type: "payment" | "simple" | "rateus". messageText supports {name} and
 * {link} tokens; linkUrl is the therapist-pasted URL ({link}); delayHours is
 * how long after the meeting ends to send (0–24). Mirrors the server default
 * DEFAULT_END_MEETING_SMS in functions/index.js.
 */
export const DEFAULT_END_MEETING_SMS = {
  enabled: false,
  type: "payment",
  messageText: "היי {name}, תודה על הפגישה! לתשלום נוח בקישור: {link}",
  linkUrl: "",
  delayHours: 1,
};

/**
 * Save clinic-level end-meeting SMS settings on the clinic doc.
 * @param {string} clinicId
 * @param {object} endMeetingSms
 * Error Code: CK-5360
 */
export const updateClinicEndMeetingSms = async (clinicId, endMeetingSms) => {
  // Guard: block if trial expired
  await guardTrialWrite(clinicId);

  try {
    const clampedDelay = Math.max(
      0,
      Math.min(24, Number(endMeetingSms?.delayHours) || 0)
    );
    const payload = {
      enabled: endMeetingSms?.enabled ?? DEFAULT_END_MEETING_SMS.enabled,
      type: endMeetingSms?.type ?? DEFAULT_END_MEETING_SMS.type,
      messageText:
        endMeetingSms?.messageText ?? DEFAULT_END_MEETING_SMS.messageText,
      linkUrl: endMeetingSms?.linkUrl ?? DEFAULT_END_MEETING_SMS.linkUrl,
      delayHours: clampedDelay,
    };
    await updateClinicDocument(clinicId, { endMeetingSms: payload });
    return payload;
  } catch (error) {
    console.error("[CK-5360] Error saving end-meeting SMS settings:", {
      errorCode: "CK-5360",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Save the clinic-level "show address in approval link" preference.
 * Absent field = true; gated everywhere with !== false so existing clinics
 * keep showing the address until an admin turns it off.
 * @param {string} clinicId
 * @param {boolean} show
 * Error Code: CK-5362
 */
export const updateClinicShowAddressInApprovalLink = async (clinicId, show) => {
  // Guard: block if trial expired
  await guardTrialWrite(clinicId);

  try {
    const value = show !== false;
    await updateClinicDocument(clinicId, { showAddressInApprovalLink: value });
    return value;
  } catch (error) {
    console.error("[CK-5362] Error saving show-address-in-approval-link:", {
      errorCode: "CK-5362",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Default clinic-level payment summary SMS settings (patient profile finance
 * tab). type: "template" | "template_link" | "free". messageText supports
 * {name}, {amount}, {dates} and {link} tokens; linkUrl is the therapist-pasted
 * payment URL ({link}). Mirrors DEFAULT_PAYMENT_SUMMARY_SMS in
 * functions/index.js. Persisted server-side by the sendPaymentSummarySMS
 * Cloud Function (clinic doc updates are admin-only in Firestore rules).
 */
export const DEFAULT_PAYMENT_SUMMARY_SMS = {
  type: "template",
  messageText:
    'היי {name}, סיכום תשלומים ממתינים עבור הטיפולים בתאריכים: {dates}. סה"כ לתשלום: {amount} ש"ח. נא ליצור קשר עם הקליניקה להסדרת התשלום, תודה!',
  linkUrl: "",
};

/**
 * Superseded default templates. A clinic doc that still stores one of these
 * never customised its message, so it is upgraded to the current default on
 * read instead of being frozen on the old wording. Mirrors
 * LEGACY_PAYMENT_SUMMARY_TEMPLATES in functions/index.js — keep both in sync
 * and append (never edit) when the default text changes again.
 */
export const LEGACY_PAYMENT_SUMMARY_TEMPLATES = [
  'היי {name}, סיכום תשלומים ממתינים עבור הטיפולים בתאריכים: {dates}. סה"כ לתשלום: {amount} ש"ח. תודה!',
];

/**
 * Returns the current default when the stored text is a superseded default,
 * otherwise the therapist's own text untouched.
 */
export const upgradeLegacyPaymentSummaryText = (text) => {
  if (!text) return text;
  return LEGACY_PAYMENT_SUMMARY_TEMPLATES.includes(text.trim())
    ? DEFAULT_PAYMENT_SUMMARY_SMS.messageText
    : text;
};

/**
 * Send a payment summary SMS for selected pending transactions of a patient.
 * The server builds the message (template modes), validates transaction
 * statuses, enforces the monthly SMS quota and a 15-minute per-(therapist,
 * patient) cooldown, and persists the template config on the clinic doc.
 *
 * @param {Object} params
 * @param {string} params.clinicId
 * @param {string} params.patientId
 * @param {string[]} params.transactionIds - 1..10 pending transaction IDs
 * @param {string} params.mode - "template" | "template_link" | "free"
 * @param {string} [params.freeText] - required when mode === "free"
 * @param {object} [params.config] - { type, messageText, linkUrl } to persist
 * @returns {Promise<{success: boolean, data?: object, error?: string, code?: string}>}
 *
 * Error Code: CK-8935
 */
export const sendPaymentSummarySMS = async ({
  clinicId,
  patientId,
  transactionIds,
  mode,
  freeText = null,
  config = null,
}) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      return {
        success: false,
        error: "משתמש לא מחובר. אנא התחבר מחדש.",
      };
    }

    const sendSMS = httpsCallable(functions, "sendPaymentSummarySMS");
    const result = await sendSMS({
      clinicId,
      patientId,
      transactionIds,
      mode,
      freeText,
      config,
    });

    return {
      success: true,
      data: result.data,
    };
  } catch (error) {
    console.error("[CK-8935] Error sending payment summary SMS:", {
      errorCode: error.code,
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });

    let errorMessage = "שגיאה בשליחת הודעת סיכום התשלומים";
    if (error.code === "functions/unauthenticated") {
      errorMessage = "משתמש לא מחובר. אנא התחבר מחדש ונסה שוב.";
    } else if (error.message) {
      // Cloud Function HttpsError messages are already Hebrew and user-facing.
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      code: error.code,
    };
  }
};

// ============================================
// CONFIRMATION LINKS (Public appointment confirmation)
// ============================================

/**
 * Generate a random ID for the confirmation link
 */
const generateLinkId = () => {
  const chars =
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let result = "";
  for (let i = 0; i < 12; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

/**
 * Generate a random ID for a public video meeting link (clinikit.co.il/meet/{id}).
 * Crypto-random and longer than confirmation links - it grants call access.
 */
export const generateVideoMeetLinkId = () => {
  const chars =
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  let result = "";
  for (let i = 0; i < bytes.length; i++) {
    result += chars.charAt(bytes[i] % chars.length);
  }
  return result;
};

/**
 * Create a confirmation link for an appointment
 * @param {string} clinicId - The clinic ID
 * @param {object} linkData - Link data including appointmentId, type, phoneNumber
 * @returns {object} - The created link document
 */
export const createConfirmationLink = async (clinicId, linkData) => {
  const linkId = generateLinkId();
  const linksRef = doc(db, "clinics", clinicId, "confirmationLinks", linkId);

  // Extract last 4 digits of phone number (remove non-digits first)
  const cleanPhone = linkData.phoneNumber.replace(/\D/g, "");
  const lastFourDigits = cleanPhone.slice(-4);

  const linkDoc = {
    id: linkId,
    clinicId,
    appointmentId: linkData.appointmentId,
    type: linkData.type, // 'initial' or 'reminder'
    phoneNumber: linkData.phoneNumber,
    lastFourDigits,
    patientName: linkData.patientName,
    // Communication language snapshot. Read by the reminder/scheduled SMS
    // functions and by the public /confirm page. Absent means Hebrew.
    language: linkData.language === "en" ? "en" : "he",
    appointmentDate: linkData.appointmentDate, // Store as ISO string
    appointmentTime: linkData.appointmentTime,
    appointmentDuration: linkData.appointmentDuration,
    isActive: true,
    sentAutomatically: linkData.sentAutomatically || false,
    isViewed: false,
    viewedAt: null,
    response: null, // 'confirmed' | 'declined' | null
    respondedAt: null,
    deviceInfo: null, // Will store browser/device info on response
    cancelRestrictionHoursBefore: linkData.cancelRestrictionHoursBefore ?? null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(linksRef, linkDoc);
  return serializeForRedux({ ...linkDoc, id: linkId });
};

/**
 * Sync the phone number on all active confirmation links of an appointment.
 * Used when a temp customer's phone is edited — links snapshot the phone at
 * creation time, and both reminder SMS and last-4-digits verification read it
 * from the link doc.
 */
export const syncConfirmationLinkPhone = async (
  clinicId,
  appointmentId,
  phoneNumber
) => {
  try {
    const linksRef = collection(db, "clinics", clinicId, "confirmationLinks");
    const q = query(
      linksRef,
      where("appointmentId", "==", appointmentId),
      where("isActive", "==", true)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return;

    const cleanPhone = phoneNumber.replace(/\D/g, "");
    const lastFourDigits = cleanPhone.slice(-4);

    await Promise.all(
      snapshot.docs.map((linkDoc) =>
        updateDoc(linkDoc.ref, {
          phoneNumber,
          lastFourDigits,
          updatedAt: serverTimestamp(),
        })
      )
    );
  } catch (error) {
    console.error("[CK-8360] Error syncing confirmation link phone:", {
      errorCode: "CK-8360",
      errorMessage: error.message,
      clinicId,
      appointmentId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get a confirmation link by ID (public - no auth required)
 * Uses Cloud Function to avoid exposing data to unauthenticated users
 */
export const getConfirmationLinkPublic = async (linkId) => {
  try {
    const getLink = httpsCallable(functions, "getConfirmationLinkPublic");
    const result = await getLink({ linkId });
    return result.data;
  } catch (error) {
    console.error("[CK-6000] Error getting confirmation link:", {
      errorCode: "CK-6000",
      errorMessage: error.message,
      linkId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get a 100ms host token for the therapist to join an online video meeting.
 * Server verifies clinic membership, feature flag and monthly video quota.
 */
export const getVideoMeetingHostToken = async (clinicId, appointmentId) => {
  try {
    const getToken = httpsCallable(functions, "getVideoMeetingHostToken");
    const result = await getToken({ clinicId, appointmentId });
    return result.data;
  } catch (error) {
    console.error("[CK-8480] Error getting video meeting host token:", {
      errorCode: "CK-8480",
      errorMessage: error.message,
      clinicId,
      appointmentId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get a 100ms guest token for the patient's public join page (/meet/:linkId).
 * Unauthenticated - all validation happens server-side.
 */
export const getVideoMeetingGuestToken = async (linkId) => {
  try {
    const getToken = httpsCallable(functions, "getVideoMeetingGuestToken");
    const result = await getToken({ linkId });
    return result.data;
  } catch (error) {
    console.error("[CK-6060] Error getting video meeting guest token:", {
      errorCode: "CK-6060",
      errorMessage: error.message,
      linkId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Verify the last 4 digits of the phone number
 * Uses Cloud Function for security
 */
export const verifyConfirmationLink = async (linkId, lastFourDigits) => {
  try {
    const verifyCode = httpsCallable(functions, "verifyConfirmationCode");
    const result = await verifyCode({ linkId, code: lastFourDigits });
    return result.data;
  } catch (error) {
    console.error("[CK-6001] Error verifying confirmation link:", {
      errorCode: "CK-6001",
      errorMessage: error.message,
      linkId,
      timestamp: new Date().toISOString(),
    });
    return { success: false, error: "VERIFICATION_ERROR" };
  }
};

/**
 * Update the confirmation response (confirm or decline)
 * Uses Cloud Function for security - no direct public Firestore access
 */
export const updateConfirmationResponse = async (
  linkId,
  response,
  deviceInfo = null
) => {
  try {
    const updateResponse = httpsCallable(
      functions,
      "updateConfirmationResponse"
    );
    const result = await updateResponse({ linkId, response, deviceInfo });

    return result.data;
  } catch (error) {
    console.error("[CK-6010] Error updating confirmation response:", {
      errorCode: "CK-6010",
      errorMessage: error.message,
      linkId,
      response,
      timestamp: new Date().toISOString(),
    });
    return {
      success: false,
      error: "UPDATE_FAILED",
      errorMessage: error.message,
    };
  }
};

// ============================================
// PUBLIC SELF-BOOKING (/book/:slug)
// ============================================

/**
 * Load the public booking page data (clinic + available slots) by slug.
 * Returns { found, available, reason?, clinic, therapistName, duration, days,
 *   services: [{ id, name, color, duration?, price? }],   // public treatment types
 *   slotsByService: { [serviceId]: days } }               // slots at that service's duration
 */
export const getBookingPagePublic = async (slug) => {
  try {
    const fn = httpsCallable(functions, "getBookingPagePublic");
    const result = await fn({ slug });
    return result.data;
  } catch (error) {
    console.error("[CK-6020] Error loading booking page:", {
      errorCode: "CK-6020",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Send an SMS OTP to the patient's phone for self-booking.
 * Returns { success, error? } — error in COOLDOWN | INVALID_PHONE | SMS_QUOTA | NOT_AVAILABLE.
 */
export const sendBookingOtp = async (slug, phone) => {
  try {
    const fn = httpsCallable(functions, "sendBookingOtp");
    const result = await fn({ slug, phone });
    return result.data;
  } catch (error) {
    console.error("[CK-6021] Error sending booking OTP:", {
      errorCode: "CK-6021",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    return { success: false, error: "SEND_FAILED" };
  }
};

/**
 * Verify the OTP and create the (pending-approval) appointment.
 * `treatmentTypeId` is the public service the patient picked (optional — the
 * legacy no-services flow sends none).
 * Returns { success, error?, appointment? }.
 */
export const verifyBookingAndCreate = async (
  slug,
  phone,
  code,
  name,
  startISO,
  treatmentTypeId = null,
) => {
  try {
    const fn = httpsCallable(functions, "verifyBookingAndCreate");
    const result = await fn({
      slug,
      phone,
      code,
      name,
      startISO,
      ...(treatmentTypeId ? { treatmentTypeId } : {}),
    });
    return result.data;
  } catch (error) {
    console.error("[CK-6022] Error creating booking:", {
      errorCode: "CK-6022",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    return { success: false, error: "BOOKING_FAILED" };
  }
};

/**
 * SMS the booker that the therapist approved or rejected their self-booked
 * request. Best-effort — failures here do not block the approval/rejection.
 */
export const sendBookingDecision = async (clinicId, appointmentId, approved) => {
  try {
    const fn = httpsCallable(functions, "sendBookingDecision");
    const result = await fn({ clinicId, appointmentId, approved });
    return result.data;
  } catch (error) {
    console.error("[CK-6024] Error sending booking decision SMS:", {
      errorCode: "CK-6024",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    return { success: false, error: "SEND_FAILED" };
  }
};

/**
 * Get all confirmation links for an appointment
 */
export const getConfirmationLinksForAppointment = async (
  clinicId,
  appointmentId
) => {
  const linksRef = collection(db, "clinics", clinicId, "confirmationLinks");
  const q = query(linksRef, where("appointmentId", "==", appointmentId));
  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) =>
    serializeForRedux({ id: doc.id, ...doc.data() })
  );
};

/**
 * Deactivate a confirmation link
 */
export const deactivateConfirmationLink = async (clinicId, linkId) => {
  const linkRef = doc(db, "clinics", clinicId, "confirmationLinks", linkId);
  await updateDoc(linkRef, {
    isActive: false,
    updatedAt: serverTimestamp(),
  });
};

// ============================================
// SMS FUNCTIONS (via Firebase Cloud Functions)
// ============================================

/**
 * Format date to Hebrew display format
 * Example: "יום ראשון, 12 בינואר 2026"
 */
export const formatHebrewDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString("he-IL", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

/**
 * Format date for an English-speaking patient (clinic-level opt-in via
 * `englishCommunicationEnabled` + patient `communicationLanguage: "en"`).
 * en-GB is day-first, which matches how dates are written in Israel.
 * Example: "Monday, 12 January 2026"
 */
export const formatEnglishDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

/**
 * Send appointment confirmation SMS via Cloud Function
 * @param {object} params - SMS parameters
 * @param {string} params.name - Patient name
 * @param {string} params.date - Appointment date (will be formatted to Hebrew)
 * @param {string} params.time - Appointment time (HH:MM)
 * @param {string} params.linkId - Confirmation link ID
 * @param {string} params.phone - Patient phone number (Israeli format)
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const sendAppointmentSMS = async ({
  name,
  date,
  time,
  linkId,
  phone,
  clinicName = null,
  treatmentTypeName = null,
  customMessage = null,
  videoMeetLink = null,
  // When set, used as the appended link instead of the confirmation URL
  // (e.g. a standalone video-meeting link SMS with no confirmation link).
  rawLink = null,
  // Patient communication language. The server re-checks the clinic opt-in
  // before honouring "en", so a stale client cannot force English.
  language = "he",
  // Messaging router (WhatsApp-primary / SMS-fallback) hints — all optional.
  // The server infers `messageType` from the payload shape when absent, so an
  // old client keeps working; passing them lets the WhatsApp template be
  // filled with structured vars instead of fallbacks.
  messageType = null,
  appointmentId = null,
  patientId = null,
  templateVars = null,
}) => {
  try {
    // Ensure user is authenticated before calling the Cloud Function
    const currentUser = auth.currentUser;

    if (!currentUser) {
      console.error("No authenticated user found when trying to send SMS");
      return {
        success: false,
        error: "משתמש לא מחובר. אנא התחבר מחדש.",
      };
    }



    const sendSMS = httpsCallable(functions, "sendAppointmentSMS");

    // Format the date in the patient's language
    const formattedDate =
      language === "en" ? formatEnglishDate(date) : formatHebrewDate(date);

    // Build the confirmation link (or use the caller-provided raw link)
    const link = rawLink || `https://clinikit.co.il/confirm/${linkId}`;

    const result = await sendSMS({
      name,
      date: formattedDate,
      time,
      link,
      phone,
      clinicName,
      treatmentTypeName,
      customMessage,
      videoMeetLink,
      language,
      messageType,
      appointmentId,
      patientId,
      templateVars,
    });

    return {
      success: true,
      data: result.data,
    };
  } catch (error) {
    console.error("[CK-8510] Error sending appointment SMS:", {
      errorCode: error.code,
      errorMessage: error.message,
    });

    // Extract meaningful error message
    let errorMessage = "שגיאה לא ידועה בשליחת SMS";

    if (error.code === "functions/unauthenticated") {
      errorMessage = "משתמש לא מחובר. אנא התחבר מחדש ונסה שוב.";
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Send a payment reminder SMS to a patient for an outstanding (pending) payment.
 * @param {object} params
 * @param {string} params.clinicId
 * @param {string} params.transactionId
 * @param {string} params.name - Patient name
 * @param {string} params.phone - Patient phone (may be empty; server rejects)
 * @param {string} params.clinicName
 * @param {string} params.date - Appointment date, pre-formatted for display
 * @param {string} params.time - Appointment time, pre-formatted for display
 * @returns {Promise<{success: boolean, data?: object, error?: string, code?: string}>}
 */
export const sendPaymentReminderSMS = async ({
  clinicId,
  transactionId,
  name,
  phone,
  clinicName = null,
  date,
  time,
}) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      return {
        success: false,
        error: "משתמש לא מחובר. אנא התחבר מחדש.",
      };
    }

    const sendSMS = httpsCallable(functions, "sendPaymentReminderSMS");
    const result = await sendSMS({
      clinicId,
      transactionId,
      name,
      phone,
      clinicName,
      date,
      time,
    });

    return {
      success: true,
      data: result.data,
    };
  } catch (error) {
    console.error("[CK-8854] Error sending payment reminder SMS:", {
      errorCode: error.code,
      errorMessage: error.message,
    });

    let errorMessage = "שגיאה בשליחת תזכורת התשלום";
    if (error.code === "functions/unauthenticated") {
      errorMessage = "משתמש לא מחובר. אנא התחבר מחדש ונסה שוב.";
    } else if (error.message) {
      // Cloud Function HttpsError messages are already Hebrew and user-facing.
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      code: error.code,
    };
  }
};

// ============================================
// AI SUMMARY FUNCTIONS
// ============================================

/**
 * Generate AI summary for a treatment document
 *
 * Calls the Cloud Function to generate an AI-powered summary
 * of the treatment fields using GPT-4o.
 *
 * @param {Object} params
 * @param {string} params.clinicId - The clinic ID
 * @param {string} params.treatmentId - The treatment document ID
 * @returns {Promise<{success: boolean, summary?: string, error?: string}>}
 *
 * Error Code: CK-8520
 */
export const generateTreatmentSummary = async ({ clinicId, treatmentId }) => {
  try {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      console.error("[CK-8520] No authenticated user found");
      return {
        success: false,
        error: "משתמש לא מחובר. אנא התחבר מחדש.",
      };
    }



    const generateSummary = httpsCallable(
      functions,
      "generateTreatmentSummary"
    );

    const result = await generateSummary({
      clinicId,
      treatmentId,
    });

    return {
      success: result.data?.success ?? true,
      summary: result.data?.summary,
      status: result.data?.status,
    };
  } catch (error) {
    console.error("[CK-8520] Error generating AI summary:", {
      errorCode: error.code,
      errorMessage: error.message,
      clinicId,
      treatmentId,
    });

    // Extract meaningful error message
    let errorMessage = "שגיאה ביצירת סיכום אוטומטי";

    if (error.code === "functions/unauthenticated") {
      errorMessage = "משתמש לא מחובר. אנא התחבר מחדש ונסה שוב.";
    } else if (error.code === "functions/not-found") {
      errorMessage = "מסמך הטיפול לא נמצא.";
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Enrich Treatment Summary with AI (Generic)
 *
 * Sends template fields to the Cloud Function for AI text enrichment.
 * Only text fields (short_text, long_text) are sent; the rest are skipped.
 *
 * @param {Object} params
 * @param {string} params.clinicId - The clinic ID
 * @param {Array} params.fields - Array of { id, label, type, value }
 * @returns {Promise<{success: boolean, enrichedFields?: Object, originalFields?: Object, hasErrors?: boolean, error?: string}>}
 *
 * Error Code: CK-8521
 */
export const enrichTreatmentSummary = async ({ clinicId, fields }) => {
  try {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      console.error("[CK-8521] No authenticated user found");
      return {
        success: false,
        error: "משתמש לא מחובר. אנא התחבר מחדש.",
      };
    }

    const enrichSummaryFn = httpsCallable(functions, "enrichTreatmentSummary");

    const result = await enrichSummaryFn({
      clinicId,
      fields,
    });

    return {
      success: result.data?.success ?? true,
      enrichedFields: result.data?.enrichedFields,
      originalFields: result.data?.originalFields,
      hasErrors: result.data?.hasErrors,
    };
  } catch (error) {
    console.error("[CK-8521] Error enriching treatment summary:", {
      errorCode: error.code,
      errorMessage: error.message,
      clinicId,
    });

    let errorMessage = "שגיאה בהעשרת הסיכום. אנא נסה שוב או פנה לתמיכה.";

    if (error.code === "functions/unauthenticated") {
      errorMessage = "משתמש לא מחובר. אנא התחבר מחדש ונסה שוב.";
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};

// ============================================
// AI LIVE SESSION FUNCTIONS
// ============================================

/**
 * AI Game Selection - Get game recommendations based on history and goals
 *
 * @param {Object} params
 * @param {string} params.clinicId - The clinic ID
 * @param {string} params.patientId - The patient ID
 * @param {string} params.therapistGoal - What goal/purpose the therapist needs a game for
 * @returns {Promise<{success: boolean, response?: string, error?: string}>}
 *
 * Error Code: CK-8522
 */
export const aiGameSelection = async ({
  clinicId,
  patientId,
  therapistGoal,
}) => {
  try {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      console.error("[CK-8522] No authenticated user found");
      return {
        success: false,
        error: "משתמש לא מחובר. אנא התחבר מחדש.",
      };
    }



    const gameSelectionFn = httpsCallable(functions, "aiGameSelection");

    const result = await gameSelectionFn({
      clinicId,
      patientId,
      therapistGoal,
    });

    return {
      success: result.data?.success ?? true,
      response: result.data?.response,
    };
  } catch (error) {
    console.error("[CK-8522] Error in AI game selection:", {
      errorCode: error.code,
      errorMessage: error.message,
      clinicId,
      patientId,
    });

    return {
      success: false,
      error: error.message || "שגיאה בקבלת המלצות למשחקים",
    };
  }
};

/**
 * AI Patient Consultation - Get AI consultation about the patient
 *
 * @param {Object} params
 * @param {string} params.clinicId - The clinic ID
 * @param {string} params.patientId - The patient ID
 * @param {string} params.therapistQuestion - The therapist's question
 * @returns {Promise<{success: boolean, response?: string, error?: string}>}
 *
 * Error Code: CK-8523
 */
export const aiPatientConsultation = async ({
  clinicId,
  patientId,
  therapistQuestion,
}) => {
  try {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      console.error("[CK-8523] No authenticated user found");
      return {
        success: false,
        error: "משתמש לא מחובר. אנא התחבר מחדש.",
      };
    }



    const patientConsultationFn = httpsCallable(
      functions,
      "aiPatientConsultation"
    );

    const result = await patientConsultationFn({
      clinicId,
      patientId,
      therapistQuestion,
    });

    return {
      success: result.data?.success ?? true,
      response: result.data?.response,
    };
  } catch (error) {
    console.error("[CK-8523] Error in AI patient consultation:", {
      errorCode: error.code,
      errorMessage: error.message,
      clinicId,
      patientId,
    });

    return {
      success: false,
      error: error.message || "שגיאה בהתייעצות לגבי המטופל",
    };
  }
};

/**
 * AI Treatment Consultation - Get AI consultation about the current treatment
 *
 * @param {Object} params
 * @param {string} params.clinicId - The clinic ID
 * @param {string} params.patientId - The patient ID
 * @param {Object} params.currentFormData - The current form data from live session
 * @param {string} params.therapistQuestion - The therapist's question
 * @returns {Promise<{success: boolean, response?: string, error?: string}>}
 *
 * Error Code: CK-8524
 */
export const aiTreatmentConsultation = async ({
  clinicId,
  patientId,
  currentFormData,
  therapistQuestion,
}) => {
  try {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      console.error("[CK-8524] No authenticated user found");
      return {
        success: false,
        error: "משתמש לא מחובר. אנא התחבר מחדש.",
      };
    }



    const treatmentConsultationFn = httpsCallable(
      functions,
      "aiTreatmentConsultation"
    );

    const result = await treatmentConsultationFn({
      clinicId,
      patientId,
      currentFormData,
      therapistQuestion,
    });

    return {
      success: result.data?.success ?? true,
      response: result.data?.response,
    };
  } catch (error) {
    console.error("[CK-8524] Error in AI treatment consultation:", {
      errorCode: error.code,
      errorMessage: error.message,
      clinicId,
      patientId,
    });

    return {
      success: false,
      error: error.message || "שגיאה בהתייעצות לגבי הטיפול",
    };
  }
};

/**
 * AI Treatment Chat - Multi-turn chat with the AI while writing a treatment report
 *
 * @param {Object} params
 * @param {string} params.clinicId - The clinic ID
 * @param {string} params.patientId - The patient ID
 * @param {Object} params.currentFormData - The current report form data ({ fields })
 * @param {Array} params.messages - Conversation history [{ role: 'user'|'assistant', content }]
 * @returns {Promise<{success: boolean, response?: string, error?: string}>}
 *
 * Error Code: CK-8525
 */
export const aiTreatmentChat = async ({
  clinicId,
  patientId,
  currentFormData,
  messages,
}) => {
  try {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      console.error("[CK-8525] No authenticated user found");
      return {
        success: false,
        error: "משתמש לא מחובר. אנא התחבר מחדש.",
      };
    }

    const treatmentChatFn = httpsCallable(functions, "aiTreatmentChat");

    const result = await treatmentChatFn({
      clinicId,
      patientId,
      currentFormData,
      messages,
    });

    return {
      success: result.data?.success ?? true,
      response: result.data?.response,
    };
  } catch (error) {
    console.error("[CK-8525] Error in AI treatment chat:", {
      errorCode: error.code,
      errorMessage: error.message,
      clinicId,
      patientId,
    });

    return {
      success: false,
      error: error.message || "שגיאה בשיחה עם ה-AI",
    };
  }
};

/**
 * AI Support Chat (Lenny) - Ask a support question about using the system
 *
 * History is stored server-side under therapists/{uid}/supportChat by the
 * Cloud Function; the client only sends the new question.
 *
 * @param {Object} params
 * @param {string} params.clinicId - The clinic ID
 * @param {string} params.question - The user's question (max 1000 chars)
 * @param {Object} [params.uiContext] - Best-effort UI location { path, tabs, popup }
 * @returns {Promise<{success: boolean, answer?: string, lang?: string, dailyRemaining?: number, errorType?: string, retryAfterMs?: number, error?: string}>}
 *
 * Error Code: CK-8530
 */
export const aiSupportChat = async ({ clinicId, question, uiContext }) => {
  try {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      console.error("[CK-8530] No authenticated user found");
      return {
        success: false,
        error: "משתמש לא מחובר. אנא התחבר מחדש.",
      };
    }

    const supportChatFn = httpsCallable(functions, "aiSupportChat");

    const result = await supportChatFn({ clinicId, question, uiContext });

    return {
      success: result.data?.success ?? false,
      answer: result.data?.answer,
      lang: result.data?.lang,
      dailyRemaining: result.data?.dailyRemaining,
      errorType: result.data?.errorType,
      retryAfterMs: result.data?.retryAfterMs,
    };
  } catch (error) {
    console.error("[CK-8530] Error in AI support chat:", {
      errorCode: error.code,
      errorMessage: error.message,
      clinicId,
    });

    return {
      success: false,
      errorType: "ai_error",
      error: "אירעה שגיאה, נסו שוב",
    };
  }
};

// ============================================
// AI PATIENT SUMMARY FUNCTIONS
// ============================================

/**
 * Generate Initial Patient Summary - Called when creating a new patient
 *
 * @param {Object} params
 * @param {string} params.clinicId - The clinic ID
 * @param {Object} params.patientData - Sanitized patient data (no PII!)
 * @returns {Promise<{success: boolean, summary?: string, error?: string}>}
 *
 * Error Code: CK-8526
 */
export const generateInitialPatientSummary = async ({
  clinicId,
  patientData,
}) => {
  try {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      console.error("[CK-8526] No authenticated user found");
      return {
        success: false,
        error: "משתמש לא מחובר. אנא התחבר מחדש.",
      };
    }



    const generateSummaryFn = httpsCallable(
      functions,
      "generateInitialPatientSummary"
    );

    const result = await generateSummaryFn({
      clinicId,
      patientData,
    });

    return {
      success: result.data?.success ?? true,
      summary: result.data?.summary,
    };
  } catch (error) {
    console.error("[CK-8526] Error generating initial patient summary:", {
      errorCode: error.code,
      errorMessage: error.message,
      clinicId,
    });

    return {
      success: false,
      error: error.message || "שגיאה ביצירת סיכום מטופל",
    };
  }
};

/**
 * Update Patient Summary - Called when a treatment is locked
 *
 * @param {Object} params
 * @param {string} params.clinicId - The clinic ID
 * @param {string} params.patientId - The patient ID
 * @param {string} params.currentSummary - Current patient summary
 * @param {string} params.treatmentSummary - Treatment summary to incorporate (legacy, optional)
 * @param {Array} params.treatmentFields - Full treatment fields array [{label, value}, ...]
 * @param {string} params.treatmentDate - Treatment date (ISO string)
 * @param {string} params.treatmentTitle - Treatment title
 * @param {number} params.currentAge - Current age
 * @returns {Promise<{success: boolean, summary?: string, error?: string}>}
 *
 * Error Code: CK-8527
 */
export const updatePatientSummary = async ({
  clinicId,
  patientId,
  currentSummary,
  treatmentSummary,
  treatmentFields,
  treatmentDate,
  treatmentTitle,
  currentAge,
}) => {
  try {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      console.error("[CK-8527] No authenticated user found");
      return {
        success: false,
        error: "משתמש לא מחובר. אנא התחבר מחדש.",
      };
    }



    const updateSummaryFn = httpsCallable(functions, "updatePatientSummary");

    const result = await updateSummaryFn({
      clinicId,
      patientId,
      currentSummary,
      treatmentSummary,
      treatmentFields,
      treatmentDate,
      treatmentTitle,
      currentAge,
    });

    return {
      success: result.data?.success ?? true,
      summary: result.data?.summary,
    };
  } catch (error) {
    console.error("[CK-8527] Error updating patient summary:", {
      errorCode: error.code,
      errorMessage: error.message,
      clinicId,
      patientId,
    });

    return {
      success: false,
      error: error.message || "שגיאה בעדכון סיכום מטופל",
    };
  }
};

/**
 * Rebuild the patient's AI summary from scratch (server-side).
 * Used after a locked treatment report is soft-deleted so the evolving
 * summary no longer carries content from the invalidated report.
 * The Cloud Function reads patient info + all remaining valid reports.
 *
 * @param {Object} params
 * @param {string} params.clinicId
 * @param {string} params.patientId
 * @returns {Promise<{success: boolean, summary?: string, error?: string}>}
 *
 * Error Code: CK-8528
 */
export const rebuildPatientSummary = async ({ clinicId, patientId }) => {
  try {
    if (!auth.currentUser) {
      console.error("[CK-8528] No authenticated user found");
      return {
        success: false,
        error: "משתמש לא מחובר. אנא התחבר מחדש.",
      };
    }

    const rebuildFn = httpsCallable(functions, "rebuildPatientSummary");
    const result = await rebuildFn({ clinicId, patientId });

    return {
      success: result.data?.success ?? true,
      summary: result.data?.summary,
    };
  } catch (error) {
    console.error("[CK-8528] Error rebuilding patient summary:", {
      errorCode: error.code,
      errorMessage: error.message,
      clinicId,
      patientId,
      timestamp: new Date().toISOString(),
    });

    return {
      success: false,
      error: error.message || "שגיאה בבניית סיכום מטופל מחדש",
    };
  }
};

// ============================================
// TASKS & REMINDERS FUNCTIONS
// ============================================

// Create a new task
// Combine a date string ("YYYY-MM-DD" or ISO) with an optional "HH:mm"
// into a single Date. Returns null when dateStr is falsy.
// Without a time, the date stays at 00:00 (the existing date-only behavior).
const buildTaskDueAt = (dateStr, timeStr) => {
  if (!dateStr) return null;
  const raw = String(dateStr);
  // Full ISO instants (e.g. "2026-09-10T20:30:00.000Z" from auto-tasks) must be
  // resolved to the local calendar day — slicing the UTC date part would shift
  // evening-created tasks back a day for Israel users.
  const isInstant = /T.*(Z|[+-]\d{2}:?\d{2})$/.test(raw);
  let datePart = raw.split("T")[0];
  if (isInstant) {
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) {
      datePart = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    }
  }
  if (timeStr && /^\d{1,2}:\d{2}$/.test(timeStr)) {
    return new Date(`${datePart}T${timeStr.padStart(5, "0")}:00`);
  }
  return new Date(`${datePart}T00:00:00`);
};

export const createTask = async (
  clinicId,
  taskData,
  creatorId,
  creatorName
) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  const tasksRef = collection(db, "clinics", clinicId, "tasks");
  const newTaskRef = doc(tasksRef);

  const dueAt = buildTaskDueAt(taskData.dueDate, taskData.dueTime);

  const taskDoc = {
    id: newTaskRef.id,
    clinicId,
    createdBy: creatorId,
    assignedTo: taskData.assignedTo || null,
    scope: taskData.scope || "personal", // 'personal' or 'clinic'
    title: taskData.title,
    // Auto-generated tasks (payment / confirmation reminders) send `description`
    note: taskData.note || taskData.description || null,
    categoryId: taskData.categoryId || null,
    status: taskData.status || "todo", // 'todo', 'in_progress', 'waiting', 'done'
    dueDate: dueAt ? Timestamp.fromDate(dueAt) : null,
    hasDueTime: dueAt ? !!taskData.dueTime : false,
    relatedPatientId: taskData.relatedPatientId || taskData.patientId || null,
    relatedAppointmentId: taskData.relatedAppointmentId || null,
    expirationTaskName: null,
    expirationNotifiedAt: null,
    editHistory: [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(newTaskRef, taskDoc);

  // Send notification if task is assigned to someone else (Notification 17)
  if (taskData.assignedTo && taskData.assignedTo !== creatorId) {
    sendNotificationWithPrefCheck(
      clinicId,
      NOTIFICATION_PREF_KEYS.TASK_ASSIGNED,
      {
        title: "משימה הוקצתה לך",
        message: `${creatorName || "מטפל"} הקצה/תה לך משימה: "${
          taskData.title
        }"`,
        type: NOTIFICATION_TYPES.TASK,
        urgency: NOTIFICATION_URGENCY.MEDIUM,
        therapistId: taskData.assignedTo,
        link: "/dashboard/tasks",
        timestamp: new Date().toISOString(),
      },
      true // Default enabled
    ).catch((error) => {
      console.error(
        "[CK-2320] Failed to send task assignment notification:",
        error.message
      );
    });
  }

  // Send clinic-wide notification for clinic-scope tasks (Notification 18)
  // Only if task is NOT assigned to a specific person (to avoid double notification)
  if (taskData.scope === "clinic" && !taskData.assignedTo) {
    sendNotification(clinicId, {
      title: "משימה חדשה בקליניקה",
      message: `${creatorName || "מנהל"} יצר/ה משימת קליניקה: "${
        taskData.title
      }"`,
      type: NOTIFICATION_TYPES.TASK,
      urgency: NOTIFICATION_URGENCY.MEDIUM,
      therapistId: null, // Clinic-wide
      link: "/dashboard/tasks",
      createdBy: creatorId,
      timestamp: new Date().toISOString(),
    });
  }

  return getTask(clinicId, newTaskRef.id);
};

// Get a single task
export const getTask = async (clinicId, taskId) => {
  const taskRef = doc(db, "clinics", clinicId, "tasks", taskId);
  const taskSnap = await getDoc(taskRef);

  if (taskSnap.exists()) {
    return serializeForRedux({ id: taskSnap.id, ...taskSnap.data() });
  }
  return null;
};

// Get tasks for a clinic (with filters)
export const getTasks = async (clinicId, options = {}) => {
  const { scope, therapistId, status, categoryId } = options;

  const tasksRef = collection(db, "clinics", clinicId, "tasks");
  let constraints = [];

  // Filter by scope
  if (scope === "personal" && therapistId) {
    // Personal tasks: created by the therapist
    constraints.push(where("scope", "==", "personal"));
    constraints.push(where("createdBy", "==", therapistId));
  } else if (scope === "clinic") {
    constraints.push(where("scope", "==", "clinic"));
  }

  // Filter by status
  if (status && status !== "all") {
    constraints.push(where("status", "==", status));
  }

  // Filter by category
  if (categoryId) {
    constraints.push(where("categoryId", "==", categoryId));
  }

  constraints.push(orderBy("createdAt", "desc"));

  try {
    const q = query(tasksRef, ...constraints);
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) =>
      serializeForRedux({ id: doc.id, ...doc.data() })
    );
  } catch (error) {
    console.error("[CK-8430] Error fetching tasks:", {
      errorCode: "CK-8430",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// Update a task
export const updateTask = async (
  clinicId,
  taskId,
  taskData,
  editorId,
  editorName
) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  const taskRef = doc(db, "clinics", clinicId, "tasks", taskId);

  // Get current task for history
  const currentTask = await getTask(clinicId, taskId);

  const updateData = { ...taskData };

  // Combine dueDate + optional dueTime; track time-precision in hasDueTime.
  // dueTime is form-only — strip it before writing to Firestore.
  const hasDueDateField = Object.prototype.hasOwnProperty.call(updateData, "dueDate");
  const hasDueTimeField = Object.prototype.hasOwnProperty.call(updateData, "dueTime");
  if (hasDueDateField || hasDueTimeField) {
    const dueAt = buildTaskDueAt(updateData.dueDate, updateData.dueTime);
    updateData.dueDate = dueAt ? Timestamp.fromDate(dueAt) : null;
    updateData.hasDueTime = dueAt ? !!updateData.dueTime : false;
    delete updateData.dueTime;
  }

  // Add to edit history.
  // Firestore updateDoc() rejects any undefined field value, so coalesce every
  // snapshot field to null — missing fields on the doc or omitted update fields
  // would otherwise throw "Unsupported field value: undefined".
  const historyEntry = {
    editedBy: editorId ?? null,
    editorName: editorName ?? null,
    editedAt: new Date().toISOString(),
    changes: {
      before: {
        title: currentTask.title ?? null,
        note: currentTask.note ?? null,
        status: currentTask.status ?? null,
        assignedTo: currentTask.assignedTo ?? null,
        categoryId: currentTask.categoryId ?? null,
        dueDate: currentTask.dueDate ?? null,
      },
      after: {
        title: updateData.title ?? null,
        note: updateData.note ?? null,
        status: updateData.status ?? null,
        assignedTo: updateData.assignedTo ?? null,
        categoryId: updateData.categoryId ?? null,
        dueDate: updateData.dueDate
          ? updateData.dueDate.toDate().toISOString()
          : null,
      },
    },
  };

  const editHistory = [...(currentTask.editHistory || []), historyEntry];

  await updateDoc(taskRef, {
    ...updateData,
    editHistory,
    updatedAt: serverTimestamp(),
  });

  // Send notification if task assignment changed to someone else (Notification 17)
  if (
    updateData.assignedTo &&
    updateData.assignedTo !== currentTask.assignedTo &&
    updateData.assignedTo !== editorId
  ) {
    sendNotificationWithPrefCheck(
      clinicId,
      NOTIFICATION_PREF_KEYS.TASK_ASSIGNED,
      {
        title: "משימה הוקצתה לך",
        message: `${editorName || "מטפל"} הקצה/תה לך משימה: "${
          updateData.title || currentTask.title
        }"`,
        type: NOTIFICATION_TYPES.TASK,
        urgency: NOTIFICATION_URGENCY.MEDIUM,
        therapistId: updateData.assignedTo,
        link: "/dashboard/tasks",
        timestamp: new Date().toISOString(),
      },
      true // Default enabled
    ).catch((error) => {
      console.error(
        "[CK-2485] Failed to send task re-assignment notification:",
        error.message
      );
    });
  }

  return getTask(clinicId, taskId);
};

// Persist a manual ordering of tasks.
// Writes a sequential `order` (0..n-1, top first) to each task in one batch so
// the list keeps the user's drag-and-drop arrangement across sessions/devices.
export const reorderTasks = async (clinicId, orderedTaskIds) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  if (!Array.isArray(orderedTaskIds) || orderedTaskIds.length === 0) return;

  const batch = writeBatch(db);
  orderedTaskIds.forEach((taskId, index) => {
    const taskRef = doc(db, "clinics", clinicId, "tasks", taskId);
    batch.update(taskRef, { order: index, updatedAt: serverTimestamp() });
  });

  await batch.commit();
};

// Delete a task
export const deleteTask = async (clinicId, taskId) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  const taskRef = doc(db, "clinics", clinicId, "tasks", taskId);
  await deleteDoc(taskRef);
  return taskId;
};

// Create a new reminder
export const createReminder = async (
  clinicId,
  reminderData,
  creatorId,
  creatorName
) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  const remindersRef = collection(db, "clinics", clinicId, "reminders");
  const newReminderRef = doc(remindersRef);

  const reminderDoc = {
    id: newReminderRef.id,
    clinicId,
    createdBy: creatorId,
    scope: reminderData.scope || "personal", // 'personal' or 'clinic'
    title: reminderData.title,
    content: reminderData.content || "", // Rich text content
    categoryId: reminderData.categoryId || null,
    editHistory: [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(newReminderRef, reminderDoc);

  // Send clinic-wide notification for clinic-scope reminders (Notification 19)
  if (reminderData.scope === "clinic") {
    sendNotification(clinicId, {
      title: "תזכורת חדשה בקליניקה",
      message: `${creatorName || "מנהל"} יצר/ה תזכורת קליניקה: "${
        reminderData.title
      }"`,
      type: NOTIFICATION_TYPES.REMINDER,
      urgency: NOTIFICATION_URGENCY.MEDIUM,
      therapistId: null, // Clinic-wide
      link: "/dashboard/tasks",
      createdBy: creatorId,
      timestamp: new Date().toISOString(),
    });
  }

  return getReminder(clinicId, newReminderRef.id);
};

// Get a single reminder
export const getReminder = async (clinicId, reminderId) => {
  const reminderRef = doc(db, "clinics", clinicId, "reminders", reminderId);
  const reminderSnap = await getDoc(reminderRef);

  if (reminderSnap.exists()) {
    return serializeForRedux({ id: reminderSnap.id, ...reminderSnap.data() });
  }
  return null;
};

// Get reminders for a clinic (with filters)
export const getReminders = async (clinicId, options = {}) => {
  const { scope, therapistId, categoryId } = options;

  const remindersRef = collection(db, "clinics", clinicId, "reminders");
  let constraints = [];

  // Filter by scope
  if (scope === "personal" && therapistId) {
    constraints.push(where("scope", "==", "personal"));
    constraints.push(where("createdBy", "==", therapistId));
  } else if (scope === "clinic") {
    constraints.push(where("scope", "==", "clinic"));
  }

  // Filter by category
  if (categoryId) {
    constraints.push(where("categoryId", "==", categoryId));
  }

  constraints.push(orderBy("createdAt", "desc"));

  try {
    const q = query(remindersRef, ...constraints);
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) =>
      serializeForRedux({ id: doc.id, ...doc.data() })
    );
  } catch (error) {
    console.error("[CK-8431] Error fetching reminders:", {
      errorCode: "CK-8431",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// Update a reminder
export const updateReminder = async (
  clinicId,
  reminderId,
  reminderData,
  editorId,
  editorName
) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  const reminderRef = doc(db, "clinics", clinicId, "reminders", reminderId);

  // Get current reminder for history
  const currentReminder = await getReminder(clinicId, reminderId);

  // Add to edit history
  const historyEntry = {
    editedBy: editorId,
    editorName: editorName,
    editedAt: new Date().toISOString(),
    changes: {
      before: {
        title: currentReminder.title,
        content: currentReminder.content,
        categoryId: currentReminder.categoryId,
      },
      after: {
        title: reminderData.title,
        content: reminderData.content,
        categoryId: reminderData.categoryId,
      },
    },
  };

  const editHistory = [...(currentReminder.editHistory || []), historyEntry];

  await updateDoc(reminderRef, {
    ...reminderData,
    editHistory,
    updatedAt: serverTimestamp(),
  });

  return getReminder(clinicId, reminderId);
};

// Delete a reminder
export const deleteReminder = async (clinicId, reminderId) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  const reminderRef = doc(db, "clinics", clinicId, "reminders", reminderId);
  await deleteDoc(reminderRef);
  return reminderId;
};

// ============================================
// CATEGORIES FUNCTIONS (tasks, reminders & treatment types)
// Treatment types (type === "treatmentType") also carry rate/duration and the
// public self-booking flags (isPublic / showPrice / showDuration) — see
// src/utils/treatmentTypeRules.js for the read rules.
// ============================================

// Create a category
export const createCategory = async (clinicId, categoryData) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  const categoriesRef = collection(db, "clinics", clinicId, "categories");
  const newCategoryRef = doc(categoriesRef);

  const categoryDoc = {
    id: newCategoryRef.id,
    name: categoryData.name,
    color: categoryData.color,
    type: categoryData.type, // 'task', 'reminder', or 'treatmentType'
    ...(categoryData.type === "treatmentType" && {
      rate: categoryData.rate ? Number(categoryData.rate) : null,
      duration: categoryData.duration ? Number(categoryData.duration) : null,
      isPublic: categoryData.isPublic === true,
      showPrice: categoryData.showPrice === true,
      showDuration: categoryData.showDuration !== false,
    }),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(newCategoryRef, categoryDoc);
  return serializeForRedux({ ...categoryDoc, id: newCategoryRef.id });
};

/**
 * Seed treatment-type categories in one batch (signup wizard).
 * Same doc shape as createCategory(type: "treatmentType"); public-booking
 * flags are left off — the therapist enables them later in settings.
 *
 * @param {string} clinicId
 * @param {Array<{name:string,color:string,duration?:number|null,rate?:number|null}>} types
 * @returns {Array} created category docs (Redux-serializable)
 */
export const seedTreatmentTypes = async (clinicId, types = []) => {
  const valid = (types || []).filter((t) => t && String(t.name || "").trim());
  if (!clinicId || valid.length === 0) return [];

  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  try {
    const categoriesRef = collection(db, "clinics", clinicId, "categories");
    const batch = writeBatch(db);
    const created = [];

    valid.forEach((t) => {
      const ref = doc(categoriesRef);
      const durationNum = Number(t.duration);
      const rateNum = Number(t.rate);
      const categoryDoc = {
        id: ref.id,
        name: String(t.name).trim(),
        color: t.color,
        type: "treatmentType",
        rate: t.rate != null && t.rate !== "" && !Number.isNaN(rateNum) ? rateNum : null,
        duration: durationNum > 0 ? durationNum : null,
        isPublic: false,
        showPrice: false,
        showDuration: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      batch.set(ref, categoryDoc);
      created.push(categoryDoc);
    });

    await batch.commit();
    return created.map((c) => serializeForRedux(c));
  } catch (error) {
    console.error("[CK-8440] Error seeding treatment types:", {
      errorCode: "CK-8440",
      errorMessage: error.message,
      clinicId,
      count: valid.length,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// Get categories for a clinic
export const getCategories = async (clinicId, type = null) => {
  const categoriesRef = collection(db, "clinics", clinicId, "categories");

  let q;
  if (type) {
    q = query(categoriesRef, where("type", "==", type), orderBy("name", "asc"));
  } else {
    q = query(categoriesRef, orderBy("name", "asc"));
  }

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) =>
    serializeForRedux({ id: doc.id, ...doc.data() })
  );
};

// Update a category
export const updateCategory = async (clinicId, categoryId, categoryData) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  const categoryRef = doc(db, "clinics", clinicId, "categories", categoryId);

  await updateDoc(categoryRef, {
    ...categoryData,
    updatedAt: serverTimestamp(),
  });

  const categorySnap = await getDoc(categoryRef);
  return serializeForRedux({ id: categorySnap.id, ...categorySnap.data() });
};

// Delete a category
export const deleteCategory = async (clinicId, categoryId) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  const categoryRef = doc(db, "clinics", clinicId, "categories", categoryId);
  await deleteDoc(categoryRef);
  return categoryId;
};

// ============================================
// ACTIVITY LOGS FUNCTIONS
// ============================================

// Action types for activity logs
export const ACTIVITY_ACTIONS = {
  // Appointments
  CREATE_APPOINTMENT: "create_appointment",
  CREATE_APPOINTMENT_FOR_THERAPIST: "create_appointment_for_therapist",
  UPDATE_APPOINTMENT: "update_appointment",
  DELETE_APPOINTMENT: "delete_appointment",
  SEND_APPOINTMENT_REMINDER: "send_appointment_reminder",
  // Patients
  CREATE_PATIENT: "create_patient",
  UPDATE_PATIENT: "update_patient",
  DELETE_PATIENT: "delete_patient",
  // Tasks
  CREATE_TASK: "create_task",
  UPDATE_TASK: "update_task",
  DELETE_TASK: "delete_task",
  // Reminders
  CREATE_REMINDER: "create_reminder",
  UPDATE_REMINDER: "update_reminder",
  DELETE_REMINDER: "delete_reminder",
  // Categories
  CREATE_CATEGORY: "create_category",
  UPDATE_CATEGORY: "update_category",
  DELETE_CATEGORY: "delete_category",
  // Therapists
  CREATE_THERAPIST: "create_therapist",
  UPDATE_THERAPIST_STATUS: "update_therapist_status",
  // Finance/Payments
  PAYMENT: "payment",
  // Reports
  GENERATE_PATIENT_REPORT: "generate_patient_report",
  GENERATE_AI_TREATMENTS_SUMMARY: "generate_ai_treatments_summary",
  EXPORT_TREATMENT_PDF: "export_treatment_pdf",
  // Punch cards (כרטיסיות)
  CREATE_PUNCH_CARD: "create_punch_card",
  UPDATE_PUNCH_CARD: "update_punch_card",
  CLOSE_PUNCH_CARD: "close_punch_card",
  DELETE_PUNCH_CARD: "delete_punch_card",
  PUNCH_VISIT: "punch_visit",
  RESTORE_PUNCH_VISIT: "restore_punch_visit",
};

// Hebrew labels for action types
export const ACTION_LABELS = {
  create_appointment: "קביעת תור",
  create_appointment_for_therapist: "קביעת תור למטפל ע״י מנהל",
  update_appointment: "עדכון תור",
  delete_appointment: "מחיקת תור",
  send_appointment_reminder: "שליחת תזכורת ידנית למטופל",
  create_patient: "יצירת מטופל",
  update_patient: "עדכון מטופל",
  delete_patient: "מחיקת מטופל",
  create_task: "יצירת משימה",
  update_task: "עדכון משימה",
  delete_task: "מחיקת משימה",
  create_reminder: "יצירת תזכורת",
  update_reminder: "עדכון תזכורת",
  delete_reminder: "מחיקת תזכורת",
  create_category: "יצירת קטגוריה",
  update_category: "עדכון קטגוריה",
  delete_category: "מחיקת קטגוריה",
  create_therapist: "הוספת מטפל",
  update_therapist_status: "עדכון סטטוס מטפל",
  payment: "עדכון תשלום",
  generate_patient_report: "הפקת דוח",
  generate_ai_treatments_summary: "יצירת סיכום טיפולים AI",
  create_punch_card: "יצירת כרטיסיה",
  update_punch_card: "עדכון כרטיסיה",
  close_punch_card: "סגירת כרטיסיה",
  delete_punch_card: "מחיקת כרטיסיה",
  punch_visit: "ניקוב מפגש בכרטיסיה",
  restore_punch_visit: "החזרת ניקוב לכרטיסיה",
};

// Entity types for activity logs
export const ENTITY_TYPES = {
  APPOINTMENT: "appointment",
  PATIENT: "patient",
  TASK: "task",
  REMINDER: "reminder",
  CATEGORY: "category",
  THERAPIST: "therapist",
  PUNCH_CARD: "punch_card",
};

/**
 * Create an activity log entry
 * @param {string} clinicId - The clinic ID
 * @param {object} logData - Log data
 * @returns {object} - The created log document
 */
export const createActivityLog = async (clinicId, logData) => {
  try {
    const logsRef = collection(db, "clinics", clinicId, "activityLogs");
    const newLogRef = doc(logsRef);

    const logDoc = {
      id: newLogRef.id,
      clinicId,
      therapistId: logData.therapistId,
      therapistName: logData.therapistName || "לא ידוע",
      action: logData.action,
      actionLabel: ACTION_LABELS[logData.action] || logData.action,
      entityType: logData.entityType,
      entityId: logData.entityId || null,
      entityName: logData.entityName || null, // e.g., patient name, task title
      // Firestore rejects undefined field values — replace with null so a
      // sloppy caller can't break the audit log write
      details: Object.fromEntries(
        Object.entries(logData.details || {}).map(([k, v]) => [
          k,
          v === undefined ? null : v,
        ])
      ),
      success: logData.success !== false, // Default to true
      errorMessage: logData.errorMessage || null,
      // Client info for audit trail (Israeli medical data regulations)
      ipAddress: logData.ipAddress || null,
      userAgent: logData.userAgent || null,
      createdAt: serverTimestamp(),
    };

    await setDoc(newLogRef, logDoc);
    return serializeForRedux({ ...logDoc, id: newLogRef.id });
  } catch (error) {
    // Don't throw - logging should not break main operations
    console.error("[CK-8100] Error creating activity log:", {
      errorCode: "CK-8100",
      errorMessage: error.message,
      clinicId,
      logData,
      timestamp: new Date().toISOString(),
    });
    return null;
  }
};

/**
 * Get activity logs with pagination and filters
 * @param {string} clinicId - The clinic ID
 * @param {object} options - Filter options
 * @returns {object} - { logs, nextCursor, hasMore }
 */
export const getActivityLogs = async (clinicId, options = {}) => {
  const {
    therapistId,
    action,
    entityType,
    startDate,
    endDate,
    cursor,
    pageSize = 50,
  } = options;

  const logsRef = collection(db, "clinics", clinicId, "activityLogs");
  let constraints = [];

  // Filter by therapist
  if (therapistId) {
    constraints.push(where("therapistId", "==", therapistId));
  }

  // Filter by action type
  if (action && action !== "all") {
    constraints.push(where("action", "==", action));
  }

  // Filter by entity type
  if (entityType && entityType !== "all") {
    constraints.push(where("entityType", "==", entityType));
  }

  // Filter by date range
  if (startDate) {
    constraints.push(
      where("createdAt", ">=", Timestamp.fromDate(new Date(startDate)))
    );
  }
  if (endDate) {
    // Add one day to include the end date fully
    const endDateObj = new Date(endDate);
    endDateObj.setDate(endDateObj.getDate() + 1);
    constraints.push(where("createdAt", "<", Timestamp.fromDate(endDateObj)));
  }

  // Order by creation date (newest first)
  constraints.push(orderBy("createdAt", "desc"));

  // Pagination using cursor
  if (cursor) {
    const cursorDate = new Date(cursor);
    constraints.push(startAfter(Timestamp.fromDate(cursorDate)));
  }

  constraints.push(limit(pageSize));

  try {
    const q = query(logsRef, ...constraints);
    const snapshot = await getDocs(q);

    const logs = snapshot.docs.map((doc) =>
      serializeForRedux({ id: doc.id, ...doc.data() })
    );

    // Get the last document's createdAt for cursor-based pagination
    const lastDocData = snapshot.docs[snapshot.docs.length - 1]?.data();
    const nextCursor = lastDocData?.createdAt
      ? lastDocData.createdAt.toDate().toISOString()
      : null;

    return {
      logs,
      nextCursor,
      hasMore: snapshot.docs.length === pageSize,
    };
  } catch (error) {
    console.error("[CK-8101] Error fetching activity logs:", {
      errorCode: "CK-8101",
      errorMessage: error.message,
      clinicId,
      options,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Delete activity logs older than specified days
 * @param {string} clinicId - The clinic ID
 * @param {number} daysOld - Delete logs older than this many days (default 2555 = 7 years for medical data compliance)
 * @returns {number} - Number of deleted logs
 */
export const deleteOldActivityLogs = async (clinicId, daysOld = 2555) => {
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - daysOld);

  const logsRef = collection(db, "clinics", clinicId, "activityLogs");
  const q = query(
    logsRef,
    where("createdAt", "<", Timestamp.fromDate(cutoffDate)),
    limit(500) // Delete in batches to avoid timeout
  );

  try {
    const snapshot = await getDocs(q);

    const deletePromises = snapshot.docs.map((docSnap) =>
      deleteDoc(doc(db, "clinics", clinicId, "activityLogs", docSnap.id))
    );

    await Promise.all(deletePromises);

    return snapshot.docs.length;
  } catch (error) {
    console.error("[CK-8102] Error deleting old activity logs:", {
      errorCode: "CK-8102",
      errorMessage: error.message,
      clinicId,
      daysOld,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get client information for audit logging
 * Captures IP address (via external service) and User Agent
 * @returns {object} - Client info object with ipAddress and userAgent
 */
export const getClientInfo = async () => {
  try {
    // Get User Agent from browser
    const userAgent =
      typeof navigator !== "undefined" ? navigator.userAgent : null;

    // Get IP address via public API (fire-and-forget, fallback to null)
    let ipAddress = null;
    try {
      const response = await fetch("https://api.ipify.org?format=json", {
        signal: AbortSignal.timeout(3000), // 3 second timeout
      });
      if (response.ok) {
        const data = await response.json();
        ipAddress = data.ip;
      }
    } catch {
      // IP fetch failed - continue without it
    }

    return { ipAddress, userAgent };
  } catch {
    return { ipAddress: null, userAgent: null };
  }
};

/**
 * Helper function to log activity - to be called from other functions
 * This is a fire-and-forget function that won't block the main operation
 * Automatically captures client info (IP, User Agent) for audit trail
 */
export const logActivity = (clinicId, logData) => {
  // Fire and forget - don't await
  (async () => {
    try {
      // Get client info for audit trail
      const clientInfo = await getClientInfo();

      await createActivityLog(clinicId, {
        ...logData,
        ipAddress: clientInfo.ipAddress,
        userAgent: clientInfo.userAgent,
      });
    } catch {
      // Silently fail - logging should not break main operations
    }
  })();
};

// ============================================
// FORMS & TEMPLATES FUNCTIONS
// ============================================

/**
 * Get all forms for a clinic
 * @param {string} clinicId - The clinic ID
 * @returns {Array} - Array of form objects
 */
export const getForms = async (clinicId) => {
  try {
    const formsRef = collection(db, "clinics", clinicId, "forms");
    const q = query(formsRef, orderBy("createdAt", "desc"));
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) =>
      serializeForRedux({ id: doc.id, ...doc.data() })
    );
  } catch (error) {
    console.error("[CK-8200] Error fetching forms:", {
      errorCode: "CK-8200",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get a single form by ID
 * @param {string} clinicId - The clinic ID
 * @param {string} formId - The form ID
 * @returns {Object} - Form object
 */
export const getForm = async (clinicId, formId) => {
  try {
    const formRef = doc(db, "clinics", clinicId, "forms", formId);
    const formSnap = await getDoc(formRef);

    if (!formSnap.exists()) {
      throw new Error("Form not found");
    }

    return serializeForRedux({ id: formSnap.id, ...formSnap.data() });
  } catch (error) {
    console.error("[CK-8201] Error fetching form:", {
      errorCode: "CK-8201",
      errorMessage: error.message,
      clinicId,
      formId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Create a new form
 * @param {string} clinicId - The clinic ID
 * @param {Object} formData - The form data
 * @param {string} creatorId - The therapist ID who created the form
 * @param {string} creatorName - The name of the creator
 * @returns {Object} - Created form object
 */
export const createForm = async (
  clinicId,
  formData,
  creatorId,
  creatorName
) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  try {
    const formsRef = collection(db, "clinics", clinicId, "forms");
    const newFormRef = doc(formsRef);

    const form = {
      ...formData,
      createdBy: creatorId,
      creatorName: creatorName,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(newFormRef, form);

    // Log activity
    logActivity(clinicId, {
      therapistId: creatorId,
      therapistName: creatorName,
      action: "create_form",
      actionLabel: "יצירת טופס",
      entityType: "form",
      entityId: newFormRef.id,
      entityName: formData.name,
      details: { formName: formData.name },
      success: true,
    });

    return serializeForRedux({
      id: newFormRef.id,
      ...form,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error("[CK-8202] Error creating form:", {
      errorCode: "CK-8202",
      errorMessage: error.message,
      clinicId,
      formData,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Update an existing form
 * @param {string} clinicId - The clinic ID
 * @param {string} formId - The form ID
 * @param {Object} formData - The updated form data
 * @param {string} editorId - The therapist ID who edited the form
 * @param {string} editorName - The name of the editor
 * @returns {Object} - Updated form object
 */
export const updateForm = async (
  clinicId,
  formId,
  formData,
  editorId,
  editorName
) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  try {
    const formRef = doc(db, "clinics", clinicId, "forms", formId);

    const updates = {
      ...formData,
      updatedAt: serverTimestamp(),
    };

    await updateDoc(formRef, updates);

    // Log activity
    logActivity(clinicId, {
      therapistId: editorId,
      therapistName: editorName,
      action: "update_form",
      actionLabel: "עדכון טופס",
      entityType: "form",
      entityId: formId,
      entityName: formData.name,
      details: { formName: formData.name },
      success: true,
    });

    const updatedForm = await getForm(clinicId, formId);
    return updatedForm;
  } catch (error) {
    console.error("[CK-8203] Error updating form:", {
      errorCode: "CK-8203",
      errorMessage: error.message,
      clinicId,
      formId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Delete a form
 * @param {string} clinicId - The clinic ID
 * @param {string} formId - The form ID
 * @param {string} formName - The form name (for logging)
 * @param {string} therapistId - The therapist ID who deleted the form
 * @param {string} therapistName - The name of the therapist
 */
export const deleteForm = async (
  clinicId,
  formId,
  formName,
  therapistId,
  therapistName
) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  try {
    const formRef = doc(db, "clinics", clinicId, "forms", formId);
    await deleteDoc(formRef);

    // Log activity
    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: "delete_form",
      actionLabel: "מחיקת טופס",
      entityType: "form",
      entityId: formId,
      entityName: formName,
      details: { formName },
      success: true,
    });
  } catch (error) {
    console.error("[CK-8204] Error deleting form:", {
      errorCode: "CK-8204",
      errorMessage: error.message,
      clinicId,
      formId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Duplicate a form
 * @param {string} clinicId - The clinic ID
 * @param {string} formId - The form ID to duplicate
 * @param {string} creatorId - The therapist ID who is duplicating
 * @param {string} creatorName - The name of the creator
 * @returns {Object} - The duplicated form object
 */
export const duplicateForm = async (
  clinicId,
  formId,
  creatorId,
  creatorName
) => {
  try {
    const originalForm = await getForm(clinicId, formId);

    const newFormData = {
      name: `${originalForm.name} (עותק)`,
      content: originalForm.content,
    };

    return await createForm(clinicId, newFormData, creatorId, creatorName);
  } catch (error) {
    console.error("[CK-8205] Error duplicating form:", {
      errorCode: "CK-8205",
      errorMessage: error.message,
      clinicId,
      formId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// ============================================
// TEMPLATES FUNCTIONS
// ============================================

/**
 * Get all templates for a clinic
 * @param {string} clinicId - The clinic ID
 * @returns {Array} - Array of template objects
 */
export const getTemplates = async (clinicId) => {
  try {
    const templatesRef = collection(db, "clinics", clinicId, "templates");
    const q = query(templatesRef, orderBy("createdAt", "desc"));
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) =>
      serializeForRedux({ id: doc.id, ...doc.data() })
    );
  } catch (error) {
    console.error("[CK-8210] Error fetching templates:", {
      errorCode: "CK-8210",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get a single template by ID
 * @param {string} clinicId - The clinic ID
 * @param {string} templateId - The template ID
 * @returns {Object} - Template object
 */
export const getTemplate = async (clinicId, templateId) => {
  try {
    const templateRef = doc(db, "clinics", clinicId, "templates", templateId);
    const templateSnap = await getDoc(templateRef);

    if (!templateSnap.exists()) {
      throw new Error("Template not found");
    }

    return serializeForRedux({ id: templateSnap.id, ...templateSnap.data() });
  } catch (error) {
    console.error("[CK-8211] Error fetching template:", {
      errorCode: "CK-8211",
      errorMessage: error.message,
      clinicId,
      templateId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Create a new template
 * @param {string} clinicId - The clinic ID
 * @param {Object} templateData - The template data including fields array
 * @param {string} creatorId - The therapist ID who created the template
 * @param {string} creatorName - The name of the creator
 * @returns {Object} - Created template object
 */
export const createTemplate = async (
  clinicId,
  templateData,
  creatorId,
  creatorName
) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  try {
    const templatesRef = collection(db, "clinics", clinicId, "templates");
    const newTemplateRef = doc(templatesRef);

    const template = {
      ...templateData,
      createdBy: creatorId,
      creatorName: creatorName,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(newTemplateRef, template);

    // Log activity
    logActivity(clinicId, {
      therapistId: creatorId,
      therapistName: creatorName,
      action: "create_template",
      actionLabel: "יצירת תבנית",
      entityType: "template",
      entityId: newTemplateRef.id,
      entityName: templateData.name,
      details: {
        templateName: templateData.name,
        fieldsCount: templateData.fields?.length || 0,
      },
      success: true,
    });

    return serializeForRedux({
      id: newTemplateRef.id,
      ...template,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error("[CK-8212] Error creating template:", {
      errorCode: "CK-8212",
      errorMessage: error.message,
      clinicId,
      templateData,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Update an existing template
 * @param {string} clinicId - The clinic ID
 * @param {string} templateId - The template ID
 * @param {Object} templateData - The updated template data
 * @param {string} editorId - The therapist ID who edited the template
 * @param {string} editorName - The name of the editor
 * @returns {Object} - Updated template object
 */
export const updateTemplate = async (
  clinicId,
  templateId,
  templateData,
  editorId,
  editorName
) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  try {
    const templateRef = doc(db, "clinics", clinicId, "templates", templateId);

    const updates = {
      ...templateData,
      updatedAt: serverTimestamp(),
    };

    await updateDoc(templateRef, updates);

    // Log activity
    logActivity(clinicId, {
      therapistId: editorId,
      therapistName: editorName,
      action: "update_template",
      actionLabel: "עדכון תבנית",
      entityType: "template",
      entityId: templateId,
      entityName: templateData.name,
      details: {
        templateName: templateData.name,
        fieldsCount: templateData.fields?.length || 0,
      },
      success: true,
    });

    const updatedTemplate = await getTemplate(clinicId, templateId);
    return updatedTemplate;
  } catch (error) {
    console.error("[CK-8213] Error updating template:", {
      errorCode: "CK-8213",
      errorMessage: error.message,
      clinicId,
      templateId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Delete a template
 * @param {string} clinicId - The clinic ID
 * @param {string} templateId - The template ID
 * @param {string} templateName - The template name (for logging)
 * @param {string} therapistId - The therapist ID who deleted the template
 * @param {string} therapistName - The name of the therapist
 */
export const deleteTemplate = async (
  clinicId,
  templateId,
  templateName,
  therapistId,
  therapistName
) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);
  try {
    const templateRef = doc(db, "clinics", clinicId, "templates", templateId);
    await deleteDoc(templateRef);

    // Log activity
    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: "delete_template",
      actionLabel: "מחיקת תבנית",
      entityType: "template",
      entityId: templateId,
      entityName: templateName,
      details: { templateName },
      success: true,
    });
  } catch (error) {
    console.error("[CK-8214] Error deleting template:", {
      errorCode: "CK-8214",
      errorMessage: error.message,
      clinicId,
      templateId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Duplicate a template
 * @param {string} clinicId - The clinic ID
 * @param {string} templateId - The template ID to duplicate
 * @param {string} creatorId - The therapist ID who is duplicating
 * @param {string} creatorName - The name of the creator
 * @returns {Object} - The duplicated template object
 */
export const duplicateTemplate = async (
  clinicId,
  templateId,
  creatorId,
  creatorName
) => {
  try {
    const originalTemplate = await getTemplate(clinicId, templateId);

    const newTemplateData = {
      name: `${originalTemplate.name} (עותק)`,
      fields: originalTemplate.fields.map((field) => ({
        ...field,
        id: `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      })),
    };

    return await createTemplate(
      clinicId,
      newTemplateData,
      creatorId,
      creatorName
    );
  } catch (error) {
    console.error("[CK-8215] Error duplicating template:", {
      errorCode: "CK-8215",
      errorMessage: error.message,
      clinicId,
      templateId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// ============================================
// DIGITAL SIGNATURE FUNCTIONS
// ============================================

/**
 * Send a form to a patient for digital signature
 * Creates a signature link and sends SMS to the patient
 * @param {string} clinicId - The clinic ID
 * @param {string} formId - The form template ID
 * @param {string} patientId - The patient ID
 * @param {string} messageTemplate - Optional custom SMS template
 * @returns {Object} - { success, linkId, signatureUrl }
 */
export const sendFormForSignature = async (
  clinicId,
  formId,
  patientId,
  messageTemplate = null,
  systemForm = null
) => {
  try {
    const sendFormForSignatureFn = httpsCallable(
      functions,
      "sendFormForSignature"
    );
    const result = await sendFormForSignatureFn({
      clinicId,
      formId,
      patientId,
      messageTemplate,
      systemForm,
    });

    return result.data;
  } catch (error) {
    console.error("[CK-8252] Error sending form for signature:", {
      errorCode: "CK-8252",
      errorMessage: error.message,
      clinicId,
      formId,
      patientId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get signature form data for public signing page (no auth required)
 * @param {string} linkId - The signature link ID
 * @returns {Object} - Form data for signing
 */
export const getSignatureFormPublic = async (linkId) => {
  try {
    const getSignatureFormPublicFn = httpsCallable(
      functions,
      "getSignatureFormPublic"
    );
    const result = await getSignatureFormPublicFn({ linkId });

    return result.data;
  } catch (error) {
    console.error("[CK-8255] Error getting signature form:", {
      errorCode: "CK-8255",
      errorMessage: error.message,
      linkId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Verify patient identity for signature
 * @param {string} linkId - The signature link ID
 * @param {string} idNumber - Patient's ID number (תעודת זהות)
 * @param {string} phoneNumber - Patient's phone number
 * @returns {Object} - { success, verified, error }
 */
export const verifyPatientIdentity = async (linkId, idNumber, phoneNumber) => {
  try {
    const verifyPatientIdentityFn = httpsCallable(
      functions,
      "verifyPatientIdentity"
    );
    const result = await verifyPatientIdentityFn({
      linkId,
      idNumber,
      phoneNumber,
    });

    return result.data;
  } catch (error) {
    console.error("[CK-8258] Error verifying patient identity:", {
      errorCode: "CK-8258",
      errorMessage: error.message,
      linkId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Submit a signed form
 * @param {string} linkId - The signature link ID
 * @param {Object} signatures - Map of slot ID to { data, type } (e.g. { "1": { data, type: "drawn" }, "2": { data, type: "typed" } })
 * @param {Object} deviceInfo - Browser/device information
 * @param {Object} freeTextValues - Map of label to value from free-text inputs
 * @returns {Object} - { success, signedFormId, pdfUrl }
 */
export const submitSignedForm = async (
  linkId,
  signatures,
  deviceInfo,
  freeTextValues = {}
) => {
  try {
    const submitSignedFormFn = httpsCallable(functions, "submitSignedForm");
    const result = await submitSignedFormFn({
      linkId,
      signatures: signatures || {},
      deviceInfo,
      freeTextValues,
    });

    return result.data;
  } catch (error) {
    console.error("[CK-8261] Error submitting signed form:", {
      errorCode: "CK-8261",
      errorMessage: error.message,
      linkId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Regenerate (or just refresh the URL of) the PDF for an existing signed form.
 * If the file in Storage is intact, this issues a fresh stable download-token URL.
 * If it's missing, the PDF is rebuilt from the stored signed-form data.
 * @param {string} clinicId
 * @param {string} signedFormId
 * @returns {{ success: boolean, pdfUrl: string, regenerated: boolean }}
 */
export const regenerateSignedFormPdf = async (clinicId, signedFormId) => {
  try {
    const fn = httpsCallable(functions, "regenerateSignedFormPdf");
    const result = await fn({ clinicId, signedFormId });
    return result.data;
  } catch (error) {
    console.error("[CK-8262] Error regenerating signed form PDF:", {
      errorCode: "CK-8262",
      errorMessage: error.message,
      clinicId,
      signedFormId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Generate a patient report PDF server-side (Reports page wizard).
 * The Cloud Function renders the compiled payload with Chromium, uploads the
 * PDF to Storage and writes a generatedReports history doc.
 * @param {Object} payload - { clinicId, patientId, patientName, reportTypeId,
 *   reportTypeLabel, rangeStart, rangeEnd, columns, rows }
 * @returns {{ success: boolean, pdfUrl: string, reportId: string }}
 */
export const generatePatientReportPdf = async (payload) => {
  try {
    const fn = httpsCallable(functions, "generatePatientReportPdf");
    const result = await fn(payload);
    return result.data;
  } catch (error) {
    console.error("[CK-8270] Error generating patient report PDF:", {
      errorCode: "CK-8270",
      errorMessage: error.message,
      clinicId: payload?.clinicId,
      reportTypeId: payload?.reportTypeId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Ask the AI to draft a multi-treatment summary (סיכום טיפולים AI report).
 * Server loads the locked treatments, sends PII-free content to OpenAI and
 * returns editable HTML.
 * @param {Object} payload - { clinicId, patientId, treatmentIds, therapistNotes }
 * @returns {{ success, summaryHtml, treatmentsUsed, skippedIds, truncated, dropped }}
 */
export const generateTreatmentsReportSummary = async (payload) => {
  try {
    const fn = httpsCallable(functions, "generateTreatmentsReportSummary");
    const result = await fn(payload);
    return result.data;
  } catch (error) {
    console.error("[CK-8946] Error generating AI treatments summary:", {
      errorCode: "CK-8946",
      errorMessage: error.message,
      clinicId: payload?.clinicId,
      treatmentCount: payload?.treatmentIds?.length,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Render the edited treatments summary to a (optionally signed) PDF.
 * @param {Object} payload - { clinicId, patientId, patientName, title,
 *   contentHtml, treatmentIds, includeSignature }
 * @returns {{ success: boolean, pdfUrl: string, reportId: string }}
 */
export const generateTreatmentsSummaryPdf = async (payload) => {
  try {
    const fn = httpsCallable(functions, "generateTreatmentsSummaryPdf");
    const result = await fn(payload);
    return result.data;
  } catch (error) {
    console.error("[CK-8947] Error generating treatments summary PDF:", {
      errorCode: "CK-8947",
      errorMessage: error.message,
      clinicId: payload?.clinicId,
      treatmentCount: payload?.treatmentIds?.length,
      signatureMode: payload?.options?.signature || null,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Render ONE locked treatment report to a branded PDF server-side (patient
 * page › treatments › "ייצוא PDF"). The server loads the treatment, patient
 * PII, branding, type/tag names itself — the client sends ids + layout toggles
 * + free texts only (see components/pdfExport/pdfExportOptions.js).
 * @param {Object} payload - { clinicId, patientId, treatmentId, options }
 * @returns {{ success: boolean, pdfUrl: string, reportId: string }}
 */
export const generateTreatmentPdf = async (payload) => {
  try {
    const fn = httpsCallable(functions, "generateTreatmentPdf");
    const result = await fn(payload);
    return result.data;
  } catch (error) {
    console.error("[CK-8952] Error generating treatment PDF:", {
      errorCode: "CK-8952",
      errorMessage: error.message,
      clinicId: payload?.clinicId,
      treatmentId: payload?.treatmentId,
      signatureMode: payload?.options?.signature || null,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Remember the therapist's last-used layout toggles for the treatment PDF
 * export (toggles only — never the free texts). Rules: therapist self-update
 * allow-list includes `treatmentPdfPrefs`.
 * @param {string} uid
 * @param {Object} prefs - { header, patient, content, signature }
 */
export const saveTreatmentPdfPrefs = async (uid, prefs) => {
  try {
    if (!uid || !prefs || typeof prefs !== "object") {
      throw new Error("Invalid treatment PDF prefs payload");
    }
    await updateTherapistDocument(uid, {
      treatmentPdfPrefs: { ...prefs, updatedAt: serverTimestamp() },
    });
    return { success: true };
  } catch (error) {
    console.error("[CK-8953] Error saving treatment PDF prefs:", {
      errorCode: "CK-8953",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Render the finance report (KPIs, chart datasets, tables — pre-aggregated on
 * the client) to a PDF server-side. Charts are drawn as inline SVG.
 * @param {Object} payload - { clinicId, rangeStart, rangeEnd, scopeTherapistId,
 *   statusLabels, kpis, charts, tables, rowCount }
 * @returns {{ success: boolean, pdfUrl: string, reportId: string }}
 */
export const generateFinanceReportPdf = async (payload) => {
  try {
    const fn = httpsCallable(functions, "generateFinanceReportPdf");
    const result = await fn(payload);
    return result.data;
  } catch (error) {
    console.error("[CK-8949] Error generating finance report PDF:", {
      errorCode: "CK-8949",
      errorMessage: error.message,
      clinicId: payload?.clinicId,
      rowCount: payload?.rowCount,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Save the therapist's drawn signature (PNG data URL) on their own document
 * so it can be reused on every signed PDF.
 * @param {string} uid - Therapist uid
 * @param {string} dataUrl - "data:image/png;base64,..."
 */
export const saveTherapistSignature = async (uid, dataUrl) => {
  try {
    if (!uid || typeof dataUrl !== "string" || !dataUrl.startsWith("data:image/")) {
      throw new Error("Invalid signature payload");
    }
    await updateTherapistDocument(uid, {
      signature: { dataUrl, updatedAt: serverTimestamp() },
    });
    return { success: true };
  } catch (error) {
    console.error("[CK-8948] Error saving therapist signature:", {
      errorCode: "CK-8948",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Fetch the generated patient reports history (audit trail), newest first.
 * @param {string} clinicId - The clinic ID
 * @param {Object} options - { limit, createdBy }
 *   createdBy: required for non-admins — rules only allow them to read rows
 *   they created, and rules are not filters (composite index createdBy+createdAt).
 * @returns {Array} - Serialized generatedReports docs
 */
export const getGeneratedReports = async (
  clinicId,
  { limit: maxDocs = 20, createdBy = null } = {}
) => {
  try {
    const reportsRef = collection(db, "clinics", clinicId, "generatedReports");
    const constraints = [];
    if (createdBy) constraints.push(where("createdBy", "==", createdBy));
    constraints.push(orderBy("createdAt", "desc"), limit(maxDocs));
    const q = query(reportsRef, ...constraints);
    const snapshot = await getDocs(q);
    return snapshot.docs.map((docSnap) =>
      serializeForRedux({ id: docSnap.id, ...docSnap.data() })
    );
  } catch (error) {
    console.error("[CK-8271] Error fetching generated reports:", {
      errorCode: "CK-8271",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Fetch ONE generated report row (re-edit seed for the AI treatments summary
 * page). Rules: readable by clinic admins or the row's creator only.
 * @param {string} clinicId
 * @param {string} reportId
 * @returns {Object|null} - Serialized generatedReports doc or null when missing
 */
export const getGeneratedReport = async (clinicId, reportId) => {
  try {
    const ref = doc(db, "clinics", clinicId, "generatedReports", reportId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return serializeForRedux({ id: snap.id, ...snap.data() });
  } catch (error) {
    console.error("[CK-8273] Error fetching generated report:", {
      errorCode: "CK-8273",
      errorMessage: error.message,
      clinicId,
      reportId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Cancel a pending signature link
 * @param {string} clinicId - The clinic ID
 * @param {string} linkId - The signature link ID
 * @returns {Object} - { success }
 */
export const cancelSignatureLink = async (clinicId, linkId) => {
  try {
    const cancelSignatureLinkFn = httpsCallable(
      functions,
      "cancelSignatureLink"
    );
    const result = await cancelSignatureLinkFn({ clinicId, linkId     });

    return result.data;
  } catch (error) {
    console.error("[CK-8264] Error cancelling signature link:", {
      errorCode: "CK-8264",
      errorMessage: error.message,
      clinicId,
      linkId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get all pending signature links for a clinic
 * @param {string} clinicId - The clinic ID
 * @param {string} therapistId - Optional therapist ID to filter by
 * @returns {Array} - Array of pending signature links
 */
export const getPendingSignatureLinks = async (
  clinicId,
  therapistId = null
) => {
  try {
    const linksRef = collection(db, "clinics", clinicId, "signatureLinks");
    let q = query(
      linksRef,
      where("status", "==", "pending"),
      orderBy("createdAt", "desc")
    );

    if (therapistId) {
      q = query(
        linksRef,
        where("status", "==", "pending"),
        where("therapistId", "==", therapistId),
        orderBy("createdAt", "desc")
      );
    }

    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) =>
      serializeForRedux({ id: doc.id, ...doc.data() })
    );
  } catch (error) {
    console.error("[CK-8265] Error fetching pending signature links:", error.message);
    console.error("[CK-8265] Details:", { clinicId, therapistId, timestamp: new Date().toISOString() });
    throw error;
  }
};

/**
 * Get all signed forms for a patient
 * @param {string} clinicId - The clinic ID
 * @param {string} patientId - The patient ID
 * @returns {Array} - Array of signed forms
 */
export const getSignedFormsForPatient = async (clinicId, patientId) => {
  try {
    const formsRef = collection(db, "clinics", clinicId, "signedForms");
    const q = query(
      formsRef,
      where("patientId", "==", patientId),
      orderBy("signedAt", "desc")
    );

    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) =>
      serializeForRedux({ id: doc.id, ...doc.data() })
    );
  } catch (error) {
    console.error("[CK-8266] Error fetching signed forms for patient:", {
      errorCode: "CK-8266",
      errorMessage: error.message,
      clinicId,
      patientId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get a single signed form by ID
 * @param {string} clinicId - The clinic ID
 * @param {string} signedFormId - The signed form ID
 * @returns {Object} - Signed form object
 */
export const getSignedForm = async (clinicId, signedFormId) => {
  try {
    const formRef = doc(db, "clinics", clinicId, "signedForms", signedFormId);
    const formSnap = await getDoc(formRef);

    if (!formSnap.exists()) {
      throw new Error("Signed form not found");
    }

    return serializeForRedux({ id: formSnap.id, ...formSnap.data() });
  } catch (error) {
    console.error("[CK-8267] Error fetching signed form:", {
      errorCode: "CK-8267",
      errorMessage: error.message,
      clinicId,
      signedFormId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Delete a pending signature link (only pending status)
 * @param {string} clinicId - The clinic ID
 * @param {string} linkId - The signature link ID
 * @param {string} therapistId - The therapist ID (for logging)
 * @param {string} therapistName - The therapist name (for logging)
 */
export const deleteSignatureLink = async (
  clinicId,
  linkId,
  therapistId,
  therapistName
) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  try {
    const linkRef = doc(db, "clinics", clinicId, "signatureLinks", linkId);
    const linkSnap = await getDoc(linkRef);

    if (!linkSnap.exists()) {
      throw new Error("Signature link not found");
    }

    const linkData = linkSnap.data();

    // Can only delete pending links
    if (linkData.status !== "pending") {
      throw new Error("Cannot delete a signed or cancelled form");
    }

    await deleteDoc(linkRef);

    // Log activity
    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: "delete_signature_link",
      actionLabel: "מחיקת קישור חתימה",
      entityType: "signatureLink",
      entityId: linkId,
      entityName: linkData.formName,
      details: {
        patientId: linkData.patientId,
        patientName: linkData.patientName,
      },
      success: true,
    });

  } catch (error) {
    console.error("[CK-8269] Error deleting signature link:", {
      errorCode: "CK-8269",
      errorMessage: error.message,
      clinicId,
      linkId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// ============================================
// TREATMENT DOCUMENTATION FUNCTIONS
// ============================================

/**
 * Create a new treatment record
 * @param {string} clinicId - The clinic ID
 * @param {Object} treatmentData - Treatment data
 * @param {string} therapistId - The therapist ID who owns this treatment
 * @param {string} therapistName - The therapist name (for logging)
 * @returns {Object} - Created treatment object
 */
export const createTreatment = async (
  clinicId,
  treatmentData,
  therapistId,
  therapistName
) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  try {
    const treatmentsRef = collection(db, "clinics", clinicId, "treatments");
    const newTreatmentRef = doc(treatmentsRef);

    const treatmentDoc = {
      id: newTreatmentRef.id,
      clinicId,
      patientId: treatmentData.patientId,
      patientName: treatmentData.patientName || null,
      therapistId,
      therapistName: therapistName || null,
      appointmentId: treatmentData.appointmentId || null,
      templateId: treatmentData.templateId || null,
      status: treatmentData.status || "draft", // 'draft' or 'locked'
      treatmentTypeId: treatmentData.treatmentTypeId || null,
      treatmentDate: treatmentData.treatmentDate
        ? Timestamp.fromDate(new Date(treatmentData.treatmentDate))
        : Timestamp.now(),
      tagIds: treatmentData.tagIds || [],
      title: treatmentData.title || "",
      fields: treatmentData.fields || [],
      summary: treatmentData.summary || "מייצר תקציר פגישה...",
      shortSummary: treatmentData.shortSummary || "מייצר תקציר פגישה...",
      selectedGameIds: treatmentData.selectedGameIds || [],
      comments: [],
      draftSavedAt: treatmentData.status === "draft" ? Timestamp.now() : null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(newTreatmentRef, treatmentDoc);

    // Log activity
    logActivity(clinicId, {
      therapistId,
      therapistName: therapistName || "לא ידוע",
      action: ACTIVITY_ACTIONS.CREATE_TREATMENT,
      entityType: ENTITY_TYPES.TREATMENT,
      entityId: newTreatmentRef.id,
      entityName: treatmentData.patientName || "מטופל",
      details: {
        patientId: treatmentData.patientId,
        status: treatmentDoc.status,
      },
      success: true,
    });

    return getTreatment(clinicId, newTreatmentRef.id);
  } catch (error) {
    console.error("[CK-8300] Error creating treatment:", {
      errorCode: "CK-8300",
      errorMessage: error.message,
      clinicId,
      therapistId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get a single treatment by ID
 * @param {string} clinicId - The clinic ID
 * @param {string} treatmentId - The treatment ID
 * @returns {Object} - Treatment object
 */
export const getTreatment = async (clinicId, treatmentId) => {
  try {
    const treatmentRef = doc(
      db,
      "clinics",
      clinicId,
      "treatments",
      treatmentId
    );
    const treatmentSnap = await getDoc(treatmentRef);

    if (!treatmentSnap.exists()) {
      return null;
    }

    return serializeForRedux({ id: treatmentSnap.id, ...treatmentSnap.data() });
  } catch (error) {
    console.error("[CK-8301] Error fetching treatment:", {
      errorCode: "CK-8301",
      errorMessage: error.message,
      clinicId,
      treatmentId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * View a treatment and log the access (for audit trail)
 * Use this function when a user opens/views a treatment document
 * @param {string} clinicId - The clinic ID
 * @param {string} treatmentId - The treatment ID
 * @param {string} therapistId - The viewing therapist's ID
 * @param {string} therapistName - The viewing therapist's name
 * @returns {Object} - Treatment object
 */
export const viewTreatment = async (
  clinicId,
  treatmentId,
  therapistId,
  therapistName
) => {
  try {
    const treatment = await getTreatment(clinicId, treatmentId);

    if (!treatment) {
      return null;
    }

    // Log the view activity for audit trail
    logActivity(clinicId, {
      therapistId,
      therapistName: therapistName || "לא ידוע",
      action: ACTIVITY_ACTIONS.VIEW_TREATMENT,
      entityType: ENTITY_TYPES.TREATMENT,
      entityId: treatmentId,
      entityName: treatment.patientName || "מטופל",
      details: {
        patientId: treatment.patientId,
        treatmentDate: treatment.treatmentDate,
        status: treatment.status,
      },
      success: true,
    });

    return treatment;
  } catch (error) {
    console.error("[CK-8307] Error viewing treatment:", {
      errorCode: "CK-8307",
      errorMessage: error.message,
      clinicId,
      treatmentId,
      therapistId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get the treatment document linked to a specific appointment, if any.
 * Used by the cancellation flow to warn the therapist that a linked
 * treatment summary exists (we keep it — the warning is informational).
 *
 * Returns `null` when no treatment is linked.
 *
 * Error Code: CK-8305
 */
export const getTreatmentByAppointment = async (clinicId, appointmentId) => {
  try {
    if (!clinicId || !appointmentId) return null;
    const treatmentsRef = collection(db, "clinics", clinicId, "treatments");
    const q = query(
      treatmentsRef,
      where("appointmentId", "==", appointmentId),
      limit(3)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    // Soft-deleted reports are not valid links (filtered in memory)
    const d = snapshot.docs.find((x) => x.data().isDeleted !== true);
    if (!d) return null;
    return serializeForRedux({ id: d.id, ...d.data() });
  } catch (error) {
    console.error("[CK-8305] Error fetching treatment by appointment:", {
      errorCode: "CK-8305",
      errorMessage: error.message,
      clinicId,
      appointmentId,
      timestamp: new Date().toISOString(),
    });
    return null;
  }
};

/**
 * Get all treatments for a patient
 * NOTE: includes soft-deleted reports (isDeleted === true) so the patient
 * timeline can render them as muted "deleted" rows. Every other consumer
 * MUST filter `t.isDeleted !== true` in memory.
 * @param {string} clinicId - The clinic ID
 * @param {string} patientId - The patient ID
 * @param {Object} options - Optional filters
 * @returns {Array} - Array of treatment objects
 */
export const getTreatmentsForPatient = async (
  clinicId,
  patientId,
  options = {}
) => {
  try {
    const { status, therapistId } = options;

    const treatmentsRef = collection(db, "clinics", clinicId, "treatments");
    let constraints = [where("patientId", "==", patientId)];

    // Filter by therapist if provided
    if (therapistId) {
      constraints.push(where("therapistId", "==", therapistId));
    }

    // Filter by status
    if (status && status !== "all") {
      constraints.push(where("status", "==", status));
    }

    // Order by treatment date (newest first)
    constraints.push(orderBy("treatmentDate", "desc"));

    const q = query(treatmentsRef, ...constraints);
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) =>
      serializeForRedux({ id: doc.id, ...doc.data() })
    );
  } catch (error) {
    console.error("[CK-8302] Error fetching treatments for patient:", {
      errorCode: "CK-8302",
      errorMessage: error.message,
      clinicId,
      patientId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get all treatments in a date range, clinic-wide (patient reports overview).
 * @param {string} clinicId - The clinic ID
 * @param {Object} options - { start, end (ISO strings/Dates), therapistId }
 * @returns {Array} - Serialized treatment objects
 */
export const getTreatmentsInRange = async (
  clinicId,
  { start, end, therapistId = null } = {}
) => {
  try {
    const treatmentsRef = collection(db, "clinics", clinicId, "treatments");
    const constraints = [];
    if (therapistId) constraints.push(where("therapistId", "==", therapistId));
    constraints.push(where("treatmentDate", ">=", Timestamp.fromDate(new Date(start))));
    constraints.push(where("treatmentDate", "<=", Timestamp.fromDate(new Date(end))));
    constraints.push(orderBy("treatmentDate", "asc"));

    const snapshot = await getDocs(query(treatmentsRef, ...constraints));
    return snapshot.docs
      .map((doc) => serializeForRedux({ id: doc.id, ...doc.data() }))
      .filter((t) => t.isDeleted !== true);
  } catch (error) {
    console.error("[CK-8272] Error fetching treatments in range:", {
      errorCode: "CK-8272",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get all treatments for a therapist (for timeline view)
 * @param {string} clinicId - The clinic ID
 * @param {string} therapistId - The therapist ID
 * @param {Object} options - Optional filters
 * @returns {Array} - Array of treatment objects
 */
export const getTreatmentsForTherapist = async (
  clinicId,
  therapistId,
  options = {}
) => {
  try {
    const { status, startDate, endDate, patientId } = options;

    const treatmentsRef = collection(db, "clinics", clinicId, "treatments");
    let constraints = [where("therapistId", "==", therapistId)];

    // Filter by patient
    if (patientId) {
      constraints.push(where("patientId", "==", patientId));
    }

    // Filter by status
    if (status && status !== "all") {
      constraints.push(where("status", "==", status));
    }

    // Order by treatment date (newest first)
    constraints.push(orderBy("treatmentDate", "desc"));

    const q = query(treatmentsRef, ...constraints);
    const snapshot = await getDocs(q);

    let treatments = snapshot.docs
      .map((doc) => serializeForRedux({ id: doc.id, ...doc.data() }))
      .filter((t) => t.isDeleted !== true);

    // Filter by date range client-side (to avoid composite index issues)
    if (startDate) {
      treatments = treatments.filter(
        (t) => new Date(t.treatmentDate) >= new Date(startDate)
      );
    }
    if (endDate) {
      treatments = treatments.filter(
        (t) => new Date(t.treatmentDate) <= new Date(endDate)
      );
    }

    return treatments;
  } catch (error) {
    console.error("[CK-8303] Error fetching treatments for therapist:", {
      errorCode: "CK-8303",
      errorMessage: error.message,
      clinicId,
      therapistId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Update a treatment (drafts update normally, locked treatments create a version)
 * @param {string} clinicId - The clinic ID
 * @param {string} treatmentId - The treatment ID
 * @param {Object} treatmentData - Updated treatment data
 * @param {string} therapistId - The therapist ID making the update
 * @param {string} therapistName - The therapist name
 * @returns {Object} - Updated treatment object
 */
/**
 * Update only the AI chat conversation on a treatment document.
 * Targeted single-field write — deliberately does not touch any other
 * treatment field, so it can never collide with a concurrent autosave.
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} treatmentId - The treatment ID
 * @param {Array} aiConversation - [{ role, content, timestamp }] (ISO string timestamps)
 *
 * Error Code: CK-8216
 */
export const updateTreatmentAiConversation = async (
  clinicId,
  treatmentId,
  aiConversation
) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  try {
    const treatmentRef = doc(
      db,
      "clinics",
      clinicId,
      "treatments",
      treatmentId
    );
    await updateDoc(treatmentRef, {
      aiConversation,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("[CK-8216] Error updating treatment AI conversation:", {
      errorCode: "CK-8216",
      errorMessage: error.message,
      clinicId,
      treatmentId,
      messagesCount: Array.isArray(aiConversation) ? aiConversation.length : 0,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

export const updateTreatment = async (
  clinicId,
  treatmentId,
  treatmentData,
  therapistId,
  therapistName
) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  try {
    const treatmentRef = doc(
      db,
      "clinics",
      clinicId,
      "treatments",
      treatmentId
    );
    const existingTreatment = await getTreatment(clinicId, treatmentId);

    if (!existingTreatment) {
      throw new Error("Treatment not found");
    }

    const isEditingLocked =
      existingTreatment.status === "locked" && treatmentData.editLocked;

    // Only drafts can be updated normally (except comments and locked edits)
    if (
      existingTreatment.status === "locked" &&
      !treatmentData.commentsOnly &&
      !treatmentData.editLocked
    ) {
      throw new Error("Cannot update a locked treatment");
    }

    // If editing a locked treatment, save current state as a version first
    if (isEditingLocked) {
      await saveTreatmentVersion(
        clinicId,
        treatmentId,
        existingTreatment,
        therapistId,
        therapistName
      );
    }

    const updateData = { ...treatmentData };

    // Remove meta fields
    delete updateData.id;
    delete updateData.createdAt;
    delete updateData.commentsOnly;
    delete updateData.editLocked;

    // Convert treatmentDate to Timestamp
    if (updateData.treatmentDate) {
      updateData.treatmentDate = Timestamp.fromDate(
        new Date(updateData.treatmentDate)
      );
    }

    // If locking the treatment, set the status
    if (updateData.status === "locked") {
      updateData.draftSavedAt = null;
    } else if (updateData.status === "draft") {
      updateData.draftSavedAt = Timestamp.now();
    }

    // If editing a locked treatment, increment version and track editor
    if (isEditingLocked) {
      updateData.currentVersion = (existingTreatment.currentVersion || 1) + 1;
      updateData.lastEditedAt = Timestamp.now();
      updateData.lastEditedBy = therapistId;
      updateData.lastEditedByName = therapistName || "לא ידוע";
    }

    await updateDoc(treatmentRef, {
      ...updateData,
      updatedAt: serverTimestamp(),
    });

    // Log activity - different action for locking vs regular update vs locked edit
    if (isEditingLocked) {
      logActivity(clinicId, {
        therapistId,
        therapistName: therapistName || "לא ידוע",
        action: ACTIVITY_ACTIONS.EDIT_LOCKED_TREATMENT,
        entityType: ENTITY_TYPES.TREATMENT,
        entityId: treatmentId,
        entityName: existingTreatment.patientName || "מטופל",
        details: {
          patientId: existingTreatment.patientId,
          treatmentDate: existingTreatment.treatmentDate,
          previousVersion: existingTreatment.currentVersion || 1,
          newVersion: (existingTreatment.currentVersion || 1) + 1,
        },
        success: true,
      });
    } else if (
      treatmentData.status === "locked" &&
      existingTreatment.status === "draft"
    ) {
      logActivity(clinicId, {
        therapistId,
        therapistName: therapistName || "לא ידוע",
        action: ACTIVITY_ACTIONS.LOCK_TREATMENT,
        entityType: ENTITY_TYPES.TREATMENT,
        entityId: treatmentId,
        entityName: existingTreatment.patientName || "מטופל",
        details: {
          patientId: existingTreatment.patientId,
          treatmentDate: existingTreatment.treatmentDate,
        },
        success: true,
      });
    } else {
      logActivity(clinicId, {
        therapistId,
        therapistName: therapistName || "לא ידוע",
        action: ACTIVITY_ACTIONS.UPDATE_TREATMENT,
        entityType: ENTITY_TYPES.TREATMENT,
        entityId: treatmentId,
        entityName: existingTreatment.patientName || "מטופל",
        details: {
          patientId: existingTreatment.patientId,
          treatmentDate: existingTreatment.treatmentDate,
          status: treatmentData.status || existingTreatment.status,
        },
        success: true,
      });
    }

    return getTreatment(clinicId, treatmentId);
  } catch (error) {
    console.error("[CK-8304] Error updating treatment:", {
      errorCode: "CK-8304",
      errorMessage: error.message,
      clinicId,
      treatmentId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Save the current state of a treatment as a version snapshot.
 * Called internally before updating a locked treatment.
 * @param {string} clinicId - The clinic ID
 * @param {string} treatmentId - The treatment ID
 * @param {Object} treatmentSnapshot - The current treatment data to snapshot
 * @param {string} therapistId - Who initiated the edit
 * @param {string} therapistName - Name of the editor
 */
const saveTreatmentVersion = async (
  clinicId,
  treatmentId,
  treatmentSnapshot,
  therapistId,
  therapistName
) => {
  try {
    const versionsRef = collection(
      db,
      "clinics",
      clinicId,
      "treatments",
      treatmentId,
      "versions"
    );
    const newVersionRef = doc(versionsRef);

    const versionDoc = {
      id: newVersionRef.id,
      versionNumber: treatmentSnapshot.currentVersion || 1,
      fields: treatmentSnapshot.fields || [],
      content: treatmentSnapshot.content || "",
      title: treatmentSnapshot.title || "",
      tagIds: treatmentSnapshot.tagIds || [],
      treatmentDate: treatmentSnapshot.treatmentDate
        ? Timestamp.fromDate(new Date(treatmentSnapshot.treatmentDate))
        : null,
      treatmentTypeId: treatmentSnapshot.treatmentTypeId || null,
      templateId: treatmentSnapshot.templateId || null,
      selectedGameIds: treatmentSnapshot.selectedGameIds || [],
      summary: treatmentSnapshot.summary || null,
      shortSummary: treatmentSnapshot.shortSummary || null,
      savedAt: serverTimestamp(),
      savedBy: therapistId,
      savedByName: therapistName || "לא ידוע",
    };

    await setDoc(newVersionRef, versionDoc);
    return versionDoc;
  } catch (error) {
    console.error("[CK-8306] Error saving treatment version:", {
      errorCode: "CK-8306",
      errorMessage: error.message,
      clinicId,
      treatmentId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get all version snapshots for a treatment (ordered by versionNumber desc)
 * @param {string} clinicId - The clinic ID
 * @param {string} treatmentId - The treatment ID
 * @returns {Array} - Array of version objects
 */
export const getTreatmentVersions = async (clinicId, treatmentId) => {
  try {
    const versionsRef = collection(
      db,
      "clinics",
      clinicId,
      "treatments",
      treatmentId,
      "versions"
    );
    const q = query(versionsRef, orderBy("versionNumber", "desc"));
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) =>
      serializeForRedux({ id: doc.id, ...doc.data() })
    );
  } catch (error) {
    console.error("[CK-8307] Error fetching treatment versions:", {
      errorCode: "CK-8307",
      errorMessage: error.message,
      clinicId,
      treatmentId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Add a comment to a treatment (can be done even on locked treatments)
 * @param {string} clinicId - The clinic ID
 * @param {string} treatmentId - The treatment ID
 * @param {string} content - Comment content
 * @param {string} authorId - The author's therapist ID
 * @param {string} authorName - The author's name
 * @returns {Object} - Updated treatment object
 */
export const addTreatmentComment = async (
  clinicId,
  treatmentId,
  content,
  authorId,
  authorName
) => {
  try {
    const treatmentRef = doc(
      db,
      "clinics",
      clinicId,
      "treatments",
      treatmentId
    );
    const existingTreatment = await getTreatment(clinicId, treatmentId);

    if (!existingTreatment) {
      throw new Error("Treatment not found");
    }

    const newComment = {
      id: `comment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      authorId,
      authorName: authorName || "לא ידוע",
      content,
      createdAt: new Date().toISOString(),
    };

    const updatedComments = [...(existingTreatment.comments || []), newComment];

    await updateDoc(treatmentRef, {
      comments: updatedComments,
      updatedAt: serverTimestamp(),
    });

    // Log activity for adding comment
    logActivity(clinicId, {
      therapistId: authorId,
      therapistName: authorName || "לא ידוע",
      action: ACTIVITY_ACTIONS.ADD_TREATMENT_COMMENT,
      entityType: ENTITY_TYPES.TREATMENT,
      entityId: treatmentId,
      entityName: existingTreatment.patientName || "מטופל",
      details: {
        patientId: existingTreatment.patientId,
        treatmentDate: existingTreatment.treatmentDate,
        commentId: newComment.id,
      },
      success: true,
    });

    // Send notification to treatment owner if commenter is different (Notification 13)
    if (
      existingTreatment.therapistId &&
      existingTreatment.therapistId !== authorId
    ) {
      sendNotificationWithPrefCheck(
        clinicId,
        NOTIFICATION_PREF_KEYS.TREATMENT_COMMENT_ADDED,
        {
          title: "תגובה חדשה לתיעוד טיפול",
          message: `${authorName || "מטפל"} הגיב/ה על תיעוד הטיפול של ${
            existingTreatment.patientName || "מטופל"
          }`,
          type: NOTIFICATION_TYPES.TREATMENT,
          urgency: NOTIFICATION_URGENCY.LOW,
          therapistId: existingTreatment.therapistId,
          patientId: existingTreatment.patientId,
          link: `/dashboard/patients/${existingTreatment.patientId}/treatments/${treatmentId}`,
          timestamp: new Date().toISOString(),
        },
        false // Default disabled
      );
    }

    return getTreatment(clinicId, treatmentId);
  } catch (error) {
    console.error("[CK-8305] Error adding treatment comment:", {
      errorCode: "CK-8305",
      errorMessage: error.message,
      clinicId,
      treatmentId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Delete a treatment (only drafts can be deleted)
 * @param {string} clinicId - The clinic ID
 * @param {string} treatmentId - The treatment ID
 * @param {string} therapistId - The therapist ID making the deletion
 * @param {string} therapistName - The therapist name
 */
export const deleteTreatment = async (
  clinicId,
  treatmentId,
  therapistId,
  therapistName
) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  try {
    const existingTreatment = await getTreatment(clinicId, treatmentId);

    if (!existingTreatment) {
      throw new Error("Treatment not found");
    }

    // Only drafts can be deleted
    if (existingTreatment.status === "locked") {
      throw new Error("Cannot delete a locked treatment");
    }

    const treatmentRef = doc(
      db,
      "clinics",
      clinicId,
      "treatments",
      treatmentId
    );
    await deleteDoc(treatmentRef);

    // Log activity
    logActivity(clinicId, {
      therapistId,
      therapistName: therapistName || "לא ידוע",
      action: ACTIVITY_ACTIONS.DELETE_TREATMENT,
      entityType: ENTITY_TYPES.TREATMENT,
      entityId: treatmentId,
      entityName: existingTreatment.patientName || "מטופל",
      details: {
        patientId: existingTreatment.patientId,
      },
      success: true,
    });

    return treatmentId;
  } catch (error) {
    console.error("[CK-8306] Error deleting treatment:", {
      errorCode: "CK-8306",
      errorMessage: error.message,
      clinicId,
      treatmentId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Soft-delete a LOCKED treatment report.
 *
 * Treatment reports are medical records and are never hard-deleted. This
 * marks the report as deleted (isDeleted / deletedAt / deletedBy /
 * deletedReason) so the system and AI tools ignore it, while the document
 * stays in Firestore for legal/backup purposes. There is no restore.
 *
 * Also resets the linked appointment (treatmentId / summarized flags) so it
 * shows as undocumented again — best-effort, never blocks the delete.
 *
 * Drafts use deleteTreatment (hard delete). Idempotent.
 *
 * @param {string} clinicId
 * @param {string} treatmentId
 * @param {string} therapistId - Acting therapist (author or clinic admin)
 * @param {string} therapistName
 * @param {string|null} reason - Optional free-text reason (never logged)
 * @returns {Promise<string>} treatmentId
 *
 * Error Code: CK-8308
 */
export const softDeleteTreatment = async (
  clinicId,
  treatmentId,
  therapistId,
  therapistName,
  reason = null
) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  let existingTreatment = null;
  const trimmedReason = typeof reason === "string" ? reason.trim() : "";

  try {
    existingTreatment = await getTreatment(clinicId, treatmentId);

    if (!existingTreatment) {
      throw new Error("דוח הטיפול לא נמצא");
    }

    // Only locked (finalized) reports go through soft delete
    if (existingTreatment.status !== "locked") {
      throw new Error("ניתן למחוק כך רק דוחות טיפול נעולים");
    }

    // Idempotent: a second delete is a no-op
    if (existingTreatment.isDeleted === true) {
      return treatmentId;
    }

    const treatmentRef = doc(
      db,
      "clinics",
      clinicId,
      "treatments",
      treatmentId
    );
    await updateDoc(treatmentRef, {
      isDeleted: true,
      deletedAt: serverTimestamp(),
      deletedBy: therapistId || null,
      deletedByName: therapistName || "לא ידוע",
      deletedReason: trimmedReason || null,
      updatedAt: serverTimestamp(),
    });

    // Best-effort: make the linked appointment look undocumented again.
    // Separate write (not a batch) so a rules edge case never blocks the
    // delete itself.
    const appointmentId = existingTreatment.appointmentId || null;
    if (appointmentId) {
      try {
        const aptRef = doc(
          db,
          "clinics",
          clinicId,
          "appointments",
          appointmentId
        );
        const aptSnap = await getDoc(aptRef);
        if (aptSnap.exists()) {
          const apt = aptSnap.data();
          const aptUpdate = {
            treatmentId: null,
            isTherapistSummarizedTheMeeting: false,
            updatedAt: serverTimestamp(),
          };
          // Re-open the "summarize this meeting" pending step only for
          // meetings that actually happened for a real patient record.
          if (
            apt.status === "completed" &&
            apt.isTempCustomer !== true &&
            apt.isDeleted !== true
          ) {
            aptUpdate.NotifyAboutThisMeetingSummarize = true;
          }
          await updateDoc(aptRef, aptUpdate);
        }
      } catch (aptError) {
        console.warn(
          "[CK-8308] Treatment soft-deleted but appointment reset failed:",
          {
            errorCode: "CK-8308",
            errorMessage: aptError.message,
            clinicId,
            treatmentId,
            appointmentId,
            timestamp: new Date().toISOString(),
          }
        );
      }
    }

    // Log activity (reason text is never logged — may contain PII)
    logActivity(clinicId, {
      therapistId,
      therapistName: therapistName || "לא ידוע",
      action: ACTIVITY_ACTIONS.SOFT_DELETE_TREATMENT,
      entityType: ENTITY_TYPES.TREATMENT,
      entityId: treatmentId,
      entityName: existingTreatment.patientName || "מטופל",
      details: {
        patientId: existingTreatment.patientId || null,
        treatmentDate: existingTreatment.treatmentDate || null,
        appointmentId,
        hasReason: !!trimmedReason,
      },
      success: true,
    });

    return treatmentId;
  } catch (error) {
    console.error("[CK-8308] Error soft-deleting treatment:", {
      errorCode: "CK-8308",
      errorMessage: error.message,
      clinicId,
      treatmentId,
      timestamp: new Date().toISOString(),
    });

    logActivity(clinicId, {
      therapistId,
      therapistName: therapistName || "לא ידוע",
      action: ACTIVITY_ACTIONS.SOFT_DELETE_TREATMENT,
      entityType: ENTITY_TYPES.TREATMENT,
      entityId: treatmentId,
      entityName: existingTreatment?.patientName || "מטופל",
      details: {
        patientId: existingTreatment?.patientId || null,
      },
      success: false,
      errorMessage: error.message,
    });

    throw error;
  }
};

/**
 * Get patient's treatment draft (if exists)
 * @param {string} clinicId - The clinic ID
 * @param {string} patientId - The patient ID
 * @param {string} therapistId - The therapist ID
 * @returns {Object|null} - Draft treatment or null
 */
export const getPatientTreatmentDraft = async (
  clinicId,
  patientId,
  therapistId
) => {
  try {
    const treatmentsRef = collection(db, "clinics", clinicId, "treatments");
    const q = query(
      treatmentsRef,
      where("patientId", "==", patientId),
      where("therapistId", "==", therapistId),
      where("status", "==", "draft"),
      orderBy("draftSavedAt", "desc"),
      limit(1)
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return null;
    }

    return serializeForRedux({
      id: snapshot.docs[0].id,
      ...snapshot.docs[0].data(),
    });
  } catch (error) {
    console.error("[CK-8307] Error fetching patient treatment draft:", {
      errorCode: "CK-8307",
      errorMessage: error.message,
      clinicId,
      patientId,
      therapistId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// ============================================
// TREATMENT TAGS FUNCTIONS (separate from other categories)
// ============================================

/**
 * Create a treatment tag
 * @param {string} clinicId - The clinic ID
 * @param {Object} tagData - Tag data
 * @returns {Object} - Created tag object
 */
export const createTreatmentTag = async (clinicId, tagData) => {
  try {
    const tagsRef = collection(db, "clinics", clinicId, "treatmentTags");
    const newTagRef = doc(tagsRef);

    const tagDoc = {
      id: newTagRef.id,
      name: tagData.name,
      color: tagData.color || "#3b82f6",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(newTagRef, tagDoc);
    return serializeForRedux({ ...tagDoc, id: newTagRef.id });
  } catch (error) {
    console.error("[CK-8310] Error creating treatment tag:", {
      errorCode: "CK-8310",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get all treatment tags for a clinic
 * @param {string} clinicId - The clinic ID
 * @returns {Array} - Array of tag objects
 */
export const getTreatmentTags = async (clinicId) => {
  try {
    const tagsRef = collection(db, "clinics", clinicId, "treatmentTags");
    const q = query(tagsRef, orderBy("name", "asc"));
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) =>
      serializeForRedux({ id: doc.id, ...doc.data() })
    );
  } catch (error) {
    console.error("[CK-8311] Error fetching treatment tags:", {
      errorCode: "CK-8311",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Update a treatment tag
 * @param {string} clinicId - The clinic ID
 * @param {string} tagId - The tag ID
 * @param {Object} tagData - Updated tag data
 * @returns {Object} - Updated tag object
 */
export const updateTreatmentTag = async (clinicId, tagId, tagData) => {
  try {
    const tagRef = doc(db, "clinics", clinicId, "treatmentTags", tagId);

    await updateDoc(tagRef, {
      ...tagData,
      updatedAt: serverTimestamp(),
    });

    const tagSnap = await getDoc(tagRef);
    return serializeForRedux({ id: tagSnap.id, ...tagSnap.data() });
  } catch (error) {
    console.error("[CK-8312] Error updating treatment tag:", {
      errorCode: "CK-8312",
      errorMessage: error.message,
      clinicId,
      tagId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Delete a treatment tag
 * @param {string} clinicId - The clinic ID
 * @param {string} tagId - The tag ID
 */
export const deleteTreatmentTag = async (clinicId, tagId) => {
  try {
    const tagRef = doc(db, "clinics", clinicId, "treatmentTags", tagId);
    await deleteDoc(tagRef);
    return tagId;
  } catch (error) {
    console.error("[CK-8313] Error deleting treatment tag:", {
      errorCode: "CK-8313",
      errorMessage: error.message,
      clinicId,
      tagId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// ============================================
// CLINIC GAMES/TOYS FUNCTIONS
// ============================================

/**
 * Create a new game/toy for a clinic
 * @param {string} clinicId - The clinic ID
 * @param {Object} gameData - Game data
 * @returns {Object} - Created game object
 */
export const createClinicGame = async (clinicId, gameData) => {
  try {
    await guardTrialWrite(clinicId);

    const gamesRef = collection(db, "clinics", clinicId, "games");
    const newGameRef = doc(gamesRef);

    const gameDoc = {
      id: newGameRef.id,
      name: gameData.name,
      minAge: gameData.minAge || null,
      maxAge: gameData.maxAge || null,
      goalIds: gameData.goalIds || [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(newGameRef, gameDoc);
    return serializeForRedux({ ...gameDoc, id: newGameRef.id });
  } catch (error) {
    console.error("[CK-8420] Error creating clinic game:", {
      errorCode: "CK-8420",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get all games for a clinic
 * @param {string} clinicId - The clinic ID
 * @returns {Array} - Array of game objects
 */
export const getClinicGames = async (clinicId) => {
  try {
    const gamesRef = collection(db, "clinics", clinicId, "games");
    const q = query(gamesRef, orderBy("name", "asc"));
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) =>
      serializeForRedux({ id: doc.id, ...doc.data() })
    );
  } catch (error) {
    console.error("[CK-8421] Error fetching clinic games:", {
      errorCode: "CK-8421",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Update a game
 * @param {string} clinicId - The clinic ID
 * @param {string} gameId - The game ID
 * @param {Object} gameData - Updated game data
 * @returns {Object} - Updated game object
 */
export const updateClinicGame = async (clinicId, gameId, gameData) => {
  try {
    await guardTrialWrite(clinicId);

    const gameRef = doc(db, "clinics", clinicId, "games", gameId);

    const updateData = {
      updatedAt: serverTimestamp(),
    };

    if (gameData.name !== undefined) updateData.name = gameData.name;
    if (gameData.minAge !== undefined) updateData.minAge = gameData.minAge;
    if (gameData.maxAge !== undefined) updateData.maxAge = gameData.maxAge;
    if (gameData.goalIds !== undefined) updateData.goalIds = gameData.goalIds;

    await updateDoc(gameRef, updateData);

    const updatedDoc = await getDoc(gameRef);
    return serializeForRedux({ id: gameId, ...updatedDoc.data() });
  } catch (error) {
    console.error("[CK-8422] Error updating clinic game:", {
      errorCode: "CK-8422",
      errorMessage: error.message,
      clinicId,
      gameId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Delete a game
 * @param {string} clinicId - The clinic ID
 * @param {string} gameId - The game ID
 */
export const deleteClinicGame = async (clinicId, gameId) => {
  try {
    await guardTrialWrite(clinicId);

    const gameRef = doc(db, "clinics", clinicId, "games", gameId);
    await deleteDoc(gameRef);
    return gameId;
  } catch (error) {
    console.error("[CK-8423] Error deleting clinic game:", {
      errorCode: "CK-8423",
      errorMessage: error.message,
      clinicId,
      gameId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// Add new activity actions and entity types for treatments
ACTIVITY_ACTIONS.CREATE_TREATMENT = "create_treatment";
ACTIVITY_ACTIONS.VIEW_TREATMENT = "view_treatment";
ACTIVITY_ACTIONS.UPDATE_TREATMENT = "update_treatment";
ACTIVITY_ACTIONS.LOCK_TREATMENT = "lock_treatment";
ACTIVITY_ACTIONS.EDIT_LOCKED_TREATMENT = "edit_locked_treatment";
ACTIVITY_ACTIONS.DELETE_TREATMENT = "delete_treatment";
ACTIVITY_ACTIONS.SOFT_DELETE_TREATMENT = "soft_delete_treatment";
ACTIVITY_ACTIONS.ADD_TREATMENT_COMMENT = "add_treatment_comment";

ACTION_LABELS.create_treatment = "יצירת תיעוד טיפול";
ACTION_LABELS.view_treatment = "צפייה בתיעוד טיפול";
ACTION_LABELS.update_treatment = "עדכון תיעוד טיפול";
ACTION_LABELS.lock_treatment = "נעילת תיעוד טיפול";
ACTION_LABELS.edit_locked_treatment = "עריכת תיעוד טיפול נעול";
ACTION_LABELS.delete_treatment = "מחיקת תיעוד טיפול";
ACTION_LABELS.soft_delete_treatment = "מחיקת דוח טיפול נעול";
ACTION_LABELS.add_treatment_comment = "הוספת הערה לטיפול";

ENTITY_TYPES.TREATMENT = "treatment";
ENTITY_TYPES.NOTIFICATION = "notification";

// Add notification activity actions
ACTIVITY_ACTIONS.CREATE_NOTIFICATION = "create_notification";
ACTIVITY_ACTIONS.READ_NOTIFICATION = "read_notification";
ACTIVITY_ACTIONS.DELETE_NOTIFICATION = "delete_notification";

ACTION_LABELS.create_notification = "יצירת התראה";
ACTION_LABELS.read_notification = "קריאת התראה";
ACTION_LABELS.delete_notification = "מחיקת התראה";

// ============================================
// NOTIFICATIONS FUNCTIONS
// ============================================

/**
 * Notification types for classification
 */
export const NOTIFICATION_TYPES = {
  APPOINTMENT: "appointment",
  APPOINTMENT_SCHEDULED_BY_ADMIN: "appointment_scheduled_by_admin",
  APPOINTMENT_CANCELLED_BY_ADMIN: "appointment_cancelled_by_admin",
  APPOINTMENT_RESCHEDULED_BY_ADMIN: "appointment_rescheduled_by_admin",
  PAYMENT: "payment",
  TASK: "task",
  PATIENT: "patient",
  SYSTEM: "system",
  REMINDER: "reminder",
  TREATMENT: "treatment",
};

/**
 * Notification urgency levels
 * 1 = Low (info), 2 = Medium (warning), 3 = High (urgent)
 */
export const NOTIFICATION_URGENCY = {
  LOW: 1,
  MEDIUM: 2,
  HIGH: 3,
};

/**
 * Notification preference keys
 * Maps to NOTIFICATION_PREFERENCES in src/pages/dashboard/SettingsPage.js
 * Used to check if therapist has enabled specific notification types
 */
export const NOTIFICATION_PREF_KEYS = {
  // Leads v2 (leads_plan.md §7) — mirrored in functions/notificationsHelper.js
  LEAD_INBOUND: "leadInbound",
  LEAD_FOLLOW_UP_DUE: "leadFollowUpDue",
  APPOINTMENT_CONFIRMED: "appointmentConfirmed",
  APPOINTMENT_DECLINED: "appointmentDeclined",
  APPOINTMENT_SCHEDULED_BY_ADMIN: "appointmentScheduledByAdmin",
  APPOINTMENT_CANCELLED_BY_ADMIN: "appointmentCancelledByAdmin",
  APPOINTMENT_RESCHEDULED_BY_ADMIN: "appointmentRescheduledByAdmin",
  BOOKING_REQUEST_RECEIVED: "bookingRequestReceived",
  SMS_SENDING_FAILED: "smsSendingFailed",
  UNCONFIRMED_APPOINTMENT_REMINDER: "unconfirmedAppointmentReminder",
  PATIENTS_TRANSFERRED: "patientsTransferred",
  TASK_DUE_DATE_APPROACHING: "taskDueDateApproaching",
  TASK_OVERDUE: "taskOverdue",
  TASK_ASSIGNED: "taskAssigned",
  CLINIC_TASK_CREATED: "clinicTaskCreated",
  CLINIC_REMINDER_CREATED: "clinicReminderCreated",
  CLOUD_TASK_FAILED: "cloudTaskFailed",
  TRIAL_ENDING_SOON: "trialEndingSoon",
  PATIENT_DRAFT_REMINDER: "patientDraftReminder",
  TREATMENT_COMMENT_ADDED: "treatmentCommentAdded",
  TREATMENT_DRAFT_REMINDER: "treatmentDraftReminder",
  PATIENT_OUTSTANDING_DEBT: "patientOutstandingDebt",
  PATIENT_BIRTHDAY: "patientBirthday",
  PERMISSIONS_UPDATED: "permissionsUpdated",
  TEAM_MEMBER_FIRST_LOGIN: "teamMemberFirstLogin",
  // Grow payments (grow_integration.md Phase 7)
  GROW_PAYMENT_RECEIVED: "growPaymentReceived",
  GROW_LINK_UNPAID_REMINDER: "growLinkUnpaidReminder",
  GROW_PAYMENT_FAILED: "growPaymentFailed",
};

/**
 * Get therapist notification preferences
 * @param {string} therapistId - The therapist ID
 * @returns {Object} - Notification preferences
 */
export const getTherapistNotificationPrefs = async (therapistId) => {
  try {
    const therapistRef = doc(db, "therapists", therapistId);
    const therapistSnap = await getDoc(therapistRef);

    if (!therapistSnap.exists()) {
      return {};
    }

    return therapistSnap.data().notificationPreferences || {};
  } catch (error) {
    console.error(
      "[CK-8409] Error fetching therapist notification prefs:",
      error
    );
    return {};
  }
};

/**
 * Check if a notification type is enabled for a therapist
 * @param {string} therapistId - The therapist ID
 * @param {string} prefKey - The notification preference key
 * @param {boolean} defaultValue - Default value if preference not set
 * @returns {boolean} - Whether the notification is enabled
 */
export const isNotificationEnabledForTherapist = async (
  therapistId,
  prefKey,
  defaultValue = true
) => {
  const prefs = await getTherapistNotificationPrefs(therapistId);
  return prefs[prefKey] !== undefined ? prefs[prefKey] : defaultValue;
};

/**
 * Send notification with preference check (client-side)
 * Only sends if therapist has the notification type enabled
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} prefKey - The notification preference key
 * @param {Object} options - Notification options
 * @param {boolean} [defaultEnabled=true] - Default value if preference not set
 */
export const sendNotificationWithPrefCheck = async (
  clinicId,
  prefKey,
  options,
  defaultEnabled = true
) => {
  try {
    // If no therapistId, send clinic-wide notification without check
    if (!options.therapistId) {
      sendNotification(clinicId, options);
      return;
    }

    // Check if therapist has this notification enabled
    const isEnabled = await isNotificationEnabledForTherapist(
      options.therapistId,
      prefKey,
      defaultEnabled
    );

    if (!isEnabled) {
      return;
    }

    sendNotification(clinicId, options);
  } catch (error) {
    // Log error but still try to send notification
    console.error(
      "[CK-8410] Error in sendNotificationWithPrefCheck:",
      error.message
    );
    // Fallback: try to send notification anyway
    sendNotification(clinicId, options);
  }
};

/**
 * Create a notification
 * @param {string} clinicId - The clinic ID
 * @param {Object} notificationData - Notification data
 * @param {string} notificationData.title - Notification title (required)
 * @param {string} notificationData.message - Notification message (required)
 * @param {string} notificationData.type - Notification type (required)
 * @param {number} notificationData.urgency - Urgency level 1-3 (required)
 * @param {string} [notificationData.therapistId] - Target therapist (null = clinic-wide)
 * @param {string} [notificationData.link] - Optional link to navigate to
 * @param {string} [notificationData.patientId] - Optional related patient ID
 * @param {string} [notificationData.createdBy] - Who created the notification
 * @param {string|Date} [notificationData.timestamp] - Event date+time (ISO string or Date, defaults to now)
 * @returns {Object} - Created notification object
 */
export const createNotification = async (clinicId, notificationData) => {
  try {
    const notificationsRef = collection(
      db,
      "clinics",
      clinicId,
      "notifications"
    );
    const newNotificationRef = doc(notificationsRef);

    const notificationDoc = {
      id: newNotificationRef.id,
      clinicId,
      therapistId: notificationData.therapistId || null, // null = clinic-wide
      title: notificationData.title,
      message: notificationData.message,
      type: notificationData.type,
      urgency: notificationData.urgency || NOTIFICATION_URGENCY.MEDIUM,
      link: notificationData.link || null,
      patientId: notificationData.patientId || null,
      createdBy: notificationData.createdBy || null,
      readBy: [], // Array of therapist IDs who have read this
      timestamp: notificationData.timestamp || new Date().toISOString(), // Date + time for display
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(newNotificationRef, notificationDoc);
    return serializeForRedux({ ...notificationDoc, id: newNotificationRef.id });
  } catch (error) {
    console.error("[CK-8400] Error creating notification:", {
      errorCode: "CK-8400",
      errorMessage: error.message,
      clinicId,
      notificationData,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get a single notification
 * @param {string} clinicId - The clinic ID
 * @param {string} notificationId - The notification ID
 * @returns {Object|null} - Notification object or null
 */
export const getNotification = async (clinicId, notificationId) => {
  try {
    const notificationRef = doc(
      db,
      "clinics",
      clinicId,
      "notifications",
      notificationId
    );
    const notificationSnap = await getDoc(notificationRef);

    if (!notificationSnap.exists()) {
      return null;
    }

    return serializeForRedux({
      id: notificationSnap.id,
      ...notificationSnap.data(),
    });
  } catch (error) {
    console.error("[CK-8401] Error fetching notification:", {
      errorCode: "CK-8401",
      errorMessage: error.message,
      clinicId,
      notificationId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get notifications for a clinic or therapist
 * @param {string} clinicId - The clinic ID
 * @param {Object} options - Filter options
 * @param {string} [options.therapistId] - Get notifications for this therapist (+ clinic-wide)
 * @param {boolean} [options.unreadOnly] - Get only unread notifications
 * @param {string} [options.type] - Filter by notification type
 * @param {number} [options.urgency] - Filter by urgency level
 * @param {number} [options.pageSize] - Number of notifications to return (default 50)
 * @param {string} [options.cursor] - Pagination cursor
 * @returns {Object} - { notifications, nextCursor, hasMore }
 */
export const getNotifications = async (clinicId, options = {}) => {
  try {
    const {
      therapistId,
      unreadOnly,
      type,
      urgency,
      pageSize = 50,
      cursor,
    } = options;

    const notificationsRef = collection(
      db,
      "clinics",
      clinicId,
      "notifications"
    );
    let constraints = [];

    // Order by creation date (newest first)
    constraints.push(orderBy("createdAt", "desc"));

    // Pagination using cursor
    if (cursor) {
      const cursorDate = new Date(cursor);
      constraints.push(startAfter(Timestamp.fromDate(cursorDate)));
    }

    constraints.push(limit(pageSize));

    const q = query(notificationsRef, ...constraints);
    const snapshot = await getDocs(q);

    let notifications = snapshot.docs.map((doc) =>
      serializeForRedux({ id: doc.id, ...doc.data() })
    );

    // Client-side filtering for therapist-specific notifications
    if (therapistId) {
      notifications = notifications.filter(
        (n) => n.therapistId === null || n.therapistId === therapistId
      );
    }

    // Filter by type
    if (type && type !== "all") {
      notifications = notifications.filter((n) => n.type === type);
    }

    // Filter by urgency
    if (urgency) {
      notifications = notifications.filter((n) => n.urgency === urgency);
    }

    // Filter unread only
    if (unreadOnly && therapistId) {
      notifications = notifications.filter(
        (n) => !n.readBy || !n.readBy.includes(therapistId)
      );
    }

    // Get the last document's createdAt for cursor-based pagination
    const lastDocData = snapshot.docs[snapshot.docs.length - 1]?.data();
    const nextCursor = lastDocData?.createdAt
      ? lastDocData.createdAt.toDate().toISOString()
      : null;

    return {
      notifications,
      nextCursor,
      hasMore: snapshot.docs.length === pageSize,
    };
  } catch (error) {
    console.error("[CK-8402] Error fetching notifications:", {
      errorCode: "CK-8402",
      errorMessage: error.message,
      clinicId,
      options,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Mark a notification as read by a therapist
 * @param {string} clinicId - The clinic ID
 * @param {string} notificationId - The notification ID
 * @param {string} therapistId - The therapist ID who read the notification
 * @returns {Object} - Updated notification object
 */
export const markNotificationAsRead = async (
  clinicId,
  notificationId,
  therapistId
) => {
  try {
    const notificationRef = doc(
      db,
      "clinics",
      clinicId,
      "notifications",
      notificationId
    );
    const notificationSnap = await getDoc(notificationRef);

    if (!notificationSnap.exists()) {
      throw new Error("Notification not found");
    }

    const currentData = notificationSnap.data();
    const readBy = currentData.readBy || [];

    // Only add if not already in the array
    if (!readBy.includes(therapistId)) {
      await updateDoc(notificationRef, {
        readBy: [...readBy, therapistId],
        updatedAt: serverTimestamp(),
      });
    }

    return getNotification(clinicId, notificationId);
  } catch (error) {
    console.error("[CK-8403] Error marking notification as read:", {
      errorCode: "CK-8403",
      errorMessage: error.message,
      clinicId,
      notificationId,
      therapistId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Mark all notifications as read for a therapist
 * @param {string} clinicId - The clinic ID
 * @param {string} therapistId - The therapist ID
 * @returns {number} - Number of notifications marked as read
 */
export const markAllNotificationsAsRead = async (clinicId, therapistId) => {
  try {
    const notificationsRef = collection(
      db,
      "clinics",
      clinicId,
      "notifications"
    );
    const snapshot = await getDocs(notificationsRef);

    let count = 0;
    const updates = [];

    snapshot.docs.forEach((docSnap) => {
      const data = docSnap.data();
      const readBy = data.readBy || [];

      // Check if this notification is relevant to the therapist and unread
      const isRelevant =
        data.therapistId === null || data.therapistId === therapistId;
      const isUnread = !readBy.includes(therapistId);

      if (isRelevant && isUnread) {
        updates.push(
          updateDoc(docSnap.ref, {
            readBy: [...readBy, therapistId],
            updatedAt: serverTimestamp(),
          })
        );
        count++;
      }
    });

    await Promise.all(updates);
    return count;
  } catch (error) {
    console.error("[CK-8404] Error marking all notifications as read:", {
      errorCode: "CK-8404",
      errorMessage: error.message,
      clinicId,
      therapistId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Delete a notification
 * @param {string} clinicId - The clinic ID
 * @param {string} notificationId - The notification ID
 */
export const deleteNotification = async (clinicId, notificationId) => {
  try {
    const notificationRef = doc(
      db,
      "clinics",
      clinicId,
      "notifications",
      notificationId
    );
    await deleteDoc(notificationRef);
    return notificationId;
  } catch (error) {
    console.error("[CK-8405] Error deleting notification:", {
      errorCode: "CK-8405",
      errorMessage: error.message,
      clinicId,
      notificationId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Delete old notifications (cleanup)
 * @param {string} clinicId - The clinic ID
 * @param {number} daysOld - Delete notifications older than this (default 30)
 * @returns {number} - Number of deleted notifications
 */
export const deleteOldNotifications = async (clinicId, daysOld = 30) => {
  try {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysOld);

    const notificationsRef = collection(
      db,
      "clinics",
      clinicId,
      "notifications"
    );
    const q = query(
      notificationsRef,
      where("createdAt", "<", Timestamp.fromDate(cutoffDate)),
      limit(500)
    );

    const snapshot = await getDocs(q);

    const deletePromises = snapshot.docs.map((docSnap) =>
      deleteDoc(doc(db, "clinics", clinicId, "notifications", docSnap.id))
    );

    await Promise.all(deletePromises);
    return snapshot.docs.length;
  } catch (error) {
    console.error("[CK-8406] Error deleting old notifications:", {
      errorCode: "CK-8406",
      errorMessage: error.message,
      clinicId,
      daysOld,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get unread notification count for a therapist
 * @param {string} clinicId - The clinic ID
 * @param {string} therapistId - The therapist ID
 * @returns {number} - Unread notification count
 */
export const getUnreadNotificationCount = async (clinicId, therapistId) => {
  try {
    const { notifications } = await getNotifications(clinicId, {
      therapistId,
      unreadOnly: true,
      pageSize: 100,
    });
    return notifications.length;
  } catch (error) {
    console.error("[CK-8407] Error getting unread notification count:", {
      errorCode: "CK-8407",
      errorMessage: error.message,
      clinicId,
      therapistId,
      timestamp: new Date().toISOString(),
    });
    return 0;
  }
};

// ============================================
// NOTIFICATION HELPER FUNCTIONS (Fire-and-forget)
// ============================================

/**
 * Helper function to send a notification - fire-and-forget
 * Use this from anywhere in the app to add notifications easily
 *
 * @param {string} clinicId - The clinic ID
 * @param {Object} options - Notification options
 * @param {string} options.title - Notification title (required)
 * @param {string} options.message - Notification message (required)
 * @param {string} options.type - One of NOTIFICATION_TYPES (required)
 * @param {number} [options.urgency] - 1=low, 2=medium, 3=high (default: 2)
 * @param {string} [options.therapistId] - Target therapist (null = clinic-wide)
 * @param {string} [options.link] - Link to navigate to
 * @param {string} [options.patientId] - Related patient ID
 * @param {string} [options.createdBy] - Who created the notification
 * @param {string|Date} [options.timestamp] - Event date+time (defaults to now)
 *
 * @example
 * // Send clinic-wide notification
 * sendNotification(clinicId, {
 *   title: "מטופל חדש נרשם",
 *   message: "המטופל ישראל ישראלי נוסף למערכת",
 *   type: NOTIFICATION_TYPES.PATIENT,
 *   urgency: NOTIFICATION_URGENCY.LOW,
 *   patientId: "abc123",
 *   link: "/dashboard/patients/abc123",
 *   createdBy: therapistId,
 *   timestamp: new Date().toISOString()
 * });
 *
 * // Send notification to specific therapist
 * sendNotification(clinicId, {
 *   title: "תזכורת לתור",
 *   message: "יש לך תור עם יוסי כהן בעוד שעה",
 *   type: NOTIFICATION_TYPES.APPOINTMENT,
 *   urgency: NOTIFICATION_URGENCY.HIGH,
 *   therapistId: "xyz789",
 *   link: "/dashboard/calendar",
 *   timestamp: "2026-01-15T14:30:00.000Z"
 * });
 */
export const sendNotification = (clinicId, options) => {
  // Fire and forget - don't await
  createNotification(clinicId, {
    title: options.title,
    message: options.message,
    type: options.type,
    urgency: options.urgency || NOTIFICATION_URGENCY.MEDIUM,
    therapistId: options.therapistId || null,
    link: options.link || null,
    patientId: options.patientId || null,
    createdBy: options.createdBy || null,
    timestamp: options.timestamp || null, // Event date+time (ISO string or Date)
  }).catch((error) => {
    // Silently fail - notification should not break main operations
    console.error("[CK-8408] sendNotification failed silently:", error.message);
  });
};

/**
 * Send notification to all therapists in a clinic
 * @param {string} clinicId - The clinic ID
 * @param {Array} therapistIds - Array of therapist IDs
 * @param {Object} options - Notification options (same as sendNotification)
 */
export const sendNotificationToAll = (clinicId, therapistIds, options) => {
  // Send a clinic-wide notification (therapistId = null)
  sendNotification(clinicId, {
    ...options,
    therapistId: null, // Clinic-wide
  });
};

// ============================================
// PUSH NOTIFICATIONS (FCM) FUNCTIONS
// ============================================

/**
 * VAPID key for Firebase Cloud Messaging web push.
 * Generated in Firebase Console → Project Settings → Cloud Messaging → Web Push certificates.
 * IMPORTANT: Replace this with your actual VAPID key from Firebase Console.
 */
const FCM_VAPID_KEY = "BKRJCH4ooZguseHnQkxp5ohcEPQ8c2fLfiBsAGl7DBYCeHRBkwfWwRBnKBXMlZvMrdLC8bHCPXzfjUXmHAlPzsk";

let messagingInstance = null;
let _fcmConfigWarned = false;

/**
 * Returns true if FCM is properly configured (VAPID key is set).
 * Logs a single warning if not, then stays silent.
 */
const isFcmConfigured = () => {
  if (!FCM_VAPID_KEY || FCM_VAPID_KEY === "REPLACE_WITH_YOUR_VAPID_KEY") {
    if (!_fcmConfigWarned) {
      _fcmConfigWarned = true;
      console.warn(
        "[CliniKit] FCM VAPID key not configured — push notifications disabled. " +
        "Set FCM_VAPID_KEY in src/firebase.js."
      );
    }
    return false;
  }
  return true;
};

/**
 * Get the FCM messaging instance (lazy-initialized).
 * Returns null if the browser doesn't support FCM or VAPID key is missing.
 */
const getMessagingInstance = async () => {
  if (!isFcmConfigured()) return null;
  if (messagingInstance) return messagingInstance;
  try {
    const supported = await isMessagingSupported();
    if (!supported) return null;
    messagingInstance = getMessaging(app);
    return messagingInstance;
  } catch {
    return null;
  }
};

/**
 * Check if the current browser/device supports push notifications.
 * @returns {boolean}
 */
export const isPushNotificationSupported = () => {
  return "Notification" in window && "serviceWorker" in navigator && "PushManager" in window;
};

/**
 * Get the current push notification permission state.
 * @returns {"granted" | "denied" | "default" | "unsupported"}
 */
export const getPushPermissionState = () => {
  if (!isPushNotificationSupported()) return "unsupported";
  return Notification.permission;
};

/**
 * Request push notification permission, obtain an FCM token, and save it to Firestore.
 *
 * Flow:
 * 1. Calls Notification.requestPermission() (on iOS PWA, must be triggered by user gesture)
 * 2. Gets FCM token via getToken() using the registered service worker
 * 3. Saves the token to /therapists/{therapistId}/pushTokens/{deviceId}
 *
 * @param {string} therapistId - The therapist UID
 * @param {string} clinicId - The clinic ID
 * @returns {{ success: boolean, token: string|null, permission: string }}
 */
export const requestPushPermissionAndSaveToken = async (therapistId, clinicId) => {
  try {
    if (!isPushNotificationSupported() || !isFcmConfigured()) {
      return { success: false, token: null, permission: "unsupported" };
    }

    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      return { success: false, token: null, permission };
    }

    const messaging = await getMessagingInstance();
    if (!messaging) {
      return { success: false, token: null, permission: "unsupported" };
    }

    const swRegistration = await navigator.serviceWorker.getRegistration("/sw.js");
    if (!swRegistration) {
      console.warn("[CliniKit] Service worker not registered — cannot get FCM token.");
      return { success: false, token: null, permission: "granted" };
    }

    const token = await getToken(messaging, {
      vapidKey: FCM_VAPID_KEY,
      serviceWorkerRegistration: swRegistration,
    });

    if (!token) {
      return { success: false, token: null, permission: "granted" };
    }

    await savePushToken(therapistId, clinicId, token);

    await updateTherapistDocument(therapistId, {
      pushNotificationsEnabled: true,
      pushEnabledAt: serverTimestamp(),
    });

    return { success: true, token, permission: "granted" };
  } catch (error) {
    console.warn("[CliniKit] Error requesting push permission:", error.message);
    return { success: false, token: null, permission: Notification.permission || "error" };
  }
};

const PUSH_DEVICE_ID_KEY = "clinikit_deviceId";

const getOrCreateDeviceId = () => {
  try {
    let id = localStorage.getItem(PUSH_DEVICE_ID_KEY);
    if (!id) {
      id =
        (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
        `dev_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(PUSH_DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    // Strict-privacy / SSR: fall back to a session-scoped id so callers don't crash.
    // The 30d TTL cleanup still bounds duplicate accumulation in this rare case.
    return `dev_session_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  }
};

/**
 * Save an FCM push token to Firestore at /therapists/{uid}/pushTokens/{deviceId}.
 * Doc ID is a stable browser-local device id so the same physical device always
 * overwrites its own entry across FCM token rotations.
 *
 * @param {string} therapistId
 * @param {string} clinicId
 * @param {string} token - The FCM registration token
 */
const savePushToken = async (therapistId, clinicId, token) => {
  try {
    const deviceId = getOrCreateDeviceId();
    const tokenRef = doc(db, "therapists", therapistId, "pushTokens", deviceId);

    // Migration cleanup: a previous session may have written this same FCM token under a
    // token-keyed doc id. Remove any such legacy doc so this device has exactly one entry.
    try {
      const dupSnap = await getDocs(
        query(
          collection(db, "therapists", therapistId, "pushTokens"),
          where("token", "==", token)
        )
      );
      const stale = dupSnap.docs.filter((d) => d.id !== deviceId);
      await Promise.all(stale.map((d) => deleteDoc(d.ref)));
    } catch (cleanupErr) {
      console.warn("[CK-9214] Push token migration cleanup failed:", cleanupErr.message);
    }

    const existing = await getDoc(tokenRef);
    const payload = {
      token,
      deviceId,
      clinicId,
      platform: detectPlatform(),
      userAgent: (navigator.userAgent || "").slice(0, 200),
      updatedAt: serverTimestamp(),
      lastUsedAt: serverTimestamp(),
    };
    if (!existing.exists()) payload.createdAt = serverTimestamp();

    await setDoc(tokenRef, payload, { merge: true });
  } catch (error) {
    console.warn("[CK-9214] Error saving push token:", error.message);
    throw error;
  }
};

/**
 * Detect the platform (ios / android / desktop) from the user agent.
 * @returns {"ios" | "android" | "desktop"}
 */
const detectPlatform = () => {
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/.test(ua)) return "ios";
  if (/Android/.test(ua)) return "android";
  return "desktop";
};

/**
 * Delete the current device's push token doc. Deletes by deviceId, with a legacy
 * token-keyed fallback so users who registered before the deviceId migration also get
 * their entry removed.
 *
 * @param {string} therapistId
 * @param {string} token
 */
export const deletePushToken = async (therapistId, token) => {
  try {
    const deviceId = getOrCreateDeviceId();
    await deleteDoc(doc(db, "therapists", therapistId, "pushTokens", deviceId)).catch(() => {});
    if (token) {
      await deleteDoc(doc(db, "therapists", therapistId, "pushTokens", token)).catch(() => {});
    }
  } catch (error) {
    console.warn("[CK-9214] Error deleting push token:", error.message);
  }
};

/**
 * Disable push notifications for a therapist.
 * Removes the current device's token and updates the therapist document.
 *
 * @param {string} therapistId
 */
export const disablePushNotifications = async (therapistId) => {
  try {
    const messaging = await getMessagingInstance();
    if (messaging) {
      const swRegistration = await navigator.serviceWorker.getRegistration("/sw.js");
      if (swRegistration) {
        try {
          const currentToken = await getToken(messaging, {
            vapidKey: FCM_VAPID_KEY,
            serviceWorkerRegistration: swRegistration,
          });
          if (currentToken) {
            await deletePushToken(therapistId, currentToken);
          }
        } catch {
          // Token retrieval may fail if permission was revoked — that's fine
        }
      }
    }

    await updateTherapistDocument(therapistId, {
      pushNotificationsEnabled: false,
    });
  } catch (error) {
    console.warn("[CliniKit] Error disabling push notifications:", error.message);
    throw error;
  }
};

/**
 * Send a test push notification to the current therapist's own devices.
 * Calls the sendTestPushNotification Cloud Function (target is derived from auth.uid server-side).
 *
 * @returns {Promise<{ successCount: number, failureCount: number, tokensCleanedUp: number }>}
 */
export const sendTestPushNotification = async () => {
  try {
    const sendTestPushFunc = httpsCallable(functions, "sendTestPushNotification");
    const result = await sendTestPushFunc();
    return result.data;
  } catch (error) {
    console.error("[CK-8460] Error sending test push notification:", {
      errorCode: "CK-8460",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Refresh the FCM token for the current device.
 * Should be called on app startup (DashboardLayout mount) to keep tokens fresh.
 *
 * @param {string} therapistId
 * @param {string} clinicId
 */
export const refreshPushTokenIfNeeded = async (therapistId, clinicId) => {
  try {
    if (!isPushNotificationSupported() || !isFcmConfigured()) return;
    if (Notification.permission !== "granted") return;

    const messaging = await getMessagingInstance();
    if (!messaging) return;

    const swRegistration = await navigator.serviceWorker.getRegistration("/sw.js");
    if (!swRegistration) return;

    const token = await getToken(messaging, {
      vapidKey: FCM_VAPID_KEY,
      serviceWorkerRegistration: swRegistration,
    });

    if (token) {
      await savePushToken(therapistId, clinicId, token);
    }
  } catch {
    // Silently fail — token refresh is best-effort
  }
};

/**
 * Listen for foreground push messages.
 * Returns an unsubscribe function.
 *
 * @param {function} callback - Called with the FCM message payload
 * @returns {function|null} unsubscribe function, or null if unsupported
 */
export const onForegroundPushMessage = async (callback) => {
  try {
    const messaging = await getMessagingInstance();
    if (!messaging) return null;
    return onMessage(messaging, callback);
  } catch {
    return null;
  }
};

/**
 * Check if the therapist should be prompted for push notification permission.
 * Returns true if:
 * 1. Push is supported on this device
 * 2. Permission has not been granted or denied yet (state is "default")
 * 3. The therapist has been registered for at least 3 days
 * 4. The therapist hasn't already dismissed the prompt (localStorage check)
 * 5. pushNotificationsEnabled is not already true on the profile
 *
 * @param {Object} therapistProfile - The therapist profile from Redux
 * @returns {boolean}
 */
export const shouldPromptForPush = (therapistProfile) => {
  if (!therapistProfile) return false;
  if (!isPushNotificationSupported() || !isFcmConfigured()) return false;
  if (Notification.permission !== "default") return false;
  if (therapistProfile.pushNotificationsEnabled === true) return false;

  // Check localStorage for dismissal
  const dismissKey = `clinikit_push_prompt_dismissed_${therapistProfile.uid}`;
  const dismissed = localStorage.getItem(dismissKey);
  if (dismissed) {
    const dismissedDate = new Date(dismissed);
    const now = new Date();
    const daysSinceDismissal = (now - dismissedDate) / (1000 * 60 * 60 * 24);
    if (daysSinceDismissal < 30) return false;
  }

  // Check registration date — must be at least 3 days ago
  const registrationDate = therapistProfile.registrationDate;
  if (!registrationDate) return false;

  let regDate;
  if (registrationDate?.toDate) {
    regDate = registrationDate.toDate();
  } else if (registrationDate?.seconds) {
    regDate = new Date(registrationDate.seconds * 1000);
  } else {
    regDate = new Date(registrationDate);
  }

  if (isNaN(regDate.getTime())) return false;

  const now = new Date();
  const daysSinceRegistration = (now - regDate) / (1000 * 60 * 60 * 24);
  return daysSinceRegistration >= 3;
};

/**
 * Record that the user dismissed the push notification prompt.
 * @param {string} therapistId
 */
export const dismissPushPrompt = (therapistId) => {
  const dismissKey = `clinikit_push_prompt_dismissed_${therapistId}`;
  localStorage.setItem(dismissKey, new Date().toISOString());
};

// ============================================
// FINANCE MODULE FUNCTIONS
// ============================================

/**
 * Payment status constants
 * Note: DEBT is deprecated and treated the same as PENDING (unified "pending payment" model)
 */
export const PAYMENT_STATUS = {
  PENDING: "pending", // Awaiting payment (unified - includes what was "debt")
  PAID: "paid", // Fully paid
  PARTIAL: "partial", // Partially paid
  SKIPPED: "skipped", // User chose to skip (no follow-up needed)
  DEBT: "debt", // @deprecated - Kept for backwards compatibility, treated as PENDING
};

/**
 * Payment method constants
 */
export const PAYMENT_METHODS = {
  CASH: "cash",
  CREDIT: "credit",
  TRANSFER: "transfer",
  CHECK: "check",
  BIT: "bit",
  OTHER: "other",
  PUNCH_CARD: "punch_card", // כרטיסיה - meeting covered by a prepaid punch card
};

/**
 * Get monthly income statistics for a therapist
 * Calculates: actual income, expected income, pending collections
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} therapistId - The therapist ID
 * @param {number} year - Year (e.g., 2026)
 * @param {number} month - Month (1-12)
 * @returns {Object} - Income statistics
 *
 * Error Code: CK-8500
 */
export const getMonthlyIncomeStats = async (
  clinicId,
  therapistId,
  year,
  month
) => {
  try {
    const appointmentsRef = collection(db, "clinics", clinicId, "appointments");

    // Calculate month boundaries
    const monthStart = new Date(year, month - 1, 1, 0, 0, 0);
    const monthEnd = new Date(year, month, 0, 23, 59, 59); // Last day of month

    // Query all appointments for the month
    const constraints = [
      where("therapistId", "==", therapistId),
      where("startTime", ">=", Timestamp.fromDate(monthStart)),
      where("startTime", "<=", Timestamp.fromDate(monthEnd)),
      orderBy("startTime", "asc"),
    ];

    const q = query(appointmentsRef, ...constraints);
    const snapshot = await getDocs(q);
    const appointments = snapshot.docs
      .map((doc) => serializeForRedux({ id: doc.id, ...doc.data() }))
      .filter((apt) => apt.isDeleted !== true);

    // Calculate statistics
    const now = new Date();
    let actualIncome = 0; // Sum of actualPaidAmount where paymentStatus is "paid"
    let expectedIncome = 0; // Sum of paymentAmount for future appointments
    let pendingCollectionAmount = 0; // Sum of paymentAmount where paymentStatus is "pending" or "debt"
    let pendingCollectionCount = 0;
    let futureAppointmentsWithRate = 0;
    let futureAppointmentsWithoutRate = 0;
    let totalAppointments = 0;
    let completedAppointments = 0;
    let cancelledAppointments = 0;

    appointments.forEach((apt) => {
      // Blocked time (חסימת זמן) is a calendar visual, not a meeting
      if (apt.isBlockedTime === true) return;

      const aptDate = new Date(apt.startTime);
      totalAppointments++;

      if (apt.status === "cancelled") {
        cancelledAppointments++;
        return;
      }

      // Past/completed appointments
      if (aptDate <= now || apt.status === "completed") {
        completedAppointments++;

        // Actually paid income
        if (apt.paymentStatus === PAYMENT_STATUS.PAID && apt.actualPaidAmount) {
          actualIncome += apt.actualPaidAmount;
        } else if (apt.isPaid === true && apt.paymentAmount) {
          // Backwards compatibility: if using old isPaid flag
          actualIncome += apt.paymentAmount;
        }

        // Pending collection (past appointments not yet paid)
        if (
          apt.paymentStatus === PAYMENT_STATUS.PENDING ||
          apt.paymentStatus === PAYMENT_STATUS.DEBT ||
          apt.paymentStatus === PAYMENT_STATUS.PARTIAL
        ) {
          if (apt.paymentAmount) {
            const remaining =
              apt.paymentStatus === PAYMENT_STATUS.PARTIAL
                ? apt.paymentAmount - (apt.actualPaidAmount || 0)
                : apt.paymentAmount;
            pendingCollectionAmount += remaining;
            pendingCollectionCount++;
          }
        }
        // Backwards compatibility: old model without paymentStatus
        else if (
          !apt.paymentStatus &&
          apt.isPaid !== true &&
          apt.paymentAmount
        ) {
          pendingCollectionAmount += apt.paymentAmount;
          pendingCollectionCount++;
        }
      }
      // Future appointments
      else {
        if (apt.paymentAmount) {
          expectedIncome += apt.paymentAmount;
          futureAppointmentsWithRate++;
        } else {
          futureAppointmentsWithoutRate++;
        }
      }
    });

    return {
      actualIncome,
      expectedIncome,
      pendingCollectionAmount,
      pendingCollectionCount,
      totalAppointments,
      completedAppointments,
      cancelledAppointments,
      futureAppointmentsWithRate,
      futureAppointmentsWithoutRate,
      month,
      year,
    };
  } catch (error) {
    console.error("[CK-8500] Error fetching monthly income stats:", {
      errorCode: "CK-8500",
      errorMessage: error.message,
      clinicId,
      therapistId,
      year,
      month,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get appointments with pending payments (for collection tracking)
 * Returns appointments where payment is pending, partial, or marked as debt
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} therapistId - The therapist ID
 * @param {Object} options - Filter options
 * @param {string} options.status - Filter by paymentStatus (optional)
 * @param {number} options.limit - Max results (default 50)
 * @returns {Array} - Array of appointments with pending payments
 *
 * Error Code: CK-8501
 */
export const getPendingPayments = async (
  clinicId,
  therapistId,
  options = {}
) => {
  try {
    const appointmentsRef = collection(db, "clinics", clinicId, "appointments");
    const now = new Date();

    // We need to query completed appointments with pending payments
    // Unfortunately, Firestore doesn't support OR queries well, so we need to handle this differently

    // Query all completed/past appointments for this therapist
    const constraints = [
      where("therapistId", "==", therapistId),
      where("startTime", "<=", Timestamp.fromDate(now)),
      orderBy("startTime", "desc"),
      limit(options.limit || 100),
    ];

    const q = query(appointmentsRef, ...constraints);
    const snapshot = await getDocs(q);
    const allAppointments = snapshot.docs
      .map((doc) => serializeForRedux({ id: doc.id, ...doc.data() }))
      .filter((apt) => apt.isDeleted !== true);

    // Filter in JavaScript for pending payments
    const pendingPayments = allAppointments.filter((apt) => {
      // Skip cancelled appointments
      if (apt.status === "cancelled") return false;

      // Skip if no payment expected
      if (!apt.paymentAmount) return false;

      // New model: check paymentStatus
      if (apt.paymentStatus) {
        if (options.status) {
          return apt.paymentStatus === options.status;
        }
        return [
          PAYMENT_STATUS.PENDING,
          PAYMENT_STATUS.PARTIAL,
          PAYMENT_STATUS.DEBT,
        ].includes(apt.paymentStatus);
      }

      // Backwards compatibility: old model with isPaid
      return apt.isPaid !== true;
    });

    return pendingPayments;
  } catch (error) {
    console.error("[CK-8501] Error fetching pending payments:", {
      errorCode: "CK-8501",
      errorMessage: error.message,
      clinicId,
      therapistId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get future appointments with expected payment amounts
 * For calculating expected income
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} therapistId - The therapist ID
 * @param {Date} startDate - Start of date range
 * @param {Date} endDate - End of date range
 * @returns {Array} - Array of upcoming appointments with payment info
 *
 * Error Code: CK-8502
 */
export const getUpcomingAppointmentsWithPayment = async (
  clinicId,
  therapistId,
  startDate,
  endDate
) => {
  try {
    const appointmentsRef = collection(db, "clinics", clinicId, "appointments");

    const constraints = [
      where("therapistId", "==", therapistId),
      where("startTime", ">=", Timestamp.fromDate(startDate)),
      where("startTime", "<=", Timestamp.fromDate(endDate)),
      orderBy("startTime", "asc"),
    ];

    const q = query(appointmentsRef, ...constraints);
    const snapshot = await getDocs(q);

    const appointments = snapshot.docs
      .map((doc) => serializeForRedux({ id: doc.id, ...doc.data() }))
      .filter((apt) => apt.isDeleted !== true)
      .filter((apt) => apt.status !== "cancelled")
      .filter((apt) => apt.isBlockedTime !== true);

    return appointments;
  } catch (error) {
    console.error(
      "[CK-8502] Error fetching upcoming appointments with payment:",
      {
        errorCode: "CK-8502",
        errorMessage: error.message,
        clinicId,
        therapistId,
        startDate,
        endDate,
        timestamp: new Date().toISOString(),
      }
    );
    throw error;
  }
};

/**
 * Update payment status for an appointment
 * This is a specialized function for the finance module
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} appointmentId - The appointment ID
 * @param {Object} paymentData - Payment update data
 * @param {string} paymentData.paymentStatus - New payment status
 * @param {number} paymentData.actualPaidAmount - Amount actually paid
 * @param {string} paymentData.paymentMethod - Payment method used
 * @param {Date|string} paymentData.paymentDate - When payment was made
 * @returns {Object} - Updated appointment
 *
 * Error Code: CK-8503
 */
export const updatePaymentStatus = async (
  clinicId,
  appointmentId,
  paymentData
) => {
  try {
    const appointmentRef = doc(
      db,
      "clinics",
      clinicId,
      "appointments",
      appointmentId
    );

    const updateData = {
      updatedAt: serverTimestamp(),
    };

    // Add payment fields if provided
    if (paymentData.paymentStatus !== undefined) {
      updateData.paymentStatus = paymentData.paymentStatus;

      // Also set isPaid for backwards compatibility
      updateData.isPaid =
        paymentData.paymentStatus === PAYMENT_STATUS.PAID ||
        paymentData.paymentStatus === PAYMENT_STATUS.SKIPPED;
    }

    if (paymentData.actualPaidAmount !== undefined) {
      updateData.actualPaidAmount = paymentData.actualPaidAmount;
    }

    if (paymentData.paymentMethod !== undefined) {
      updateData.paymentMethod = paymentData.paymentMethod;
    }

    if (paymentData.paymentDate !== undefined) {
      updateData.paymentDate = paymentData.paymentDate
        ? Timestamp.fromDate(new Date(paymentData.paymentDate))
        : null;
    }

    // If paid, also update paymentAmount if not set
    if (paymentData.paymentAmount !== undefined) {
      updateData.paymentAmount = paymentData.paymentAmount;
    }

    await updateDoc(appointmentRef, updateData);

    // Return updated appointment
    return getAppointment(clinicId, appointmentId);
  } catch (error) {
    console.error("[CK-8503] Error updating payment status:", {
      errorCode: "CK-8503",
      errorMessage: error.message,
      clinicId,
      appointmentId,
      paymentData,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get patient payment history (all paid appointments for a patient)
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} patientId - The patient ID
 * @param {string} therapistId - The therapist ID (optional, filters by therapist)
 * @returns {Array} - Array of appointments with payment history
 *
 * Error Code: CK-8504
 */
export const getPatientPaymentHistory = async (
  clinicId,
  patientId,
  therapistId = null
) => {
  try {
    const appointmentsRef = collection(db, "clinics", clinicId, "appointments");

    const constraints = [where("patientId", "==", patientId)];

    if (therapistId) {
      constraints.push(where("therapistId", "==", therapistId));
    }

    constraints.push(orderBy("startTime", "desc"));
    constraints.push(limit(50));

    const q = query(appointmentsRef, ...constraints);
    const snapshot = await getDocs(q);

    const appointments = snapshot.docs
      .map((doc) => serializeForRedux({ id: doc.id, ...doc.data() }))
      .filter((apt) => apt.isDeleted !== true)
      .filter((apt) => apt.status !== "cancelled")
      .filter((apt) => apt.isBlockedTime !== true);

    return appointments;
  } catch (error) {
    console.error("[CK-8504] Error fetching patient payment history:", {
      errorCode: "CK-8504",
      errorMessage: error.message,
      clinicId,
      patientId,
      therapistId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get patient debt summary
 * Calculates total outstanding debt for a patient
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} patientId - The patient ID
 * @param {string} therapistId - The therapist ID
 * @returns {Object} - Debt summary
 *
 * Error Code: CK-8505
 */
export const getPatientDebtSummary = async (
  clinicId,
  patientId,
  therapistId
) => {
  try {
    const appointments = await getPatientPaymentHistory(
      clinicId,
      patientId,
      therapistId
    );

    let totalDebt = 0;
    let unpaidAppointments = 0;
    let totalPaid = 0;
    let paidAppointments = 0;

    appointments.forEach((apt) => {
      if (!apt.paymentAmount) return;

      // Check new model
      if (apt.paymentStatus) {
        if (apt.paymentStatus === PAYMENT_STATUS.PAID) {
          totalPaid += apt.actualPaidAmount || apt.paymentAmount;
          paidAppointments++;
        } else if (apt.paymentStatus === PAYMENT_STATUS.PARTIAL) {
          totalPaid += apt.actualPaidAmount || 0;
          totalDebt += apt.paymentAmount - (apt.actualPaidAmount || 0);
          unpaidAppointments++;
        } else if (
          apt.paymentStatus === PAYMENT_STATUS.PENDING ||
          apt.paymentStatus === PAYMENT_STATUS.DEBT
        ) {
          totalDebt += apt.paymentAmount;
          unpaidAppointments++;
        }
        // skipped = no debt
      }
      // Backwards compatibility
      else {
        if (apt.isPaid === true) {
          totalPaid += apt.paymentAmount;
          paidAppointments++;
        } else {
          totalDebt += apt.paymentAmount;
          unpaidAppointments++;
        }
      }
    });

    return {
      totalDebt,
      unpaidAppointments,
      totalPaid,
      paidAppointments,
      totalAppointments: appointments.length,
    };
  } catch (error) {
    console.error("[CK-8505] Error fetching patient debt summary:", {
      errorCode: "CK-8505",
      errorMessage: error.message,
      clinicId,
      patientId,
      therapistId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// =============================================
// FINANCIAL TRANSACTIONS MODULE
// =============================================

/**
 * Transaction status constants
 * 
 * Main Flow:
 * EXPECTED (future appointment) → PENDING (confirmed) → PAID → INVOICE_MANUAL/INVOICE_AUTO
 * 
 * Status Descriptions:
 * - EXPECTED: צפוי להתקבל תשלום - Future appointment, not yet confirmed
 * - PENDING: ממתין לתשלום - Therapist confirmed appointment occurred, awaiting payment
 * - PAID: תשלום התקבל - Payment received
 * - INVOICE_MANUAL: הופקה חשבונית (ידני) - Manual invoice issued
 * - INVOICE_AUTO: הופקה חשבונית (אוטומטי) - Auto invoice via Morning
 * 
 * Special Statuses:
 * - SPLIT: רשומה שעברה פיצול - Original record that was split
 * - REFUNDED: זיכוי - Refunded
 * - CANCELLED: בוטל - Cancelled
 * 
 * Kanban Board: Only PENDING, PAID, INVOICE_MANUAL, INVOICE_AUTO
 * EXPECTED is shown separately (not in kanban)
 */
export const TRANSACTION_STATUS = {
  EXPECTED: "expected", // צפוי להתקבל תשלום - future appointment
  PENDING: "pending", // ממתין לתשלום - awaiting payment (confirmed)
  PAID: "paid", // תשלום התקבל - payment received
  INVOICE_MANUAL: "invoice_manual", // הופקה חשבונית (ידני) - manual invoice issued
  INVOICE_AUTO: "invoice_auto", // הופקה חשבונית (אוטומטי) - auto invoice via Morning
  SPLIT: "split", // רשומה שעברה פיצול - original record that was split
  REFUNDED: "refunded", // זיכוי - refunded
  CANCELLED: "cancelled", // בוטל - cancelled
  // Legacy status - for backward compatibility
  PENDING_COLLECTION: "pending_collection", // Maps to PENDING for display
};

/**
 * Helper to normalize legacy statuses to new statuses
 * Note: EXPECTED is now a real status, not normalized to PENDING
 */
export const normalizeTransactionStatus = (status) => {
  // Only legacy pending_collection maps to PENDING
  if (status === "pending_collection") {
    return TRANSACTION_STATUS.PENDING;
  }
  return status;
};

/**
 * Check if a status is part of the Kanban workflow
 * EXPECTED is NOT in kanban (shown separately)
 */
export const isKanbanStatus = (status) => {
  const normalized = normalizeTransactionStatus(status);
  return [
    TRANSACTION_STATUS.PENDING,
    TRANSACTION_STATUS.PAID,
    TRANSACTION_STATUS.INVOICE_MANUAL,
    TRANSACTION_STATUS.INVOICE_AUTO,
  ].includes(normalized);
};

/**
 * Get display label for transaction status
 */
export const getTransactionStatusLabel = (status) => {
  const normalized = normalizeTransactionStatus(status);
  switch (normalized) {
    case TRANSACTION_STATUS.EXPECTED:
      return "צפוי להתקבל תשלום";
    case TRANSACTION_STATUS.PENDING:
      return "ממתין לתשלום";
    case TRANSACTION_STATUS.PAID:
      return "תשלום התקבל";
    case TRANSACTION_STATUS.INVOICE_MANUAL:
      return "הופקה חשבונית (ידני)";
    case TRANSACTION_STATUS.INVOICE_AUTO:
      return "הופקה חשבונית (אוטומטי)";
    case TRANSACTION_STATUS.SPLIT:
      return "פוצל";
    case TRANSACTION_STATUS.REFUNDED:
      return "זיכוי";
    case TRANSACTION_STATUS.CANCELLED:
      return "בוטל";
    default:
      return status;
  }
};

/**
 * Transaction type constants
 */
export const TRANSACTION_TYPE = {
  APPOINTMENT: "appointment", // From appointment
  MANUAL: "manual", // Manual income entry
  SPLIT_PAID: "split_paid", // Result of split - paid portion
  SPLIT_PENDING: "split_pending", // Result of split - remaining pending
};

/**
 * Create a financial transaction
 * Called when an appointment is created or for manual income entries
 *
 * @param {string} clinicId - The clinic ID
 * @param {Object} transactionData - Transaction data
 * @returns {Object} - Created transaction with ID
 *
 * Error Code: CK-8600
 */
export const createFinancialTransaction = async (clinicId, transactionData) => {
  try {
    const transactionsRef = collection(
      db,
      "clinics",
      clinicId,
      "financial_transactions"
    );
    const newTransactionRef = doc(transactionsRef);

    const transactionDoc = {
      id: newTransactionRef.id,
      clinicId,
      therapistId: transactionData.therapistId,

      // Source reference
      appointmentId: transactionData.appointmentId || null,

      // Patient info
      patientId: transactionData.patientId || null,
      patientName: transactionData.patientName || null,

      // Transaction type
      type: transactionData.type || TRANSACTION_TYPE.APPOINTMENT,

      // Financial data
      amount: transactionData.amount || 0,
      paymentMethod: transactionData.paymentMethod || null,

      // Status tracking
      status: transactionData.status || TRANSACTION_STATUS.EXPECTED,

      // Date tracking
      expectedPaymentDate: transactionData.expectedPaymentDate
        ? Timestamp.fromDate(new Date(transactionData.expectedPaymentDate))
        : null,
      actualPaymentDate: transactionData.actualPaymentDate
        ? Timestamp.fromDate(new Date(transactionData.actualPaymentDate))
        : null,

      // Description
      description: transactionData.description || null,
      notes: transactionData.notes || null,

      // Invoice tracking (same structure as appointments.RecepitsDocsRefArray)
      // Each item: { documentId, invoiceNumber, invoiceUrl, invoiceProvider, createdAt }
      RecepitsDocsRefArray: transactionData.RecepitsDocsRefArray || [],

      // Debt settlement tracking
      settledDebtIds: transactionData.settledDebtIds || [],
      systemNotes: transactionData.systemNotes || null,

      // Cancellation fee flag - set when this transaction represents a
      // cancellation fee (created by the cancel flow). Lets finance views
      // label the row distinctly from regular income.
      isCancellationFee: transactionData.isCancellationFee === true,

      // Punch card (כרטיסיה) tracking
      // isPunchCard: the linked appointment was covered by a punch card
      // isPunchCardPurchase: this row records the upfront purchase of a card
      isPunchCard: transactionData.isPunchCard === true,
      punchCardId: transactionData.punchCardId || null,
      isPunchCardPurchase: transactionData.isPunchCardPurchase === true,

      // Metadata
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(newTransactionRef, transactionDoc);

    return serializeForRedux({ ...transactionDoc, id: newTransactionRef.id });
  } catch (error) {
    console.error("[CK-8600] Error creating financial transaction:", {
      errorCode: "CK-8600",
      errorMessage: error.message,
      clinicId,
      transactionData,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Update a financial transaction
 * Called when appointment status changes or payment is received
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} transactionId - The transaction ID
 * @param {Object} updateData - Fields to update
 * @returns {Object} - Updated transaction
 *
 * Error Code: CK-8601
 */
export const updateFinancialTransaction = async (
  clinicId,
  transactionId,
  updateData
) => {
  try {
    const transactionRef = doc(
      db,
      "clinics",
      clinicId,
      "financial_transactions",
      transactionId
    );

    const updates = {
      updatedAt: serverTimestamp(),
    };

    // Status moving into the paid family → mirror onto the legacy appointment
    // fields after the write (needs the current doc for old status / links).
    let paidMirror = null;
    if (
      updateData.status !== undefined &&
      PAID_FAMILY_STATUSES.includes(updateData.status)
    ) {
      const currentSnap = await getDoc(transactionRef);
      if (currentSnap.exists()) {
        const cur = currentSnap.data();
        paidMirror = {
          appointmentId: cur.appointmentId || null,
          oldStatus: cur.status,
          newStatus: updateData.status,
          isPunchCard: cur.isPunchCard === true,
          amount: updateData.amount !== undefined ? updateData.amount : cur.amount ?? null,
          paymentMethod: updateData.paymentMethod || cur.paymentMethod || null,
          actualPaymentDate: updateData.actualPaymentDate || null,
        };
      }
    }

    // Add provided fields
    if (updateData.status !== undefined) {
      updates.status = updateData.status;
    }
    if (updateData.amount !== undefined) {
      updates.amount = updateData.amount;
    }
    if (updateData.paymentMethod !== undefined) {
      updates.paymentMethod = updateData.paymentMethod;
    }
    if (updateData.actualPaymentDate !== undefined) {
      updates.actualPaymentDate = updateData.actualPaymentDate
        ? Timestamp.fromDate(new Date(updateData.actualPaymentDate))
        : null;
    }
    if (updateData.expectedPaymentDate !== undefined) {
      const epd = updateData.expectedPaymentDate;
      updates.expectedPaymentDate = epd
        ? Timestamp.fromDate(epd.toDate ? epd.toDate() : new Date(epd))
        : null;
    }
    if (updateData.notes !== undefined) {
      updates.notes = updateData.notes;
    }
    if (updateData.description !== undefined) {
      updates.description = updateData.description;
    }
    // Invoice tracking - array of invoice references
    if (updateData.RecepitsDocsRefArray !== undefined) {
      updates.RecepitsDocsRefArray = updateData.RecepitsDocsRefArray;
    }
    // System notes - automatic notes for tracking payment history
    if (updateData.systemNotes !== undefined) {
      updates.systemNotes = updateData.systemNotes;
    }
    // Original amount - store original debt amount before partial payment
    if (updateData.originalAmount !== undefined) {
      updates.originalAmount = updateData.originalAmount;
    }
    // Linked payment ID - reference to payment that settled this debt
    if (updateData.linkedPaymentId !== undefined) {
      updates.linkedPaymentId = updateData.linkedPaymentId;
    }
    // Cancellation fee flag - set when the transaction represents a
    // cancellation fee (either auto-created by the cancel flow or when an
    // existing PAID transaction is re-flagged after the appointment was
    // cancelled and the therapist chose "convert to cancellation fee").
    if (updateData.isCancellationFee !== undefined) {
      updates.isCancellationFee = updateData.isCancellationFee;
    }
    // Punch card (כרטיסיה) flags - set when a meeting is punched against a
    // card or cleared when the punch is restored
    if (updateData.isPunchCard !== undefined) {
      updates.isPunchCard = updateData.isPunchCard;
    }
    if (updateData.punchCardId !== undefined) {
      updates.punchCardId = updateData.punchCardId;
    }

    await updateDoc(transactionRef, updates);

    if (paidMirror) {
      await mirrorPaidStatusOntoAppointment(clinicId, paidMirror.appointmentId, paidMirror);
    }

    // Fetch and return updated document
    const updatedDoc = await getDoc(transactionRef);
    return serializeForRedux({ id: updatedDoc.id, ...updatedDoc.data() });
  } catch (error) {
    console.error("[CK-8601] Error updating financial transaction:", {
      errorCode: "CK-8601",
      errorMessage: error.message,
      clinicId,
      transactionId,
      updateData,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Delete a financial transaction
 * Called when an appointment is deleted
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} transactionId - The transaction ID
 *
 * Error Code: CK-8602
 */
export const deleteFinancialTransaction = async (clinicId, transactionId) => {
  try {
    const transactionRef = doc(
      db,
      "clinics",
      clinicId,
      "financial_transactions",
      transactionId
    );
    await deleteDoc(transactionRef);
  } catch (error) {
    console.error("[CK-8602] Error deleting financial transaction:", {
      errorCode: "CK-8602",
      errorMessage: error.message,
      clinicId,
      transactionId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get transaction by appointment ID
 * Used to find the transaction linked to a specific appointment
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} appointmentId - The appointment ID
 * @returns {Object|null} - Transaction or null if not found
 *
 * Error Code: CK-8606
 */
export const getTransactionByAppointment = async (clinicId, appointmentId) => {
  try {
    const transactionsRef = collection(
      db,
      "clinics",
      clinicId,
      "financial_transactions"
    );
    const q = query(
      transactionsRef,
      where("appointmentId", "==", appointmentId)
    );
    let snapshot;
    try {
      snapshot = await getDocs(q);
    } catch (queryError) {
      // Rules only allow this unfiltered query for admins / clinic-wide
      // readers. A plain therapist is allowed to read their OWN rows
      // (ownsResource), but Firestore can only prove that when the query
      // itself filters on therapistId — so retry scoped to the caller.
      const uid = auth.currentUser?.uid;
      if (queryError?.code !== "permission-denied" || !uid) {
        throw queryError;
      }
      snapshot = await getDocs(
        query(
          transactionsRef,
          where("appointmentId", "==", appointmentId),
          where("therapistId", "==", uid)
        )
      );
    }

    if (snapshot.empty) {
      return null;
    }

    // Several rows can share an appointmentId after a partial payment
    // (a dead "split" parent + paid child + pending remainder). limit(1)
    // returned an arbitrary one, so cancel / delete / reschedule / catch-up
    // could act on the wrong row. Prefer: an open unpaid row (so cancel
    // flows close the live debt), then any non-split row, newest first.
    const rows = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    const live = rows.filter((r) => r.status !== "split");
    if (live.length === 0) {
      return serializeForRedux(rows[0]);
    }
    const createdMs = (r) =>
      r.createdAt?.toDate ? r.createdAt.toDate().getTime() : new Date(r.createdAt || 0).getTime();
    live.sort((a, b) => createdMs(b) - createdMs(a));
    const open = live.find((r) =>
      ["expected", "pending", "pending_collection"].includes(r.status)
    );
    return serializeForRedux(open || live[0]);
  } catch (error) {
    // permission-denied after the scoped retry means the therapist has no
    // finance access at all (allowFinanceAccess=false) — expected, not a bug.
    const logFn = error?.code === "permission-denied" ? console.warn : console.error;
    logFn("[CK-8606] Error fetching transaction by appointment:", {
      errorCode: "CK-8606",
      errorMessage: error.message,
      clinicId,
      appointmentId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get monthly transactions for financial calculations
 * Returns all transactions for a specific month based on their relevant date
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} therapistId - The therapist ID
 * @param {number} year - Year (e.g., 2026)
 * @param {number} month - Month (1-12)
 * @returns {Object} - Calculated financial stats from transactions
 *
 * Error Code: CK-8603
 */
export const getMonthlyTransactionStats = async (
  clinicId,
  therapistId,
  year,
  month,
  { includeAllTherapists = false } = {}
) => {
  try {
    const transactionsRef = collection(
      db,
      "clinics",
      clinicId,
      "financial_transactions"
    );

    // Calculate month boundaries
    const monthStart = new Date(year, month - 1, 1);
    const monthEnd = new Date(year, month, 0, 23, 59, 59);

    const constraints = includeAllTherapists
      ? [orderBy("createdAt", "desc")]
      : [where("therapistId", "==", therapistId), orderBy("createdAt", "desc")];

    const q = query(transactionsRef, ...constraints);

    const snapshot = await getDocs(q);
    const allTransactions = snapshot.docs.map((doc) =>
      serializeForRedux({ id: doc.id, ...doc.data() })
    );

    // Calculate stats
    const now = new Date();
    let expectedIncome = 0; // צפי הכנסה - expected transactions for this month
    let actualIncome = 0; // הכנסה בפועל - paid in this month (by actualPaymentDate)
    let pendingCollectionAmount = 0; // ממתין לתשלום - all pending_collection (any time)
    let pendingCollectionCount = 0;
    let expectedCount = 0;
    let paidCount = 0;
    // Per-tile breakdown rows (what each KPI number is built from). Slim
    // shape on purpose — the stats payload is cached in Redux.
    const expectedItems = [];
    const actualItems = [];
    const pendingItems = [];
    const toItem = (tx, date) => ({
      id: tx.id,
      patientName: tx.patientName || null,
      description: tx.description || null,
      amount: Number(tx.amount) || 0,
      date: date ? date.toISOString() : null,
    });

    allTransactions.forEach((tx) => {
      // Check if transaction's expected date falls in this month
      const expectedDate = tx.expectedPaymentDate
        ? new Date(tx.expectedPaymentDate)
        : null;
      const actualDate = tx.actualPaymentDate
        ? new Date(tx.actualPaymentDate)
        : null;

      // Determine effective status: EXPECTED transactions whose date has passed
      // are treated as pending collection (consistent with auto-promote in kanban)
      const isStaleExpected =
        tx.status === TRANSACTION_STATUS.EXPECTED &&
        expectedDate &&
        expectedDate <= now;
      const effectiveStatus = isStaleExpected
        ? TRANSACTION_STATUS.PENDING_COLLECTION
        : tx.status;

      // Expected income: status is "expected" and expectedPaymentDate is in this month
      if (
        effectiveStatus === TRANSACTION_STATUS.EXPECTED &&
        expectedDate &&
        expectedDate >= monthStart &&
        expectedDate <= monthEnd
      ) {
        expectedIncome += Number(tx.amount) || 0;
        expectedCount++;
        expectedItems.push(toItem(tx, expectedDate));
      }

      // Actual income: paid OR invoiced (an invoice is issued for money that
      // was collected — the tile's own tooltip promises "כולל תשלומים שהופקה
      // עבורם חשבונית" and every report counts paid+invoiced). Bucketed by
      // actualPaymentDate in this month.
      if (
        (effectiveStatus === TRANSACTION_STATUS.PAID ||
          effectiveStatus === TRANSACTION_STATUS.INVOICE_MANUAL ||
          effectiveStatus === TRANSACTION_STATUS.INVOICE_AUTO) &&
        actualDate &&
        actualDate >= monthStart &&
        actualDate <= monthEnd
      ) {
        actualIncome += Number(tx.amount) || 0;
        paidCount++;
        actualItems.push(toItem(tx, actualDate));
      }

      // Pending collection: "pending_collection" OR plain "pending" (kanban
      // drag, confirmAppointmentPayment, split remainders, IncomeModal all
      // write "pending") — regardless of date
      if (
        normalizeTransactionStatus(effectiveStatus) === TRANSACTION_STATUS.PENDING
      ) {
        pendingCollectionAmount += Number(tx.amount) || 0;
        pendingCollectionCount++;
        pendingItems.push(toItem(tx, expectedDate));
      }
    });

    return {
      expectedIncome,
      actualIncome,
      pendingCollectionAmount,
      pendingCollectionCount,
      expectedCount,
      paidCount,
      expectedItems,
      actualItems,
      pendingItems,
      month,
      year,
    };
  } catch (error) {
    console.error("[CK-8603] Error fetching monthly transaction stats:", {
      errorCode: "CK-8603",
      errorMessage: error.message,
      clinicId,
      therapistId,
      year,
      month,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get pending collection transactions (debtors list)
 * Returns all transactions with pending_collection status
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} therapistId - The therapist ID
 * @param {number} limitCount - Max results (default 50)
 * @returns {Array} - Array of pending collection transactions
 *
 * Error Code: CK-8604
 */
export const getPendingCollectionTransactions = async (
  clinicId,
  therapistId,
  limitCount = 50,
  { includeAllTherapists = false } = {}
) => {
  try {
    const transactionsRef = collection(
      db,
      "clinics",
      clinicId,
      "financial_transactions"
    );

    const effectiveTherapistId = includeAllTherapists ? null : therapistId;

    // Query explicitly pending transactions — both spellings are live:
    // "pending_collection" (post-meeting flows) and "pending" (kanban/manual)
    const PENDING_BOTH = [
      TRANSACTION_STATUS.PENDING_COLLECTION,
      TRANSACTION_STATUS.PENDING,
    ];
    const pendingQuery = effectiveTherapistId
      ? query(
          transactionsRef,
          where("therapistId", "==", effectiveTherapistId),
          where("status", "in", PENDING_BOTH),
          orderBy("expectedPaymentDate", "desc"),
          limit(limitCount)
        )
      : query(
          transactionsRef,
          where("status", "in", PENDING_BOTH),
          orderBy("expectedPaymentDate", "desc"),
          limit(limitCount)
        );

    // Also query stale "expected" transactions whose date has passed
    const expectedQuery = effectiveTherapistId
      ? query(
          transactionsRef,
          where("therapistId", "==", effectiveTherapistId),
          where("status", "==", TRANSACTION_STATUS.EXPECTED),
          where("expectedPaymentDate", "<=", Timestamp.fromDate(new Date())),
          orderBy("expectedPaymentDate", "desc"),
          limit(limitCount)
        )
      : query(
          transactionsRef,
          where("status", "==", TRANSACTION_STATUS.EXPECTED),
          where("expectedPaymentDate", "<=", Timestamp.fromDate(new Date())),
          orderBy("expectedPaymentDate", "desc"),
          limit(limitCount)
        );

    const [pendingSnap, expectedSnap] = await Promise.all([
      getDocs(pendingQuery),
      getDocs(expectedQuery),
    ]);

    const pendingDocs = pendingSnap.docs.map((d) =>
      serializeForRedux({ id: d.id, ...d.data() })
    );

    // Auto-promote stale expected transactions and include them
    const staleExpected = expectedSnap.docs.map((d) =>
      serializeForRedux({ id: d.id, ...d.data() })
    );
    if (staleExpected.length > 0) {
      const promotePromises = staleExpected.map((tx) => {
        const txRef = doc(db, "clinics", clinicId, "financial_transactions", tx.id);
        return updateDoc(txRef, {
          status: TRANSACTION_STATUS.PENDING_COLLECTION,
          updatedAt: serverTimestamp(),
          systemNotes: (tx.systemNotes || "") +
            (tx.systemNotes ? "\n" : "") +
            `${new Date().toLocaleString("he-IL")} - סטטוס שונה אוטומטית מ"צפוי" ל"ממתין לתשלום" (תאריך הפגישה עבר)`,
        }).catch((err) => {
          console.error("[CK-8604a] Auto-promote failed for tx:", tx.id, err.message);
        });
      });
      await Promise.all(promotePromises);
    }

    // Merge and deduplicate
    const allIds = new Set();
    const result = [];
    for (const tx of [...pendingDocs, ...staleExpected]) {
      if (!allIds.has(tx.id)) {
        allIds.add(tx.id);
        result.push(tx);
      }
    }

    return result.slice(0, limitCount);
  } catch (error) {
    console.error("[CK-8604] Error fetching pending collection transactions:", {
      errorCode: "CK-8604",
      errorMessage: error.message,
      clinicId,
      therapistId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get paid transactions for a specific month (payment history)
 * Returns all transactions with paid status where actualPaymentDate is in the given month
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} therapistId - The therapist ID
 * @param {number} year - Year
 * @param {number} month - Month (1-12)
 * @returns {Array} - Array of paid transactions for the month
 *
 * Error Code: CK-8609
 */
export const getPaidTransactionsForMonth = async (
  clinicId,
  therapistId,
  year,
  month,
  { includeAllTherapists = false } = {}
) => {
  try {
    const transactionsRef = collection(
      db,
      "clinics",
      clinicId,
      "financial_transactions"
    );

    const monthStart = new Date(year, month - 1, 1);
    const monthEnd = new Date(year, month, 0, 23, 59, 59);

    const constraints = includeAllTherapists
      ? [
          where("status", "==", TRANSACTION_STATUS.PAID),
          where("actualPaymentDate", ">=", Timestamp.fromDate(monthStart)),
          where("actualPaymentDate", "<=", Timestamp.fromDate(monthEnd)),
          orderBy("actualPaymentDate", "desc"),
        ]
      : [
          where("therapistId", "==", therapistId),
          where("status", "==", TRANSACTION_STATUS.PAID),
          where("actualPaymentDate", ">=", Timestamp.fromDate(monthStart)),
          where("actualPaymentDate", "<=", Timestamp.fromDate(monthEnd)),
          orderBy("actualPaymentDate", "desc"),
        ];

    const q = query(transactionsRef, ...constraints);

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) =>
      serializeForRedux({ id: doc.id, ...doc.data() })
    );
  } catch (error) {
    console.error("[CK-8609] Error fetching paid transactions for month:", {
      errorCode: "CK-8609",
      errorMessage: error.message,
      clinicId,
      therapistId,
      year,
      month,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get expected transactions for a month (future appointments)
 * Returns all transactions with expected status for the given month
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} therapistId - The therapist ID
 * @param {number} year - Year
 * @param {number} month - Month (1-12)
 * @returns {Array} - Array of expected transactions
 *
 * Error Code: CK-8607
 */
export const getExpectedTransactions = async (
  clinicId,
  therapistId,
  year,
  month,
  { includeAllTherapists = false } = {}
) => {
  try {
    const transactionsRef = collection(
      db,
      "clinics",
      clinicId,
      "financial_transactions"
    );

    const monthStart = new Date(year, month - 1, 1);
    const monthEnd = new Date(year, month, 0, 23, 59, 59);

    const constraints = includeAllTherapists
      ? [
          where("status", "==", TRANSACTION_STATUS.EXPECTED),
          where("expectedPaymentDate", ">=", Timestamp.fromDate(monthStart)),
          where("expectedPaymentDate", "<=", Timestamp.fromDate(monthEnd)),
          orderBy("expectedPaymentDate", "asc"),
        ]
      : [
          where("therapistId", "==", therapistId),
          where("status", "==", TRANSACTION_STATUS.EXPECTED),
          where("expectedPaymentDate", ">=", Timestamp.fromDate(monthStart)),
          where("expectedPaymentDate", "<=", Timestamp.fromDate(monthEnd)),
          orderBy("expectedPaymentDate", "asc"),
        ];

    const q = query(transactionsRef, ...constraints);

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) =>
      serializeForRedux({ id: doc.id, ...doc.data() })
    );
  } catch (error) {
    console.error("[CK-8607] Error fetching expected transactions:", {
      errorCode: "CK-8607",
      errorMessage: error.message,
      clinicId,
      therapistId,
      year,
      month,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// ============================================================================
// GROW PAYMENT LINKS — read-only client helpers (grow_integration.md §3)
// Links are written ONLY by Cloud Functions (createGrowPaymentLink, callbacks).
// Rules: clinics/{id}/growPaymentLinks readable by admins / finance staff with
// finance.collectViaGrow; `private/token` is never readable.
// Error codes: CK-8670..CK-8673
// ============================================================================

export const GROW_LINK_STATUS = {
  CREATING: "creating",
  CREATED: "created",
  SENT: "sent",
  PAID: "paid",
  EXPIRED: "expired",
  CANCELLED: "cancelled",
  FAILED: "failed",
};

export const GROW_ACTIVE_LINK_STATUSES = [
  GROW_LINK_STATUS.CREATING,
  GROW_LINK_STATUS.CREATED,
  GROW_LINK_STATUS.SENT,
];

const growLinksRef = (clinicId) =>
  collection(db, "clinics", clinicId, "growPaymentLinks");

/**
 * Get one Grow payment link.
 * @returns {Object|null}
 * Error Code: CK-8670
 */
export const getGrowLink = async (clinicId, linkId) => {
  try {
    const snap = await getDoc(doc(db, "clinics", clinicId, "growPaymentLinks", linkId));
    if (!snap.exists()) return null;
    return serializeForRedux({ id: snap.id, ...snap.data() });
  } catch (error) {
    console.error("[CK-8670] Error fetching Grow link:", {
      errorCode: "CK-8670",
      errorMessage: error.message,
      clinicId,
      linkId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Grow links of one patient, newest first (index: patientId + createdAt desc).
 * Error Code: CK-8671
 */
export const getGrowLinksForPatient = async (clinicId, patientId, limitCount = 50) => {
  try {
    const q = query(
      growLinksRef(clinicId),
      where("patientId", "==", patientId),
      orderBy("createdAt", "desc"),
      limit(limitCount)
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => serializeForRedux({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error("[CK-8671] Error fetching Grow links for patient:", {
      errorCode: "CK-8671",
      errorMessage: error.message,
      clinicId,
      patientId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Grow links covering any of the given transactions. `array-contains-any`
 * accepts ≤10 values per query → chunked and deduped by link id.
 * Returns links newest first.
 * Error Code: CK-8672
 */
export const getGrowLinksForTransactions = async (clinicId, transactionIds = []) => {
  try {
    const ids = [...new Set((transactionIds || []).filter(Boolean))];
    if (ids.length === 0) return [];
    const chunks = [];
    for (let i = 0; i < ids.length; i += 10) chunks.push(ids.slice(i, i + 10));
    const results = await Promise.all(
      chunks.map((chunk) =>
        getDocs(query(growLinksRef(clinicId), where("transactionIds", "array-contains-any", chunk)))
      )
    );
    const byId = new Map();
    results.forEach((snap) =>
      snap.docs.forEach((d) => byId.set(d.id, serializeForRedux({ id: d.id, ...d.data() })))
    );
    return [...byId.values()].sort((a, b) => {
      const ta = new Date(a.createdAt || 0).getTime();
      const tb = new Date(b.createdAt || 0).getTime();
      return tb - ta;
    });
  } catch (error) {
    console.error("[CK-8672] Error fetching Grow links for transactions:", {
      errorCode: "CK-8672",
      errorMessage: error.message,
      clinicId,
      count: (transactionIds || []).length,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Count of ACTIVE links (created/sent) — used by the disconnect confirmation.
 * Error Code: CK-8674
 */
export const getActiveGrowLinks = async (clinicId, limitCount = 200) => {
  try {
    const q = query(
      growLinksRef(clinicId),
      where("status", "in", [GROW_LINK_STATUS.CREATED, GROW_LINK_STATUS.SENT]),
      orderBy("createdAt", "desc"),
      limit(limitCount)
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => serializeForRedux({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error("[CK-8674] Error fetching active Grow links:", {
      errorCode: "CK-8674",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Realtime listener on the clinic's ACTIVE Grow links (created/sent). Fires
 * `onChange(links)` on every snapshot; the caller (hooks/useGrowLinksRealtime)
 * uses it to invalidate RTK caches when a server-side callback flips a link
 * to paid/cancelled (server writes never invalidate RTK on their own).
 * Returns the unsubscribe function. Never throws — errors are logged and the
 * listener simply stops.
 * Error Code: CK-8673
 */
export const subscribeToActiveGrowLinks = (clinicId, onChange, { limitCount = 200 } = {}) => {
  try {
    const q = query(
      growLinksRef(clinicId),
      where("status", "in", [GROW_LINK_STATUS.CREATED, GROW_LINK_STATUS.SENT]),
      orderBy("createdAt", "desc"),
      limit(limitCount)
    );
    return onSnapshot(
      q,
      (snap) => {
        try {
          onChange(snap.docs.map((d) => serializeForRedux({ id: d.id, ...d.data() })), snap);
        } catch (cbError) {
          console.error("[CK-8673] Grow links listener callback failed:", {
            errorCode: "CK-8673",
            errorMessage: cbError.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
        }
      },
      (error) => {
        console.error("[CK-8673] Grow links listener error:", {
          errorCode: "CK-8673",
          errorMessage: error.message,
          clinicId,
          timestamp: new Date().toISOString(),
        });
      }
    );
  } catch (error) {
    console.error("[CK-8673] Failed to start Grow links listener:", {
      errorCode: "CK-8673",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    return () => {};
  }
};

/**
 * Get patient transactions (all transactions linked to a specific patient)
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} patientId - The patient ID
 * @param {string} therapistId - The therapist ID (optional, filters by therapist)
 * @param {number} limitCount - Max transactions to return (default 50)
 * @returns {Array} - Array of transactions for the patient
 *
 * Error Code: CK-8608
 */
export const getPatientTransactions = async (
  clinicId,
  patientId,
  therapistId = null,
  limitCount = 50
) => {
  try {
    const transactionsRef = collection(
      db,
      "clinics",
      clinicId,
      "financial_transactions"
    );

    // Simple query by patientId only to avoid composite index requirement
    const q = query(transactionsRef, where("patientId", "==", patientId));

    const snapshot = await getDocs(q);

    // Filter by therapistId and sort in memory
    let transactions = snapshot.docs.map((doc) =>
      serializeForRedux({ id: doc.id, ...doc.data() })
    );

    // Filter by therapistId if provided
    if (therapistId) {
      transactions = transactions.filter(
        (tx) => tx.therapistId === therapistId
      );
    }

    // Sort by createdAt descending
    transactions.sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
      const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
      return dateB - dateA;
    });

    // Apply limit
    return transactions.slice(0, limitCount);
  } catch (error) {
    console.error("[CK-8608] Error fetching patient transactions:", {
      errorCode: "CK-8608",
      errorMessage: error.message,
      clinicId,
      patientId,
      therapistId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get patient transactions via Cloud Function (secure, for co-therapist access).
 * Bypasses Firestore security rules that can't evaluate complex list-query
 * authorization involving get() calls.
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} patientId - The patient ID
 * @param {string} therapistId - Optional therapist ID filter
 * @param {number} limitCount - Max transactions to return (default 50)
 * @returns {Array} - Array of transactions for the patient
 *
 * Error Code: CK-8610
 */
export const getPatientTransactionsSecure = async (
  clinicId,
  patientId,
  therapistId = null,
  limitCount = 50
) => {
  try {
    const fn = httpsCallable(functions, "getPatientTransactionsSecure");
    const result = await fn({
      clinicId,
      patientId,
      therapistId,
      limit: limitCount,
    });
    return result.data.transactions;
  } catch (error) {
    console.error("[CK-8610] Error fetching patient transactions (secure):", {
      errorCode: "CK-8610",
      errorMessage: error.message,
      clinicId,
      patientId,
      therapistId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Create a manual income entry
 * For income not linked to appointments
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} therapistId - The therapist ID
 * @param {Object} incomeData - Income entry data
 * @returns {Object} - Created transaction
 *
 * Error Code: CK-8605
 */
export const createManualIncomeEntry = async (
  clinicId,
  therapistId,
  incomeData
) => {
  try {
    const paymentDate = incomeData.paymentDate
      ? new Date(incomeData.paymentDate)
      : new Date();

    const transactionData = {
      therapistId,
      appointmentId: null,
      patientId: incomeData.patientId || null,
      patientName: incomeData.patientName || null,
      type: TRANSACTION_TYPE.MANUAL,
      amount: incomeData.amount,
      paymentMethod: incomeData.paymentMethod || null,
      status: TRANSACTION_STATUS.PAID,
      expectedPaymentDate: paymentDate.toISOString(),
      actualPaymentDate: paymentDate.toISOString(),
      description: incomeData.description || "הכנסה ידנית",
      notes: incomeData.notes || null,
      // Punch card purchase tracking (optional)
      isPunchCardPurchase: incomeData.isPunchCardPurchase === true,
      punchCardId: incomeData.punchCardId || null,
    };

    return await createFinancialTransaction(clinicId, transactionData);
  } catch (error) {
    console.error("[CK-8605] Error creating manual income entry:", {
      errorCode: "CK-8605",
      errorMessage: error.message,
      clinicId,
      therapistId,
      incomeData,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// ============================================
// PUNCH CARD (כרטיסיה) FUNCTIONS
// ============================================
// A punch card is a prepaid bundle of visits a patient buys from the clinic.
// Stored at /clinics/{clinicId}/patients/{patientId}/punchCards/{cardId}.
// The appointment itself is the punch record (appointments carry punchCardId),
// so there is no separate punches subcollection.

export const PUNCH_CARD_STATUS = {
  ACTIVE: "active", // כרטיסיה פעילה - has remaining visits
  USED_UP: "used_up", // נוצלה במלואה - all visits punched
  EXPIRED: "expired", // פגת תוקף - expiry date passed
  CLOSED: "closed", // נסגרה ידנית - manually closed by therapist/admin
};

const punchCardsCollectionRef = (clinicId, patientId) =>
  collection(db, "clinics", clinicId, "patients", patientId, "punchCards");

const punchCardDocRef = (clinicId, patientId, cardId) =>
  doc(db, "clinics", clinicId, "patients", patientId, "punchCards", cardId);

const isPunchCardDateExpired = (card) => {
  if (!card?.expiryDate) return false;
  const expiryMs = card.expiryDate?.toDate
    ? card.expiryDate.toDate().getTime()
    : new Date(card.expiryDate).getTime();
  return Number.isFinite(expiryMs) && expiryMs < Date.now();
};

/**
 * Get all punch cards for a patient (newest first).
 * Lazily flips ACTIVE cards whose expiry date passed to EXPIRED.
 *
 * Error Code: CK-8750
 */
export const getPatientPunchCards = async (clinicId, patientId) => {
  try {
    const q = query(
      punchCardsCollectionRef(clinicId, patientId),
      orderBy("createdAt", "desc")
    );
    const snapshot = await getDocs(q);

    const cards = [];
    const expiryUpdates = [];

    snapshot.docs.forEach((docSnap) => {
      const card = { id: docSnap.id, ...docSnap.data() };
      if (
        card.status === PUNCH_CARD_STATUS.ACTIVE &&
        isPunchCardDateExpired(card)
      ) {
        card.status = PUNCH_CARD_STATUS.EXPIRED;
        expiryUpdates.push(
          updateDoc(docSnap.ref, {
            status: PUNCH_CARD_STATUS.EXPIRED,
            updatedAt: serverTimestamp(),
          })
        );
      }
      cards.push(serializeForRedux(card));
    });

    // Fire-and-forget; lazy expiry must never block the read
    if (expiryUpdates.length > 0) {
      Promise.allSettled(expiryUpdates);
    }

    return cards;
  } catch (error) {
    console.error("[CK-8750] Error fetching patient punch cards:", {
      errorCode: "CK-8750",
      errorMessage: error.message,
      clinicId,
      patientId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get the patient's single active punch card (or null).
 * A card whose expiry date passed is not returned (and is lazily expired).
 *
 * Error Code: CK-8751
 */
export const getActivePunchCard = async (clinicId, patientId) => {
  try {
    const q = query(
      punchCardsCollectionRef(clinicId, patientId),
      where("status", "==", PUNCH_CARD_STATUS.ACTIVE)
    );
    const snapshot = await getDocs(q);

    let active = null;
    for (const docSnap of snapshot.docs) {
      const card = { id: docSnap.id, ...docSnap.data() };
      if (isPunchCardDateExpired(card)) {
        // Lazy expiry - fire and forget
        updateDoc(docSnap.ref, {
          status: PUNCH_CARD_STATUS.EXPIRED,
          updatedAt: serverTimestamp(),
        }).catch(() => {});
        continue;
      }
      active = serializeForRedux(card);
      break;
    }

    return active;
  } catch (error) {
    console.error("[CK-8751] Error fetching active punch card:", {
      errorCode: "CK-8751",
      errorMessage: error.message,
      clinicId,
      patientId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Create a punch card for a patient.
 * Enforces a single active card per patient.
 *
 * @param {string} clinicId
 * @param {string} patientId
 * @param {Object} cardData - { pricePerVisit, totalVisits, purchaseDate, expiryDate, notes, paidUpfront, patientName }
 * @param {string} therapistId
 * @param {string|null} therapistName
 *
 * Error Code: CK-8752
 */
export const createPunchCard = async (
  clinicId,
  patientId,
  cardData,
  therapistId,
  therapistName = null
) => {
  try {
    await guardTrialWrite(clinicId);

    const pricePerVisit = Number(cardData.pricePerVisit);
    const totalVisits = Number(cardData.totalVisits);
    if (!Number.isFinite(pricePerVisit) || pricePerVisit < 0) {
      throw new Error("מחיר למפגש אינו תקין");
    }
    if (!Number.isInteger(totalVisits) || totalVisits <= 0) {
      throw new Error("מספר המפגשים אינו תקין");
    }

    const existingActive = await getActivePunchCard(clinicId, patientId);
    if (existingActive) {
      throw new Error("למטופל כבר קיימת כרטיסיה פעילה");
    }

    const newCardRef = doc(punchCardsCollectionRef(clinicId, patientId));
    const cardDoc = {
      id: newCardRef.id,
      clinicId,
      patientId,
      patientName: cardData.patientName || null,

      pricePerVisit,
      totalVisits,
      usedVisits: 0,

      purchaseDate: cardData.purchaseDate
        ? Timestamp.fromDate(new Date(cardData.purchaseDate))
        : Timestamp.fromDate(new Date()),
      expiryDate: cardData.expiryDate
        ? Timestamp.fromDate(new Date(cardData.expiryDate))
        : null,
      notes: cardData.notes || null,

      // Payment model: paid upfront (punched meetings are already paid) or
      // tracked per meeting (card is only a counter + price source)
      paidUpfront: cardData.paidUpfront === true,
      // Set to true by the RTK mutation after it records the upfront income
      purchaseIncomeRecorded: false,
      purchaseTransactionId: null,

      status: PUNCH_CARD_STATUS.ACTIVE,
      closedAt: null,
      closedBy: null,
      closeReason: null,

      createdBy: therapistId || null,
      createdByName: therapistName || null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(newCardRef, cardDoc);
    return serializeForRedux({ ...cardDoc, id: newCardRef.id });
  } catch (error) {
    console.error("[CK-8752] Error creating punch card:", {
      errorCode: "CK-8752",
      errorMessage: error.message,
      clinicId,
      patientId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Update a punch card (whitelisted fields only).
 * totalVisits can never drop below usedVisits; raising it on a used-up
 * (non-expired, non-closed) card reactivates it.
 *
 * Error Code: CK-8753
 */
export const updatePunchCard = async (
  clinicId,
  patientId,
  cardId,
  updateData
) => {
  try {
    await guardTrialWrite(clinicId);

    const cardRef = punchCardDocRef(clinicId, patientId, cardId);

    const updated = await runTransaction(db, async (transaction) => {
      const cardSnap = await transaction.get(cardRef);
      if (!cardSnap.exists()) {
        throw new Error("הכרטיסיה לא נמצאה");
      }
      const card = cardSnap.data();

      const updates = { updatedAt: serverTimestamp() };

      if (updateData.totalVisits !== undefined) {
        const totalVisits = Number(updateData.totalVisits);
        if (!Number.isInteger(totalVisits) || totalVisits <= 0) {
          throw new Error("מספר המפגשים אינו תקין");
        }
        if (totalVisits < (card.usedVisits || 0)) {
          throw new Error("לא ניתן להגדיר מספר מפגשים נמוך ממספר הניקובים שבוצעו");
        }
        updates.totalVisits = totalVisits;

        // Reactivate a used-up card when visits are added
        if (
          card.status === PUNCH_CARD_STATUS.USED_UP &&
          totalVisits > (card.usedVisits || 0)
        ) {
          updates.status = PUNCH_CARD_STATUS.ACTIVE;
        }
        // Flip to used-up if edited down to exactly the used count
        if (
          card.status === PUNCH_CARD_STATUS.ACTIVE &&
          totalVisits === (card.usedVisits || 0)
        ) {
          updates.status = PUNCH_CARD_STATUS.USED_UP;
        }
      }

      if (updateData.pricePerVisit !== undefined) {
        const pricePerVisit = Number(updateData.pricePerVisit);
        if (!Number.isFinite(pricePerVisit) || pricePerVisit < 0) {
          throw new Error("מחיר למפגש אינו תקין");
        }
        updates.pricePerVisit = pricePerVisit;
      }

      if (updateData.expiryDate !== undefined) {
        updates.expiryDate = updateData.expiryDate
          ? Timestamp.fromDate(new Date(updateData.expiryDate))
          : null;
        // Un-expire a card whose expiry was extended/removed
        if (
          card.status === PUNCH_CARD_STATUS.EXPIRED &&
          !isPunchCardDateExpired({ expiryDate: updates.expiryDate })
        ) {
          const effectiveTotal =
            updates.totalVisits !== undefined
              ? updates.totalVisits
              : card.totalVisits;
          updates.status =
            (card.usedVisits || 0) >= effectiveTotal
              ? PUNCH_CARD_STATUS.USED_UP
              : PUNCH_CARD_STATUS.ACTIVE;
        }
      }

      if (updateData.notes !== undefined) {
        updates.notes = updateData.notes || null;
      }
      if (updateData.purchaseDate !== undefined && updateData.purchaseDate) {
        updates.purchaseDate = Timestamp.fromDate(
          new Date(updateData.purchaseDate)
        );
      }
      if (updateData.purchaseIncomeRecorded !== undefined) {
        updates.purchaseIncomeRecorded =
          updateData.purchaseIncomeRecorded === true;
      }
      if (updateData.purchaseTransactionId !== undefined) {
        updates.purchaseTransactionId =
          updateData.purchaseTransactionId || null;
      }

      transaction.update(cardRef, updates);
      return { ...card, ...updates, id: cardSnap.id };
    });

    return serializeForRedux(updated);
  } catch (error) {
    console.error("[CK-8753] Error updating punch card:", {
      errorCode: "CK-8753",
      errorMessage: error.message,
      clinicId,
      patientId,
      cardId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Close a punch card (soft close - remaining visits are forfeited).
 * Already-punched meetings are unaffected.
 *
 * Error Code: CK-8754
 */
export const closePunchCard = async (
  clinicId,
  patientId,
  cardId,
  { closedBy = null, closeReason = null } = {}
) => {
  try {
    await guardTrialWrite(clinicId);

    const cardRef = punchCardDocRef(clinicId, patientId, cardId);
    const cardSnap = await getDoc(cardRef);
    if (!cardSnap.exists()) {
      throw new Error("הכרטיסיה לא נמצאה");
    }

    await updateDoc(cardRef, {
      status: PUNCH_CARD_STATUS.CLOSED,
      closedAt: serverTimestamp(),
      closedBy: closedBy || null,
      closeReason: closeReason || null,
      updatedAt: serverTimestamp(),
    });

    return serializeForRedux({
      ...cardSnap.data(),
      id: cardSnap.id,
      status: PUNCH_CARD_STATUS.CLOSED,
    });
  } catch (error) {
    console.error("[CK-8754] Error closing punch card:", {
      errorCode: "CK-8754",
      errorMessage: error.message,
      clinicId,
      patientId,
      cardId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Hard-delete a punch card. Allowed only when no visits were punched
 * (usedVisits === 0) - cards with history must be closed instead.
 *
 * Error Code: CK-8755
 */
export const deletePunchCard = async (clinicId, patientId, cardId) => {
  try {
    await guardTrialWrite(clinicId);

    const cardRef = punchCardDocRef(clinicId, patientId, cardId);

    let purchaseTransactionId = null;
    await runTransaction(db, async (transaction) => {
      const cardSnap = await transaction.get(cardRef);
      if (!cardSnap.exists()) {
        throw new Error("הכרטיסיה לא נמצאה");
      }
      if ((cardSnap.data().usedVisits || 0) > 0) {
        throw new Error(
          "לא ניתן למחוק כרטיסיה שבוצעו בה ניקובים - ניתן לסגור אותה במקום"
        );
      }
      purchaseTransactionId = cardSnap.data().purchaseTransactionId || null;
      transaction.delete(cardRef);
    });

    // The card's purchase was recorded as PAID income ("רכישת כרטיסיה").
    // Deleting an unused card (a mistaken sale) must not leave that phantom
    // income pointing at a card that no longer exists. Best-effort.
    if (purchaseTransactionId) {
      try {
        await updateTransactionStatus(
          clinicId,
          purchaseTransactionId,
          TRANSACTION_STATUS.CANCELLED
        );
      } catch (txError) {
        console.error("[CK-8755a] Error cancelling punch-card purchase income:", {
          errorCode: "CK-8755a",
          errorMessage: txError.message,
          clinicId,
          cardId,
          purchaseTransactionId,
          timestamp: new Date().toISOString(),
        });
      }
    }

    return cardId;
  } catch (error) {
    console.error("[CK-8755] Error deleting punch card:", {
      errorCode: "CK-8755",
      errorMessage: error.message,
      clinicId,
      patientId,
      cardId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Punch a visit: atomically deduct one visit from the card and mark the
 * appointment as covered by it.
 *
 * Inside one Firestore transaction:
 * - Validates the card is active, not expired, with remaining visits
 * - Increments usedVisits (flips to USED_UP at the limit)
 * - Stamps the appointment with isPunchCard/punchCardId/punchCardPrepaid
 * - For prepaid cards also marks the appointment as paid (method punch_card,
 *   amount 0 when the upfront purchase was recorded as income, otherwise the
 *   per-visit price) and sets isTherapistUpdatedPaymentStatus so the catch-up
 *   payment step never fires for it
 *
 * Idempotent: an appointment that already carries a punchCardId is a no-op.
 *
 * @returns {Object} { card, alreadyPunched, prepaid, incomeRecorded, pricePerVisit, amount }
 *
 * Error Code: CK-8756
 */
export const punchVisit = async (
  clinicId,
  patientId,
  cardId,
  appointmentId
) => {
  try {
    await guardTrialWrite(clinicId);

    const cardRef = punchCardDocRef(clinicId, patientId, cardId);
    const appointmentRef = doc(
      db,
      "clinics",
      clinicId,
      "appointments",
      appointmentId
    );

    const result = await runTransaction(db, async (transaction) => {
      const cardSnap = await transaction.get(cardRef);
      if (!cardSnap.exists()) {
        throw new Error("הכרטיסיה לא נמצאה");
      }
      const card = cardSnap.data();

      if (card.status === PUNCH_CARD_STATUS.CLOSED) {
        throw new Error("הכרטיסיה סגורה");
      }
      if (
        card.status === PUNCH_CARD_STATUS.EXPIRED ||
        isPunchCardDateExpired(card)
      ) {
        throw new Error("הכרטיסיה פגת תוקף");
      }
      const remaining = (card.totalVisits || 0) - (card.usedVisits || 0);
      if (card.status !== PUNCH_CARD_STATUS.ACTIVE || remaining <= 0) {
        throw new Error("לא נותרו ניקובים בכרטיסיה");
      }

      const aptSnap = await transaction.get(appointmentRef);
      if (!aptSnap.exists()) {
        throw new Error("הפגישה לא נמצאה");
      }
      const apt = aptSnap.data();
      if (apt.punchCardId) {
        // Already punched (e.g. double-submit) - no-op
        return {
          card: { ...card, id: cardSnap.id },
          alreadyPunched: true,
          prepaid: apt.punchCardPrepaid === true,
          incomeRecorded: card.purchaseIncomeRecorded === true,
          pricePerVisit: card.pricePerVisit || 0,
          amount: apt.paymentAmount || 0,
        };
      }

      const newUsedVisits = (card.usedVisits || 0) + 1;
      const newStatus =
        newUsedVisits >= (card.totalVisits || 0)
          ? PUNCH_CARD_STATUS.USED_UP
          : card.status;

      transaction.update(cardRef, {
        usedVisits: newUsedVisits,
        status: newStatus,
        updatedAt: serverTimestamp(),
      });

      const prepaid = card.paidUpfront === true;
      const incomeRecorded = card.purchaseIncomeRecorded === true;
      const pricePerVisit = card.pricePerVisit || 0;
      // Prepaid + income already recorded at purchase -> 0 so revenue is not
      // counted twice; otherwise income is recognized per punched meeting
      const amount = prepaid && incomeRecorded ? 0 : pricePerVisit;

      const aptUpdate = {
        isPunchCard: true,
        punchCardId: cardId,
        punchCardPrepaid: prepaid,
        paymentAmount: amount,
        updatedAt: serverTimestamp(),
      };

      if (prepaid) {
        // Already paid via the card - nothing to collect for this meeting
        aptUpdate.paymentStatus = "paid";
        aptUpdate.paymentMethod = PAYMENT_METHODS.PUNCH_CARD;
        aptUpdate.actualPaidAmount = amount;
        aptUpdate.paymentDate = Timestamp.fromDate(new Date());
        aptUpdate.isTherapistUpdatedPaymentStatus = true;
        aptUpdate.NotifyAboutThisMeetingPaymentStatus = false;
      }

      transaction.update(appointmentRef, aptUpdate);

      return {
        card: {
          ...card,
          id: cardSnap.id,
          usedVisits: newUsedVisits,
          status: newStatus,
        },
        alreadyPunched: false,
        prepaid,
        incomeRecorded,
        pricePerVisit,
        amount,
      };
    });

    return {
      ...result,
      card: serializeForRedux(result.card),
    };
  } catch (error) {
    console.error("[CK-8756] Error punching visit:", {
      errorCode: "CK-8756",
      errorMessage: error.message,
      clinicId,
      patientId,
      cardId,
      appointmentId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Restore a punched visit back to the card (appointment cancelled/deleted,
 * or the therapist unchecked "from punch card" while editing).
 *
 * Idempotent: restores only while the appointment still carries this cardId.
 * Decrements usedVisits (floor 0), reactivates a USED_UP card (never an
 * expired/closed one), clears the appointment punch fields, and for prepaid
 * punches also reverts the appointment payment fields to the unpaid default.
 *
 * @returns {Object} { restored, wasPrepaid }
 *
 * Error Code: CK-8757
 */
export const restorePunchVisit = async (
  clinicId,
  patientId,
  cardId,
  appointmentId
) => {
  try {
    await guardTrialWrite(clinicId);

    const cardRef = punchCardDocRef(clinicId, patientId, cardId);
    const appointmentRef = doc(
      db,
      "clinics",
      clinicId,
      "appointments",
      appointmentId
    );

    return await runTransaction(db, async (transaction) => {
      const aptSnap = await transaction.get(appointmentRef);
      if (!aptSnap.exists()) {
        return { restored: false, wasPrepaid: false };
      }
      const apt = aptSnap.data();
      if (apt.punchCardId !== cardId) {
        // Already restored (or never punched against this card) - no-op
        return { restored: false, wasPrepaid: false };
      }

      const cardSnap = await transaction.get(cardRef);
      if (cardSnap.exists()) {
        const card = cardSnap.data();
        const newUsedVisits = Math.max(0, (card.usedVisits || 0) - 1);
        const cardUpdate = {
          usedVisits: newUsedVisits,
          updatedAt: serverTimestamp(),
        };
        if (
          card.status === PUNCH_CARD_STATUS.USED_UP &&
          newUsedVisits < (card.totalVisits || 0) &&
          !isPunchCardDateExpired(card)
        ) {
          cardUpdate.status = PUNCH_CARD_STATUS.ACTIVE;
        }
        transaction.update(cardRef, cardUpdate);
      }

      const wasPrepaid = apt.punchCardPrepaid === true;
      const aptUpdate = {
        isPunchCard: false,
        punchCardId: null,
        punchCardPrepaid: false,
        updatedAt: serverTimestamp(),
      };
      if (wasPrepaid) {
        aptUpdate.paymentStatus = "pending";
        aptUpdate.paymentMethod = null;
        aptUpdate.actualPaidAmount = null;
        aptUpdate.paymentDate = null;
        aptUpdate.isTherapistUpdatedPaymentStatus = false;
      }
      transaction.update(appointmentRef, aptUpdate);

      return { restored: true, wasPrepaid };
    });
  } catch (error) {
    console.error("[CK-8757] Error restoring punched visit:", {
      errorCode: "CK-8757",
      errorMessage: error.message,
      clinicId,
      patientId,
      cardId,
      appointmentId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get all appointments punched against a card (the punch history).
 * Requires composite index: appointments(punchCardId ASC, startTime DESC).
 *
 * Error Code: CK-8758
 */
export const getPunchedAppointments = async (clinicId, cardId) => {
  try {
    const appointmentsRef = collection(db, "clinics", clinicId, "appointments");
    const q = query(
      appointmentsRef,
      where("punchCardId", "==", cardId),
      orderBy("startTime", "desc")
    );
    const snapshot = await getDocs(q);

    return snapshot.docs
      .map((docSnap) => serializeForRedux({ id: docSnap.id, ...docSnap.data() }))
      .filter((apt) => apt.isDeleted !== true);
  } catch (error) {
    console.error("[CK-8758] Error fetching punched appointments:", {
      errorCode: "CK-8758",
      errorMessage: error.message,
      clinicId,
      cardId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get patient's pending debts (pending_collection transactions)
 * Used when adding income to show which debts can be settled
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} patientId - The patient ID
 * @param {string} therapistId - The therapist ID (optional)
 * @returns {Array} - Array of pending debt transactions
 *
 * Error Code: CK-8610
 */
export const getPatientPendingDebts = async (
  clinicId,
  patientId,
  therapistId = null
) => {
  try {
    const transactionsRef = collection(
      db,
      "clinics",
      clinicId,
      "financial_transactions"
    );

    const now = new Date();

    // Query explicitly pending transactions (both "pending_collection" and
    // the kanban/manual "pending" spelling — the debt-settlement picker in
    // IncomeModal could not find "pending" debts before)
    const pendingQ = query(
      transactionsRef,
      where("patientId", "==", patientId),
      where("status", "in", [
        TRANSACTION_STATUS.PENDING_COLLECTION,
        TRANSACTION_STATUS.PENDING,
      ])
    );

    // Also query stale "expected" transactions whose date has passed
    const staleQ = query(
      transactionsRef,
      where("patientId", "==", patientId),
      where("status", "==", TRANSACTION_STATUS.EXPECTED),
      where("expectedPaymentDate", "<=", Timestamp.fromDate(now))
    );

    const [pendingSnap, staleSnap] = await Promise.all([
      getDocs(pendingQ),
      getDocs(staleQ),
    ]);

    const pendingDebts = pendingSnap.docs.map((d) =>
      serializeForRedux({ id: d.id, ...d.data() })
    );

    const staleExpected = staleSnap.docs.map((d) =>
      serializeForRedux({ id: d.id, ...d.data() })
    );

    // Auto-promote stale expected transactions
    if (staleExpected.length > 0) {
      await Promise.all(
        staleExpected.map((tx) => {
          const txRef = doc(db, "clinics", clinicId, "financial_transactions", tx.id);
          return updateDoc(txRef, {
            status: TRANSACTION_STATUS.PENDING_COLLECTION,
            updatedAt: serverTimestamp(),
            systemNotes: (tx.systemNotes || "") +
              (tx.systemNotes ? "\n" : "") +
              `${now.toLocaleString("he-IL")} - סטטוס שונה אוטומטית מ"צפוי" ל"ממתין לתשלום" (תאריך הפגישה עבר)`,
          }).catch((err) => {
            console.error("[CK-8610a] Auto-promote failed for tx:", tx.id, err.message);
          });
        })
      );
    }

    // Merge and deduplicate
    const allIds = new Set();
    let debts = [];
    for (const tx of [...pendingDebts, ...staleExpected]) {
      if (!allIds.has(tx.id)) {
        allIds.add(tx.id);
        debts.push(tx);
      }
    }

    // Filter by therapistId if provided
    if (therapistId) {
      debts = debts.filter((debt) => debt.therapistId === therapistId);
    }

    // Sort by date (oldest first - for display)
    debts.sort((a, b) => {
      const dateA = a.expectedPaymentDate
        ? new Date(a.expectedPaymentDate)
        : new Date(0);
      const dateB = b.expectedPaymentDate
        ? new Date(b.expectedPaymentDate)
        : new Date(0);
      return dateA - dateB;
    });

    return debts;
  } catch (error) {
    console.error("[CK-8610] Error fetching patient pending debts:", {
      errorCode: "CK-8610",
      errorMessage: error.message,
      clinicId,
      patientId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Settle debts with a payment
 * Creates a payment transaction and updates/closes selected debts
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} therapistId - The therapist ID
 * @param {Object} paymentData - Payment data (amount, method, date, etc.)
 * @param {Array} selectedDebts - Array of debt objects to settle (in order)
 * @returns {Object} - Result with created payment and updated debts
 *
 * Error Code: CK-8611
 */
export const settleDebtsWithPayment = async (
  clinicId,
  therapistId,
  paymentData,
  selectedDebts
) => {
  try {
    const paymentDate = paymentData.paymentDate
      ? new Date(paymentData.paymentDate)
      : new Date();
    const paymentAmount = parseFloat(paymentData.amount);
    let remainingAmount = paymentAmount;
    const updatedDebts = [];
    const now = new Date();
    const dateTimeStr = now.toLocaleString("he-IL", {
      day: "numeric",
      month: "numeric",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });

    // Process each debt in order
    for (const debt of selectedDebts) {
      if (remainingAmount <= 0) break;

      const debtAmount = debt.amount || 0;
      const amountToApply = Math.min(remainingAmount, debtAmount);
      const isFullPayment = amountToApply >= debtAmount;
      const remainingDebt = debtAmount - amountToApply;

      // Prepare update data
      const updateData = {
        updatedAt: serverTimestamp(),
      };

      if (isFullPayment) {
        // Full payment - close the debt
        updateData.status = TRANSACTION_STATUS.PAID;
        updateData.actualPaymentDate = Timestamp.fromDate(paymentDate);
        updateData.paymentMethod = paymentData.paymentMethod;

        // Add system note
        const existingNotes = debt.systemNotes || "";
        const newNote = `שולם במלואו (${paymentData.amount} ₪) בתאריך ${dateTimeStr}`;
        updateData.systemNotes = existingNotes
          ? `${existingNotes}\n${newNote}`
          : newNote;
      } else {
        // Partial payment - reduce the debt amount
        updateData.amount = remainingDebt;

        // Store original amount if not already stored
        if (!debt.originalAmount) {
          updateData.originalAmount = debtAmount;
        }

        // Add system note
        const existingNotes = debt.systemNotes || "";
        const originalAmt = debt.originalAmount || debtAmount;
        const newNote = `שולם ${amountToApply} ₪ מתוך חוב של ${originalAmt} ₪ בתאריך ${dateTimeStr}. נותר: ${remainingDebt} ₪`;
        updateData.systemNotes = existingNotes
          ? `${existingNotes}\n${newNote}`
          : newNote;
      }

      // Update the debt transaction
      const debtRef = doc(
        db,
        "clinics",
        clinicId,
        "financial_transactions",
        debt.id
      );
      await updateDoc(debtRef, updateData);

      updatedDebts.push({
        id: debt.id,
        applied: amountToApply,
        closed: isFullPayment,
        remaining: remainingDebt,
      });

      remainingAmount -= amountToApply;
    }

    // Create the payment transaction (manual income)
    const transactionData = {
      therapistId,
      appointmentId: null,
      patientId: paymentData.patientId || null,
      patientName: paymentData.patientName || null,
      type: TRANSACTION_TYPE.MANUAL,
      amount: paymentAmount,
      paymentMethod: paymentData.paymentMethod || null,
      status: TRANSACTION_STATUS.PAID,
      expectedPaymentDate: paymentDate.toISOString(),
      actualPaymentDate: paymentDate.toISOString(),
      description: paymentData.description || "תשלום",
      notes: paymentData.notes || null,
      // Link to settled debts
      settledDebtIds: selectedDebts.map((d) => d.id),
    };

    const payment = await createFinancialTransaction(clinicId, transactionData);

    return {
      payment,
      updatedDebts,
      totalSettled: paymentAmount - remainingAmount,
      remainingFromPayment: remainingAmount,
    };
  } catch (error) {
    console.error("[CK-8611] Error settling debts with payment:", {
      errorCode: "CK-8611",
      errorMessage: error.message,
      clinicId,
      therapistId,
      paymentData,
      selectedDebts: selectedDebts?.map((d) => d.id),
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Forgive a debt (write off without payment)
 * Marks the debt as cancelled with a system note
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} transactionId - The debt transaction ID
 * @param {string} reason - Optional reason for forgiving
 * @returns {Object} - Updated transaction
 *
 * Error Code: CK-8612
 */
export const forgiveDebt = async (clinicId, transactionId, reason = null) => {
  try {
    const transactionRef = doc(
      db,
      "clinics",
      clinicId,
      "financial_transactions",
      transactionId
    );

    // Get current transaction
    const currentDoc = await getDoc(transactionRef);
    if (!currentDoc.exists()) {
      throw new Error("Transaction not found");
    }

    const currentData = currentDoc.data();
    const now = new Date();
    const dateTimeStr = now.toLocaleString("he-IL", {
      day: "numeric",
      month: "numeric",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });

    // Build system note
    const existingNotes = currentData.systemNotes || "";
    let newNote = `חוב נמחק (${currentData.amount} ₪) בתאריך ${dateTimeStr}`;
    if (reason) {
      newNote += `. סיבה: ${reason}`;
    }

    const updateData = {
      status: TRANSACTION_STATUS.CANCELLED,
      systemNotes: existingNotes ? `${existingNotes}\n${newNote}` : newNote,
      forgivenAt: serverTimestamp(),
      forgivenAmount: currentData.amount,
      updatedAt: serverTimestamp(),
    };

    await updateDoc(transactionRef, updateData);

    return serializeForRedux({
      id: transactionId,
      ...currentData,
      ...updateData,
    });
  } catch (error) {
    console.error("[CK-8612] Error forgiving debt:", {
      errorCode: "CK-8612",
      errorMessage: error.message,
      clinicId,
      transactionId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// ============================================
// ADVANCED FINANCIAL TRANSACTIONS
// ============================================

/**
 * Split a payment transaction
 * When partial payment is received, the original is marked as SPLIT
 * and two new records are created: one PAID and one PENDING
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} transactionId - The original transaction ID
 * @param {number} paidAmount - Amount that was paid
 * @param {string} paymentMethod - Payment method
 * @param {string} therapistId - Who performed the split
 * @returns {Object} - { originalTransaction, paidTransaction, pendingTransaction }
 *
 * Error Code: CK-8620
 */
export const splitPaymentTransaction = async (
  clinicId,
  transactionId,
  paidAmount,
  paymentMethod,
  therapistId,
  // Optional overrides from the IncomeModal partial-payment path: the date
  // the therapist picked (a back-dated partial payment must land in ITS
  // month, not today's) and the income notes.
  { actualPaymentDate = null, notes = undefined } = {}
) => {
  try {
    const transactionRef = doc(
      db,
      "clinics",
      clinicId,
      "financial_transactions",
      transactionId
    );

    // Get original transaction
    const originalDoc = await getDoc(transactionRef);
    if (!originalDoc.exists()) {
      throw new Error("Transaction not found");
    }

    const originalData = originalDoc.data();
    const remainingAmount = originalData.amount - paidAmount;

    if (remainingAmount <= 0) {
      throw new Error("Paid amount must be less than total amount for split");
    }

    const now = new Date();
    const dateTimeStr = now.toLocaleString("he-IL", {
      day: "numeric",
      month: "numeric",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });

    // Create the paid portion
    const paidTransactionData = {
      therapistId: originalData.therapistId,
      appointmentId: originalData.appointmentId,
      patientId: originalData.patientId,
      patientName: originalData.patientName,
      type: TRANSACTION_TYPE.SPLIT_PAID,
      amount: paidAmount,
      paymentMethod: paymentMethod,
      status: TRANSACTION_STATUS.PAID,
      expectedPaymentDate: originalData.expectedPaymentDate
        ? new Date(originalData.expectedPaymentDate.toDate()).toISOString()
        : null,
      actualPaymentDate: actualPaymentDate
        ? new Date(actualPaymentDate).toISOString()
        : now.toISOString(),
      description: originalData.description
        ? `${originalData.description} (תשלום חלקי)`
        : "תשלום חלקי",
      notes: notes !== undefined && notes !== "" ? notes : originalData.notes,
      // Split reference
      splitFromTransactionId: transactionId,
      systemNotes: `נוצר מפיצול בתאריך ${dateTimeStr}. סכום מקורי: ₪${originalData.amount}`,
    };

    const paidTransaction = await createFinancialTransaction(
      clinicId,
      paidTransactionData
    );

    // Create the pending portion
    const pendingTransactionData = {
      therapistId: originalData.therapistId,
      appointmentId: originalData.appointmentId,
      patientId: originalData.patientId,
      patientName: originalData.patientName,
      type: TRANSACTION_TYPE.SPLIT_PENDING,
      amount: remainingAmount,
      paymentMethod: null,
      status: TRANSACTION_STATUS.PENDING,
      expectedPaymentDate: originalData.expectedPaymentDate
        ? new Date(originalData.expectedPaymentDate.toDate()).toISOString()
        : null,
      actualPaymentDate: null,
      description: originalData.description
        ? `${originalData.description} (יתרה לתשלום)`
        : "יתרה לתשלום",
      notes: originalData.notes,
      // Split reference
      splitFromTransactionId: transactionId,
      systemNotes: `נוצר מפיצול בתאריך ${dateTimeStr}. סכום מקורי: ₪${originalData.amount}`,
    };

    const pendingTransaction = await createFinancialTransaction(
      clinicId,
      pendingTransactionData
    );

    // Update original to SPLIT status
    const existingNotes = originalData.systemNotes || "";
    const splitNote = `פוצל ל-2 רשומות בתאריך ${dateTimeStr}: שולם ₪${paidAmount}, נותר ₪${remainingAmount}`;

    await updateDoc(transactionRef, {
      status: TRANSACTION_STATUS.SPLIT,
      systemNotes: existingNotes ? `${existingNotes}\n${splitNote}` : splitNote,
      splitToTransactionIds: [paidTransaction.id, pendingTransaction.id],
      splitAt: serverTimestamp(),
      splitBy: therapistId,
      updatedAt: serverTimestamp(),
    });

    const updatedOriginal = await getDoc(transactionRef);

    return {
      originalTransaction: serializeForRedux({
        id: transactionId,
        ...updatedOriginal.data(),
      }),
      paidTransaction,
      pendingTransaction,
    };
  } catch (error) {
    console.error("[CK-8620] Error splitting payment transaction:", {
      errorCode: "CK-8620",
      errorMessage: error.message,
      clinicId,
      transactionId,
      paidAmount,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Mirror a PAID / INVOICE_* transaction transition onto the legacy appointment
 * payment fields. Both finance systems are live: the catch-up carousel and the
 * monthly-report meeting list read appointment.paymentStatus, so a card moved
 * to "שולם" on the kanban (or marked paid from the patient profile) still asked
 * "did the patient pay?" — and answering "no" flipped the paid row back to
 * pending. Punched (prepaid) meetings are skipped: their appointment fields
 * are owned by the punch-card flow. Never throws (the transaction write has
 * already succeeded).
 */
const PAID_FAMILY_STATUSES = ["paid", "invoice_manual", "invoice_auto"];
const mirrorPaidStatusOntoAppointment = async (
  clinicId,
  appointmentId,
  { oldStatus, newStatus, isPunchCard, amount, paymentMethod, actualPaymentDate }
) => {
  if (!appointmentId) return;
  if (!PAID_FAMILY_STATUSES.includes(newStatus)) return;
  if (PAID_FAMILY_STATUSES.includes(oldStatus)) return; // already mirrored
  if (isPunchCard) return;
  try {
    const legacyUpdate = {
      paymentStatus: "paid",
      isPaid: true,
      isTherapistUpdatedPaymentStatus: true,
      NotifyAboutThisMeetingPaymentStatus: false,
      isApprovedByTherapist: true,
      NotifyAboutThisMeetingApproval: false,
      actualPaidAmount: amount ?? null,
      paymentDate: actualPaymentDate
        ? Timestamp.fromDate(new Date(actualPaymentDate))
        : serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    if (paymentMethod) legacyUpdate.paymentMethod = paymentMethod;
    await updateDoc(
      doc(db, "clinics", clinicId, "appointments", appointmentId),
      legacyUpdate
    );
  } catch (aptError) {
    console.error("[CK-8621c] Error mirroring paid status onto appointment:", {
      errorCode: "CK-8621c",
      errorMessage: aptError.message,
      appointmentId,
      timestamp: new Date().toISOString(),
    });
  }
};

/**
 * Update transaction status with proper validation and notes
 * Handles status transitions: PENDING → PAID → INVOICE_MANUAL/INVOICE_AUTO
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} transactionId - The transaction ID
 * @param {string} newStatus - New status
 * @param {Object} additionalData - Additional fields to update
 * @returns {Object} - Updated transaction
 *
 * Error Code: CK-8621
 */
export const updateTransactionStatus = async (
  clinicId,
  transactionId,
  newStatus,
  additionalData = {}
) => {
  try {
    const transactionRef = doc(
      db,
      "clinics",
      clinicId,
      "financial_transactions",
      transactionId
    );

    const currentDoc = await getDoc(transactionRef);
    if (!currentDoc.exists()) {
      throw new Error("Transaction not found");
    }

    const currentData = currentDoc.data();
    const oldStatus = currentData.status;
    const now = new Date();
    const dateTimeStr = now.toLocaleString("he-IL", {
      day: "numeric",
      month: "numeric",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });

    // Build system note for status change
    const existingNotes = currentData.systemNotes || "";
    const oldStatusLabel = getTransactionStatusLabel(oldStatus);
    const newStatusLabel = getTransactionStatusLabel(newStatus);
    let statusNote = `סטטוס שונה מ-"${oldStatusLabel}" ל-"${newStatusLabel}" בתאריך ${dateTimeStr}`;

    const updateData = {
      status: newStatus,
      updatedAt: serverTimestamp(),
    };
    // No-op transition (e.g. the invoice Cloud Function already promoted the
    // row to invoice_auto before this client call) → don't append a
    // "changed from X to X" note.
    if (oldStatus !== newStatus) {
      updateData.systemNotes = existingNotes
        ? `${existingNotes}\n${statusNote}`
        : statusNote;
    }

    // Handle specific status transitions
    if (newStatus === TRANSACTION_STATUS.PAID) {
      updateData.actualPaymentDate = additionalData.actualPaymentDate
        ? Timestamp.fromDate(new Date(additionalData.actualPaymentDate))
        : serverTimestamp();
      if (additionalData.paymentMethod) {
        updateData.paymentMethod = additionalData.paymentMethod;
      }
    }

    if (
      newStatus === TRANSACTION_STATUS.INVOICE_MANUAL ||
      newStatus === TRANSACTION_STATUS.INVOICE_AUTO
    ) {
      updateData.invoiceIssuedAt = serverTimestamp();
      if (additionalData.invoiceNumber) {
        updateData.manualInvoiceNumber = additionalData.invoiceNumber;
      }
      // User-entered invoice date (for invoices issued in an external system).
      // Separate from invoiceIssuedAt (server time the status was set).
      if (additionalData.invoiceDate) {
        updateData.manualInvoiceDate = Timestamp.fromDate(
          new Date(additionalData.invoiceDate)
        );
      }
      if (additionalData.invoiceNotes) {
        updateData.invoiceNotes = additionalData.invoiceNotes;
      }
      // Invoice issued via an integration from the kanban: keep the PDF link
      // and document id on the row (same shape the Cloud Functions write in
      // RecepitsDocsRefArray). Previously only invoiceNumber survived, so the
      // row had no reachable PDF.
      if (additionalData.invoiceUrl || additionalData.invoiceDocumentId) {
        const existing = Array.isArray(currentData.RecepitsDocsRefArray)
          ? currentData.RecepitsDocsRefArray
          : [];
        const alreadyLinked = existing.some(
          (r) =>
            (additionalData.invoiceDocumentId &&
              r?.documentId === additionalData.invoiceDocumentId) ||
            (additionalData.invoiceUrl && r?.invoiceUrl === additionalData.invoiceUrl)
        );
        if (!alreadyLinked) {
          updateData.RecepitsDocsRefArray = [
            ...existing,
            {
              documentId: additionalData.invoiceDocumentId || null,
              invoiceNumber: additionalData.invoiceNumber || null,
              invoiceUrl: additionalData.invoiceUrl || null,
              invoiceProvider: additionalData.invoiceProvider || null,
              createdAt: new Date().toISOString(),
            },
          ];
        }
      }
    }

    if (newStatus === TRANSACTION_STATUS.REFUNDED) {
      updateData.refundedAt = serverTimestamp();
      if (additionalData.refundReason) {
        updateData.refundReason = additionalData.refundReason;
        const refundNote = `. סיבה: ${additionalData.refundReason}`;
        updateData.systemNotes = (updateData.systemNotes || existingNotes || "") + refundNote;
      }
    }

    // Add any other additional data
    if (additionalData.amount !== undefined) {
      updateData.amount = additionalData.amount;
    }
    if (additionalData.notes !== undefined) {
      updateData.notes = additionalData.notes;
    }
    if (additionalData.description !== undefined) {
      updateData.description = additionalData.description;
    }

    await updateDoc(transactionRef, updateData);

    // Handle EXPECTED <-> PENDING transitions - also update linked appointment
    const appointmentId = currentData.appointmentId;
    if (appointmentId) {
      const appointmentRef = doc(db, "clinics", clinicId, "appointments", appointmentId);
      
      // EXPECTED -> PENDING: Mark appointment as confirmed (occurred)
      if (oldStatus === TRANSACTION_STATUS.EXPECTED && newStatus === TRANSACTION_STATUS.PENDING) {
        try {
          await updateDoc(appointmentRef, {
            isApprovedByTherapist: true,
            NotifyAboutThisMeetingApproval: false,
            updatedAt: serverTimestamp(),
          });
        } catch (aptError) {
          console.error("[CK-8621a] Error updating appointment to confirmed:", {
            errorCode: "CK-8621a",
            errorMessage: aptError.message,
            appointmentId,
            timestamp: new Date().toISOString(),
          });
          // Don't fail - transaction update succeeded
        }
      }
      
      // PAID / INVOICE_*: mirror onto the legacy appointment payment fields
      // (see mirrorPaidStatusOntoAppointment).
      await mirrorPaidStatusOntoAppointment(clinicId, appointmentId, {
        oldStatus,
        newStatus,
        isPunchCard: currentData.isPunchCard === true,
        amount:
          additionalData.amount !== undefined
            ? additionalData.amount
            : currentData.amount ?? null,
        paymentMethod: additionalData.paymentMethod || currentData.paymentMethod || null,
        actualPaymentDate: additionalData.actualPaymentDate || null,
      });

      // PENDING -> EXPECTED: Revert appointment to unconfirmed (future)
      if (oldStatus === TRANSACTION_STATUS.PENDING && newStatus === TRANSACTION_STATUS.EXPECTED) {
        try {
          await updateDoc(appointmentRef, {
            isApprovedByTherapist: false,
            NotifyAboutThisMeetingApproval: true,
            updatedAt: serverTimestamp(),
          });
        } catch (aptError) {
          console.error("[CK-8621b] Error reverting appointment to unconfirmed:", {
            errorCode: "CK-8621b",
            errorMessage: aptError.message,
            appointmentId,
            timestamp: new Date().toISOString(),
          });
          // Don't fail - transaction update succeeded
        }
      }
    }

    const updatedDoc = await getDoc(transactionRef);
    return serializeForRedux({ id: updatedDoc.id, ...updatedDoc.data(), appointmentId });
  } catch (error) {
    console.error("[CK-8621] Error updating transaction status:", {
      errorCode: "CK-8621",
      errorMessage: error.message,
      clinicId,
      transactionId,
      newStatus,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Confirm appointment and transition transaction from EXPECTED to PENDING
 * Called when therapist confirms that an appointment occurred
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} appointmentId - The appointment ID
 * @returns {Object} - Updated transaction or null if no transaction found
 *
 * Error Code: CK-8625
 */
export const confirmAppointmentPayment = async (clinicId, appointmentId) => {
  try {
    // Find the transaction linked to this appointment
    const transactionsRef = collection(
      db,
      "clinics",
      clinicId,
      "financial_transactions"
    );
    const q = query(
      transactionsRef,
      where("appointmentId", "==", appointmentId),
      where("status", "==", TRANSACTION_STATUS.EXPECTED)
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      // No expected transaction found - this is OK, might be manual income
      return null;
    }

    // Update the transaction to PENDING
    const transactionDoc = snapshot.docs[0];
    const transactionRef = doc(
      db,
      "clinics",
      clinicId,
      "financial_transactions",
      transactionDoc.id
    );

    const now = new Date().toISOString();
    const updateData = {
      status: TRANSACTION_STATUS.PENDING,
      confirmedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      systemNotes: `${now} - סטטוס שונה מ"צפוי להתקבל תשלום" ל"ממתין לתשלום" (הפגישה אושרה)`,
    };

    await updateDoc(transactionRef, updateData);

    const updatedDoc = await getDoc(transactionRef);
    return serializeForRedux({ id: updatedDoc.id, ...updatedDoc.data() });
  } catch (error) {
    console.error("[CK-8625] Error confirming appointment payment:", {
      errorCode: "CK-8625",
      errorMessage: error.message,
      clinicId,
      appointmentId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get completed appointments without linked payments
 * Used to suggest appointments when creating pending payment records
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} therapistId - The therapist ID
 * @param {string} patientId - Optional patient ID filter
 * @param {number} limitCount - Max results
 * @returns {Array} - Appointments without payments
 *
 * Error Code: CK-8622
 */
export const getAppointmentsWithoutPayments = async (
  clinicId,
  therapistId,
  patientId = null,
  limitCount = 20,
  { includeAllTherapists = false } = {}
) => {
  try {
    // Get completed appointments
    const appointmentsRef = collection(db, "clinics", clinicId, "appointments");
    let appointmentsQuery;

    if (includeAllTherapists) {
      appointmentsQuery = patientId
        ? query(appointmentsRef, where("patientId", "==", patientId), where("status", "==", "completed"), orderBy("startTime", "desc"), limit(limitCount * 2))
        : query(appointmentsRef, where("status", "==", "completed"), orderBy("startTime", "desc"), limit(limitCount * 2));
    } else {
      appointmentsQuery = patientId
        ? query(appointmentsRef, where("therapistId", "==", therapistId), where("patientId", "==", patientId), where("status", "==", "completed"), orderBy("startTime", "desc"), limit(limitCount * 2))
        : query(appointmentsRef, where("therapistId", "==", therapistId), where("status", "==", "completed"), orderBy("startTime", "desc"), limit(limitCount * 2));
    }

    const appointmentsSnapshot = await getDocs(appointmentsQuery);
    const appointments = appointmentsSnapshot.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }))
      .filter((apt) => apt.isDeleted !== true);

    // Get transactions
    const transactionsRef = collection(
      db,
      "clinics",
      clinicId,
      "financial_transactions"
    );
    const transactionsQuery = includeAllTherapists
      ? query(transactionsRef)
      : query(transactionsRef, where("therapistId", "==", therapistId));
    const transactionsSnapshot = await getDocs(transactionsQuery);

    // Create set of appointment IDs that have transactions
    const appointmentIdsWithPayments = new Set();
    transactionsSnapshot.docs.forEach((doc) => {
      const data = doc.data();
      if (data.appointmentId) {
        appointmentIdsWithPayments.add(data.appointmentId);
      }
    });

    // Filter appointments without payments
    const appointmentsWithoutPayments = appointments
      .filter((apt) => !appointmentIdsWithPayments.has(apt.id))
      .slice(0, limitCount)
      .map((apt) => serializeForRedux(apt));

    return appointmentsWithoutPayments;
  } catch (error) {
    console.error("[CK-8622] Error getting appointments without payments:", {
      errorCode: "CK-8622",
      errorMessage: error.message,
      clinicId,
      therapistId,
      patientId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get all transactions for kanban display
 * Returns transactions grouped by status for kanban columns
 * 
 * Note: EXPECTED status is NOT part of the Kanban board (kept separate)
 * Kanban columns: PENDING, PAID, INVOICED (INVOICE_MANUAL + INVOICE_AUTO)
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} therapistId - The therapist ID
 * @returns {Object} - { expected, pending, paid, invoiced, split, refunded, cancelled }
 *
 * Error Code: CK-8623
 */
/**
 * Fetch a single financial transaction by id (Redux-serialized, same item shape
 * as the Kanban buckets). Used by the Finance page `?tx=<id>` deep link that
 * Grow payment notifications emit. Returns null when the doc does not exist;
 * the caller decides what to do with a transaction outside the current view.
 *
 * Error Code: CK-8675
 */
export const getTransactionById = async (clinicId, transactionId) => {
  try {
    if (!clinicId || !transactionId) return null;
    const snap = await getDoc(doc(db, "clinics", clinicId, "financial_transactions", transactionId));
    if (!snap.exists()) return null;
    return serializeForRedux({ id: snap.id, ...snap.data() });
  } catch (error) {
    console.error("[CK-8675] Error fetching transaction by id:", {
      errorCode: "CK-8675",
      errorMessage: error.message,
      clinicId,
      transactionId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

export const getTransactionsForKanban = async (clinicId, therapistId, { includeAllTherapists = false } = {}) => {
  try {
    const transactionsRef = collection(
      db,
      "clinics",
      clinicId,
      "financial_transactions"
    );

    const constraints = includeAllTherapists
      ? [orderBy("createdAt", "desc")]
      : [where("therapistId", "==", therapistId), orderBy("createdAt", "desc")];

    const q = query(transactionsRef, ...constraints);

    const snapshot = await getDocs(q);
    const transactions = snapshot.docs.map((doc) =>
      serializeForRedux({ id: doc.id, ...doc.data() })
    );

    const now = new Date();

    // Auto-promote stale EXPECTED transactions whose date has passed to PENDING.
    // This self-heals the gap between appointment paymentStatus and transaction status.
    const promotePromises = [];
    const promotedIds = new Set();

    transactions.forEach((tx) => {
      if (tx.status === TRANSACTION_STATUS.EXPECTED && tx.expectedPaymentDate) {
        const expectedDate = new Date(tx.expectedPaymentDate);
        if (expectedDate <= now) {
          promotedIds.add(tx.id);
          const txRef = doc(db, "clinics", clinicId, "financial_transactions", tx.id);
          promotePromises.push(
            updateDoc(txRef, {
              status: TRANSACTION_STATUS.PENDING_COLLECTION,
              updatedAt: serverTimestamp(),
              systemNotes: (tx.systemNotes || "") +
                (tx.systemNotes ? "\n" : "") +
                `${now.toLocaleString("he-IL")} - סטטוס שונה אוטומטית מ"צפוי" ל"ממתין לתשלום" (תאריך הפגישה עבר)`,
            }).catch((err) => {
              console.error("[CK-8623a] Auto-promote failed for tx:", tx.id, err.message);
            })
          );
        }
      }
    });

    if (promotePromises.length > 0) {
      await Promise.all(promotePromises);
    }

    // Group by status
    // EXPECTED is separate from Kanban (shown in stats/separate section)
    const grouped = {
      expected: [], // צפוי להתקבל תשלום - NOT in Kanban
      pending: [], // ממתין לתשלום - Kanban column 1
      paid: [], // תשלום התקבל - Kanban column 2
      invoiced: [], // הופקה חשבונית - Kanban column 3 (INVOICE_MANUAL + INVOICE_AUTO)
      split: [], // פוצל
      refunded: [], // זיכוי
      cancelled: [], // בוטל
    };

    transactions.forEach((tx) => {
      // If we just promoted this transaction, treat it as pending_collection
      const effectiveStatus = promotedIds.has(tx.id)
        ? TRANSACTION_STATUS.PENDING_COLLECTION
        : tx.status;
      const status = normalizeTransactionStatus(effectiveStatus);
      switch (status) {
        case TRANSACTION_STATUS.EXPECTED:
          grouped.expected.push(tx);
          break;
        case TRANSACTION_STATUS.PENDING:
          grouped.pending.push(tx);
          break;
        case TRANSACTION_STATUS.PAID:
          grouped.paid.push(tx);
          break;
        case TRANSACTION_STATUS.INVOICE_MANUAL:
        case TRANSACTION_STATUS.INVOICE_AUTO:
          grouped.invoiced.push(tx);
          break;
        case TRANSACTION_STATUS.SPLIT:
          grouped.split.push(tx);
          break;
        case TRANSACTION_STATUS.REFUNDED:
          grouped.refunded.push(tx);
          break;
        case TRANSACTION_STATUS.CANCELLED:
          grouped.cancelled.push(tx);
          break;
        default:
          // Unknown status goes to pending
          grouped.pending.push(tx);
      }
    });

    // Sort each column by payment date ascending (oldest first at the top).
    // Uses the same date precedence the Kanban card displays:
    //   actualPaymentDate -> expectedPaymentDate -> createdAt (fallback)
    // Tie-breaker: createdAt ascending so order is deterministic.
    const getSortDate = (tx) => {
      const raw = tx.actualPaymentDate || tx.expectedPaymentDate || tx.createdAt;
      if (!raw) return Number.POSITIVE_INFINITY;
      const t = new Date(raw).getTime();
      return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
    };
    const getCreatedAtMs = (tx) => {
      if (!tx.createdAt) return 0;
      const t = new Date(tx.createdAt).getTime();
      return Number.isNaN(t) ? 0 : t;
    };
    const sortByDateAsc = (a, b) => {
      const diff = getSortDate(a) - getSortDate(b);
      if (diff !== 0) return diff;
      return getCreatedAtMs(a) - getCreatedAtMs(b);
    };
    Object.keys(grouped).forEach((key) => {
      grouped[key].sort(sortByDateAsc);
    });

    return grouped;
  } catch (error) {
    console.error("[CK-8623] Error getting transactions for kanban:", {
      errorCode: "CK-8623",
      errorMessage: error.message,
      clinicId,
      therapistId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Create income record with smart linking
 * Can link to existing pending payments or create new
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} therapistId - The therapist ID
 * @param {Object} incomeData - Income data
 * @param {string} linkToTransactionId - Optional: link to existing pending transaction
 * @returns {Object} - Created/updated transaction(s)
 *
 * Error Code: CK-8624
 */
export const createIncomeWithLinking = async (
  clinicId,
  therapistId,
  incomeData,
  linkToTransactionId = null
) => {
  try {
    const now = new Date();
    const paymentDate = incomeData.paymentDate
      ? new Date(incomeData.paymentDate)
      : now;

    // If linking to existing pending transaction
    if (linkToTransactionId) {
      const transactionRef = doc(
        db,
        "clinics",
        clinicId,
        "financial_transactions",
        linkToTransactionId
      );

      const existingDoc = await getDoc(transactionRef);
      if (!existingDoc.exists()) {
        throw new Error("Linked transaction not found");
      }

      const existingData = existingDoc.data();
      const paidAmount = parseFloat(incomeData.amount);
      const existingAmount = existingData.amount || 0;

      // Check if amounts match
      if (Math.abs(paidAmount - existingAmount) < 0.01) {
        // Exact match - just update status to PAID
        return await updateTransactionStatus(
          clinicId,
          linkToTransactionId,
          TRANSACTION_STATUS.PAID,
          {
            actualPaymentDate: paymentDate.toISOString(),
            paymentMethod: incomeData.paymentMethod,
            notes: incomeData.notes,
          }
        );
      } else if (paidAmount < existingAmount) {
        // Partial payment - split the transaction
        return await splitPaymentTransaction(
          clinicId,
          linkToTransactionId,
          paidAmount,
          incomeData.paymentMethod,
          therapistId,
          { actualPaymentDate: paymentDate, notes: incomeData.notes }
        );
      } else {
        // Overpayment - update to paid with note about overpayment
        const dateTimeStr = now.toLocaleString("he-IL", {
          day: "numeric",
          month: "numeric",
          year: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        });
        const existingNotes = existingData.systemNotes || "";
        const overpayNote = `תשלום יתר: התקבל ₪${paidAmount} במקום ₪${existingAmount} בתאריך ${dateTimeStr}`;

        await updateDoc(transactionRef, {
          status: TRANSACTION_STATUS.PAID,
          amount: paidAmount,
          originalAmount: existingAmount,
          actualPaymentDate: Timestamp.fromDate(paymentDate),
          paymentMethod: incomeData.paymentMethod,
          notes: incomeData.notes,
          systemNotes: existingNotes
            ? `${existingNotes}\n${overpayNote}`
            : overpayNote,
          updatedAt: serverTimestamp(),
        });

        const updatedDoc = await getDoc(transactionRef);
        return serializeForRedux({ id: updatedDoc.id, ...updatedDoc.data() });
      }
    }

    // Create new transaction
    const transactionData = {
      therapistId,
      appointmentId: incomeData.appointmentId || null,
      patientId: incomeData.patientId || null,
      patientName: incomeData.patientName || null,
      type: TRANSACTION_TYPE.MANUAL,
      amount: parseFloat(incomeData.amount),
      paymentMethod: incomeData.paymentMethod || null,
      status: incomeData.status || TRANSACTION_STATUS.PAID,
      expectedPaymentDate: paymentDate.toISOString(),
      actualPaymentDate:
        incomeData.status === TRANSACTION_STATUS.PENDING
          ? null
          : paymentDate.toISOString(),
      description: incomeData.description || "הכנסה",
      notes: incomeData.notes || null,
    };

    return await createFinancialTransaction(clinicId, transactionData);
  } catch (error) {
    console.error("[CK-8624] Error creating income with linking:", {
      errorCode: "CK-8624",
      errorMessage: error.message,
      clinicId,
      therapistId,
      incomeData,
      linkToTransactionId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// ============================================
// LEADS FUNCTIONS
// ============================================

/**
 * Default lead statuses (used when therapist has no custom statuses)
 */
export const DEFAULT_LEAD_STATUSES = [
  { value: "new", label: "חדש", color: "#3b82f6", order: 0 },
  {
    value: "waiting_call",
    label: "ממתין לשיחה טלפונית",
    color: "#f59e0b",
    order: 1,
  },
  {
    value: "wants_appointment",
    label: "מעוניין לקבוע תור ראשון",
    color: "#8b5cf6",
    order: 2,
  },
  {
    value: "waiting_continuation",
    label: "ממתין להמשך טיפול",
    color: "#06b6d4",
    order: 3,
  },
  { value: "converted", label: "הפך למטופל", color: "#22c55e", order: 4 },
  // Leads v2: terminal "lost" status (reason stored on the lead as lostReason/lostNote)
  { value: "lost", label: "לא רלוונטי", color: "#6b7280", order: 5 },
];

/** Statuses clinics cannot rename/remove. Re-exported for UI convenience. */
export const SYSTEM_LEAD_STATUSES = LEAD_SYSTEM_STATUSES;
export const ARCHIVED_LEAD_STATUSES = LEAD_ARCHIVED_STATUSES;

const ensureSystemLeadStatuses = (list) => {
  const out = list.map((st) => ({ ...st }));
  const seen = new Set(out.map((st) => st.value));
  let nextOrder = out.reduce((m, st) => Math.max(m, Number(st.order) || 0), -1) + 1;
  DEFAULT_LEAD_STATUSES.forEach((def) => {
    if (LEAD_SYSTEM_STATUSES.includes(def.value) && !seen.has(def.value)) {
      out.push({ ...def, order: nextOrder });
      nextOrder += 1;
    }
  });
  return out.sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
};

/**
 * Leads v2 status resolver (leads_plan.md §5). Clinic list wins when present;
 * otherwise merge defaults with the therapist's legacy custom statuses so old
 * `custom_*` values keep rendering. System statuses are always present.
 * @param {Object|null} clinic   clinic profile (reads clinic.leadStatuses)
 * @param {Object|null} therapist therapist profile (legacy therapist.leadStatuses)
 */
export const resolveLeadStatuses = (clinic, therapist) => {
  if (Array.isArray(clinic?.leadStatuses) && clinic.leadStatuses.length > 0) {
    return ensureSystemLeadStatuses(clinic.leadStatuses);
  }
  const byValue = new Map(DEFAULT_LEAD_STATUSES.map((st) => [st.value, { ...st }]));
  const custom = Array.isArray(therapist?.leadStatuses) ? therapist.leadStatuses : [];
  custom.forEach((st) => {
    if (st && st.value) byValue.set(st.value, { ...byValue.get(st.value), ...st });
  });
  return ensureSystemLeadStatuses([...byValue.values()]);
};

/** True when the clinic already manages a shared status list. */
export const clinicHasLeadStatuses = (clinic) =>
  Array.isArray(clinic?.leadStatuses) && clinic.leadStatuses.length > 0;

/** Unknown status values render as a neutral chip instead of statuses[0]. */
export const UNKNOWN_LEAD_STATUS = { value: "__unknown", label: "לא ידוע", color: "#9ca3af", order: 999 };
export const findLeadStatus = (statuses, value) =>
  (statuses || []).find((st) => st.value === value) || UNKNOWN_LEAD_STATUS;

const leadDateToTimestamp = (v) => {
  if (!v) return null;
  if (v instanceof Timestamp) return v;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : Timestamp.fromDate(d);
};

const logLeadActivity = (clinicId, therapistId, therapistName, action, leadId, leadName, details, success, error) =>
  logActivity(clinicId, {
    therapistId,
    therapistName: therapistName || "לא ידוע",
    action,
    entityType: ENTITY_TYPES.LEAD,
    entityId: leadId,
    entityName: leadName || "לא ידוע",
    details: details || {},
    success,
    ...(error ? { errorMessage: error.message } : {}),
  });

// ============================================
// PATIENT TYPES (Therapist-level configuration)
// ============================================

/**
 * Default patient types - empty by default (therapist defines their own)
 * Each type has: id, label, color, order
 * Stored in therapistProfile.patientTypes
 */
export const DEFAULT_PATIENT_TYPES = [];

/**
 * Create a new lead (Leads v2 contract — leads_plan.md §2).
 * @param {string} clinicId
 * @param {Object} leadData - raw form data (any alias accepted by normalizeLeadInput)
 * @param {string} therapistId - acting user
 * @param {string} therapistName - acting user's name (activity log)
 * @param {{ therapistId: string|null, therapistName: string|null }|null} owner
 *   Explicit assignee. `null`/omitted ⇒ legacy behavior: assigned to the actor.
 *   `{ therapistId: null }` ⇒ unassigned (pool).
 * @returns {Object} - Created lead object
 *
 * Error Code: CK-8700
 */
export const createLead = async (
  clinicId,
  leadData,
  therapistId,
  therapistName,
  owner = null
) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  try {
    const normalized = normalizeLeadInput(leadData, {
      channel: leadData?.source?.channel || "manual",
      requireContact: false,
      strictPhone: false,
    });
    if (!normalized.ok) {
      const err = new Error(normalized.error);
      err.code = normalized.error;
      err.field = normalized.field;
      throw err;
    }

    const assigneeId = owner ? owner.therapistId || null : therapistId;
    const assigneeName = owner ? owner.therapistName || null : therapistName || null;

    const leadsRef = collection(db, "clinics", clinicId, "leads");
    const newLeadRef = doc(leadsRef);

    const leadDoc = {
      id: newLeadRef.id,
      clinicId,
      ...normalized.data,
      nextFollowUpAt: leadDateToTimestamp(normalized.data.nextFollowUpAt),
      lastContactAt: null,

      // Assignment (optional in v2)
      therapistId: assigneeId,
      therapistName: assigneeName,
      assignedAt: assigneeId ? serverTimestamp() : null,
      assignedBy: assigneeId ? therapistId : null,
      createdBy: therapistId,

      // Development logs (תיעודי התפתחות)
      developmentLogs: [],

      // Conversion tracking
      convertedToPatientId: null,
      convertedAt: null,

      // Soft delete
      isDeleted: false,
      deletedAt: null,
      deletedBy: null,
      deletedReason: null,

      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(newLeadRef, leadDoc);

    logLeadActivity(clinicId, therapistId, therapistName, ACTIVITY_ACTIONS.CREATE_LEAD, newLeadRef.id, leadDoc.name, {
      status: leadDoc.status,
      assignedTo: assigneeId,
      channel: leadDoc.source?.channel,
    }, true);

    return getLead(clinicId, newLeadRef.id);
  } catch (error) {
    console.error("[CK-8700] Error creating lead:", {
      errorCode: "CK-8700",
      errorMessage: error.message,
      clinicId,
      therapistId,
      timestamp: new Date().toISOString(),
    });
    logLeadActivity(clinicId, therapistId, therapistName, ACTIVITY_ACTIONS.CREATE_LEAD, null, leadData?.name, {}, false, error);
    throw error;
  }
};

/**
 * Get a single lead by ID
 * @param {string} clinicId - The clinic ID
 * @param {string} leadId - The lead ID
 * @returns {Object|null} - Lead object or null
 *
 * Error Code: CK-8701
 */
export const getLead = async (clinicId, leadId) => {
  try {
    const leadRef = doc(db, "clinics", clinicId, "leads", leadId);
    const leadSnap = await getDoc(leadRef);

    if (leadSnap.exists()) {
      const data = leadSnap.data();
      // Leads v2: soft-deleted leads are invisible to regular readers
      if (data.isDeleted === true) return null;
      return serializeForRedux({ id: leadSnap.id, ...data });
    }
    return null;
  } catch (error) {
    console.error("[CK-8701] Error fetching lead:", {
      errorCode: "CK-8701",
      errorMessage: error.message,
      clinicId,
      leadId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get the clinic's leads (Leads v2: always clinic-wide).
 * The `therapistId` argument is kept for call-site compatibility and IGNORED —
 * filtering by assignee / status / archive is done in memory by the UI.
 * Soft-deleted leads are filtered out unless `includeDeleted` is true (admin archive).
 * @param {string} clinicId
 * @param {string|null} _therapistId - ignored (legacy)
 * @param {{ includeDeleted?: boolean }} options
 * @returns {Array}
 *
 * Error Code: CK-8702
 */
export const getLeads = async (clinicId, _therapistId, options = {}) => {
  try {
    const { includeDeleted = false } = options;
    const leadsRef = collection(db, "clinics", clinicId, "leads");
    const q = query(leadsRef, orderBy("createdAt", "desc"));
    const snapshot = await getDocs(q);

    return snapshot.docs
      .map((d) => serializeForRedux({ id: d.id, ...d.data() }))
      .filter((l) => includeDeleted || l.isDeleted !== true);
  } catch (error) {
    console.error("[CK-8702] Error fetching leads:", {
      errorCode: "CK-8702",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Update a lead (Leads v2).
 * @param {string} clinicId
 * @param {string} leadId
 * @param {Object} leadData - form data; passes through normalizeLeadInput when
 *   `name` is present (full edit). Partial updates (e.g. `{ status }`) are
 *   written as-is after stripping protected fields.
 * @param {string} therapistId - acting user (activity log)
 * @param {string} therapistName
 * @param {{ therapistId: string|null, therapistName: string|null }|null} owner
 *   Omitted/null ⇒ assignment untouched. `{ therapistId: null }` ⇒ unassign.
 * @returns {Object} - Updated lead object
 *
 * Error Code: CK-8703
 */
export const updateLead = async (
  clinicId,
  leadId,
  leadData,
  therapistId,
  therapistName,
  owner = null
) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);
  try {
    const leadRef = doc(db, "clinics", clinicId, "leads", leadId);

    let payload = { ...(leadData || {}) };
    if (payload.name !== undefined) {
      const normalized = normalizeLeadInput(payload, {
        channel: payload?.source?.channel || "manual",
        requireContact: false,
        strictPhone: false,
      });
      if (!normalized.ok) {
        const err = new Error(normalized.error);
        err.code = normalized.error;
        err.field = normalized.field;
        throw err;
      }
      // Keep the original source block (channel/key/utm) — only the contract
      // fields that the form edits are rewritten.
      const { source: _ignoredSource, ...contractFields } = normalized.data;
      payload = { ...payload, ...contractFields, schemaVersion: LEAD_SCHEMA_VERSION };
    }
    if (payload.nextFollowUpAt !== undefined) {
      payload.nextFollowUpAt = leadDateToTimestamp(payload.nextFollowUpAt);
    }
    if (payload.status && payload.status !== "lost") {
      // Leaving "lost" clears the reason
      if (payload.lostReason === undefined) payload.lostReason = null;
      if (payload.lostNote === undefined) payload.lostNote = null;
    }

    const updateData = {
      ...payload,
      updatedAt: serverTimestamp(),
    };

    // Remove fields that must never be updated through this path
    delete updateData.id;
    delete updateData.clinicId;
    delete updateData.therapistId;
    delete updateData.therapistName;
    delete updateData.assignedAt;
    delete updateData.assignedBy;
    delete updateData.createdBy;
    delete updateData.createdAt;
    delete updateData.developmentLogs; // Use addLeadDevelopmentLog instead
    delete updateData.source; // immutable origin
    delete updateData.isDeleted; // use deleteLead / restoreLead
    delete updateData.deletedAt;
    delete updateData.deletedBy;
    delete updateData.deletedReason;
    delete updateData.convertedToPatientId; // use convertLeadToPatient
    delete updateData.convertedAt;

    if (owner) {
      updateData.therapistId = owner.therapistId || null;
      updateData.therapistName = owner.therapistId ? owner.therapistName || null : null;
      updateData.assignedAt = owner.therapistId ? serverTimestamp() : null;
      updateData.assignedBy = owner.therapistId ? therapistId : null;
    }

    await updateDoc(leadRef, updateData);

    logLeadActivity(clinicId, therapistId, therapistName, ACTIVITY_ACTIONS.UPDATE_LEAD, leadId, leadData?.name, {
      status: leadData?.status,
      ...(owner ? { assignedTo: owner.therapistId || null } : {}),
    }, true);

    return getLead(clinicId, leadId);
  } catch (error) {
    console.error("[CK-8703] Error updating lead:", {
      errorCode: "CK-8703",
      errorMessage: error.message,
      clinicId,
      leadId,
      timestamp: new Date().toISOString(),
    });
    logLeadActivity(clinicId, therapistId, therapistName, ACTIVITY_ACTIONS.UPDATE_LEAD, leadId, leadData?.name, {}, false, error);
    throw error;
  }
};

/**
 * Add a development log to a lead (תיעוד התפתחות)
 * @param {string} clinicId - The clinic ID
 * @param {string} leadId - The lead ID
 * @param {string} content - Log content (HTML from RichTextEditor)
 * @param {string} authorId - The author's therapist ID
 * @param {string} authorName - The author's name
 * @returns {Object} - Updated lead object
 *
 * Error Code: CK-8704
 */
export const addLeadDevelopmentLog = async (
  clinicId,
  leadId,
  content,
  authorId,
  authorName
) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);
  try {
    const leadRef = doc(db, "clinics", clinicId, "leads", leadId);
    const leadSnap = await getDoc(leadRef);

    if (!leadSnap.exists() || leadSnap.data().isDeleted === true) {
      throw new Error("Lead not found");
    }

    const existingLead = leadSnap.data();
    const newLog = {
      id: `log_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      content,
      authorId,
      authorName,
      createdAt: new Date().toISOString(),
    };

    const updatedLogs = [...(existingLead.developmentLogs || []), newLog];

    await updateDoc(leadRef, {
      developmentLogs: updatedLogs,
      updatedAt: serverTimestamp(),
    });

    // Log activity
    logActivity(clinicId, {
      therapistId: authorId,
      therapistName: authorName || "לא ידוע",
      action: ACTIVITY_ACTIONS.ADD_LEAD_LOG,
      entityType: ENTITY_TYPES.LEAD,
      entityId: leadId,
      entityName: existingLead.name,
      details: {
        logId: newLog.id,
      },
      success: true,
    });

    return getLead(clinicId, leadId);
  } catch (error) {
    console.error("[CK-8704] Error adding lead development log:", {
      errorCode: "CK-8704",
      errorMessage: error.message,
      clinicId,
      leadId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Soft-delete a lead (Leads v2). The doc stays for stats/audit; readers filter
 * `isDeleted !== true`. Restore and hard delete are admin-only (see below).
 * @returns {string} - Deleted lead ID
 *
 * Error Code: CK-8705
 */
export const deleteLead = async (
  clinicId,
  leadId,
  therapistId,
  therapistName,
  leadName,
  reason = null
) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);
  try {
    const leadRef = doc(db, "clinics", clinicId, "leads", leadId);
    await updateDoc(leadRef, {
      isDeleted: true,
      deletedAt: serverTimestamp(),
      deletedBy: therapistId,
      deletedReason: reason || null,
      updatedAt: serverTimestamp(),
    });

    logLeadActivity(clinicId, therapistId, therapistName, ACTIVITY_ACTIONS.DELETE_LEAD, leadId, leadName, { soft: true }, true);
    return leadId;
  } catch (error) {
    console.error("[CK-8705] Error deleting lead:", {
      errorCode: "CK-8705",
      errorMessage: error.message,
      clinicId,
      leadId,
      timestamp: new Date().toISOString(),
    });
    logLeadActivity(clinicId, therapistId, therapistName, ACTIVITY_ACTIONS.DELETE_LEAD, leadId, leadName, {}, false, error);
    throw error;
  }
};

/**
 * Restore a soft-deleted lead (admin only — rules enforce).
 * Error Code: CK-8710
 */
export const restoreLead = async (clinicId, leadId, therapistId, therapistName, leadName) => {
  await guardTrialWrite(clinicId);
  try {
    const leadRef = doc(db, "clinics", clinicId, "leads", leadId);
    await updateDoc(leadRef, {
      isDeleted: false,
      deletedAt: null,
      deletedBy: null,
      deletedReason: null,
      updatedAt: serverTimestamp(),
    });
    logLeadActivity(clinicId, therapistId, therapistName, ACTIVITY_ACTIONS.RESTORE_LEAD, leadId, leadName, {}, true);
    return getLead(clinicId, leadId);
  } catch (error) {
    console.error("[CK-8710] Error restoring lead:", {
      errorCode: "CK-8710",
      errorMessage: error.message,
      clinicId,
      leadId,
      timestamp: new Date().toISOString(),
    });
    logLeadActivity(clinicId, therapistId, therapistName, ACTIVITY_ACTIONS.RESTORE_LEAD, leadId, leadName, {}, false, error);
    throw error;
  }
};

/**
 * Permanently delete a lead (admin only — rules enforce). Intended for the
 * archive view only; normal flows use deleteLead (soft).
 * Error Code: CK-8711
 */
export const hardDeleteLead = async (clinicId, leadId, therapistId, therapistName, leadName) => {
  await guardTrialWrite(clinicId);
  try {
    await deleteDoc(doc(db, "clinics", clinicId, "leads", leadId));
    logLeadActivity(clinicId, therapistId, therapistName, ACTIVITY_ACTIONS.DELETE_LEAD, leadId, leadName, { hard: true }, true);
    return leadId;
  } catch (error) {
    console.error("[CK-8711] Error hard-deleting lead:", {
      errorCode: "CK-8711",
      errorMessage: error.message,
      clinicId,
      leadId,
      timestamp: new Date().toISOString(),
    });
    logLeadActivity(clinicId, therapistId, therapistName, ACTIVITY_ACTIONS.DELETE_LEAD, leadId, leadName, { hard: true }, false, error);
    throw error;
  }
};

/**
 * Assign / unassign a lead. `assignee = { therapistId: null }` ⇒ pool.
 * Error Code: CK-8712
 */
export const assignLead = async (clinicId, leadId, assignee, therapistId, therapistName, leadName) => {
  await guardTrialWrite(clinicId);
  try {
    const leadRef = doc(db, "clinics", clinicId, "leads", leadId);
    const toId = assignee?.therapistId || null;
    await updateDoc(leadRef, {
      therapistId: toId,
      therapistName: toId ? assignee?.therapistName || null : null,
      assignedAt: toId ? serverTimestamp() : null,
      assignedBy: toId ? therapistId : null,
      updatedAt: serverTimestamp(),
    });
    logLeadActivity(clinicId, therapistId, therapistName, ACTIVITY_ACTIONS.ASSIGN_LEAD, leadId, leadName, { assignedTo: toId }, true);
    return getLead(clinicId, leadId);
  } catch (error) {
    console.error("[CK-8712] Error assigning lead:", {
      errorCode: "CK-8712",
      errorMessage: error.message,
      clinicId,
      leadId,
      timestamp: new Date().toISOString(),
    });
    logLeadActivity(clinicId, therapistId, therapistName, ACTIVITY_ACTIONS.ASSIGN_LEAD, leadId, leadName, {}, false, error);
    throw error;
  }
};

/**
 * Mark a lead as lost with a reason (status "lost").
 * Error Code: CK-8713
 */
export const setLeadLost = async (clinicId, leadId, { lostReason, lostNote }, therapistId, therapistName, leadName) => {
  await guardTrialWrite(clinicId);
  try {
    const leadRef = doc(db, "clinics", clinicId, "leads", leadId);
    await updateDoc(leadRef, {
      status: "lost",
      lostReason: lostReason || "other",
      lostNote: lostNote ? String(lostNote).slice(0, 500) : null,
      nextFollowUpAt: null,
      updatedAt: serverTimestamp(),
    });
    logLeadActivity(clinicId, therapistId, therapistName, ACTIVITY_ACTIONS.UPDATE_LEAD, leadId, leadName, { status: "lost", lostReason: lostReason || "other" }, true);
    return getLead(clinicId, leadId);
  } catch (error) {
    console.error("[CK-8713] Error marking lead lost:", {
      errorCode: "CK-8713",
      errorMessage: error.message,
      clinicId,
      leadId,
      timestamp: new Date().toISOString(),
    });
    logLeadActivity(clinicId, therapistId, therapistName, ACTIVITY_ACTIONS.UPDATE_LEAD, leadId, leadName, { status: "lost" }, false, error);
    throw error;
  }
};

/**
 * "סמן שנוצר קשר": stamps lastContactAt and optionally the next follow-up date.
 * Error Code: CK-8714
 */
export const markLeadContacted = async (clinicId, leadId, nextFollowUpAt, therapistId, therapistName, leadName) => {
  await guardTrialWrite(clinicId);
  try {
    const leadRef = doc(db, "clinics", clinicId, "leads", leadId);
    const payload = { lastContactAt: serverTimestamp(), updatedAt: serverTimestamp() };
    if (nextFollowUpAt !== undefined) payload.nextFollowUpAt = leadDateToTimestamp(nextFollowUpAt);
    await updateDoc(leadRef, payload);
    logLeadActivity(clinicId, therapistId, therapistName, ACTIVITY_ACTIONS.UPDATE_LEAD, leadId, leadName, { contacted: true }, true);
    return getLead(clinicId, leadId);
  } catch (error) {
    console.error("[CK-8714] Error marking lead contacted:", {
      errorCode: "CK-8714",
      errorMessage: error.message,
      clinicId,
      leadId,
      timestamp: new Date().toISOString(),
    });
    logLeadActivity(clinicId, therapistId, therapistName, ACTIVITY_ACTIONS.UPDATE_LEAD, leadId, leadName, { contacted: true }, false, error);
    throw error;
  }
};

/**
 * Save the clinic-wide lead status list (anyone with leads.access — rules
 * restrict the write to this field). System statuses are always kept.
 * @param {Array<{value,label,color,order}>} statuses
 * Error Code: CK-8715
 */
export const updateClinicLeadStatuses = async (clinicId, statuses, therapistId, therapistName) => {
  await guardTrialWrite(clinicId);
  try {
    const clean = ensureSystemLeadStatuses(
      (Array.isArray(statuses) ? statuses : [])
        .filter((st) => st && typeof st.value === "string" && st.value.trim())
        .map((st, i) => ({
          value: st.value.trim(),
          label: String(st.label || st.value).trim().slice(0, 40),
          color: typeof st.color === "string" && /^#[0-9a-fA-F]{6}$/.test(st.color) ? st.color : "#6b7280",
          order: Number.isFinite(Number(st.order)) ? Number(st.order) : i,
        }))
    ).map((st, i) => ({ ...st, order: i }));

    await updateClinicDocument(clinicId, { leadStatuses: clean });

    logActivity(clinicId, {
      therapistId,
      therapistName: therapistName || "לא ידוע",
      action: ACTIVITY_ACTIONS.UPDATE_LEAD_STATUSES,
      entityType: ENTITY_TYPES.CLINIC_SETTINGS,
      entityId: clinicId,
      entityName: "סטטוסי מתעניינים",
      details: { count: clean.length },
      success: true,
    });
    return clean;
  } catch (error) {
    console.error("[CK-8715] Error updating clinic lead statuses:", {
      errorCode: "CK-8715",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    logActivity(clinicId, {
      therapistId,
      therapistName: therapistName || "לא ידוע",
      action: ACTIVITY_ACTIONS.UPDATE_LEAD_STATUSES,
      entityType: ENTITY_TYPES.CLINIC_SETTINGS,
      entityId: clinicId,
      entityName: "סטטוסי מתעניינים",
      success: false,
      errorMessage: error.message,
    });
    throw error;
  }
};

/**
 * Convert a lead to a patient
 * @param {string} clinicId - The clinic ID
 * @param {string} leadId - The lead ID to convert
 * @param {string} therapistId - The acting user (becomes the patient's
 *   therapist unless `ownerTherapistId` is given — legacy behavior)
 * @param {string} therapistName - The therapist name
 * @param {string|null} ownerTherapistId - Explicit treating therapist for the
 *   new patient (secretary flow: the lead's therapist or a picked one). Must be
 *   an assignable therapist — never a secretary.
 * @returns {Object} - { lead, patientId } - Updated lead and new patient ID
 *
 * Error Code: CK-8706
 */
export const convertLeadToPatient = async (
  clinicId,
  leadId,
  therapistId,
  therapistName,
  ownerTherapistId = null
) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  try {
    // Get the lead data
    const lead = await getLead(clinicId, leadId);
    if (!lead) {
      throw new Error("Lead not found");
    }

    // Leads v2: explicit owner → the lead's assignee → the actor.
    // (Rules: writing a patient for another therapist needs admin /
    // patients.createForOthers — the UI only offers that when permitted.)
    const patientTherapistId = ownerTherapistId || lead.therapistId || therapistId;

    // Parse name to first and last name
    const nameParts = lead.name.trim().split(" ");
    const firstName = nameParts[0] || lead.name;
    const lastName = nameParts.slice(1).join(" ") || "";

    // Create patient data from lead
    const patientData = {
      firstName,
      lastName,
      phone: lead.phone || "",
      email: lead.email || null,
      referralReason: lead.referralSource || "",
      referralSource: lead.referralSource || null,
      treatmentRate: lead.agreedSessionCost
        ? parseFloat(lead.agreedSessionCost)
        : null,
      isDraft: true, // Create as draft so therapist can complete the profile
      notes: lead.notes || null,
      status: "active",
    };

    // Create the patient
    const patientsRef = collection(db, "clinics", clinicId, "patients");
    const newPatientRef = doc(patientsRef);

    const patientDoc = {
      id: newPatientRef.id,
      therapistId: patientTherapistId,
      therapistIds: [patientTherapistId], // Keep in sync with createPatient (multi-therapist queries)
      createdBy: therapistId,
      ...patientData,
      // Set other fields to null/defaults
      idNumber: null,
      birthDate: null,
      gender: null,
      address: null,
      emergencyContact: null,
      emergencyPhone: null,
      medicalHistory: null,
      currentMedications: null,
      allergies: null,
      insuranceInfo: null,
      treatmentDuration: null,
      treatmentFrequency: null,
      treatmentGoals: null,
      adminNotes: null,
      convertedFromLeadId: leadId, // Track that this patient came from a lead
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(newPatientRef, patientDoc);

    // Update the lead to mark it as converted
    const leadRef = doc(db, "clinics", clinicId, "leads", leadId);
    await updateDoc(leadRef, {
      status: "converted",
      convertedToPatientId: newPatientRef.id,
      convertedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    // Log activity
    logActivity(clinicId, {
      therapistId,
      therapistName: therapistName || "לא ידוע",
      action: ACTIVITY_ACTIONS.CONVERT_LEAD,
      entityType: ENTITY_TYPES.LEAD,
      entityId: leadId,
      entityName: lead.name,
      details: {
        newPatientId: newPatientRef.id,
        patientTherapistId,
      },
      success: true,
    });

    return {
      lead: await getLead(clinicId, leadId),
      patientId: newPatientRef.id,
    };
  } catch (error) {
    console.error("[CK-8706] Error converting lead to patient:", {
      errorCode: "CK-8706",
      errorMessage: error.message,
      clinicId,
      leadId,
      timestamp: new Date().toISOString(),
    });
    logLeadActivity(clinicId, therapistId, therapistName, ACTIVITY_ACTIONS.CONVERT_LEAD, leadId, null, {}, false, error);
    throw error;
  }
};

// Add Lead activity actions
ACTIVITY_ACTIONS.CREATE_LEAD = "create_lead";
ACTIVITY_ACTIONS.UPDATE_LEAD = "update_lead";
ACTIVITY_ACTIONS.DELETE_LEAD = "delete_lead";
ACTIVITY_ACTIONS.ADD_LEAD_LOG = "add_lead_log";
ACTIVITY_ACTIONS.CONVERT_LEAD = "convert_lead";
ACTIVITY_ACTIONS.RESTORE_LEAD = "restore_lead";
ACTIVITY_ACTIONS.ASSIGN_LEAD = "assign_lead";
ACTIVITY_ACTIONS.UPDATE_LEAD_STATUSES = "update_lead_statuses";

ACTION_LABELS.create_lead = "יצירת מתעניין";
ACTION_LABELS.update_lead = "עדכון מתעניין";
ACTION_LABELS.delete_lead = "מחיקת מתעניין";
ACTION_LABELS.add_lead_log = "הוספת תיעוד התפתחות";
ACTION_LABELS.convert_lead = "המרת מתעניין למטופל";
ACTION_LABELS.restore_lead = "שחזור מתעניין";
ACTION_LABELS.assign_lead = "שיוך מתעניין";
ACTION_LABELS.update_lead_statuses = "עדכון סטטוסי מתעניינים";

ENTITY_TYPES.LEAD = "lead";
ENTITY_TYPES.CLINIC_SETTINGS = ENTITY_TYPES.CLINIC_SETTINGS || "clinic_settings";

// ============================================
// PATIENT FILES MODULE
// ============================================

/**
 * Maximum file size in bytes (15MB)
 */
export const MAX_FILE_SIZE_MB = 15;

// Mirrors isValidPatientFileType() in storage.rules — keep the two in sync.
export const ALLOWED_PATIENT_FILE_MIME_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
];
export const ALLOWED_PATIENT_FILE_ACCEPT =
  "image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt";
export const isAllowedPatientFileType = (file) => {
  const type = file?.type || "";
  return type.startsWith("image/") || ALLOWED_PATIENT_FILE_MIME_TYPES.includes(type);
};
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

/**
 * Supported preview file types
 */
export const PREVIEWABLE_FILE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/pdf",
];

/**
 * Get file type category for display
 */
export const getFileTypeCategory = (mimeType) => {
  if (!mimeType) return "other";
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType === "application/pdf") return "pdf";
  if (
    mimeType.includes("word") ||
    mimeType.includes("document") ||
    mimeType === "application/msword" ||
    mimeType ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  )
    return "word";
  if (
    mimeType.includes("excel") ||
    mimeType.includes("spreadsheet") ||
    mimeType === "application/vnd.ms-excel" ||
    mimeType ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  )
    return "excel";
  return "other";
};

/**
 * Check if file type is previewable
 */
export const isFilePreviewable = (mimeType) => {
  return PREVIEWABLE_FILE_TYPES.includes(mimeType);
};

/**
 * Upload a patient file to Firebase Storage
 * @param {File} file - The file to upload
 * @param {string} clinicId - The clinic ID
 * @param {string} patientId - The patient ID
 * @param {string} therapistId - The therapist ID
 * @returns {Object} - { url, storagePath, fileSize }
 *
 * Error Code: CK-8900
 */
export const uploadPatientFile = async (
  file,
  clinicId,
  patientId,
  therapistId
) => {
  try {
    // Check file size
    if (file.size > MAX_FILE_SIZE_BYTES) {
      throw new Error(
        `קובץ גדול מדי. הגודל המקסימלי הוא ${MAX_FILE_SIZE_MB}MB`
      );
    }

    // Check clinic storage quota
    const clinicRef = doc(db, "clinics", clinicId);
    const clinicSnap = await getDoc(clinicRef);
    if (clinicSnap.exists()) {
      const clinicData = clinicSnap.data();
      const usedStorage = clinicData.usedStorageInMb || 0;
      const allowedStorage = clinicData.allowedStorageInMbMaxPerClinic || 1000;
      const fileSizeInMb = file.size / (1024 * 1024);

      if (usedStorage + fileSizeInMb > allowedStorage) {
        throw new Error(
          `אין מספיק מקום אחסון. השתמשת ב-${usedStorage.toFixed(
            1
          )}MB מתוך ${allowedStorage}MB`
        );
      }
    }

    // Create unique file path
    const timestamp = Date.now();
    const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
    const storagePath = `clinics/${clinicId}/patients/${patientId}/files/${timestamp}_${sanitizedFileName}`;

    const storageRef = ref(storage, storagePath);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);

    return {
      url: downloadURL,
      storagePath,
      fileSize: file.size,
    };
  } catch (error) {
    console.error("[CK-8900] Error uploading patient file:", {
      errorCode: "CK-8900",
      errorMessage: error.message,
      clinicId,
      patientId,
      fileName: file?.name,
      fileSize: file?.size,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Create a patient file folder
 * @param {string} clinicId - The clinic ID
 * @param {string} patientId - The patient ID
 * @param {Object} folderData - { name, color }
 * @param {string} therapistId - The therapist ID
 * @param {string} therapistName - The therapist name
 * @returns {Object} - The created folder
 *
 * Error Code: CK-8901
 */
export const createPatientFolder = async (
  clinicId,
  patientId,
  folderData,
  therapistId,
  therapistName
) => {
  try {
    const foldersRef = collection(
      db,
      "clinics",
      clinicId,
      "patients",
      patientId,
      "folders"
    );

    // Get existing folders to determine order
    const existingFolders = await getDocs(foldersRef);
    const order = existingFolders.size;

    const newFolderRef = doc(foldersRef);

    const folderDoc = {
      id: newFolderRef.id,
      clinicId,
      patientId,
      name: folderData.name,
      color: folderData.color || null,
      order,
      createdBy: therapistId,
      createdByName: therapistName,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(newFolderRef, folderDoc);

    return serializeForRedux({
      ...folderDoc,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error("[CK-8901] Error creating patient folder:", {
      errorCode: "CK-8901",
      errorMessage: error.message,
      clinicId,
      patientId,
      folderData,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get patient folders
 * @param {string} clinicId - The clinic ID
 * @param {string} patientId - The patient ID
 * @returns {Array} - Array of folders
 *
 * Error Code: CK-8902
 */
export const getPatientFolders = async (clinicId, patientId) => {
  try {
    const foldersRef = collection(
      db,
      "clinics",
      clinicId,
      "patients",
      patientId,
      "folders"
    );
    const q = query(foldersRef, orderBy("order", "asc"));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => serializeForRedux(doc.data()));
  } catch (error) {
    console.error("[CK-8902] Error getting patient folders:", {
      errorCode: "CK-8902",
      errorMessage: error.message,
      clinicId,
      patientId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Update a patient folder
 * @param {string} clinicId - The clinic ID
 * @param {string} patientId - The patient ID
 * @param {string} folderId - The folder ID
 * @param {Object} folderData - { name, color }
 * @returns {Object} - The updated folder
 *
 * Error Code: CK-8903
 */
export const updatePatientFolder = async (
  clinicId,
  patientId,
  folderId,
  folderData
) => {
  try {
    const folderRef = doc(
      db,
      "clinics",
      clinicId,
      "patients",
      patientId,
      "folders",
      folderId
    );

    const updateData = {
      ...folderData,
      updatedAt: serverTimestamp(),
    };

    await updateDoc(folderRef, updateData);

    const updatedDoc = await getDoc(folderRef);
    return serializeForRedux(updatedDoc.data());
  } catch (error) {
    console.error("[CK-8903] Error updating patient folder:", {
      errorCode: "CK-8903",
      errorMessage: error.message,
      clinicId,
      patientId,
      folderId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Delete a patient folder (only if empty)
 * @param {string} clinicId - The clinic ID
 * @param {string} patientId - The patient ID
 * @param {string} folderId - The folder ID
 *
 * Error Code: CK-8904
 */
export const deletePatientFolder = async (clinicId, patientId, folderId) => {
  try {
    // Check if folder has files
    const filesRef = collection(
      db,
      "clinics",
      clinicId,
      "patients",
      patientId,
      "files"
    );
    const filesQuery = query(filesRef, where("folderId", "==", folderId));
    const filesSnapshot = await getDocs(filesQuery);

    if (!filesSnapshot.empty) {
      throw new Error("לא ניתן למחוק תיקייה שמכילה קבצים");
    }

    const folderRef = doc(
      db,
      "clinics",
      clinicId,
      "patients",
      patientId,
      "folders",
      folderId
    );
    await deleteDoc(folderRef);

    return folderId;
  } catch (error) {
    console.error("[CK-8904] Error deleting patient folder:", {
      errorCode: "CK-8904",
      errorMessage: error.message,
      clinicId,
      patientId,
      folderId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Create a patient file record
 * @param {string} clinicId - The clinic ID
 * @param {string} patientId - The patient ID
 * @param {Object} fileData - File data
 * @param {string} therapistId - The therapist ID
 * @param {string} therapistName - The therapist name
 * @returns {Object} - The created file record
 *
 * Error Code: CK-8905
 */
/**
 * Adjust the clinic's usedStorageInMb counter by deltaMb (positive on upload,
 * negative on delete). Never throws: the file doc/object is already committed
 * by the time this runs, so a counter failure must not surface as an upload
 * error to the user.
 *
 * Error Code: CK-8770
 */
const adjustClinicStorageUsage = async (clinicId, deltaMb) => {
  try {
    const clinicRef = doc(db, "clinics", clinicId);
    const clinicSnap = await getDoc(clinicRef);
    if (!clinicSnap.exists()) return;
    const currentUsage = clinicSnap.data().usedStorageInMb || 0;
    await updateDoc(clinicRef, {
      usedStorageInMb: Math.max(0, currentUsage + deltaMb),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("[CK-8770] Error updating clinic storage usage:", {
      errorCode: "CK-8770",
      errorMessage: error.message,
      clinicId,
      deltaMb,
      timestamp: new Date().toISOString(),
    });
  }
};

export const createPatientFile = async (
  clinicId,
  patientId,
  fileData,
  therapistId,
  therapistName
) => {
  try {
    const filesRef = collection(
      db,
      "clinics",
      clinicId,
      "patients",
      patientId,
      "files"
    );
    const newFileRef = doc(filesRef);

    const fileDoc = {
      id: newFileRef.id,
      clinicId,
      patientId,
      therapistId,
      // File info
      fileName: fileData.fileName,
      fileSize: fileData.fileSize,
      fileType: fileData.fileType,
      fileUrl: fileData.fileUrl,
      storagePath: fileData.storagePath,
      // Organization
      folderId: fileData.folderId || null,
      // Metadata
      description: fileData.description || null,
      // Treatment link
      treatmentId: fileData.treatmentId || null,
      linkedToTreatmentOnly: fileData.linkedToTreatmentOnly || false,
      // Audit
      uploadedBy: therapistId,
      uploadedByName: therapistName,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(newFileRef, fileDoc);

    // Update clinic storage usage (non-fatal — the file itself is already persisted)
    await adjustClinicStorageUsage(clinicId, fileData.fileSize / (1024 * 1024));

    // Log activity
    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: "upload_patient_file",
      entityType: "patient_file",
      entityId: newFileRef.id,
      entityName: fileData.fileName,
      details: {
        patientId,
        fileSize: fileData.fileSize,
        folderId: fileData.folderId || null,
      },
      success: true,
    });

    return serializeForRedux({
      ...fileDoc,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error("[CK-8905] Error creating patient file:", {
      errorCode: "CK-8905",
      errorMessage: error.message,
      clinicId,
      patientId,
      fileData,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get patient files
 * @param {string} clinicId - The clinic ID
 * @param {string} patientId - The patient ID
 * @param {string} therapistId - The therapist ID (for filtering)
 * @param {Object} options - { folderId, treatmentId }
 * @returns {Array} - Array of files
 *
 * Error Code: CK-8906
 */
export const getPatientFiles = async (
  clinicId,
  patientId,
  therapistId,
  options = {}
) => {
  try {
    const filesRef = collection(
      db,
      "clinics",
      clinicId,
      "patients",
      patientId,
      "files"
    );

    // therapistId === null → list every uploader's files (admins / "all"
    // sharing mode). A string keeps the legacy uploader filter.
    let constraints = [where("linkedToTreatmentOnly", "==", false)];
    if (therapistId) {
      constraints.unshift(where("therapistId", "==", therapistId));
    }

    // Filter by folder if specified
    if (options.folderId !== undefined) {
      constraints.push(where("folderId", "==", options.folderId));
    }

    constraints.push(orderBy("createdAt", "desc"));

    const q = query(filesRef, ...constraints);
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => serializeForRedux(doc.data()));
  } catch (error) {
    console.error("[CK-8906] Error getting patient files:", {
      errorCode: "CK-8906",
      errorMessage: error.message,
      clinicId,
      patientId,
      therapistId,
      options,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get files for a specific treatment
 * @param {string} clinicId - The clinic ID
 * @param {string} patientId - The patient ID
 * @param {string} treatmentId - The treatment ID
 * @returns {Array} - Array of files linked to the treatment
 *
 * Error Code: CK-8907
 */
export const getTreatmentFiles = async (clinicId, patientId, treatmentId) => {
  try {
    const filesRef = collection(
      db,
      "clinics",
      clinicId,
      "patients",
      patientId,
      "files"
    );
    const q = query(
      filesRef,
      where("treatmentId", "==", treatmentId),
      orderBy("createdAt", "desc")
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => serializeForRedux(doc.data()));
  } catch (error) {
    console.error("[CK-8907] Error getting treatment files:", {
      errorCode: "CK-8907",
      errorMessage: error.message,
      clinicId,
      patientId,
      treatmentId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Update a patient file record
 * @param {string} clinicId - The clinic ID
 * @param {string} patientId - The patient ID
 * @param {string} fileId - The file ID
 * @param {Object} fileData - { description, folderId }
 * @returns {Object} - The updated file
 *
 * Error Code: CK-8908
 */
export const updatePatientFile = async (
  clinicId,
  patientId,
  fileId,
  fileData
) => {
  try {
    const fileRef = doc(
      db,
      "clinics",
      clinicId,
      "patients",
      patientId,
      "files",
      fileId
    );

    const updateData = {
      ...fileData,
      updatedAt: serverTimestamp(),
    };

    await updateDoc(fileRef, updateData);

    const updatedDoc = await getDoc(fileRef);
    return serializeForRedux(updatedDoc.data());
  } catch (error) {
    console.error("[CK-8908] Error updating patient file:", {
      errorCode: "CK-8908",
      errorMessage: error.message,
      clinicId,
      patientId,
      fileId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Delete a patient file
 * @param {string} clinicId - The clinic ID
 * @param {string} patientId - The patient ID
 * @param {string} fileId - The file ID
 * @param {string} therapistId - The therapist ID
 * @param {string} therapistName - The therapist name
 *
 * Error Code: CK-8909
 */
export const deletePatientFile = async (
  clinicId,
  patientId,
  fileId,
  therapistId,
  therapistName
) => {
  try {
    const fileRef = doc(
      db,
      "clinics",
      clinicId,
      "patients",
      patientId,
      "files",
      fileId
    );

    // Get file data for storage path and size
    const fileSnap = await getDoc(fileRef);
    if (!fileSnap.exists()) {
      throw new Error("File not found");
    }

    const fileData = fileSnap.data();

    // Delete from storage
    try {
      const storageRef = ref(storage, fileData.storagePath);
      await import("firebase/storage").then(({ deleteObject }) =>
        deleteObject(storageRef)
      );
    } catch (storageError) {
      // Log but don't fail if storage deletion fails
      console.warn(
        "[CK-8909] Warning: Could not delete file from storage:",
        storageError
      );
    }

    // Delete from Firestore
    await deleteDoc(fileRef);

    // Update clinic storage usage (non-fatal — the file itself is already removed)
    await adjustClinicStorageUsage(clinicId, -(fileData.fileSize / (1024 * 1024)));

    // Log activity
    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: "delete_patient_file",
      entityType: "patient_file",
      entityId: fileId,
      entityName: fileData.fileName,
      details: {
        patientId,
        fileSize: fileData.fileSize,
      },
      success: true,
    });

    return fileId;
  } catch (error) {
    console.error("[CK-8909] Error deleting patient file:", {
      errorCode: "CK-8909",
      errorMessage: error.message,
      clinicId,
      patientId,
      fileId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get clinic storage usage
 * @param {string} clinicId - The clinic ID
 * @returns {Object} - { usedStorageInMb, allowedStorageInMbMaxPerClinic, percentUsed }
 *
 * Error Code: CK-8910
 */
export const getClinicStorageUsage = async (clinicId) => {
  try {
    const clinicRef = doc(db, "clinics", clinicId);
    const clinicSnap = await getDoc(clinicRef);

    if (!clinicSnap.exists()) {
      throw new Error("Clinic not found");
    }

    const clinicData = clinicSnap.data();
    const usedStorageInMb = clinicData.usedStorageInMb || 0;
    const allowedStorageInMbMaxPerClinic =
      clinicData.allowedStorageInMbMaxPerClinic || 1000;
    const percentUsed =
      (usedStorageInMb / allowedStorageInMbMaxPerClinic) * 100;

    return {
      usedStorageInMb,
      allowedStorageInMbMaxPerClinic,
      percentUsed: Math.min(100, percentUsed),
    };
  } catch (error) {
    console.error("[CK-8910] Error getting clinic storage usage:", {
      errorCode: "CK-8910",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// Add Patient File activity actions
ACTIVITY_ACTIONS.UPLOAD_PATIENT_FILE = "upload_patient_file";
ACTIVITY_ACTIONS.DELETE_PATIENT_FILE = "delete_patient_file";

ACTION_LABELS.upload_patient_file = "העלאת קובץ לתיק מטופל";
ACTION_LABELS.delete_patient_file = "מחיקת קובץ מתיק מטופל";

ENTITY_TYPES.PATIENT_FILE = "patient_file";
ENTITY_TYPES.PATIENT_FOLDER = "patient_folder";

// ============================================
// CLINIC FILES (Shared clinic-level file storage)
// ============================================

/**
 * Upload a clinic file to Firebase Storage
 * @param {File} file - The file to upload
 * @param {string} clinicId - The clinic ID
 * @param {string} therapistId - The uploading therapist ID
 * @returns {Object} - { url, storagePath, fileSize }
 *
 * Error Code: CK-8920
 */
export const uploadClinicFile = async (file, clinicId, therapistId) => {
  try {
    if (file.size > MAX_FILE_SIZE_BYTES) {
      throw new Error(
        `קובץ גדול מדי. הגודל המקסימלי הוא ${MAX_FILE_SIZE_MB}MB`
      );
    }

    // Check clinic storage quota
    const clinicRef = doc(db, "clinics", clinicId);
    const clinicSnap = await getDoc(clinicRef);
    if (clinicSnap.exists()) {
      const clinicData = clinicSnap.data();
      const usedStorage = clinicData.usedStorageInMb || 0;
      const allowedStorage = clinicData.allowedStorageInMbMaxPerClinic || 1000;
      const fileSizeInMb = file.size / (1024 * 1024);

      if (usedStorage + fileSizeInMb > allowedStorage) {
        throw new Error(
          `אין מספיק מקום אחסון. השתמשת ב-${usedStorage.toFixed(
            1
          )}MB מתוך ${allowedStorage}MB`
        );
      }
    }

    const timestamp = Date.now();
    const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
    const storagePath = `clinics/${clinicId}/clinic_files/${timestamp}_${sanitizedFileName}`;

    const storageRef = ref(storage, storagePath);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);

    return {
      url: downloadURL,
      storagePath,
      fileSize: file.size,
    };
  } catch (error) {
    console.error("[CK-8920] Error uploading clinic file:", {
      errorCode: "CK-8920",
      errorMessage: error.message,
      clinicId,
      fileName: file?.name,
      fileSize: file?.size,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Create a clinic file record in Firestore
 * @param {string} clinicId - The clinic ID
 * @param {Object} fileData - File data
 * @param {string} therapistId - The therapist ID
 * @param {string} therapistName - The therapist name
 * @returns {Object} - The created file record
 *
 * Error Code: CK-8921
 */
export const createClinicFile = async (
  clinicId,
  fileData,
  therapistId,
  therapistName
) => {
  try {
    const filesRef = collection(db, "clinics", clinicId, "clinicFiles");
    const newFileRef = doc(filesRef);

    const fileDoc = {
      id: newFileRef.id,
      clinicId,
      therapistId,
      fileName: fileData.fileName,
      fileSize: fileData.fileSize,
      fileType: fileData.fileType,
      fileUrl: fileData.fileUrl,
      storagePath: fileData.storagePath,
      folderId: fileData.folderId || null,
      description: fileData.description || null,
      uploadedBy: therapistId,
      uploadedByName: therapistName,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(newFileRef, fileDoc);

    // Update clinic storage usage (non-fatal — the file itself is already persisted)
    await adjustClinicStorageUsage(clinicId, fileData.fileSize / (1024 * 1024));

    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: "upload_clinic_file",
      entityType: "clinic_file",
      entityId: newFileRef.id,
      entityName: fileData.fileName,
      details: {
        fileSize: fileData.fileSize,
        folderId: fileData.folderId || null,
      },
      success: true,
    });

    return serializeForRedux({
      ...fileDoc,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error("[CK-8921] Error creating clinic file:", {
      errorCode: "CK-8921",
      errorMessage: error.message,
      clinicId,
      fileData,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get clinic files
 * @param {string} clinicId - The clinic ID
 * @param {Object} options - { folderId }
 * @returns {Array} - Array of files
 *
 * Error Code: CK-8922
 */
export const getClinicFiles = async (clinicId, options = {}) => {
  try {
    const filesRef = collection(db, "clinics", clinicId, "clinicFiles");

    let constraints = [];

    if (options.folderId !== undefined) {
      constraints.push(where("folderId", "==", options.folderId));
    }

    constraints.push(orderBy("createdAt", "desc"));

    const q = query(filesRef, ...constraints);
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((d) => serializeForRedux(d.data()));
  } catch (error) {
    console.error("[CK-8922] Error getting clinic files:", {
      errorCode: "CK-8922",
      errorMessage: error.message,
      clinicId,
      options,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Update a clinic file (description, folder)
 * @param {string} clinicId - The clinic ID
 * @param {string} fileId - The file ID
 * @param {Object} fileData - Updated file data
 * @returns {Object} - The updated file
 *
 * Error Code: CK-8923
 */
export const updateClinicFile = async (clinicId, fileId, fileData) => {
  try {
    const fileRef = doc(db, "clinics", clinicId, "clinicFiles", fileId);
    const updateData = {
      ...fileData,
      updatedAt: serverTimestamp(),
    };

    await updateDoc(fileRef, updateData);

    const updatedSnap = await getDoc(fileRef);
    return serializeForRedux(updatedSnap.data());
  } catch (error) {
    console.error("[CK-8923] Error updating clinic file:", {
      errorCode: "CK-8923",
      errorMessage: error.message,
      clinicId,
      fileId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Delete a clinic file
 * @param {string} clinicId - The clinic ID
 * @param {string} fileId - The file ID
 * @param {string} therapistId - The therapist ID
 * @param {string} therapistName - The therapist name
 *
 * Error Code: CK-8924
 */
export const deleteClinicFile = async (
  clinicId,
  fileId,
  therapistId,
  therapistName
) => {
  try {
    const fileRef = doc(db, "clinics", clinicId, "clinicFiles", fileId);

    const fileSnap = await getDoc(fileRef);
    if (!fileSnap.exists()) {
      throw new Error("File not found");
    }

    const fileData = fileSnap.data();

    // Delete from storage
    try {
      const storageRef = ref(storage, fileData.storagePath);
      await import("firebase/storage").then(({ deleteObject }) =>
        deleteObject(storageRef)
      );
    } catch (storageError) {
      console.warn(
        "[CK-8924] Warning: Could not delete file from storage:",
        storageError
      );
    }

    await deleteDoc(fileRef);

    // Update clinic storage usage (non-fatal — the file itself is already removed)
    await adjustClinicStorageUsage(clinicId, -(fileData.fileSize / (1024 * 1024)));

    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: "delete_clinic_file",
      entityType: "clinic_file",
      entityId: fileId,
      entityName: fileData.fileName,
      details: {
        fileSize: fileData.fileSize,
      },
      success: true,
    });

    return fileId;
  } catch (error) {
    console.error("[CK-8924] Error deleting clinic file:", {
      errorCode: "CK-8924",
      errorMessage: error.message,
      clinicId,
      fileId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Create a clinic file folder
 * @param {string} clinicId - The clinic ID
 * @param {Object} folderData - { name, color }
 * @param {string} therapistId - The therapist ID
 * @param {string} therapistName - The therapist name
 * @returns {Object} - The created folder
 *
 * Error Code: CK-8925
 */
export const createClinicFolder = async (
  clinicId,
  folderData,
  therapistId,
  therapistName
) => {
  try {
    const foldersRef = collection(db, "clinics", clinicId, "clinicFolders");

    const existingFolders = await getDocs(foldersRef);
    const order = existingFolders.size;

    const newFolderRef = doc(foldersRef);

    const folderDoc = {
      id: newFolderRef.id,
      clinicId,
      name: folderData.name,
      color: folderData.color || "#6366f1",
      order,
      createdBy: therapistId,
      createdByName: therapistName,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(newFolderRef, folderDoc);

    return serializeForRedux({
      ...folderDoc,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error("[CK-8925] Error creating clinic folder:", {
      errorCode: "CK-8925",
      errorMessage: error.message,
      clinicId,
      folderData,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get clinic folders
 * @param {string} clinicId - The clinic ID
 * @returns {Array} - Array of folders
 *
 * Error Code: CK-8926
 */
export const getClinicFolders = async (clinicId) => {
  try {
    const foldersRef = collection(db, "clinics", clinicId, "clinicFolders");
    const q = query(foldersRef, orderBy("order", "asc"));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((d) => serializeForRedux(d.data()));
  } catch (error) {
    console.error("[CK-8926] Error getting clinic folders:", {
      errorCode: "CK-8926",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Update a clinic folder
 * @param {string} clinicId - The clinic ID
 * @param {string} folderId - The folder ID
 * @param {Object} folderData - Updated folder data
 * @returns {Object} - The updated folder
 *
 * Error Code: CK-8927
 */
export const updateClinicFolder = async (clinicId, folderId, folderData) => {
  try {
    const folderRef = doc(db, "clinics", clinicId, "clinicFolders", folderId);
    const updateData = {
      ...folderData,
      updatedAt: serverTimestamp(),
    };

    await updateDoc(folderRef, updateData);

    const updatedSnap = await getDoc(folderRef);
    return serializeForRedux(updatedSnap.data());
  } catch (error) {
    console.error("[CK-8927] Error updating clinic folder:", {
      errorCode: "CK-8927",
      errorMessage: error.message,
      clinicId,
      folderId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Delete a clinic folder
 * @param {string} clinicId - The clinic ID
 * @param {string} folderId - The folder ID
 *
 * Error Code: CK-8928
 */
export const deleteClinicFolder = async (clinicId, folderId) => {
  try {
    // Check if folder has files
    const filesRef = collection(db, "clinics", clinicId, "clinicFiles");
    const filesQuery = query(filesRef, where("folderId", "==", folderId));
    const filesSnap = await getDocs(filesQuery);

    if (!filesSnap.empty) {
      throw new Error(
        "לא ניתן למחוק תיקייה שמכילה קבצים. יש להעביר או למחוק את הקבצים תחילה."
      );
    }

    const folderRef = doc(db, "clinics", clinicId, "clinicFolders", folderId);
    await deleteDoc(folderRef);

    return folderId;
  } catch (error) {
    console.error("[CK-8928] Error deleting clinic folder:", {
      errorCode: "CK-8928",
      errorMessage: error.message,
      clinicId,
      folderId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// Add Clinic File activity actions
ACTIVITY_ACTIONS.UPLOAD_CLINIC_FILE = "upload_clinic_file";
ACTIVITY_ACTIONS.DELETE_CLINIC_FILE = "delete_clinic_file";

ACTION_LABELS.upload_clinic_file = "העלאת קובץ לתיקייה המשותפת";
ACTION_LABELS.delete_clinic_file = "מחיקת קובץ מהתיקייה המשותפת";

ENTITY_TYPES.CLINIC_FILE = "clinic_file";
ENTITY_TYPES.CLINIC_FOLDER = "clinic_folder";

// ============================================
// ROOM MANAGEMENT (Clinic-level)
// ============================================

/**
 * Create a new room in a clinic
 * @param {string} clinicId - The clinic ID
 * @param {object} roomData - Room data object
 * @returns {object} - The created room with ID
 *
 * Error Code: CK-9001
 */
export const createRoom = async (clinicId, roomData) => {
  try {
    // Guard: Block if trial expired
    await guardTrialWrite(clinicId);

    const roomsRef = collection(db, "clinics", clinicId, "rooms");
    const newRoomRef = doc(roomsRef);

    const roomDoc = {
      id: newRoomRef.id,
      name: roomData.name,
      capacity: roomData.capacity || 1,
      isAccessible: roomData.isAccessible || false,
      defaultTherapistId: roomData.defaultTherapistId || null,
      allowOthersToBook: roomData.allowOthersToBook !== false, // default true
      hasCustomAddress: roomData.hasCustomAddress === true,
      customAddress: roomData.customAddress || "",
      isActive: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(newRoomRef, roomDoc);

    // Fetch and return the properly serialized room data
    return getRoom(clinicId, newRoomRef.id);
  } catch (error) {
    console.error("[CK-9001] Error creating room:", {
      errorCode: "CK-9001",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get all rooms for a clinic
 * @param {string} clinicId - The clinic ID
 * @returns {Array} - Array of rooms
 *
 * Error Code: CK-9002
 */
export const getRooms = async (clinicId) => {
  try {
    const roomsRef = collection(db, "clinics", clinicId, "rooms");
    const q = query(roomsRef, orderBy("name", "asc"));
    const snapshot = await getDocs(q);

    return snapshot.docs.map((docSnap) => serializeForRedux(docSnap.data()));
  } catch (error) {
    console.error("[CK-9002] Error getting rooms:", {
      errorCode: "CK-9002",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get a single room by ID
 * @param {string} clinicId - The clinic ID
 * @param {string} roomId - The room ID
 * @returns {object|null} - The room or null
 *
 * Error Code: CK-9003
 */
export const getRoom = async (clinicId, roomId) => {
  try {
    const roomRef = doc(db, "clinics", clinicId, "rooms", roomId);
    const snap = await getDoc(roomRef);

    if (snap.exists()) {
      return serializeForRedux(snap.data());
    }
    return null;
  } catch (error) {
    console.error("[CK-9003] Error getting room:", {
      errorCode: "CK-9003",
      errorMessage: error.message,
      clinicId,
      roomId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Update a room
 * @param {string} clinicId - The clinic ID
 * @param {string} roomId - The room ID
 * @param {object} roomData - Updated room data
 * @returns {object} - The updated room
 *
 * Error Code: CK-9004
 */
export const updateRoom = async (clinicId, roomId, roomData) => {
  try {
    // Guard: Block if trial expired
    await guardTrialWrite(clinicId);

    const roomRef = doc(db, "clinics", clinicId, "rooms", roomId);

    const updateData = {
      ...roomData,
      updatedAt: serverTimestamp(),
    };

    // Remove undefined values
    Object.keys(updateData).forEach((key) => {
      if (updateData[key] === undefined) {
        delete updateData[key];
      }
    });

    await updateDoc(roomRef, updateData);

    return getRoom(clinicId, roomId);
  } catch (error) {
    console.error("[CK-9004] Error updating room:", {
      errorCode: "CK-9004",
      errorMessage: error.message,
      clinicId,
      roomId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Delete a room (soft delete - set isActive to false)
 * @param {string} clinicId - The clinic ID
 * @param {string} roomId - The room ID
 *
 * Error Code: CK-9005
 */
export const deleteRoom = async (clinicId, roomId) => {
  try {
    const roomRef = doc(db, "clinics", clinicId, "rooms", roomId);
    await deleteDoc(roomRef);
    return roomId;
  } catch (error) {
    console.error("[CK-9005] Error deleting room:", {
      errorCode: "CK-9005",
      errorMessage: error.message,
      clinicId,
      roomId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get active rooms count for a clinic (to determine if rooms feature is enabled)
 * @param {string} clinicId - The clinic ID
 * @returns {number} - Number of active rooms
 *
 * Error Code: CK-9006
 */
export const getActiveRoomsCount = async (clinicId) => {
  try {
    const roomsRef = collection(db, "clinics", clinicId, "rooms");
    const q = query(roomsRef, where("isActive", "==", true));
    const snapshot = await getDocs(q);
    return snapshot.size;
  } catch (error) {
    console.error("[CK-9006] Error getting active rooms count:", {
      errorCode: "CK-9006",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// ============================================
// ROOM AVAILABILITY (Weekly Slots)
// ============================================

/**
 * Save weekly availability for a room
 * @param {string} clinicId - The clinic ID
 * @param {string} roomId - The room ID
 * @param {string} weekId - The week ID (format: YYYY-WW)
 * @param {Array} slots - Array of: { date: "2026-01-12", slots: [{start: "09:00", end: "13:00"}, ...] }
 *
 * Error Code: CK-9010
 */
export const saveRoomWeeklyAvailability = async (
  clinicId,
  roomId,
  weekId,
  slots
) => {
  try {
    await guardTrialWrite(clinicId);
    const availabilityRef = doc(
      db,
      "clinics",
      clinicId,
      "rooms",
      roomId,
      "availability",
      weekId
    );

    await setDoc(availabilityRef, {
      weekId,
      slots,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return { weekId, slots };
  } catch (error) {
    console.error("[CK-9010] Error saving room weekly availability:", {
      errorCode: "CK-9010",
      errorMessage: error.message,
      clinicId,
      roomId,
      weekId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Save a room's recurring schedule settings on the room doc:
 * scheduleTemplate ({ sunday..saturday: { slots: [{start,end}] } } or null),
 * alwaysAvailable, blockOnHolidays. Only provided keys are written.
 *
 * Error Code: CK-8402
 */
export const saveRoomScheduleSettings = async (
  clinicId,
  roomId,
  { scheduleTemplate, alwaysAvailable, blockOnHolidays } = {}
) => {
  try {
    await guardTrialWrite(clinicId);

    const updateData = { updatedAt: serverTimestamp() };
    if (scheduleTemplate !== undefined) updateData.scheduleTemplate = scheduleTemplate;
    if (alwaysAvailable !== undefined) updateData.alwaysAvailable = alwaysAvailable;
    if (blockOnHolidays !== undefined) updateData.blockOnHolidays = blockOnHolidays;

    const roomRef = doc(db, "clinics", clinicId, "rooms", roomId);
    await updateDoc(roomRef, updateData);

    return getRoom(clinicId, roomId);
  } catch (error) {
    console.error("[CK-8402] Error saving room schedule settings:", {
      errorCode: "CK-8402",
      errorMessage: error.message,
      clinicId,
      roomId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get weekly availability for a room
 * @param {string} clinicId - The clinic ID
 * @param {string} roomId - The room ID
 * @param {string} weekId - The week ID
 *
 * Error Code: CK-9011
 */
export const getRoomWeeklyAvailability = async (clinicId, roomId, weekId) => {
  try {
    const availabilityRef = doc(
      db,
      "clinics",
      clinicId,
      "rooms",
      roomId,
      "availability",
      weekId
    );
    const snap = await getDoc(availabilityRef);

    if (snap.exists()) {
      return serializeForRedux(snap.data());
    }
    return null;
  } catch (error) {
    console.error("[CK-9011] Error getting room weekly availability:", {
      errorCode: "CK-9011",
      errorMessage: error.message,
      clinicId,
      roomId,
      weekId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get room availability for a date range
 * @param {string} clinicId - The clinic ID
 * @param {string} roomId - The room ID
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 *
 * Error Code: CK-9012
 */
export const getRoomAvailabilityForRange = async (
  clinicId,
  roomId,
  startDate,
  endDate
) => {
  try {
    const availabilityRef = collection(
      db,
      "clinics",
      clinicId,
      "rooms",
      roomId,
      "availability"
    );
    const snapshot = await getDocs(availabilityRef);

    const startDateStr = getIsraelDateString(startDate);
    const endDateStr = getIsraelDateString(endDate);

    const allSlots = [];
    snapshot.docs.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.slots) {
        data.slots.forEach((daySlot) => {
          if (daySlot.date >= startDateStr && daySlot.date <= endDateStr) {
            allSlots.push(daySlot);
          }
        });
      }
    });

    return allSlots;
  } catch (error) {
    console.error("[CK-9012] Error getting room availability for range:", {
      errorCode: "CK-9012",
      errorMessage: error.message,
      clinicId,
      roomId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Delete old room availability (for auto-cleanup)
 * @param {string} clinicId - The clinic ID
 * @param {string} roomId - The room ID
 * @param {string} beforeWeekId - Delete weeks before this week ID
 *
 * Error Code: CK-9013
 */
export const deleteOldRoomAvailability = async (
  clinicId,
  roomId,
  beforeWeekId
) => {
  try {
    const availabilityRef = collection(
      db,
      "clinics",
      clinicId,
      "rooms",
      roomId,
      "availability"
    );
    const q = query(availabilityRef, where("weekId", "<", beforeWeekId));
    const snapshot = await getDocs(q);

    const deletePromises = snapshot.docs.map((docSnap) =>
      deleteDoc(
        doc(
          db,
          "clinics",
          clinicId,
          "rooms",
          roomId,
          "availability",
          docSnap.id
        )
      )
    );

    await Promise.all(deletePromises);
  } catch (error) {
    console.error("[CK-9013] Error deleting old room availability:", {
      errorCode: "CK-9013",
      errorMessage: error.message,
      clinicId,
      roomId,
      beforeWeekId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// ============================================
// ROOM BOOKING UTILITIES
// ============================================

/**
 * Check if a room is available at a specific time
 * Returns booking status and existing appointments
 * @param {string} clinicId - The clinic ID
 * @param {string} roomId - The room ID
 * @param {Date} startTime - Start time
 * @param {Date} endTime - End time
 * @param {string} excludeAppointmentId - Appointment ID to exclude (for editing)
 * @returns {object} - { isAvailable, conflicts: [{startTime, endTime}] }
 *
 * Error Code: CK-9020
 */
export const checkRoomAvailability = async (
  clinicId,
  roomId,
  startTime,
  endTime,
  excludeAppointmentId = null
) => {
  try {
    // Get all appointments for this room on that day
    const appointmentsRef = collection(db, "clinics", clinicId, "appointments");

    // Query appointments for this room on the same day
    const dayStart = new Date(startTime);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(startTime);
    dayEnd.setHours(23, 59, 59, 999);

    const q = query(
      appointmentsRef,
      where("roomId", "==", roomId),
      where("startTime", ">=", Timestamp.fromDate(dayStart)),
      where("startTime", "<=", Timestamp.fromDate(dayEnd))
    );

    const snapshot = await getDocs(q);
    const conflicts = [];

    const newStart = startTime.getTime();
    const newEnd = endTime.getTime();

    snapshot.docs.forEach((docSnap) => {
      const apt = docSnap.data();

      // Skip the appointment being edited (doc id or mirrored id field)
      if (
        excludeAppointmentId &&
        (docSnap.id === excludeAppointmentId || apt.id === excludeAppointmentId)
      ) {
        return;
      }

      // Skip soft-deleted and cancelled appointments
      if (apt.isDeleted === true) {
        return;
      }
      if (apt.status === "cancelled") {
        return;
      }

      const existingStart = apt.startTime.toDate().getTime();
      const existingEnd = apt.endTime.toDate().getTime();

      // Check for overlap
      if (newStart < existingEnd && newEnd > existingStart) {
        conflicts.push({
          appointmentId: docSnap.id,
          startTime: apt.startTime.toDate().toISOString(),
          endTime: apt.endTime.toDate().toISOString(),
          patientName: apt.patientName || null,
          therapistId: apt.therapistId || null,
          isBlock: apt.isBlockedTime === true,
        });
      }
    });

    return {
      isAvailable: conflicts.length === 0,
      conflicts,
    };
  } catch (error) {
    console.error("[CK-9020] Error checking room availability:", {
      errorCode: "CK-9020",
      errorMessage: error.message,
      clinicId,
      roomId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Check if a time slot is within room's defined availability hours
 * @param {string} clinicId - The clinic ID
 * @param {string} roomId - The room ID
 * @param {Date} startTime - Start time
 * @param {Date} endTime - End time
 * @returns {Object} - { isWithinAvailability, message }
 *
 * Error Code: CK-9022
 */
export const isWithinRoomAvailabilityHours = async (
  clinicId,
  roomId,
  startTime,
  endTime
) => {
  const result = await resolveRoomAvailability(clinicId, roomId, startTime, endTime);
  return {
    isWithinAvailability: result.available,
    message: result.message,
  };
};

/**
 * Resolve whether a room is open for a candidate time range, with the precise
 * reason when it is not. Availability layers (see utils/roomAvailability.js):
 * alwaysAvailable > per-date exception (week doc) > legacy week doc >
 * holiday auto-block > weekly template > default open.
 *
 * Only the appointment's own week doc is read — weeks with no doc are open,
 * so a schedule saved for one week never blocks other weeks.
 *
 * All date/time resolution is Asia/Jerusalem wall time regardless of the
 * browser's timezone.
 *
 * @param {object} [options.room] - room doc data if the caller already has it (saves a read)
 * @returns {{ available, reason: null|'closed_day'|'outside_hours'|'holiday',
 *             message, slotsForDate, source }}
 *
 * Error Code: CK-8401
 */
export const resolveRoomAvailability = async (
  clinicId,
  roomId,
  startTime,
  endTime,
  { room = null } = {}
) => {
  try {
    const dateStr = getIsraelDateString(startTime);
    const weekId = dateUtilsGetWeekId(startTime);

    const roomData = room || (await getRoom(clinicId, roomId));

    const weekDoc =
      roomData?.alwaysAvailable === true
        ? null
        : await getRoomWeeklyAvailability(clinicId, roomId, weekId);

    const ilStart = toIsraelDate(startTime);
    const ilEnd = toIsraelDate(endTime);

    return resolveRoomSlotPure({
      room: roomData,
      weekDoc,
      dateStr,
      holiday: getHolidayForDateStr(dateStr),
      startMins: ilStart.getHours() * 60 + ilStart.getMinutes(),
      endMins: ilEnd.getHours() * 60 + ilEnd.getMinutes(),
    });
  } catch (error) {
    console.error("[CK-8401] Error resolving room availability:", {
      errorCode: "CK-8401",
      errorMessage: error.message,
      clinicId,
      roomId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Resolve a room's effective open windows for each day in a date range
 * (template + exceptions + holidays + toggles). For calendar overlays.
 * Returns [{ date: "YYYY-MM-DD", mode: 'open'|'slots'|'closed'|'holiday',
 *            slots: [{start,end}], holidayName? }]
 *
 * Error Code: CK-8403
 */
export const resolveRoomAvailabilityForRange = async (
  clinicId,
  roomId,
  startDate,
  endDate
) => {
  try {
    const room = await getRoom(clinicId, roomId);

    // Collect the distinct weeks covered by the range, then fetch their docs
    const dayMs = 24 * 60 * 60 * 1000;
    const days = [];
    const seenDates = new Set();
    for (let t = startDate.getTime(); t <= endDate.getTime(); t += dayMs) {
      const d = new Date(t);
      const dateStr = getIsraelDateString(d);
      if (seenDates.has(dateStr)) continue; // DST 25h-day guard
      seenDates.add(dateStr);
      days.push({ dateStr, weekId: dateUtilsGetWeekId(d) });
    }
    const weekIds = [...new Set(days.map((d) => d.weekId))];
    const weekDocs = {};
    await Promise.all(
      weekIds.map(async (weekId) => {
        weekDocs[weekId] = await getRoomWeeklyAvailability(clinicId, roomId, weekId);
      })
    );

    return days.map(({ dateStr, weekId }) => {
      const day = resolveRoomDayPure({
        room,
        weekDoc: weekDocs[weekId],
        dateStr,
        holiday: getHolidayForDateStr(dateStr),
      });
      return {
        date: dateStr,
        mode: day.mode,
        slots: day.slots,
        holidayName: day.holidayName || null,
      };
    });
  } catch (error) {
    console.error("[CK-8403] Error resolving room availability range:", {
      errorCode: "CK-8403",
      errorMessage: error.message,
      clinicId,
      roomId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get available rooms for a time slot
 * Returns rooms that are available at the specified time
 * @param {string} clinicId - The clinic ID
 * @param {Date} startTime - Start time
 * @param {Date} endTime - End time
 * @param {string} therapistId - The therapist ID (to filter by permissions)
 * @param {string} excludeAppointmentId - Appointment ID to exclude
 * @returns {Array} - Array of { room, isAvailable, conflicts }
 *
 * Error Code: CK-9021
 */
export const getAvailableRoomsForSlot = async (
  clinicId,
  startTime,
  endTime,
  therapistId,
  excludeAppointmentId = null
) => {
  try {
    // Get all active rooms
    const rooms = await getRooms(clinicId);
    const activeRooms = rooms.filter((room) => room.isActive !== false);

    // Filter rooms by permission (therapist can book if allowOthersToBook=true OR they are the default therapist)
    const accessibleRooms = activeRooms.filter(
      (room) =>
        room.allowOthersToBook === true ||
        room.defaultTherapistId === therapistId
    );

    // Check bookings and availability hours for each accessible room
    const results = await Promise.all(
      accessibleRooms.map(async (room) => {
        const [{ isAvailable, conflicts }, hoursCheck] = await Promise.all([
          checkRoomAvailability(
            clinicId,
            room.id,
            startTime,
            endTime,
            excludeAppointmentId
          ),
          resolveRoomAvailability(clinicId, room.id, startTime, endTime, {
            room,
          }),
        ]);

        return {
          room,
          isAvailable: isAvailable && hoursCheck.available,
          conflicts,
          hoursCheck,
        };
      })
    );

    return results;
  } catch (error) {
    console.error("[CK-9021] Error getting available rooms for slot:", {
      errorCode: "CK-9021",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get room appointments for a date range (for room calendar view)
 * @param {string} clinicId - The clinic ID
 * @param {string} roomId - The room ID
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @returns {Array} - Array of appointments
 *
 * Error Code: CK-9022
 */
export const getRoomAppointments = async (
  clinicId,
  roomId,
  startDate,
  endDate
) => {
  try {
    const appointmentsRef = collection(db, "clinics", clinicId, "appointments");
    const q = query(
      appointmentsRef,
      where("roomId", "==", roomId),
      where("startTime", ">=", Timestamp.fromDate(startDate)),
      where("startTime", "<=", Timestamp.fromDate(endDate)),
      orderBy("startTime", "asc")
    );

    const snapshot = await getDocs(q);
    return snapshot.docs
      .map((docSnap) => serializeForRedux(docSnap.data()))
      .filter((apt) => apt.isDeleted !== true);
  } catch (error) {
    console.error("[CK-9022] Error getting room appointments:", {
      errorCode: "CK-9022",
      errorMessage: error.message,
      clinicId,
      roomId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// Add Room activity actions
ACTIVITY_ACTIONS.CREATE_ROOM = "create_room";
ACTIVITY_ACTIONS.UPDATE_ROOM = "update_room";
ACTIVITY_ACTIONS.DELETE_ROOM = "delete_room";

ACTION_LABELS.create_room = "יצירת חדר טיפול";
ACTION_LABELS.update_room = "עדכון חדר טיפול";
ACTION_LABELS.delete_room = "מחיקת חדר טיפול";

ENTITY_TYPES.ROOM = "room";

// =============================================================================
// LIVE SESSIONS
// =============================================================================

/**
 * Create a new live session
 * @param {string} clinicId - The clinic ID
 * @param {Object} sessionData - Session data
 * @param {string} therapistId - The therapist ID
 * @param {string} therapistName - The therapist name
 * @returns {Object} - Created live session object
 *
 * Error Code: CK-9200
 */
export const createLiveSession = async (
  clinicId,
  sessionData,
  therapistId,
  therapistName
) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  try {
    const sessionsRef = collection(db, "clinics", clinicId, "liveSessions");
    const newSessionRef = doc(sessionsRef);

    const sessionDoc = {
      id: newSessionRef.id,
      clinicId,
      patientId: sessionData.patientId,
      patientName: sessionData.patientName || null,
      appointmentId: sessionData.appointmentId || null,
      therapistId,
      therapistName: therapistName || null,
      templateId: sessionData.templateId || "default",
      fields: sessionData.fields || [],
      liveNotes: sessionData.liveNotes || [], // Array of { id, title, content }
      selectedGameIds: sessionData.selectedGameIds || [],
      status: "active", // 'active' or 'ended'
      startedAt: serverTimestamp(),
      endedAt: null,
      // Appointment details for reference
      appointmentStartTime: sessionData.appointmentStartTime
        ? Timestamp.fromDate(new Date(sessionData.appointmentStartTime))
        : null,
      appointmentEndTime: sessionData.appointmentEndTime
        ? Timestamp.fromDate(new Date(sessionData.appointmentEndTime))
        : null,
      treatmentTypeId: sessionData.treatmentTypeId || null,
      // Metadata
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(newSessionRef, sessionDoc);

    // Log activity
    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: "create_live_session",
      entityType: "live_session",
      entityId: newSessionRef.id,
      entityName: `סשן לייב - ${sessionData.patientName}`,
      details: {
        patientId: sessionData.patientId || null,
        appointmentId: sessionData.appointmentId || null,
      },
      success: true,
    });

    return serializeForRedux({ ...sessionDoc, id: newSessionRef.id });
  } catch (error) {
    console.error("[CK-9200] Error creating live session:", {
      errorCode: "CK-9200",
      errorMessage: error.message,
      clinicId,
      therapistId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get a live session by ID
 * @param {string} clinicId - The clinic ID
 * @param {string} sessionId - The session ID
 * @returns {Object} - Live session object
 *
 * Error Code: CK-9201
 */
export const getLiveSession = async (clinicId, sessionId) => {
  try {
    const sessionRef = doc(db, "clinics", clinicId, "liveSessions", sessionId);
    const sessionSnap = await getDoc(sessionRef);

    if (!sessionSnap.exists()) {
      return null;
    }

    return serializeForRedux({ id: sessionSnap.id, ...sessionSnap.data() });
  } catch (error) {
    console.error("[CK-9201] Error fetching live session:", {
      errorCode: "CK-9201",
      errorMessage: error.message,
      clinicId,
      sessionId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get active live session for therapist (if any)
 * @param {string} clinicId - The clinic ID
 * @param {string} therapistId - The therapist ID
 * @param {string} appointmentId - Optional appointment ID to check specific session
 * @returns {Object|null} - Active live session object or null
 *
 * Error Code: CK-9202
 */
export const getActiveLiveSession = async (
  clinicId,
  therapistId,
  appointmentId = null
) => {
  try {
    const sessionsRef = collection(db, "clinics", clinicId, "liveSessions");
    let constraints = [
      where("therapistId", "==", therapistId),
      where("status", "==", "active"),
    ];

    if (appointmentId) {
      constraints.push(where("appointmentId", "==", appointmentId));
    }

    const q = query(sessionsRef, ...constraints, orderBy("startedAt", "desc"));
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return null;
    }

    // Return the most recent active session
    const firstDoc = snapshot.docs[0];
    return serializeForRedux({ id: firstDoc.id, ...firstDoc.data() });
  } catch (error) {
    console.error("[CK-9202] Error fetching active live session:", {
      errorCode: "CK-9202",
      errorMessage: error.message,
      clinicId,
      therapistId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get all active live sessions for a therapist (for carousel display)
 * Returns sessions where appointment time has ended but session wasn't closed
 * @param {string} clinicId - The clinic ID
 * @param {string} therapistId - The therapist ID
 * @returns {Array} - Array of active live session objects
 *
 * Error Code: CK-9206
 */
export const getActiveLiveSessionsForTherapist = async (
  clinicId,
  therapistId
) => {
  try {
    const sessionsRef = collection(db, "clinics", clinicId, "liveSessions");
    const q = query(
      sessionsRef,
      where("therapistId", "==", therapistId),
      where("status", "==", "active"),
      orderBy("startedAt", "desc")
    );
    const snapshot = await getDocs(q);

    const now = new Date();

    // Filter to only include sessions where appointment time has ended
    const sessions = snapshot.docs
      .map((docSnap) =>
        serializeForRedux({ id: docSnap.id, ...docSnap.data() })
      )
      .filter((session) => {
        // Check if appointment end time has passed
        if (session.appointmentEndTime) {
          const endTime = new Date(session.appointmentEndTime);
          return endTime < now;
        }
        return false;
      });

    return sessions;
  } catch (error) {
    console.error(
      "[CK-9206] Error fetching active live sessions for therapist:",
      {
        errorCode: "CK-9206",
        errorMessage: error.message,
        clinicId,
        therapistId,
        timestamp: new Date().toISOString(),
      }
    );
    throw error;
  }
};

/**
 * Get live sessions for a patient (for import selection in TreatmentDocPage)
 * Only returns ended sessions that haven't been imported yet
 * @param {string} clinicId - The clinic ID
 * @param {string} patientId - The patient ID
 * @param {string} therapistId - The therapist ID (optional)
 * @returns {Array} - Array of live session objects
 *
 * Error Code: CK-9203
 */
export const getLiveSessionsForPatient = async (
  clinicId,
  patientId,
  therapistId = null
) => {
  try {
    const sessionsRef = collection(db, "clinics", clinicId, "liveSessions");
    let constraints = [
      where("patientId", "==", patientId),
      where("status", "==", "ended"),
    ];

    // Filter by therapist if provided (non-admin view)
    if (therapistId) {
      constraints.push(where("therapistId", "==", therapistId));
    }

    // Order by ended date (newest first)
    constraints.push(orderBy("endedAt", "desc"));

    const q = query(sessionsRef, ...constraints);
    const snapshot = await getDocs(q);

    // Filter out sessions that have already been imported to a treatment
    const sessions = snapshot.docs
      .map((docSnap) =>
        serializeForRedux({ id: docSnap.id, ...docSnap.data() })
      )
      .filter((session) => !session.importedToTreatmentId);

    return sessions;
  } catch (error) {
    console.error("[CK-9203] Error fetching live sessions for patient:", {
      errorCode: "CK-9203",
      errorMessage: error.message,
      clinicId,
      patientId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Update a live session (auto-save)
 * @param {string} clinicId - The clinic ID
 * @param {string} sessionId - The session ID
 * @param {Object} sessionData - Updated session data
 * @param {string} therapistId - The therapist ID
 * @returns {Object} - Updated live session object
 *
 * Error Code: CK-9204
 */
export const updateLiveSession = async (
  clinicId,
  sessionId,
  sessionData,
  therapistId
) => {
  try {
    const sessionRef = doc(db, "clinics", clinicId, "liveSessions", sessionId);

    // Only allow updating specific fields
    const updateData = {
      fields: sessionData.fields || [],
      liveNotes: sessionData.liveNotes || [],
      selectedGameIds: sessionData.selectedGameIds || [],
      templateId: sessionData.templateId || "default",
      updatedAt: serverTimestamp(),
    };

    await updateDoc(sessionRef, updateData);

    const updatedSnap = await getDoc(sessionRef);
    return serializeForRedux({ id: updatedSnap.id, ...updatedSnap.data() });
  } catch (error) {
    console.error("[CK-9204] Error updating live session:", {
      errorCode: "CK-9204",
      errorMessage: error.message,
      clinicId,
      sessionId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * End a live session
 * @param {string} clinicId - The clinic ID
 * @param {string} sessionId - The session ID
 * @param {Object} sessionData - Final session data
 * @param {string} therapistId - The therapist ID
 * @param {string} therapistName - The therapist name
 * @returns {Object} - Ended live session object
 *
 * Error Code: CK-9205
 */
export const endLiveSession = async (
  clinicId,
  sessionId,
  sessionData,
  therapistId,
  therapistName
) => {
  try {
    const sessionRef = doc(db, "clinics", clinicId, "liveSessions", sessionId);

    const updateData = {
      fields: sessionData.fields || [],
      liveNotes: sessionData.liveNotes || [],
      selectedGameIds: sessionData.selectedGameIds || [],
      templateId: sessionData.templateId || "default",
      status: "ended",
      endedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await updateDoc(sessionRef, updateData);

    // Also mark the appointment as completed (when therapist ends session early)
    // This ensures the appointment is treated as completed even before its scheduled end time
    if (sessionData.appointmentId) {
      try {
        const appointmentRef = doc(
          db,
          "clinics",
          clinicId,
          "appointments",
          sessionData.appointmentId
        );
        
        const appointmentSnap = await getDoc(appointmentRef);
        if (appointmentSnap.exists()) {
          const appointmentData = appointmentSnap.data();
          
          // Only complete if not already completed or cancelled
          if (appointmentData.status !== "completed" && appointmentData.status !== "cancelled") {
            await updateDoc(appointmentRef, {
              status: "completed",
              completedAt: serverTimestamp(),
              completedBy: therapistId, // Completed by therapist (not system)
              completionTaskName: null, // Clear any scheduled completion task
              
              // Post-completion workflow flags - set to true so carousel will display these steps.
              // Steps the therapist already handled (value flag true) stay closed,
              // otherwise the pending card gets stuck with a "done" stage and no action.
              NotifyAboutThisMeetingApproval:
                appointmentData.isApprovedByTherapist === true ? false : true,
              NotifyAboutThisMeetingSummarize:
                appointmentData.isTempCustomer === true ||
                appointmentData.isTherapistSummarizedTheMeeting === true
                  ? false
                  : true,
              NotifyAboutThisMeetingPaymentStatus:
                appointmentData.punchCardPrepaid === true ||
                appointmentData.isTherapistUpdatedPaymentStatus === true
                  ? false
                  : true,
              
              updatedAt: serverTimestamp(),
            });
          }
        }
      } catch (appointmentError) {
        // Log but don't fail - the live session itself was ended successfully
        console.error("[CK-9205] Error marking appointment as completed:", {
          errorCode: "CK-9205",
          errorMessage: appointmentError.message,
          clinicId,
          appointmentId: sessionData.appointmentId,
          timestamp: new Date().toISOString(),
        });
      }
    }

    // Log activity
    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: "end_live_session",
      entityType: "live_session",
      entityId: sessionId,
      entityName: `סשן לייב - ${sessionData.patientName}`,
      details: {
        patientId: sessionData.patientId || null,
        appointmentId: sessionData.appointmentId || null,
      },
      success: true,
    });

    const updatedSnap = await getDoc(sessionRef);
    return serializeForRedux({ id: updatedSnap.id, ...updatedSnap.data() });
  } catch (error) {
    console.error("[CK-9205] Error ending live session:", {
      errorCode: "CK-9205",
      errorMessage: error.message,
      clinicId,
      sessionId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Mark a live session as imported to a treatment
 * @param {string} clinicId - The clinic ID
 * @param {string} sessionId - The session ID
 * @param {string} treatmentId - The treatment ID it was imported to
 * @returns {Object} - Updated live session object
 *
 * Error Code: CK-9206
 */
export const markLiveSessionAsImported = async (
  clinicId,
  sessionId,
  treatmentId
) => {
  try {
    const sessionRef = doc(db, "clinics", clinicId, "liveSessions", sessionId);

    await updateDoc(sessionRef, {
      importedToTreatmentId: treatmentId,
      importedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    const updatedSnap = await getDoc(sessionRef);
    return serializeForRedux({ id: updatedSnap.id, ...updatedSnap.data() });
  } catch (error) {
    console.error("[CK-9206] Error marking live session as imported:", {
      errorCode: "CK-9206",
      errorMessage: error.message,
      clinicId,
      sessionId,
      treatmentId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// Add Live Session activity actions
ACTIVITY_ACTIONS.CREATE_LIVE_SESSION = "create_live_session";
ACTIVITY_ACTIONS.END_LIVE_SESSION = "end_live_session";

ACTION_LABELS.create_live_session = "התחלת סשן לייב";
ACTION_LABELS.end_live_session = "סיום סשן לייב";

ENTITY_TYPES.LIVE_SESSION = "live_session";

// ============================================================
// USAGE TRACKING - AI & Digital Signatures
// ============================================================

/**
 * Gets the current month key in YYYY-MM format (Israel timezone)
 * @returns {string} - Month key (e.g., "2026-01")
 */
export const getCurrentMonthKey = () => {
  const now = new Date();
  const israelDate = new Date(
    now.toLocaleString("en-US", { timeZone: "Asia/Jerusalem" })
  );
  const year = israelDate.getFullYear();
  const month = String(israelDate.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

/**
 * Get monthly usage data for a clinic
 *
 * @param {string} clinicId - The clinic ID
 * @param {string} monthKey - Optional month key (YYYY-MM), defaults to current month
 * @returns {Promise<Object|null>} - Monthly usage data or null
 *
 * Error Code: CK-9850
 */
export const getClinicMonthlyUsage = async (clinicId, monthKey = null) => {
  const targetMonth = monthKey || getCurrentMonthKey();

  try {
    const usageRef = doc(db, "clinics", clinicId, "monthlyUsage", targetMonth);
    const usageSnap = await getDoc(usageRef);

    if (!usageSnap.exists()) {
      return null;
    }

    return serializeForRedux({ id: usageSnap.id, ...usageSnap.data() });
  } catch (error) {
    console.error("[CK-9850] Error getting clinic monthly usage:", {
      errorCode: "CK-9850",
      errorMessage: error.message,
      clinicId,
      monthKey: targetMonth,
      timestamp: new Date().toISOString(),
    });
    return null;
  }
};

/**
 * Get all monthly usage history for a clinic
 *
 * @param {string} clinicId - The clinic ID
 * @param {number} limitCount - Maximum number of months to return (default: 12)
 * @returns {Promise<Array>} - Array of monthly usage documents sorted by month desc
 *
 * Error Code: CK-9851
 */
export const getClinicUsageHistory = async (clinicId, limitCount = 12) => {
  try {
    const usageRef = collection(db, "clinics", clinicId, "monthlyUsage");
    const q = query(usageRef, orderBy("month", "desc"), limit(limitCount));
    const usageSnap = await getDocs(q);

    return usageSnap.docs.map((docSnap) =>
      serializeForRedux({ id: docSnap.id, ...docSnap.data() })
    );
  } catch (error) {
    console.error("[CK-9851] Error getting clinic usage history:", {
      errorCode: "CK-9851",
      errorMessage: error.message,
      clinicId,
      limitCount,
      timestamp: new Date().toISOString(),
    });
    return [];
  }
};

/**
 * Get monthly usage data for a therapist
 *
 * @param {string} therapistId - The therapist ID
 * @param {string} monthKey - Optional month key (YYYY-MM), defaults to current month
 * @returns {Promise<Object|null>} - Monthly usage data or null
 *
 * Error Code: CK-9852
 */
export const getTherapistMonthlyUsage = async (
  therapistId,
  monthKey = null
) => {
  const targetMonth = monthKey || getCurrentMonthKey();

  try {
    const usageRef = doc(
      db,
      "therapists",
      therapistId,
      "monthlyUsage",
      targetMonth
    );
    const usageSnap = await getDoc(usageRef);

    if (!usageSnap.exists()) {
      return null;
    }

    return serializeForRedux({ id: usageSnap.id, ...usageSnap.data() });
  } catch (error) {
    console.error("[CK-9852] Error getting therapist monthly usage:", {
      errorCode: "CK-9852",
      errorMessage: error.message,
      therapistId,
      monthKey: targetMonth,
      timestamp: new Date().toISOString(),
    });
    return null;
  }
};

/**
 * Get all monthly usage history for a therapist
 *
 * @param {string} therapistId - The therapist ID
 * @param {number} limitCount - Maximum number of months to return (default: 12)
 * @returns {Promise<Array>} - Array of monthly usage documents sorted by month desc
 *
 * Error Code: CK-9853
 */
export const getTherapistUsageHistory = async (
  therapistId,
  limitCount = 12
) => {
  try {
    const usageRef = collection(db, "therapists", therapistId, "monthlyUsage");
    const q = query(usageRef, orderBy("month", "desc"), limit(limitCount));
    const usageSnap = await getDocs(q);

    return usageSnap.docs.map((docSnap) =>
      serializeForRedux({ id: docSnap.id, ...docSnap.data() })
    );
  } catch (error) {
    console.error("[CK-9853] Error getting therapist usage history:", {
      errorCode: "CK-9853",
      errorMessage: error.message,
      therapistId,
      limitCount,
      timestamp: new Date().toISOString(),
    });
    return [];
  }
};

/**
 * Get aggregated usage totals for a clinic (sum of all months)
 *
 * @param {string} clinicId - The clinic ID
 * @returns {Promise<Object>} - Aggregated usage totals
 *
 * Error Code: CK-9854
 */
export const getClinicUsageTotals = async (clinicId) => {
  try {
    const history = await getClinicUsageHistory(clinicId, 100); // Get up to 100 months

    const totals = {
      aiRequestsCount: 0,
      aiTokensUsed: 0,
      digitalSignatureCount: 0,
      aiUsageByFunction: {
        generateTreatmentSummary: { requests: 0, tokens: 0 },
        aiGameSelection: { requests: 0, tokens: 0 },
        aiPatientConsultation: { requests: 0, tokens: 0 },
        aiTreatmentConsultation: { requests: 0, tokens: 0 },
        generateInitialPatientSummary: { requests: 0, tokens: 0 },
        updatePatientSummary: { requests: 0, tokens: 0 },
        generateTreatmentsReportSummary: { requests: 0, tokens: 0 },
        aiFoodEstimate: { requests: 0, tokens: 0 },
      },
      monthsCount: history.length,
    };

    history.forEach((month) => {
      totals.aiRequestsCount += month.aiRequestsCount || 0;
      totals.aiTokensUsed += month.aiTokensUsed || 0;
      totals.digitalSignatureCount += month.digitalSignatureCount || 0;

      // Aggregate by function
      if (month.aiUsageByFunction) {
        Object.keys(totals.aiUsageByFunction).forEach((funcName) => {
          if (month.aiUsageByFunction[funcName]) {
            totals.aiUsageByFunction[funcName].requests +=
              month.aiUsageByFunction[funcName].requests || 0;
            totals.aiUsageByFunction[funcName].tokens +=
              month.aiUsageByFunction[funcName].tokens || 0;
          }
        });
      }
    });

    return totals;
  } catch (error) {
    console.error("[CK-9854] Error getting clinic usage totals:", {
      errorCode: "CK-9854",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    return null;
  }
};

// ============================================
// QUESTIONNAIRE FUNCTIONS (Reusable Templates - Like Forms)
// ============================================

/**
 * Get all questionnaires for a clinic (clinic-level, visible to all therapists)
 * @param {string} clinicId - The clinic ID
 * @returns {Array} - Array of questionnaire objects
 *
 * Error Code: CK-7110
 */
export const getQuestionnaires = async (clinicId) => {
  try {
    const questionnairesRef = collection(
      db,
      "clinics",
      clinicId,
      "questionnaires"
    );
    const q = query(questionnairesRef, orderBy("createdAt", "desc"));
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) =>
      serializeForRedux({ id: doc.id, ...doc.data() })
    );
  } catch (error) {
    console.error("[CK-7110] Error fetching questionnaires:", {
      errorCode: "CK-7110",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get a single questionnaire by ID
 * @param {string} clinicId - The clinic ID
 * @param {string} questionnaireId - The questionnaire ID
 * @returns {Object} - Questionnaire object
 *
 * Error Code: CK-7111
 */
export const getQuestionnaire = async (clinicId, questionnaireId) => {
  try {
    const questionnaireRef = doc(
      db,
      "clinics",
      clinicId,
      "questionnaires",
      questionnaireId
    );
    const questionnaireSnap = await getDoc(questionnaireRef);

    if (!questionnaireSnap.exists()) {
      throw new Error("Questionnaire not found");
    }

    return serializeForRedux({
      id: questionnaireSnap.id,
      ...questionnaireSnap.data(),
    });
  } catch (error) {
    console.error("[CK-7111] Error fetching questionnaire:", {
      errorCode: "CK-7111",
      errorMessage: error.message,
      clinicId,
      questionnaireId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Create a new questionnaire (reusable template)
 * @param {string} clinicId - The clinic ID
 * @param {Object} questionnaireData - The questionnaire data (name, fields)
 * @param {string} creatorId - The therapist ID who created
 * @param {string} creatorName - The name of the creator
 * @returns {Object} - Created questionnaire object
 *
 * Error Code: CK-7112
 */
export const createQuestionnaire = async (
  clinicId,
  questionnaireData,
  creatorId,
  creatorName
) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  try {
    const questionnairesRef = collection(
      db,
      "clinics",
      clinicId,
      "questionnaires"
    );
    const newQuestionnaireRef = doc(questionnairesRef);

    const questionnaire = {
      ...questionnaireData,
      createdBy: creatorId,
      creatorName: creatorName,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(newQuestionnaireRef, questionnaire);

    // Log activity
    logActivity(clinicId, {
      therapistId: creatorId,
      therapistName: creatorName,
      action: "create_questionnaire",
      actionLabel: "יצירת שאלון",
      entityType: "questionnaire",
      entityId: newQuestionnaireRef.id,
      entityName: questionnaireData.name,
      details: {
        questionnaireName: questionnaireData.name,
        fieldsCount: questionnaireData.fields?.length || 0,
        requireSignature: questionnaireData.requireSignature === true,
      },
      success: true,
    });

    return serializeForRedux({
      id: newQuestionnaireRef.id,
      ...questionnaire,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error("[CK-7112] Error creating questionnaire:", {
      errorCode: "CK-7112",
      errorMessage: error.message,
      clinicId,
      questionnaireData,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Update a questionnaire (reusable template - can always be edited)
 * @param {string} clinicId - The clinic ID
 * @param {string} questionnaireId - The questionnaire ID
 * @param {Object} questionnaireData - The updated questionnaire data
 * @param {string} editorId - The therapist ID who edited
 * @param {string} editorName - The name of the editor
 * @returns {Object} - Updated questionnaire object
 *
 * Error Code: CK-7113
 */
export const updateQuestionnaire = async (
  clinicId,
  questionnaireId,
  questionnaireData,
  editorId,
  editorName
) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  try {
    const questionnaireRef = doc(
      db,
      "clinics",
      clinicId,
      "questionnaires",
      questionnaireId
    );

    const updates = {
      ...questionnaireData,
      updatedAt: serverTimestamp(),
    };

    await updateDoc(questionnaireRef, updates);

    // Log activity
    logActivity(clinicId, {
      therapistId: editorId,
      therapistName: editorName,
      action: "update_questionnaire",
      actionLabel: "עדכון שאלון",
      entityType: "questionnaire",
      entityId: questionnaireId,
      entityName: questionnaireData.name,
      details: {
        questionnaireName: questionnaireData.name,
        fieldsCount: questionnaireData.fields?.length || 0,
        requireSignature: questionnaireData.requireSignature === true,
      },
      success: true,
    });

    const updatedQuestionnaire = await getQuestionnaire(
      clinicId,
      questionnaireId
    );
    return updatedQuestionnaire;
  } catch (error) {
    console.error("[CK-7113] Error updating questionnaire:", {
      errorCode: "CK-7113",
      errorMessage: error.message,
      clinicId,
      questionnaireId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Delete a questionnaire (can delete any questionnaire - submissions in patient files are preserved)
 * @param {string} clinicId - The clinic ID
 * @param {string} questionnaireId - The questionnaire ID
 * @param {string} questionnaireName - The questionnaire name (for logging)
 * @param {string} therapistId - The therapist ID who deleted
 * @param {string} therapistName - The name of the therapist
 *
 * Error Code: CK-7114
 */
export const deleteQuestionnaire = async (
  clinicId,
  questionnaireId,
  questionnaireName,
  therapistId,
  therapistName
) => {
  // Guard: Block if trial expired
  await guardTrialWrite(clinicId);

  try {
    const questionnaireRef = doc(
      db,
      "clinics",
      clinicId,
      "questionnaires",
      questionnaireId
    );

    await deleteDoc(questionnaireRef);

    // Log activity
    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: "delete_questionnaire",
      actionLabel: "מחיקת שאלון",
      entityType: "questionnaire",
      entityId: questionnaireId,
      entityName: questionnaireName,
      details: { questionnaireName },
      success: true,
    });
  } catch (error) {
    console.error("[CK-7114] Error deleting questionnaire:", {
      errorCode: "CK-7114",
      errorMessage: error.message,
      clinicId,
      questionnaireId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Duplicate a questionnaire
 * @param {string} clinicId - The clinic ID
 * @param {string} questionnaireId - The questionnaire ID to duplicate
 * @param {string} creatorId - The therapist ID who is duplicating
 * @param {string} creatorName - The name of the creator
 * @returns {Object} - The duplicated questionnaire object
 *
 * Error Code: CK-7119
 */
export const duplicateQuestionnaire = async (
  clinicId,
  questionnaireId,
  creatorId,
  creatorName
) => {
  try {
    const originalQuestionnaire = await getQuestionnaire(
      clinicId,
      questionnaireId
    );

    const newQuestionnaireData = {
      name: `${originalQuestionnaire.name} (עותק)`,
      fields: originalQuestionnaire.fields.map((field) => ({
        ...field,
        id: `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      })),
      // Carry the signature requirement over (legacy docs lack it → false)
      requireSignature: originalQuestionnaire.requireSignature === true,
    };

    return await createQuestionnaire(
      clinicId,
      newQuestionnaireData,
      creatorId,
      creatorName
    );
  } catch (error) {
    console.error("[CK-7119] Error duplicating questionnaire:", {
      errorCode: "CK-7119",
      errorMessage: error.message,
      clinicId,
      questionnaireId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Send questionnaire to selected patients (calls Cloud Function)
 * @param {string} clinicId - The clinic ID
 * @param {string} questionnaireId - The questionnaire ID
 * @param {Array} patientIds - Array of patient IDs to send to
 * @returns {Object} - { success, sentCount, failedCount }
 *
 * Error Code: CK-7115
 */
export const sendQuestionnaireToPatients = async (
  clinicId,
  questionnaireId,
  patientIds
) => {
  try {
    const sendQuestionnaireFn = httpsCallable(
      functions,
      "sendQuestionnaireToPatients"
    );
    const result = await sendQuestionnaireFn({
      clinicId,
      questionnaireId,
      patientIds,
    });

    return result.data;
  } catch (error) {
    console.error("[CK-7115] Error sending questionnaire:", {
      errorCode: "CK-7115",
      errorMessage: error.message,
      clinicId,
      questionnaireId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Resend questionnaire SMS to patient
 * Error Code: CK-7118
 */
export const resendQuestionnaireSms = async (clinicId, linkId) => {
  try {
    const resendFn = httpsCallable(functions, "resendQuestionnaireSms");
    const result = await resendFn({ clinicId, linkId });
    return result.data;
  } catch (error) {
    console.error("[CK-7118] Error resending questionnaire SMS:", {
      errorCode: "CK-7118",
      errorMessage: error.message,
      clinicId,
      linkId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Generate (or fetch cached) PDF for a completed questionnaire link.
 * First call builds the PDF and stores pdfUrl on the link; subsequent calls return the cached URL.
 * @param {string} clinicId
 * @param {string} linkId - The questionnaireLink doc id
 * @returns {{ success: boolean, pdfUrl: string, cached: boolean }}
 */
export const generateQuestionnairePDF = async (clinicId, linkId) => {
  try {
    const fn = httpsCallable(functions, "generateQuestionnairePDF");
    const result = await fn({ clinicId, linkId });
    return result.data;
  } catch (error) {
    console.error("[CK-7119] Error generating questionnaire PDF:", {
      errorCode: "CK-7119",
      errorMessage: error.message,
      clinicId,
      linkId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get pending questionnaire links for a therapist (or all for admin)
 * @param {string} clinicId - The clinic ID
 * @param {string} therapistId - The therapist ID (null for admin to see all)
 * @returns {Array} - Array of pending questionnaire link objects
 *
 * Error Code: CK-7116
 */
export const getPendingQuestionnaireLinks = async (clinicId, therapistId) => {
  try {
    const linksRef = collection(db, "clinics", clinicId, "questionnaireLinks");
    let q;

    if (therapistId) {
      q = query(
        linksRef,
        where("status", "in", ["pending", "viewed"]),
        where("therapistId", "==", therapistId),
        orderBy("createdAt", "desc")
      );
    } else {
      q = query(
        linksRef,
        where("status", "in", ["pending", "viewed"]),
        orderBy("createdAt", "desc")
      );
    }

    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) =>
      serializeForRedux({ id: doc.id, ...doc.data() })
    );
  } catch (error) {
    console.error("[CK-7116] Error fetching pending questionnaire links:", error.message);
    console.error("[CK-7116] Details:", { clinicId, therapistId, timestamp: new Date().toISOString() });
    throw error;
  }
};

/**
 * Get questionnaire submissions for a patient (forms + questionnaires tab in patient profile)
 * @param {string} clinicId - The clinic ID
 * @param {string} patientId - The patient ID
 * @returns {Array} - Array of questionnaire submission objects
 *
 * Error Code: CK-7117
 */
export const getPatientQuestionnaireSubmissions = async (
  clinicId,
  patientId
) => {
  try {
    const linksRef = collection(db, "clinics", clinicId, "questionnaireLinks");
    const q = query(
      linksRef,
      where("patientId", "==", patientId),
      orderBy("createdAt", "desc")
    );

    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) =>
      serializeForRedux({ id: doc.id, ...doc.data() })
    );
  } catch (error) {
    console.error("[CK-7117] Error fetching patient questionnaire submissions:", {
      errorCode: "CK-7117",
      errorMessage: error.message,
      clinicId,
      patientId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get questionnaire data for public page (no auth - calls Cloud Function)
 * @param {string} linkId - The questionnaire link ID
 * @returns {Object} - Questionnaire data for display
 *
 * Error Code: CK-7117
 */
export const getQuestionnairePublic = async (linkId) => {
  try {
    const getQuestionnaireFn = httpsCallable(
      functions,
      "getQuestionnairePublic"
    );
    const result = await getQuestionnaireFn({ linkId });
    return result.data;
  } catch (error) {
    console.error("[CK-7117] Error getting public questionnaire:", {
      errorCode: "CK-7117",
      errorMessage: error.message,
      linkId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Verify patient identity for questionnaire (no auth - calls Cloud Function)
 * @param {string} linkId - The questionnaire link ID
 * @param {string} verificationValue - Phone last 4 digits or ID number
 * @param {string} verificationType - "phone" or "id"
 * @returns {Object} - { success, verified }
 *
 * Error Code: CK-7118
 */
export const verifyQuestionnaireIdentity = async (
  linkId,
  verificationValue,
  verificationType
) => {
  try {
    const verifyFn = httpsCallable(functions, "verifyQuestionnaireIdentity");
    const result = await verifyFn({
      linkId,
      verificationValue,
      verificationType,
    });
    return result.data;
  } catch (error) {
    console.error("[CK-7118] Error verifying questionnaire identity:", {
      errorCode: "CK-7118",
      errorMessage: error.message,
      linkId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Submit questionnaire response (no auth - calls Cloud Function)
 * @param {string} linkId - The questionnaire link ID
 * @param {Array} fields - Array of field objects with values
 * @param {Object} deviceInfo - Device/browser info
 * @param {Object|null} [signature] - Optional { data, type: "drawn"|"typed" };
 *   required by the server when the link has requireSignature === true
 * @returns {Object} - { success }
 *
 * Error Code: CK-7119
 */
export const submitQuestionnaireResponse = async (
  linkId,
  fields,
  deviceInfo,
  signature = null
) => {
  try {
    const submitFn = httpsCallable(functions, "submitQuestionnaireResponse");
    const result = await submitFn({
      linkId,
      fields,
      deviceInfo,
      ...(signature ? { signature } : {}),
    });
    return result.data;
  } catch (error) {
    console.error("[CK-7119] Error submitting questionnaire response:", {
      errorCode: "CK-7119",
      errorMessage: error.message,
      linkId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// ============================================
// GOOGLE CALENDAR SYNC FUNCTIONS
// ============================================

/**
 * Initialize Google Calendar sync for the current therapist.
 * Exchanges the OAuth authorization code, creates CLINIKIT calendar,
 * and syncs past 3 months of appointments.
 *
 * @param {string} code - Google OAuth authorization code
 * @returns {Object} - { success, email, calendarId, syncedCount }
 *
 * Error Code: CK-9400
 */
export const initGoogleCalendarSyncFn = async (code) => {
  try {
    const fn = httpsCallable(functions, "initGoogleCalendarSync");
    const result = await fn({ code });
    return result.data;
  } catch (error) {
    console.error("[CK-9400] Error initializing Google Calendar sync:", {
      errorCode: "CK-9400",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Re-sync Google Calendar for the current therapist.
 * Clears all events and re-creates from past 3 months.
 *
 * @returns {Object} - { success, syncedCount }
 *
 * Error Code: CK-9401
 */
export const resyncGoogleCalendarFn = async () => {
  try {
    const fn = httpsCallable(functions, "resyncGoogleCalendar");
    const result = await fn({});
    return result.data;
  } catch (error) {
    console.error("[CK-9401] Error resyncing Google Calendar:", {
      errorCode: "CK-9401",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Disconnect Google Calendar for the current therapist.
 * Revokes the refresh token at Google, deletes the local token,
 * and clears the connection flag.
 *
 * @returns {Object} - { success }
 *
 * Error Code: CK-9402
 */
export const disconnectGoogleCalendarFn = async () => {
  try {
    const fn = httpsCallable(functions, "disconnectGoogleCalendar");
    const result = await fn({});
    return result.data;
  } catch (error) {
    console.error("[CK-9402] Error disconnecting Google Calendar:", {
      errorCode: "CK-9402",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// ============================================
// MONTHLY SUMMARY REPORTS
// ============================================

/**
 * List monthly summary report metadata for a clinic, newest first.
 *
 * Returns a slim projection (no per-therapist slice / no full lists) suitable
 * for the home widget. Use getMonthlyReport for the full doc.
 *
 * Error Code: CK-7900
 */
export const getMonthlyReportsList = async (clinicId, limitCount = 24) => {
  try {
    const reportsRef = collection(
      db,
      "clinics",
      clinicId,
      "monthlyReports"
    );
    const q = query(reportsRef, orderBy("meta.monthKey", "desc"), limit(limitCount));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => {
      const data = d.data();
      return serializeForRedux({
        id: d.id,
        monthKey: data.meta?.monthKey || d.id,
        year: data.meta?.year || null,
        month: data.meta?.month || null,
        generatedAt: data.meta?.generatedAt || null,
        computedAt: data.meta?.computedAt || null,
        schemaVersion: data.meta?.schemaVersion || null,
        totalCompleted: data.meetingAnalytics?.totalCompleted || 0,
        totalScheduled: data.meetingAnalytics?.totalScheduled || 0,
        totalCancelled: data.meetingAnalytics?.totalCancelled || 0,
        collectedTotal: data.financeAnalytics?.collectedTotal || 0,
        expectedTotal: data.financeAnalytics?.expectedTotal || 0,
        // Slim per-therapist totals for scoped trend charts
        perTherapistTotals: Object.fromEntries(
          Object.entries(data.perTherapist || {}).map(([tid, slice]) => [
            tid,
            {
              collectedTotal: slice.financeAnalytics?.collectedTotal || 0,
              expectedTotal: slice.financeAnalytics?.expectedTotal || 0,
              totalCompleted: slice.meetingAnalytics?.totalCompleted || 0,
              totalCancelled: slice.meetingAnalytics?.totalCancelled || 0,
            },
          ])
        ),
      });
    });
  } catch (error) {
    console.error("[CK-7900] Error fetching monthly reports list:", {
      errorCode: "CK-7900",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Fetch a single monthly summary report doc.
 *
 * @param {string} clinicId
 * @param {string} monthKey YYYY-MM
 *
 * Error Code: CK-7901
 */
export const getMonthlyReport = async (clinicId, monthKey) => {
  try {
    const ref = doc(db, "clinics", clinicId, "monthlyReports", monthKey);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return serializeForRedux({ id: snap.id, ...snap.data() });
  } catch (error) {
    console.error("[CK-7901] Error fetching monthly report:", {
      errorCode: "CK-7901",
      errorMessage: error.message,
      clinicId,
      monthKey,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Trigger manual generation of a monthly report via the callable.
 * Used by RTK Query mutation; restricted server-side to clinic admins.
 *
 * Error Code: CK-7902
 */
export const triggerMonthlyReportGeneration = async ({ clinicId, year, month, notify = true }) => {
  try {
    const fn = httpsCallable(functions, "generateMonthlyReport");
    const result = await fn({ clinicId, year, month, notify });
    return result.data;
  } catch (error) {
    console.error("[CK-7902] Error triggering monthly report generation:", {
      errorCode: "CK-7902",
      errorMessage: error.message,
      clinicId,
      year,
      month,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Subscribe to a single monthly report doc (live reports since schema v3 —
 * the recompute pipeline updates docs server-side and the Reports page must
 * reflect that without polling).
 *
 * @param {string} clinicId
 * @param {string} monthKey YYYY-MM
 * @param {(report: Object|null) => void} onData Serialized doc or null when missing
 * @param {(error: Error) => void} [onError]
 * @returns {() => void} unsubscribe
 *
 * Error Code: CK-8941
 */
/**
 * Scoped reads (multi-clinic v2) — for members WITHOUT clinic-wide report
 * scope (non-admin, reports.viewClinicWide off). Rules deny them the doc; the
 * callable returns their own slice in the same shape. Payload is already
 * JSON (ISO strings), so serializeForRedux is a no-op pass for safety.
 */
export const getMonthlyReportScoped = async (clinicId, monthKey) => {
  try {
    const fn = httpsCallable(functions, "getMonthlyReportScoped");
    const res = await fn({ clinicId, mode: "month", monthKey });
    return res.data?.report ? serializeForRedux(res.data.report) : null;
  } catch (error) {
    console.error("[CK-8950] Error fetching scoped monthly report:", {
      errorCode: "CK-8950",
      errorMessage: error.message,
      functionCode: error.code,
      clinicId,
      monthKey,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

export const getMonthlyReportsListScoped = async (clinicId, limitCount = 24) => {
  try {
    const fn = httpsCallable(functions, "getMonthlyReportScoped");
    const res = await fn({ clinicId, mode: "list", limit: limitCount });
    return serializeForRedux(res.data?.rows || []);
  } catch (error) {
    console.error("[CK-8951] Error fetching scoped monthly reports list:", {
      errorCode: "CK-8951",
      errorMessage: error.message,
      functionCode: error.code,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

export const subscribeMonthlyReport = (clinicId, monthKey, onData, onError) => {
  const ref = doc(db, "clinics", clinicId, "monthlyReports", monthKey);
  return onSnapshot(
    ref,
    (snap) => {
      onData(snap.exists() ? serializeForRedux({ id: snap.id, ...snap.data() }) : null);
    },
    (error) => {
      console.error("[CK-8941] Monthly report subscription error:", {
        errorCode: "CK-8941",
        errorMessage: error.message,
        clinicId,
        monthKey,
        timestamp: new Date().toISOString(),
      });
      if (onError) onError(error);
    }
  );
};

/**
 * Ask the server to make sure a month's report exists on the current schema
 * (lazy backfill for the Reports page). Any clinic member may call; admins
 * may pass forceRecompute for an unconditional rebuild.
 *
 * Error Code: CK-8942
 */
export const ensureMonthlyReportGeneration = async ({
  clinicId,
  year,
  month,
  forceRecompute = false,
}) => {
  try {
    const fn = httpsCallable(functions, "ensureMonthlyReport");
    const result = await fn({ clinicId, year, month, forceRecompute });
    return result.data;
  } catch (error) {
    console.error("[CK-8942] Error ensuring monthly report:", {
      errorCode: "CK-8942",
      errorMessage: error.message,
      clinicId,
      year,
      month,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Fetch transactions relevant to a month window: created in range ∪ paid in
 * range (deduped by id). Indexed range queries — replaces the old pattern of
 * fetching the whole collection and filtering in JS.
 *
 * @param {string} clinicId
 * @param {Object} opts
 * @param {Date|string} opts.start Window start (inclusive)
 * @param {Date|string} opts.end Window end (inclusive)
 * @param {string} [opts.therapistId] Optional scope for non-admin therapists
 *
 * Error Code: CK-8943
 */
export const getTransactionsInRange = async (
  clinicId,
  { start, end, therapistId = null }
) => {
  try {
    const startTs = Timestamp.fromDate(new Date(start));
    const endTs = Timestamp.fromDate(new Date(end));
    const txRef = collection(db, "clinics", clinicId, "financial_transactions");

    const buildQuery = (dateField) => {
      const constraints = [
        where(dateField, ">=", startTs),
        where(dateField, "<=", endTs),
        orderBy(dateField, "asc"),
      ];
      if (therapistId) constraints.unshift(where("therapistId", "==", therapistId));
      return query(txRef, ...constraints);
    };

    const [byCreated, byPaid] = await Promise.all([
      getDocs(buildQuery("createdAt")),
      getDocs(buildQuery("actualPaymentDate")),
    ]);

    const byId = new Map();
    for (const snapshot of [byCreated, byPaid]) {
      snapshot.docs.forEach((d) => {
        if (!byId.has(d.id)) byId.set(d.id, { id: d.id, ...d.data() });
      });
    }
    return Array.from(byId.values())
      .sort((a, b) => {
        const at = a.createdAt?.toMillis?.() || 0;
        const bt = b.createdAt?.toMillis?.() || 0;
        return bt - at;
      })
      .map((t) => serializeForRedux(t));
  } catch (error) {
    console.error("[CK-8943] Error fetching transactions in range:", {
      errorCode: "CK-8943",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// ============================================
// FEATURE TIPS (server-computed usage insights)
// ============================================

/**
 * Read the nightly-computed unused-feature tips for a clinic.
 * Written by the nightlyTelemetryScoring Cloud Function.
 */
export const getFeatureTips = async (clinicId) => {
  try {
    const [tipsSnap, nudgesSnap] = await Promise.all([
      getDoc(doc(db, "clinics", clinicId, "insights", "featureTips")),
      // Auto-eligibility for new clinics. Absent on clinics created before the
      // nudge engine shipped — the caller then uses the NiV-manual path only.
      getDoc(doc(db, "clinics", clinicId, "insights", "featureNudges")),
    ]);
    if (!tipsSnap.exists()) return null;
    return {
      ...tipsSnap.data(),
      nudges: nudgesSnap.exists() ? nudgesSnap.data() : null,
    };
  } catch (error) {
    console.error("[CK-8470] Error fetching feature tips:", {
      errorCode: "CK-8470",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Persist a dismissed feature tip on the therapist doc (cross-device).
 * `permanent` is the "לא להציג יותר" action: the tip never returns, while a
 * plain dismissal only snoozes it for 90 days.
 * Values written before permanent-kill existed are bare Timestamps; readers
 * parse both shapes, and these dotted paths replace a legacy scalar cleanly.
 * No guardTrialWrite — dismissing a tip must work on expired trials too.
 */
export const dismissFeatureTip = async (
  uid,
  featureKey,
  { permanent = false } = {}
) => {
  try {
    const therapistRef = doc(db, "therapists", uid);
    await updateDoc(therapistRef, {
      [`dismissedTips.${featureKey}.at`]: serverTimestamp(),
      [`dismissedTips.${featureKey}.killed`]: permanent === true,
      updatedAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    console.error("[CK-8471] Error dismissing feature tip:", {
      errorCode: "CK-8471",
      errorMessage: error.message,
      featureKey,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// ============================================
// NUTRITION — FOOD CATALOG (מאגר המזונות)
// ============================================
// Two sources, one search surface:
//   /foods                            global, NiV-curated, read-only here
//   /clinics/{clinicId}/customFoods   clinic-private, coach-added
// A clinic food shadows a global one with the same name — a coach who corrected
// a product's values for their own clinic should see their version.
//
// The catalog is deliberately NOT mirrored into Redux (doof kept all 390 foods
// in a persisted slice): it grows without bound and the picker only ever shows
// one page.
// ============================================

const foodsCollectionRef = () => collection(db, "foods");

const customFoodsCollectionRef = (clinicId) =>
  collection(db, "clinics", clinicId, "customFoods");

/** How many docs we are willing to scan per source before giving up on filters. */
const FOOD_SEARCH_SCAN_CAP = 400;

/**
 * Does this food pass the coach's macro-range filters?
 * Filters are applied in memory: Firestore cannot combine them with the
 * `searchName` prefix range without a composite index per filter combination.
 */
const foodMatchesFilters = (food, filters = {}) => {
  const inRange = (value, min, max) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return false;
    if (min !== undefined && min !== null && min !== "" && num < Number(min))
      return false;
    if (max !== undefined && max !== null && max !== "" && num > Number(max))
      return false;
    return true;
  };

  const ranges = [
    ["calories", filters.minCalories, filters.maxCalories],
    ["protein", filters.minProtein, filters.maxProtein],
    ["carbohydrates", filters.minCarbohydrates, filters.maxCarbohydrates],
    ["fat", filters.minFat, filters.maxFat],
  ];

  for (const [field, min, max] of ranges) {
    const hasBound = [min, max].some(
      (bound) => bound !== undefined && bound !== null && bound !== ""
    );
    if (hasBound && !inRange(food[field], min, max)) return false;
  }

  if (filters.categoryId && food.categoryId !== filters.categoryId) return false;
  if (filters.source && food.source !== filters.source) return false;

  return true;
};

/**
 * One ordered page out of a single collection, as a prefix search on
 * `searchName` (Firestore has no substring search; coaches type the start of
 * the name). An empty query browses the whole collection in name order.
 */
const fetchFoodPage = async (collectionRef, searchName, cursor, pageSize) => {
  const constraints = [];

  if (searchName) {
    constraints.push(
      where("searchName", ">=", searchName),
      where("searchName", "<=", `${searchName}`)
    );
  }
  constraints.push(orderBy("searchName"));
  if (cursor) constraints.push(startAfter(cursor));
  constraints.push(limit(pageSize));

  const snapshot = await getDocs(query(collectionRef, ...constraints));
  return snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  }));
};

/**
 * Search the food catalog: global + this clinic's custom foods, merged into one
 * `searchName`-ordered list, deduped (clinic wins), and paginated.
 *
 * @param {string} clinicId
 * @param {string} [searchQuery] - free text; matched as a name prefix
 * @param {object} [filters] - macro ranges / categoryId / source
 * @param {object} [options]
 * @param {number} [options.pageSize=25]
 * @param {string|null} [options.cursor] - the `nextCursor` of the previous page
 * @returns {{foods: Array, nextCursor: string|null, hasMore: boolean}}
 *
 * Error Code: CK-8361
 */
export const searchFoods = async (
  clinicId,
  searchQuery = "",
  filters = {},
  options = {}
) => {
  try {
    const { pageSize = 25, cursor = null } = options;
    const searchName = buildFoodSearchName(searchQuery);

    // Both sources are ordered by the same key, so a shared string cursor is
    // enough — no per-collection document snapshots to carry around.
    let globalCursor = cursor;
    let clinicCursor = cursor;
    let globalExhausted = false;
    let clinicExhausted = !clinicId;

    const collected = [];
    const seen = new Set();
    let scanned = 0;
    let lastSearchName = cursor;

    while (
      collected.length < pageSize + 1 &&
      scanned < FOOD_SEARCH_SCAN_CAP &&
      !(globalExhausted && clinicExhausted)
    ) {
      const [globalPage, clinicPage] = await Promise.all([
        globalExhausted
          ? []
          : fetchFoodPage(foodsCollectionRef(), searchName, globalCursor, pageSize),
        clinicExhausted
          ? []
          : fetchFoodPage(
              customFoodsCollectionRef(clinicId),
              searchName,
              clinicCursor,
              pageSize
            ),
      ]);

      if (globalPage.length < pageSize) globalExhausted = true;
      if (clinicPage.length < pageSize) clinicExhausted = true;
      if (globalPage.length > 0)
        globalCursor = globalPage[globalPage.length - 1].searchName;
      if (clinicPage.length > 0)
        clinicCursor = clinicPage[clinicPage.length - 1].searchName;

      scanned += globalPage.length + clinicPage.length;

      // Clinic foods first so they shadow a global food of the same name
      // (Array.prototype.sort is stable, so an equal searchName keeps that
      // order).
      const merged = [...clinicPage, ...globalPage].sort((a, b) =>
        String(a.searchName || "").localeCompare(String(b.searchName || ""), "he")
      );

      for (const food of merged) {
        const key = food.searchName || food.id;
        if (seen.has(key)) continue;
        seen.add(key);
        if (!foodMatchesFilters(food, filters)) continue;
        collected.push(serializeForRedux(food));
        lastSearchName = food.searchName || lastSearchName;
      }
    }

    const hasMore =
      collected.length > pageSize || !(globalExhausted && clinicExhausted);
    const foods = collected.slice(0, pageSize);
    const nextCursor = hasMore
      ? foods[foods.length - 1]?.searchName || lastSearchName || null
      : null;

    return { foods, nextCursor, hasMore: hasMore && foods.length > 0 };
  } catch (error) {
    console.error("[CK-8361] Error searching foods:", {
      errorCode: "CK-8361",
      errorMessage: error.message,
      clinicId,
      hasQuery: Boolean(searchQuery),
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Add a food to this clinic's private catalog, and raise a promotion request so
 * NiV can review it for the global catalog.
 *
 * Validated through the SAME normalizer as the seed import, so a hand-typed
 * food can never enter the catalog in a shape the menu calculator chokes on.
 *
 * Error Code: CK-8362
 */
export const createCustomFood = async (clinicId, foodData, actor = {}) => {
  await guardTrialWrite(clinicId);

  const { therapistId = null, therapistName = "לא ידוע" } = actor;

  try {
    const result = normalizeFood(foodData, { source: "clinic", clinicId });
    if (!result.ok) {
      const error = new Error(result.errors.join("; "));
      error.validationErrors = result.errors;
      throw error;
    }

    const foodRef = doc(customFoodsCollectionRef(clinicId), result.id);
    const existing = await getDoc(foodRef);
    if (existing.exists()) {
      throw new Error("מזון בשם הזה כבר קיים במאגר של המרפאה");
    }

    const foodDoc = {
      ...result.food,
      id: result.id,
      createdBy: therapistId,
      createdByName: therapistName,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    await setDoc(foodRef, foodDoc);

    // Promotion request — NiV reviews it and, if it is good, copies it into the
    // global catalog. Fire-and-forget: the coach's food is already usable, and
    // a failed request must not fail the write they asked for.
    setDoc(doc(collection(db, "foodPromotionRequests")), {
      clinicId,
      foodId: result.id,
      food: result.food,
      status: "pending",
      requestedBy: therapistId,
      requestedByName: therapistName,
      createdAt: serverTimestamp(),
    }).catch((requestError) => {
      console.error("[CK-8363] Error raising food promotion request:", {
        errorCode: "CK-8363",
        errorMessage: requestError.message,
        clinicId,
        foodId: result.id,
        timestamp: new Date().toISOString(),
      });
    });

    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: ACTIVITY_ACTIONS.CREATE_CUSTOM_FOOD,
      entityType: ENTITY_TYPES.FOOD,
      entityId: result.id,
      entityName: result.food.foodName,
      details: { success: true, warnings: result.warnings.length },
    });

    return serializeForRedux({ ...foodDoc, source: "clinic" });
  } catch (error) {
    console.error("[CK-8362] Error creating custom food:", {
      errorCode: "CK-8362",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: ACTIVITY_ACTIONS.CREATE_CUSTOM_FOOD,
      entityType: ENTITY_TYPES.FOOD,
      entityName: foodData?.foodName || null,
      details: { success: false, error: error.message },
    });
    throw error;
  }
};

/**
 * Update a clinic food. Re-validated through the normalizer; the document id
 * (the name slug) never changes, so a rename keeps the same doc — menus point
 * at values copied inline, so nothing breaks either way.
 *
 * Error Code: CK-8364
 */
export const updateCustomFood = async (clinicId, foodId, foodData, actor = {}) => {
  await guardTrialWrite(clinicId);

  const { therapistId = null, therapistName = "לא ידוע" } = actor;

  try {
    const result = normalizeFood(foodData, { source: "clinic", clinicId });
    if (!result.ok) {
      const error = new Error(result.errors.join("; "));
      error.validationErrors = result.errors;
      throw error;
    }

    const foodRef = doc(customFoodsCollectionRef(clinicId), foodId);
    await setDoc(
      foodRef,
      { ...result.food, id: foodId, updatedAt: serverTimestamp() },
      { merge: true }
    );

    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: ACTIVITY_ACTIONS.UPDATE_CUSTOM_FOOD,
      entityType: ENTITY_TYPES.FOOD,
      entityId: foodId,
      entityName: result.food.foodName,
      details: { success: true },
    });

    return serializeForRedux({ ...result.food, id: foodId });
  } catch (error) {
    console.error("[CK-8364] Error updating custom food:", {
      errorCode: "CK-8364",
      errorMessage: error.message,
      clinicId,
      foodId,
      timestamp: new Date().toISOString(),
    });
    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: ACTIVITY_ACTIONS.UPDATE_CUSTOM_FOOD,
      entityType: ENTITY_TYPES.FOOD,
      entityId: foodId,
      details: { success: false, error: error.message },
    });
    throw error;
  }
};

/**
 * Remove a clinic food from the clinic catalog.
 *
 * This one IS a hard delete: a custom food is reference data, not a medical
 * record, and menus copy the values they use inline — deleting a food never
 * changes a menu that already contains it.
 *
 * Error Code: CK-8365
 */
export const deleteCustomFood = async (clinicId, foodId, actor = {}) => {
  await guardTrialWrite(clinicId);

  const { therapistId = null, therapistName = "לא ידוע", foodName = null } = actor;

  try {
    await deleteDoc(doc(customFoodsCollectionRef(clinicId), foodId));

    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: ACTIVITY_ACTIONS.DELETE_CUSTOM_FOOD,
      entityType: ENTITY_TYPES.FOOD,
      entityId: foodId,
      entityName: foodName,
      details: { success: true },
    });

    return { success: true, id: foodId };
  } catch (error) {
    console.error("[CK-8365] Error deleting custom food:", {
      errorCode: "CK-8365",
      errorMessage: error.message,
      clinicId,
      foodId,
      timestamp: new Date().toISOString(),
    });
    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: ACTIVITY_ACTIONS.DELETE_CUSTOM_FOOD,
      entityType: ENTITY_TYPES.FOOD,
      entityId: foodId,
      details: { success: false, error: error.message },
    });
    throw error;
  }
};

// ============================================
// NUTRITION — BODY MEASUREMENTS (מדידות)
// ============================================
// clinics/{clinicId}/patients/{patientId}/measurements/{id}
//
// Every field is optional, but a row with nothing in it is meaningless, so at
// least one is required. BMI / BMR / RMR (and body fat, when the inputs are
// there) are computed ON WRITE from the values plus the patient's gender and
// age, and the gender/age actually used are stored alongside — otherwise a
// birthday would silently change every historical row's BMR.
//
// Soft delete only: `isDeleted` + the quartet, rules deny `delete`, readers
// filter in memory. Same convention as locked treatment reports.
// ============================================

const measurementsCollectionRef = (clinicId, patientId) =>
  collection(db, "clinics", clinicId, "patients", patientId, "measurements");

const measurementDocRef = (clinicId, patientId, measurementId) =>
  doc(db, "clinics", clinicId, "patients", patientId, "measurements", measurementId);

/**
 * The numeric fields a measurement may carry, with the sanity range each one
 * is validated against. Ported from doof's AddMeasurement form, which is the
 * only place these limits were ever written down.
 */
export const MEASUREMENT_FIELDS = {
  weight: { min: 20, max: 220, label: "משקל", unit: "ק״ג" },
  height: { min: 30, max: 250, label: "גובה", unit: "ס״מ" },
  fatPercentage: { min: 1, max: 70, label: "אחוז שומן", unit: "%" },
  neck: { min: 5, max: 100, label: "היקף צוואר", unit: "ס״מ" },
  bust: { min: 20, max: 300, label: "היקף חזה", unit: "ס״מ" },
  arm: { min: 3, max: 100, label: "היקף זרוע", unit: "ס״מ" },
  waist: { min: 10, max: 300, label: "היקף מותניים", unit: "ס״מ" },
  hip: { min: 10, max: 300, label: "היקף אגן", unit: "ס״מ" },
  thigh: { min: 2, max: 200, label: "היקף ירך", unit: "ס״מ" },
};

/**
 * Keep only the measurement fields that were actually filled in, coercing them
 * to numbers and range-checking each one.
 *
 * @returns {{values: object, errors: string[]}}
 */
const sanitizeMeasurementValues = (data = {}) => {
  const values = {};
  const errors = [];

  Object.entries(MEASUREMENT_FIELDS).forEach(([field, spec]) => {
    const raw = data[field];
    // An untouched field is empty, not zero — leave it off the document.
    if (raw === undefined || raw === null || raw === "") return;

    const value = Number(raw);
    if (!Number.isFinite(value)) {
      errors.push(`${spec.label}: ערך לא תקין`);
      return;
    }
    if (value < spec.min || value > spec.max) {
      errors.push(`${spec.label} חייב להיות בין ${spec.min} ל-${spec.max} ${spec.unit}`);
      return;
    }
    values[field] = value;
  });

  return { values, errors };
};

/**
 * Derive BMI / BMR / RMR / body fat from the raw values and the patient's body
 * data. Anything that cannot be computed (missing height, unknown gender…) is
 * simply absent — never zero, which would draw a real-looking point on a chart.
 */
const computeMeasurementMetrics = (values, { gender, age }) => {
  const metrics = {};

  const bmi = calcBmi({ weight: values.weight, height: values.height });
  if (bmi !== null) metrics.bmi = bmi;

  const body = {
    weight: values.weight,
    height: values.height,
    age,
    gender,
  };
  const bmr = calcBmr(body);
  if (bmr !== null) metrics.bmr = bmr;

  const rmr = calcRmr(body);
  if (rmr !== null) metrics.rmr = rmr;

  // Body fat is only derived when the coach did not measure it directly.
  // `waist` stands in for the abdomen circumference the formula wants.
  if (values.fatPercentage === undefined) {
    const estimated = calcBodyFat(
      { height: values.height, abdomen: values.waist, neck: values.neck },
      gender
    );
    if (estimated !== null && estimated > 0) {
      metrics.fatPercentage = estimated;
      metrics.fatPercentageEstimated = true;
    }
  }

  return metrics;
};

/**
 * Apply the coach's manual overrides (Round 3 / Phase 20) on top of the
 * computed metrics. Contract per key in METRIC_OVERRIDE_FIELDS (bmi, bmr):
 *   - `<key>`          the effective value every reader/chart uses
 *   - `<key>Manual`    true when the coach typed it
 *   - `<key>Computed`  the formula result (or null) — never lost
 *
 * `overrides[key]`: number → set manual; null → clear (back to computed);
 * undefined → keep whatever `existing` had (an update that did not touch it).
 * Returns the fields to write; keys that must leave the doc map to deleteField().
 */
const applyMetricOverrides = (computed, overrides = {}, existing = {}) => {
  const out = {};
  Object.keys(METRIC_OVERRIDE_FIELDS).forEach((key) => {
    const computedValue = computed[key] ?? null;
    let manualValue;
    if (overrides[key] === undefined) {
      manualValue = existing[`${key}Manual`] === true ? existing[key] ?? null : null;
    } else {
      manualValue = overrides[key];
    }

    if (manualValue !== null && manualValue !== undefined) {
      out[key] = manualValue;
      out[`${key}Manual`] = true;
      out[`${key}Computed`] = computedValue;
      return;
    }

    if (computedValue !== null) out[key] = computedValue;
    else if (existing[key] !== undefined) out[key] = deleteField();
    if (existing[`${key}Manual`] !== undefined) out[`${key}Manual`] = deleteField();
    if (existing[`${key}Computed`] !== undefined) out[`${key}Computed`] = deleteField();
  });
  return out;
};

/**
 * Resolve the body data every metric depends on. Returns nulls rather than
 * throwing: a measurement with no BMR is still worth recording, and the UI
 * prompts the coach to fill the missing profile field.
 */
const getPatientBodyData = async (clinicId, patientId) => {
  const patient = await getPatient(clinicId, patientId);
  return {
    gender: patient?.gender || null,
    age: calcAgeFromBirthDate(patient?.birthDate),
    patientName: patient
      ? `${patient.firstName || ""} ${patient.lastName || ""}`.trim() || null
      : null,
  };
};

/**
 * Record a measurement.
 *
 * @param {object} data - raw field values + optional `date` (ISO or Date) and
 *   `source` ("coach" | "client"; defaults to "coach")
 *
 * Error Code: CK-8366
 */
export const createMeasurement = async (clinicId, patientId, data = {}, actor = {}) => {
  await guardTrialWrite(clinicId);

  const { therapistId = null, therapistName = "לא ידוע" } = actor;

  try {
    const { values, errors } = sanitizeMeasurementValues(data);
    if (errors.length > 0) {
      const error = new Error(errors.join("\n"));
      error.validationErrors = errors;
      throw error;
    }
    if (Object.keys(values).length === 0) {
      throw new Error("יש למלא לפחות מדידה אחת");
    }

    const { values: overrides, errors: overrideErrors } = sanitizeMetricOverrides(
      data.metricOverrides
    );
    if (overrideErrors.length > 0) {
      const error = new Error(overrideErrors.join("\n"));
      error.validationErrors = overrideErrors;
      throw error;
    }

    const { gender, age, patientName } = await getPatientBodyData(clinicId, patientId);

    const measurementRef = doc(measurementsCollectionRef(clinicId, patientId));
    const computedMetrics = computeMeasurementMetrics(values, { gender, age });
    const measurementDoc = {
      id: measurementRef.id,
      ...values,
      ...computedMetrics,
      ...applyMetricOverrides(computedMetrics, overrides),
      // Snapshot the inputs the metrics were derived from, so a later birthday
      // or a corrected gender does not retroactively rewrite this row.
      genderUsed: gender,
      ageUsed: age,
      date: data.date ? Timestamp.fromDate(new Date(data.date)) : serverTimestamp(),
      source: data.source === "client" ? "client" : "coach",
      coachNotes: [],
      isDeleted: false,
      createdBy: therapistId,
      createdByName: therapistName,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(measurementRef, measurementDoc);

    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: ACTIVITY_ACTIONS.CREATE_MEASUREMENT,
      entityType: ENTITY_TYPES.MEASUREMENT,
      entityId: measurementRef.id,
      entityName: patientName,
      details: { success: true, fields: Object.keys(values).length },
    });

    return serializeForRedux(measurementDoc);
  } catch (error) {
    console.error("[CK-8366] Error creating measurement:", {
      errorCode: "CK-8366",
      errorMessage: error.message,
      clinicId,
      patientId,
      timestamp: new Date().toISOString(),
    });
    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: ACTIVITY_ACTIONS.CREATE_MEASUREMENT,
      entityType: ENTITY_TYPES.MEASUREMENT,
      details: { success: false, error: error.message },
    });
    throw error;
  }
};

/**
 * All of a patient's measurements, newest first, soft-deleted rows filtered out
 * in memory (the same convention as getTreatmentsForPatient — no composite
 * index, and rows written before the flag existed are still returned).
 *
 * Error Code: CK-8367
 */
export const getMeasurements = async (clinicId, patientId) => {
  try {
    const q = query(
      measurementsCollectionRef(clinicId, patientId),
      orderBy("date", "desc")
    );
    const snapshot = await getDocs(q);

    return snapshot.docs
      .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
      .filter((measurement) => measurement.isDeleted !== true)
      .map((measurement) => serializeForRedux(measurement));
  } catch (error) {
    console.error("[CK-8367] Error fetching measurements:", {
      errorCode: "CK-8367",
      errorMessage: error.message,
      clinicId,
      patientId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Correct a measurement. The metrics are recomputed from the merged values, but
 * against the ORIGINAL `genderUsed` / `ageUsed` when those were recorded — a
 * typo fix must not silently re-date the row's BMR.
 *
 * Error Code: CK-8368
 */
export const updateMeasurement = async (
  clinicId,
  patientId,
  measurementId,
  data = {},
  actor = {}
) => {
  await guardTrialWrite(clinicId);

  const { therapistId = null, therapistName = "לא ידוע" } = actor;

  try {
    const measurementRef = measurementDocRef(clinicId, patientId, measurementId);
    const existingSnap = await getDoc(measurementRef);
    if (!existingSnap.exists()) throw new Error("המדידה לא נמצאה");

    const existing = existingSnap.data();
    if (existing.isDeleted === true) throw new Error("לא ניתן לערוך מדידה שנמחקה");

    // Merge over the stored row so a partial edit keeps the untouched fields.
    const merged = {};
    Object.keys(MEASUREMENT_FIELDS).forEach((field) => {
      merged[field] = field in data ? data[field] : existing[field];
    });

    const { values, errors } = sanitizeMeasurementValues(merged);
    if (errors.length > 0) {
      const error = new Error(errors.join("\n"));
      error.validationErrors = errors;
      throw error;
    }
    if (Object.keys(values).length === 0) {
      throw new Error("יש למלא לפחות מדידה אחת");
    }

    // Fall back to the patient's current body data only if the row never had a
    // snapshot (written before this field existed, or by a pre-profile client).
    let gender = existing.genderUsed ?? null;
    let age = existing.ageUsed ?? null;
    if (gender === null && age === null) {
      ({ gender, age } = await getPatientBodyData(clinicId, patientId));
    }

    // Manual overrides: a key that is absent from data.metricOverrides keeps
    // the stored override; null clears it; a number replaces it.
    const { values: overrides, errors: overrideErrors } = sanitizeMetricOverrides(
      data.metricOverrides
    );
    if (overrideErrors.length > 0) {
      const error = new Error(overrideErrors.join("\n"));
      error.validationErrors = overrideErrors;
      throw error;
    }
    Object.keys(METRIC_OVERRIDE_FIELDS).forEach((key) => {
      if (!(data.metricOverrides && key in data.metricOverrides)) delete overrides[key];
    });

    const computedMetrics = computeMeasurementMetrics(values, { gender, age });
    const updates = {
      ...values,
      ...computedMetrics,
      ...applyMetricOverrides(computedMetrics, overrides, existing),
      genderUsed: gender,
      ageUsed: age,
      updatedAt: serverTimestamp(),
    };
    // A field cleared by the coach must actually leave the document, otherwise
    // a stale value keeps drawing on the chart.
    Object.keys(MEASUREMENT_FIELDS).forEach((field) => {
      if (!(field in values) && existing[field] !== undefined) {
        updates[field] = deleteField();
      }
    });
    if (values.fatPercentage !== undefined && existing.fatPercentageEstimated) {
      updates.fatPercentageEstimated = deleteField();
    }
    if (data.date) updates.date = Timestamp.fromDate(new Date(data.date));

    await updateDoc(measurementRef, updates);

    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: ACTIVITY_ACTIONS.UPDATE_MEASUREMENT,
      entityType: ENTITY_TYPES.MEASUREMENT,
      entityId: measurementId,
      details: { success: true },
    });

    return serializeForRedux({ id: measurementId, ...existing, ...values });
  } catch (error) {
    console.error("[CK-8368] Error updating measurement:", {
      errorCode: "CK-8368",
      errorMessage: error.message,
      clinicId,
      patientId,
      measurementId,
      timestamp: new Date().toISOString(),
    });
    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: ACTIVITY_ACTIONS.UPDATE_MEASUREMENT,
      entityType: ENTITY_TYPES.MEASUREMENT,
      entityId: measurementId,
      details: { success: false, error: error.message },
    });
    throw error;
  }
};

/**
 * Soft-delete a measurement. There is no hard delete and no restore — the
 * Firestore rules deny `delete` outright, exactly like locked treatment
 * reports.
 *
 * Error Code: CK-8369
 */
export const softDeleteMeasurement = async (
  clinicId,
  patientId,
  measurementId,
  actor = {}
) => {
  await guardTrialWrite(clinicId);

  const { therapistId = null, therapistName = "לא ידוע", reason = null } = actor;

  try {
    const measurementRef = measurementDocRef(clinicId, patientId, measurementId);
    const existingSnap = await getDoc(measurementRef);
    if (!existingSnap.exists()) throw new Error("המדידה לא נמצאה");

    // Idempotent: a second delete is a no-op, not an error
    if (existingSnap.data().isDeleted === true) {
      return { success: true, id: measurementId };
    }

    await updateDoc(measurementRef, {
      isDeleted: true,
      deletedAt: serverTimestamp(),
      deletedBy: therapistId || null,
      deletedByName: therapistName || "לא ידוע",
      deletedReason: typeof reason === "string" && reason.trim() ? reason.trim() : null,
      updatedAt: serverTimestamp(),
    });

    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: ACTIVITY_ACTIONS.DELETE_MEASUREMENT,
      entityType: ENTITY_TYPES.MEASUREMENT,
      entityId: measurementId,
      details: { success: true },
    });

    return { success: true, id: measurementId };
  } catch (error) {
    console.error("[CK-8369] Error deleting measurement:", {
      errorCode: "CK-8369",
      errorMessage: error.message,
      clinicId,
      patientId,
      measurementId,
      timestamp: new Date().toISOString(),
    });
    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: ACTIVITY_ACTIONS.DELETE_MEASUREMENT,
      entityType: ENTITY_TYPES.MEASUREMENT,
      entityId: measurementId,
      details: { success: false, error: error.message },
    });
    throw error;
  }
};

// ============================================
// NUTRITION — MENUS (תפריטים)
// ============================================
// clinics/{clinicId}/patients/{patientId}/menus/{menuId}
//
// A patient may have many menus; exactly ONE is active. "Active" is stored as
// `patient.nutrition.activeMenuId` — a single field on a single document —
// rather than a flag on each menu, which makes "exactly one active" impossible
// to violate and makes the public client page a single doc read.
//
// Soft delete only (rules deny `delete`); readers filter in memory.
// ============================================

/**
 * Client-side id for a block / sub-block / ingredient inside a menu document.
 * These live inside an array field, so Firestore never mints them for us.
 * crypto.randomUUID everywhere it exists; the Math.random fallback only ever
 * runs in a non-secure context, where collisions inside one menu are still
 * vanishingly unlikely.
 */
const generateNutritionId = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `nid-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const menusCollectionRef = (clinicId, patientId) =>
  collection(db, "clinics", clinicId, "patients", patientId, "menus");

const menuDocRef = (clinicId, patientId, menuId) =>
  doc(db, "clinics", clinicId, "patients", patientId, "menus", menuId);

/**
 * Strip a menu payload down to the fields we store, and denormalize the
 * totals so lists, the PDF and the public page never have to walk the tree.
 */
const buildMenuPayload = (menuData = {}) => {
  const blocks = Array.isArray(menuData.blocks) ? menuData.blocks : [];
  const isAveragesMenu = menuData.isAveragesMenu === true;

  return {
    menuName: String(menuData.menuName || "").trim(),
    comment: menuData.comment || null,
    isAveragesMenu,
    blocks,
    totals: calcMenuNutrition(blocks, isAveragesMenu),
    categoryId: menuData.categoryId || null,
  };
};

/**
 * All of a patient's menus, newest first, soft-deleted ones filtered out in
 * memory (same convention as getTreatmentsForPatient).
 *
 * Error Code: CK-8630
 */
export const getMenusForPatient = async (clinicId, patientId) => {
  try {
    const q = query(menusCollectionRef(clinicId, patientId), orderBy("createdAt", "desc"));
    const snapshot = await getDocs(q);

    return snapshot.docs
      .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
      .filter((menu) => menu.isDeleted !== true)
      .map((menu) => serializeForRedux(menu));
  } catch (error) {
    console.error("[CK-8630] Error fetching menus:", {
      errorCode: "CK-8630",
      errorMessage: error.message,
      clinicId,
      patientId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * One menu, or null. Returns null for a soft-deleted menu too — from every
 * reader's point of view it no longer exists.
 *
 * Error Code: CK-8631
 */
export const getMenu = async (clinicId, patientId, menuId) => {
  try {
    const snapshot = await getDoc(menuDocRef(clinicId, patientId, menuId));
    if (!snapshot.exists()) return null;

    const menu = { id: snapshot.id, ...snapshot.data() };
    if (menu.isDeleted === true) return null;
    return serializeForRedux(menu);
  } catch (error) {
    console.error("[CK-8631] Error fetching menu:", {
      errorCode: "CK-8631",
      errorMessage: error.message,
      clinicId,
      patientId,
      menuId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Create a menu. Creating one never makes it active — the coach does that
 * explicitly, so sending a half-built menu to a client takes a deliberate act.
 *
 * Error Code: CK-8632
 */
export const createMenu = async (clinicId, patientId, menuData = {}, actor = {}) => {
  await guardTrialWrite(clinicId);

  const { therapistId = null, therapistName = "לא ידוע" } = actor;

  try {
    const payload = buildMenuPayload(menuData);
    if (!payload.menuName) throw new Error("יש להזין שם לתפריט");

    const menuRef = doc(menusCollectionRef(clinicId, patientId));
    const menuDoc = {
      id: menuRef.id,
      ...payload,
      coachNotes: [],
      isDeleted: false,
      createdBy: therapistId,
      createdByName: therapistName,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(menuRef, menuDoc);

    // A client's single menu is the active one automatically — no coach
    // action needed. Only when nothing is active yet: a second menu never
    // steals the ribbon from the one the client is already following.
    let autoActivated = false;
    try {
      const patientSnap = await getDoc(doc(db, "clinics", clinicId, "patients", patientId));
      if (patientSnap.exists() && !patientSnap.data()?.nutrition?.activeMenuId) {
        await updateDoc(patientSnap.ref, {
          "nutrition.activeMenuId": menuRef.id,
          updatedAt: serverTimestamp(),
        });
        autoActivated = true;
      }
    } catch (activateError) {
      // The menu exists; failing to auto-activate is a warning, not a failure
      console.error("[CK-8639] Failed to auto-activate the first menu:", {
        errorCode: "CK-8639",
        errorMessage: activateError.message,
        clinicId,
        patientId,
        timestamp: new Date().toISOString(),
      });
    }

    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: ACTIVITY_ACTIONS.CREATE_MENU,
      entityType: ENTITY_TYPES.MENU,
      entityId: menuRef.id,
      entityName: payload.menuName,
      details: { success: true, blocks: payload.blocks.length, autoActivated },
    });

    return serializeForRedux({ ...menuDoc, autoActivated });
  } catch (error) {
    console.error("[CK-8632] Error creating menu:", {
      errorCode: "CK-8632",
      errorMessage: error.message,
      clinicId,
      patientId,
      timestamp: new Date().toISOString(),
    });
    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: ACTIVITY_ACTIONS.CREATE_MENU,
      entityType: ENTITY_TYPES.MENU,
      details: { success: false, error: error.message },
    });
    throw error;
  }
};

/**
 * Save a menu. Totals are always recomputed here rather than trusted from the
 * client, so a stale editor can never write numbers that disagree with the
 * blocks beside them.
 *
 * Error Code: CK-8633
 */
export const updateMenu = async (clinicId, patientId, menuId, menuData = {}, actor = {}) => {
  await guardTrialWrite(clinicId);

  const { therapistId = null, therapistName = "לא ידוע" } = actor;

  try {
    const payload = buildMenuPayload(menuData);
    if (!payload.menuName) throw new Error("יש להזין שם לתפריט");

    const menuRef = menuDocRef(clinicId, patientId, menuId);
    const existing = await getDoc(menuRef);
    if (!existing.exists()) throw new Error("התפריט לא נמצא");
    if (existing.data().isDeleted === true) {
      throw new Error("לא ניתן לערוך תפריט שנמחק");
    }

    await updateDoc(menuRef, { ...payload, updatedAt: serverTimestamp() });

    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: ACTIVITY_ACTIONS.UPDATE_MENU,
      entityType: ENTITY_TYPES.MENU,
      entityId: menuId,
      entityName: payload.menuName,
      details: { success: true },
    });

    return serializeForRedux({ id: menuId, ...payload });
  } catch (error) {
    console.error("[CK-8633] Error updating menu:", {
      errorCode: "CK-8633",
      errorMessage: error.message,
      clinicId,
      patientId,
      menuId,
      timestamp: new Date().toISOString(),
    });
    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: ACTIVITY_ACTIONS.UPDATE_MENU,
      entityType: ENTITY_TYPES.MENU,
      entityId: menuId,
      details: { success: false, error: error.message },
    });
    throw error;
  }
};

/**
 * Point `patient.nutrition.activeMenuId` at a menu — or at nothing, when
 * `menuId` is null. A dotted field path is used so the rest of the nutrition
 * settings map is left completely alone.
 *
 * Error Code: CK-8634
 */
export const setActiveMenu = async (clinicId, patientId, menuId, actor = {}) => {
  await guardTrialWrite(clinicId);

  const { therapistId = null, therapistName = "לא ידוע" } = actor;

  try {
    if (menuId) {
      // Never point the client's page at a menu that is gone.
      const menu = await getMenu(clinicId, patientId, menuId);
      if (!menu) throw new Error("התפריט לא נמצא");
    }

    await updateDoc(doc(db, "clinics", clinicId, "patients", patientId), {
      "nutrition.activeMenuId": menuId || null,
      updatedAt: serverTimestamp(),
    });

    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: ACTIVITY_ACTIONS.SET_ACTIVE_MENU,
      entityType: ENTITY_TYPES.MENU,
      entityId: menuId || null,
      details: { success: true, cleared: !menuId },
    });

    return { success: true, activeMenuId: menuId || null };
  } catch (error) {
    console.error("[CK-8634] Error setting the active menu:", {
      errorCode: "CK-8634",
      errorMessage: error.message,
      clinicId,
      patientId,
      menuId,
      timestamp: new Date().toISOString(),
    });
    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: ACTIVITY_ACTIONS.SET_ACTIVE_MENU,
      entityType: ENTITY_TYPES.MENU,
      entityId: menuId || null,
      details: { success: false, error: error.message },
    });
    throw error;
  }
};

/**
 * Copy a menu, including every block, sub-block and ingredient. The copy is
 * never active, whatever the original was.
 *
 * Error Code: CK-8635
 */
export const duplicateMenu = async (clinicId, patientId, menuId, actor = {}) => {
  await guardTrialWrite(clinicId);

  const { therapistId = null, therapistName = "לא ידוע" } = actor;

  try {
    const source = await getMenu(clinicId, patientId, menuId);
    if (!source) throw new Error("התפריט לא נמצא");

    // The blocks are plain data, so a structural clone is enough — but new ids
    // are minted so the two menus can never share a block identity.
    const blocks = (source.blocks || []).map((block) => ({
      ...block,
      blockId: generateNutritionId(),
      subBlocks: (block.subBlocks || []).map((subBlock) => ({
        ...subBlock,
        subBlockId: generateNutritionId(),
      })),
    }));

    return await createMenu(
      clinicId,
      patientId,
      {
        menuName: `${source.menuName} (עותק)`,
        comment: source.comment,
        isAveragesMenu: source.isAveragesMenu,
        categoryId: source.categoryId,
        blocks,
      },
      { therapistId, therapistName }
    );
  } catch (error) {
    console.error("[CK-8635] Error duplicating menu:", {
      errorCode: "CK-8635",
      errorMessage: error.message,
      clinicId,
      patientId,
      menuId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Soft-delete a menu. If it was the active one, `activeMenuId` is cleared in
 * the same operation — otherwise the client's page would point at a menu that
 * no reader will return.
 *
 * Error Code: CK-8636
 */
export const softDeleteMenu = async (clinicId, patientId, menuId, actor = {}) => {
  await guardTrialWrite(clinicId);

  const { therapistId = null, therapistName = "לא ידוע", reason = null } = actor;

  try {
    const menuRef = menuDocRef(clinicId, patientId, menuId);
    const existing = await getDoc(menuRef);
    if (!existing.exists()) throw new Error("התפריט לא נמצא");

    // Idempotent: deleting twice is a no-op, not an error
    if (existing.data().isDeleted === true) {
      return { success: true, id: menuId, clearedActive: false };
    }

    await updateDoc(menuRef, {
      isDeleted: true,
      deletedAt: serverTimestamp(),
      deletedBy: therapistId || null,
      deletedByName: therapistName || "לא ידוע",
      deletedReason: typeof reason === "string" && reason.trim() ? reason.trim() : null,
      updatedAt: serverTimestamp(),
    });

    const patient = await getPatient(clinicId, patientId);
    const wasActive = patient?.nutrition?.activeMenuId === menuId;
    if (wasActive) {
      await setActiveMenu(clinicId, patientId, null, { therapistId, therapistName });
    }

    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: ACTIVITY_ACTIONS.DELETE_MENU,
      entityType: ENTITY_TYPES.MENU,
      entityId: menuId,
      details: { success: true, clearedActive: wasActive },
    });

    return { success: true, id: menuId, clearedActive: wasActive };
  } catch (error) {
    console.error("[CK-8636] Error deleting menu:", {
      errorCode: "CK-8636",
      errorMessage: error.message,
      clinicId,
      patientId,
      menuId,
      timestamp: new Date().toISOString(),
    });
    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: ACTIVITY_ACTIONS.DELETE_MENU,
      entityType: ENTITY_TYPES.MENU,
      entityId: menuId,
      details: { success: false, error: error.message },
    });
    throw error;
  }
};

// ============================================
// NUTRITION — MENU TEMPLATES (תבניות תפריט)
// ============================================
// clinics/{clinicId}/menuTemplates/{id} — clinic-wide, not per patient.
// `kind` discriminates a saved meal / option / food, so the three sidebar
// sections are one query with an in-memory split.
//
// Kept as its own collection rather than folded into clinics/{id}/templates,
// so menu payloads never appear in the treatment-template lists, which have no
// type filter of their own.
// ============================================

export const MENU_TEMPLATE_KINDS = {
  BLOCK: "block",
  SUB_BLOCK: "subBlock",
  FOOD: "food",
};

const menuTemplatesCollectionRef = (clinicId) =>
  collection(db, "clinics", clinicId, "menuTemplates");

/**
 * Every saved template for the clinic, newest first.
 *
 * Error Code: CK-8637
 */
export const getMenuTemplates = async (clinicId) => {
  try {
    const q = query(menuTemplatesCollectionRef(clinicId), orderBy("createdAt", "desc"));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((docSnap) =>
      serializeForRedux({ id: docSnap.id, ...docSnap.data() })
    );
  } catch (error) {
    console.error("[CK-8637] Error fetching menu templates:", {
      errorCode: "CK-8637",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Save a block / sub-block / ingredient for re-use in any menu.
 *
 * Error Code: CK-8638
 */
export const createMenuTemplate = async (clinicId, templateData = {}, actor = {}) => {
  await guardTrialWrite(clinicId);

  const { therapistId = null, therapistName = "לא ידוע" } = actor;

  try {
    const kind = templateData.kind;
    if (!Object.values(MENU_TEMPLATE_KINDS).includes(kind)) {
      throw new Error("סוג תבנית לא תקין");
    }

    const name = String(templateData.name || "").trim();
    if (!name) throw new Error("יש להזין שם לתבנית");
    if (!templateData.payload) throw new Error("התבנית ריקה");

    const templateRef = doc(menuTemplatesCollectionRef(clinicId));
    const templateDoc = {
      id: templateRef.id,
      kind,
      name,
      payload: templateData.payload,
      totals: templateData.totals || null,
      createdBy: therapistId,
      createdByName: therapistName,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(templateRef, templateDoc);

    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: ACTIVITY_ACTIONS.CREATE_MENU_TEMPLATE,
      entityType: ENTITY_TYPES.MENU_TEMPLATE,
      entityId: templateRef.id,
      entityName: name,
      details: { success: true, kind },
    });

    return serializeForRedux(templateDoc);
  } catch (error) {
    console.error("[CK-8638] Error creating menu template:", {
      errorCode: "CK-8638",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: ACTIVITY_ACTIONS.CREATE_MENU_TEMPLATE,
      entityType: ENTITY_TYPES.MENU_TEMPLATE,
      details: { success: false, error: error.message },
    });
    throw error;
  }
};

/**
 * Remove a saved template. A hard delete: a template is reusable scaffolding,
 * not a record — menus copy its contents in, so deleting one never changes a
 * menu that already used it.
 *
 * Error Code: CK-8639
 */
export const deleteMenuTemplate = async (clinicId, templateId, actor = {}) => {
  await guardTrialWrite(clinicId);

  const { therapistId = null, therapistName = "לא ידוע", name = null } = actor;

  try {
    await deleteDoc(doc(menuTemplatesCollectionRef(clinicId), templateId));

    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: ACTIVITY_ACTIONS.DELETE_MENU_TEMPLATE,
      entityType: ENTITY_TYPES.MENU_TEMPLATE,
      entityId: templateId,
      entityName: name,
      details: { success: true },
    });

    return { success: true, id: templateId };
  } catch (error) {
    console.error("[CK-8639] Error deleting menu template:", {
      errorCode: "CK-8639",
      errorMessage: error.message,
      clinicId,
      templateId,
      timestamp: new Date().toISOString(),
    });
    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: ACTIVITY_ACTIONS.DELETE_MENU_TEMPLATE,
      entityType: ENTITY_TYPES.MENU_TEMPLATE,
      entityId: templateId,
      details: { success: false, error: error.message },
    });
    throw error;
  }
};

/**
 * Copy the starter menu templates into this clinic's own `menuTemplates`.
 *
 * Explicitly coach-triggered rather than auto-seeded on registration: a write
 * that happens as a side effect of opening a screen is invisible and hard to
 * undo, and a coach who deleted the starters would get them back on the next
 * page load.
 *
 * Idempotent by name + kind: re-running adds only what is missing, so a coach
 * who deleted one starter on purpose does not get it back.
 *
 * Error Code: CK-8640
 */
export const seedNutritionMenuTemplates = async (clinicId, actor = {}) => {
  await guardTrialWrite(clinicId);

  const { therapistId = null, therapistName = "לא ידוע" } = actor;

  try {
    const existing = await getMenuTemplates(clinicId);
    const existingKeys = new Set(
      existing.map((template) => `${template.kind}::${template.name}`)
    );

    const missing = nutritionMenuTemplates.filter(
      (template) => !existingKeys.has(`${template.kind}::${template.name}`)
    );
    if (missing.length === 0) return { success: true, added: 0 };

    const batch = writeBatch(db);

    missing.forEach((template) => {
      const templateRef = doc(menuTemplatesCollectionRef(clinicId));

      // Mint ids here rather than in the data file, so two clinics never share
      // a block/ingredient identity.
      const payload = withNutritionIds(template.kind, template.payload);

      batch.set(templateRef, {
        id: templateRef.id,
        kind: template.kind,
        name: template.name,
        payload,
        totals: computeTemplateTotals(template.kind, payload),
        isStarter: true,
        createdBy: therapistId,
        createdByName: therapistName,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    });

    await batch.commit();

    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: ACTIVITY_ACTIONS.CREATE_MENU_TEMPLATE,
      entityType: ENTITY_TYPES.MENU_TEMPLATE,
      entityName: "תבניות התחלה",
      details: { success: true, added: missing.length },
    });

    return { success: true, added: missing.length };
  } catch (error) {
    console.error("[CK-8640] Error seeding starter menu templates:", {
      errorCode: "CK-8640",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/** Give a starter payload fresh ids at every level. */
const withNutritionIds = (kind, payload) => {
  const withIngredientIds = (ingredients = []) =>
    ingredients.map((item) => ({
      ...item,
      ingredientId: generateNutritionId(),
      connectedIngredients: (item.connectedIngredients || []).map((sub) => ({
        ...sub,
        ingredientId: generateNutritionId(),
      })),
    }));

  if (kind === MENU_TEMPLATE_KINDS.FOOD) {
    return withIngredientIds([payload])[0];
  }

  if (kind === MENU_TEMPLATE_KINDS.SUB_BLOCK) {
    return {
      ...payload,
      subBlockId: generateNutritionId(),
      ingredients: withIngredientIds(payload.ingredients),
    };
  }

  return {
    ...payload,
    blockId: generateNutritionId(),
    subBlocks: (payload.subBlocks || []).map((subBlock) => ({
      ...subBlock,
      subBlockId: generateNutritionId(),
      ingredients: withIngredientIds(subBlock.ingredients),
    })),
  };
};

/** Denormalized macro footer for a template card. */
const computeTemplateTotals = (kind, payload) => {
  if (kind === MENU_TEMPLATE_KINDS.FOOD) {
    return roundNutritionTotals(calcIngredientGroupNutrition(payload));
  }
  if (kind === MENU_TEMPLATE_KINDS.SUB_BLOCK) {
    return calcSubBlockNutrition(payload);
  }
  return calcBlockNutrition(payload, false);
};

const roundNutritionTotals = (totals) => ({
  calories: Math.round(totals.calories),
  protein: Math.round(totals.protein),
  carbohydrates: Math.round(totals.carbohydrates),
  fat: Math.round(totals.fat),
});

/**
 * Export a menu to PDF (Cloud Function `generateMenuPdf`, europe-west1).
 *
 * The payload carries pre-formatted display strings, built by
 * `buildMenuPdfPayload` below — the server brands and renders but never
 * recomputes the numbers, so there is exactly one menu calculator in the
 * system. Same split as `generatePatientReportPdf`.
 *
 * @param {Object} payload - { clinicId, patientId, menuId, menu }
 * @returns {{ success: boolean, pdfUrl: string, menuId: string }}
 *
 * Error Code: CK-8641
 */
export const generateMenuPdf = async (payload) => {
  try {
    const fn = httpsCallable(functions, "generateMenuPdf");
    const result = await fn(payload);
    return result.data;
  } catch (error) {
    console.error("[CK-8641] Error generating menu PDF:", {
      errorCode: "CK-8641",
      errorMessage: error.message,
      clinicId: payload?.clinicId,
      menuId: payload?.menuId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Flatten a menu document into the display payload `generateMenuPdf` expects.
 *
 * Every number is computed here with `nutritionCalc`, the module's single
 * calculator, and formatted to a string — the PDF template only lays strings
 * out. Keeping this in one place means the builder header and the menus list
 * cannot produce two differently-shaped PDFs.
 *
 * @param {Object} menu - a menu document
 * @param {string} [patientName]
 */
export const buildMenuPdfPayload = (menu, patientName = "") => {
  const isAveragesMenu = menu?.isAveragesMenu === true;

  const macrosLabel = (nutrition) =>
    `${Math.round(nutrition.calories)} קק״ל · ` +
    `${Math.round(nutrition.carbohydrates)} פחמ׳ · ` +
    `${Math.round(nutrition.protein)} חלבון · ` +
    `${Math.round(nutrition.fat)} שומן`;

  const mapIngredient = (item) => ({
    name: item?.foodName || "",
    amount: String(item?.amount ?? ""),
    unit: item?.selectedAmount || "",
    macrosLabel: macrosLabel(calcIngredientNutrition(item)),
  });

  return {
    menuName: menu?.menuName || "",
    comment: menu?.comment || "",
    isAveragesMenu,
    patientName: patientName || "",
    totals: calcMenuNutrition(menu?.blocks || [], isAveragesMenu),
    blocks: (menu?.blocks || []).map((block) => ({
      name: block?.blockName || "",
      comment: block?.comment || "",
      totals: calcBlockNutrition(block, isAveragesMenu),
      subBlocks: (block?.subBlocks || []).map((subBlock) => ({
        name: subBlock?.subBlockName || "",
        totals: calcSubBlockNutrition(subBlock),
        ingredients: (subBlock?.ingredients || []).map((item) => ({
          ...mapIngredient(item),
          // Substitutes print as indented "אפשרות n" lines under their parent
          substitutes: (item?.connectedIngredients || []).map(mapIngredient),
        })),
      })),
    })),
  };
};

/**
 * Create (or reuse) the patient's durable personal-page link.
 * Server-side: the top-level `menuLinkIndex` doc is Admin-SDK-only, so this
 * cannot be a client write.
 *
 * Error Code: CK-8650
 */
export const createMenuLink = async (clinicId, patientId) => {
  try {
    const fn = httpsCallable(functions, "createMenuLink");
    const result = await fn({ clinicId, patientId });
    return result.data;
  } catch (error) {
    console.error("[CK-8650] Error creating a menu link:", {
      errorCode: "CK-8650",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Send the personal-page link by SMS (counts against the clinic's quota).
 *
 * Error Code: CK-8651
 */
export const sendMenuLinkSms = async (clinicId, patientId, linkId) => {
  try {
    const fn = httpsCallable(functions, "sendMenuLinkSms");
    const result = await fn({ clinicId, patientId, linkId });
    return result.data;
  } catch (error) {
    console.error("[CK-8651] Error sending a menu link SMS:", {
      errorCode: "CK-8651",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// ============================================
// NUTRITION — DAILY EATING REPORTS (דוחות אכילה)
// ============================================
// clinics/{clinicId}/patients/{patientId}/dailyReports/{YYYY-MM-DD}
//
// Written mostly BY THE CLIENT through Cloud Functions; the coach reads them,
// corrects AI values, adds entries on the client's behalf, and marks a day
// reviewed. Client edits are never overwritten — every change appends to the
// entry's `edits[]`, so the original reported value survives.
// ============================================

const dailyReportsCollectionRef = (clinicId, patientId) =>
  collection(db, "clinics", clinicId, "patients", patientId, "dailyReports");

const dailyReportDocRef = (clinicId, patientId, dateKey) =>
  doc(db, "clinics", clinicId, "patients", patientId, "dailyReports", dateKey);

/** Sum a day's entries, skipping the ones the client removed. */
const sumEntryTotals = (entries = []) => {
  const totals = entries
    .filter((entry) => entry?.deletedByClient !== true)
    .reduce(
      (acc, entry) => ({
        calories: acc.calories + Number(entry?.totals?.calories || 0),
        protein: acc.protein + Number(entry?.totals?.protein || 0),
        carbohydrates: acc.carbohydrates + Number(entry?.totals?.carbohydrates || 0),
        fat: acc.fat + Number(entry?.totals?.fat || 0),
      }),
      { calories: 0, protein: 0, carbohydrates: 0, fat: 0 }
    );
  return {
    calories: Math.round(totals.calories),
    protein: Math.round(totals.protein),
    carbohydrates: Math.round(totals.carbohydrates),
    fat: Math.round(totals.fat),
  };
};

/** Totals for one entry's food rows. */
const sumFoodTotals = (foods = []) => {
  const totals = foods.reduce(
    (acc, food) => ({
      calories: acc.calories + Number(food?.calories || 0),
      protein: acc.protein + Number(food?.protein || 0),
      carbohydrates: acc.carbohydrates + Number(food?.carbohydrates || 0),
      fat: acc.fat + Number(food?.fat || 0),
    }),
    { calories: 0, protein: 0, carbohydrates: 0, fat: 0 }
  );
  return {
    calories: Math.round(totals.calories),
    protein: Math.round(totals.protein),
    carbohydrates: Math.round(totals.carbohydrates),
    fat: Math.round(totals.fat),
  };
};

/**
 * A patient's eating reports, newest first.
 *
 * Error Code: CK-8660
 */
export const getDailyReports = async (clinicId, patientId, limitCount = 60) => {
  try {
    const q = query(
      dailyReportsCollectionRef(clinicId, patientId),
      orderBy("date", "desc"),
      limit(limitCount)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((docSnap) =>
      serializeForRedux({ id: docSnap.id, ...docSnap.data() })
    );
  } catch (error) {
    console.error("[CK-8660] Error fetching daily reports:", {
      errorCode: "CK-8660",
      errorMessage: error.message,
      clinicId,
      patientId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Add an entry on the client's behalf, tagged `source: "coach"` so the report
 * always says who reported what.
 *
 * Error Code: CK-8661
 */
export const addCoachFoodEntry = async (
  clinicId,
  patientId,
  dateKey,
  entryData = {},
  actor = {}
) => {
  await guardTrialWrite(clinicId);

  const { therapistId = null, therapistName = "לא ידוע" } = actor;

  try {
    const foods = Array.isArray(entryData.foods) ? entryData.foods : [];
    const entry = {
      id: generateNutritionId(),
      at: Timestamp.now(),
      source: "coach",
      rawText: entryData.rawText || "",
      comment: entryData.comment || "",
      ai: null,
      foods,
      totals: sumFoodTotals(foods),
      edits: [],
      deletedByClient: false,
      createdByName: therapistName,
    };

    const reportRef = dailyReportDocRef(clinicId, patientId, dateKey);

    await runTransaction(db, async (tx) => {
      const snap = await tx.get(reportRef);

      if (!snap.exists()) {
        // The coach can log a day the client never opened, so the document may
        // not exist yet. It carries the same scoping fields the public writer
        // stamps, or the review queue would not find it.
        const patient = await getPatient(clinicId, patientId);
        tx.set(reportRef, {
          date: dateKey,
          clinicId,
          patientId,
          patientName:
            `${patient?.firstName || ""} ${patient?.lastName || ""}`.trim() || null,
          therapistId: patient?.therapistId || null,
          entries: [entry],
          totals: entry.totals,
          status: "open",
          coachReviewed: false,
          coachNotes: [],
          hasClientEdits: false,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        return;
      }

      const entries = [...(snap.data().entries || []), entry];
      tx.update(reportRef, {
        entries,
        totals: sumEntryTotals(entries),
        updatedAt: serverTimestamp(),
      });
    });

    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: ACTIVITY_ACTIONS.UPDATE_DAILY_REPORT,
      entityType: ENTITY_TYPES.DAILY_REPORT,
      entityId: dateKey,
      details: { success: true, action: "add_entry" },
    });

    return serializeForRedux(entry);
  } catch (error) {
    console.error("[CK-8661] Error adding a coach food entry:", {
      errorCode: "CK-8661",
      errorMessage: error.message,
      clinicId,
      patientId,
      dateKey,
      timestamp: new Date().toISOString(),
    });
    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: ACTIVITY_ACTIONS.UPDATE_DAILY_REPORT,
      entityType: ENTITY_TYPES.DAILY_REPORT,
      entityId: dateKey,
      details: { success: false, error: error.message },
    });
    throw error;
  }
};

/**
 * Correct an entry — typically an AI value the client accepted.
 *
 * The previous values are appended to `edits[]` with `by: "coach"`, exactly as
 * a client edit is, so the report reads as one honest history rather than a
 * current state with no provenance.
 *
 * Error Code: CK-8662
 */
export const updateFoodEntry = async (
  clinicId,
  patientId,
  dateKey,
  entryId,
  changes = {},
  actor = {}
) => {
  await guardTrialWrite(clinicId);

  const { therapistId = null, therapistName = "לא ידוע" } = actor;

  try {
    const reportRef = dailyReportDocRef(clinicId, patientId, dateKey);

    await runTransaction(db, async (tx) => {
      const snap = await tx.get(reportRef);
      if (!snap.exists()) throw new Error("הדוח לא נמצא");

      const entries = snap.data().entries || [];
      const index = entries.findIndex((entry) => entry.id === entryId);
      if (index === -1) throw new Error("הדיווח לא נמצא");

      const entry = entries[index];
      const edits = [...(entry.edits || [])];
      const at = Timestamp.now();

      if (Array.isArray(changes.foods)) {
        edits.push({
          by: "coach",
          at,
          field: "foods",
          before: entry.foods || [],
          after: changes.foods,
        });
      }
      if (
        typeof changes.comment === "string" &&
        changes.comment !== (entry.comment || "")
      ) {
        edits.push({
          by: "coach",
          at,
          field: "comment",
          before: entry.comment || "",
          after: changes.comment,
        });
      }

      const next = [...entries];
      next[index] = {
        ...entry,
        ...(Array.isArray(changes.foods)
          ? { foods: changes.foods, totals: sumFoodTotals(changes.foods) }
          : {}),
        ...(typeof changes.comment === "string" ? { comment: changes.comment } : {}),
        edits,
      };

      tx.update(reportRef, {
        entries: next,
        totals: sumEntryTotals(next),
        updatedAt: serverTimestamp(),
      });
    });

    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: ACTIVITY_ACTIONS.UPDATE_DAILY_REPORT,
      entityType: ENTITY_TYPES.DAILY_REPORT,
      entityId: dateKey,
      details: { success: true, action: "edit_entry" },
    });

    return { success: true };
  } catch (error) {
    console.error("[CK-8662] Error updating a food entry:", {
      errorCode: "CK-8662",
      errorMessage: error.message,
      clinicId,
      patientId,
      dateKey,
      timestamp: new Date().toISOString(),
    });
    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: ACTIVITY_ACTIONS.UPDATE_DAILY_REPORT,
      entityType: ENTITY_TYPES.DAILY_REPORT,
      entityId: dateKey,
      details: { success: false, error: error.message },
    });
    throw error;
  }
};

/**
 * Mark a day reviewed (with an optional 1-5 rating). This is what removes the
 * report from the coach's pending queue.
 *
 * Error Code: CK-8663
 */
export const markReportReviewed = async (
  clinicId,
  patientId,
  dateKey,
  { rating = null } = {},
  actor = {}
) => {
  await guardTrialWrite(clinicId);

  const { therapistId = null, therapistName = "לא ידוע" } = actor;

  try {
    const numericRating = Number(rating);
    await updateDoc(dailyReportDocRef(clinicId, patientId, dateKey), {
      coachReviewed: true,
      reviewedAt: serverTimestamp(),
      reviewedBy: therapistId,
      rating:
        Number.isFinite(numericRating) && numericRating >= 1 && numericRating <= 5
          ? numericRating
          : null,
      updatedAt: serverTimestamp(),
    });

    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: ACTIVITY_ACTIONS.REVIEW_DAILY_REPORT,
      entityType: ENTITY_TYPES.DAILY_REPORT,
      entityId: dateKey,
      details: { success: true },
    });

    return { success: true };
  } catch (error) {
    console.error("[CK-8663] Error marking a report reviewed:", {
      errorCode: "CK-8663",
      errorMessage: error.message,
      clinicId,
      patientId,
      dateKey,
      timestamp: new Date().toISOString(),
    });
    logActivity(clinicId, {
      therapistId,
      therapistName,
      action: ACTIVITY_ACTIONS.REVIEW_DAILY_REPORT,
      entityType: ENTITY_TYPES.DAILY_REPORT,
      entityId: dateKey,
      details: { success: false, error: error.message },
    });
    throw error;
  }
};

// ============================================
// NUTRITION — COACH NOTES
// ============================================
// One embedded `coachNotes[]` array on menus, measurements and daily reports,
// using the treatment-comment shape: {id, authorId, authorName, content,
// createdAt: ISO}. The key is `content` — PatientProfilePage used to read
// `comment.text` for treatment comments, which is why they rendered blank.
// ============================================

/** Where a note can live. Keeps the three call sites from drifting apart. */
export const NOTE_TARGETS = {
  MENU: "menus",
  MEASUREMENT: "measurements",
  DAILY_REPORT: "dailyReports",
};

const noteDocRef = (clinicId, patientId, target, docId) =>
  doc(db, "clinics", clinicId, "patients", patientId, target, docId);

/**
 * Append a coach note.
 *
 * Error Code: CK-8664
 */
export const addCoachNote = async (
  clinicId,
  patientId,
  target,
  docId,
  content,
  actor = {}
) => {
  await guardTrialWrite(clinicId);

  const { therapistId = null, therapistName = "לא ידוע" } = actor;

  try {
    if (!Object.values(NOTE_TARGETS).includes(target)) {
      throw new Error("סוג פריט לא נתמך להערות");
    }
    const trimmed = String(content || "").trim();
    if (!trimmed) throw new Error("יש להזין תוכן להערה");

    const note = {
      id: generateNutritionId(),
      authorId: therapistId,
      authorName: therapistName,
      content: trimmed,
      createdAt: new Date().toISOString(),
    };

    await updateDoc(noteDocRef(clinicId, patientId, target, docId), {
      coachNotes: arrayUnion(note),
      updatedAt: serverTimestamp(),
    });

    return note;
  } catch (error) {
    console.error("[CK-8664] Error adding a coach note:", {
      errorCode: "CK-8664",
      errorMessage: error.message,
      clinicId,
      patientId,
      target,
      docId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Remove a coach note. `arrayRemove` needs the exact stored object, so the
 * caller passes the note it rendered.
 *
 * Error Code: CK-8665
 */
export const deleteCoachNote = async (clinicId, patientId, target, docId, note) => {
  await guardTrialWrite(clinicId);

  try {
    await updateDoc(noteDocRef(clinicId, patientId, target, docId), {
      coachNotes: arrayRemove(note),
      updatedAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    console.error("[CK-8665] Error deleting a coach note:", {
      errorCode: "CK-8665",
      errorMessage: error.message,
      clinicId,
      patientId,
      target,
      docId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// ============================================
// NUTRITION — THE COACH'S PENDING QUEUE
// ============================================

/**
 * Closed eating reports still waiting for a coach's review.
 *
 * ONE collection-group query instead of walking every patient. A clinic admin
 * sees the whole clinic; a therapist is scoped to reports stamped with their
 * own id — which the Firestore rule also enforces per document, so this is not
 * a client-side-only restriction.
 *
 * Error Code: CK-8666
 */
export const getReportsAwaitingReview = async (
  clinicId,
  therapistId,
  { isAdmin = false, limitCount = 50 } = {}
) => {
  try {
    const constraints = [where("clinicId", "==", clinicId)];
    if (!isAdmin) constraints.push(where("therapistId", "==", therapistId));
    constraints.push(
      where("coachReviewed", "==", false),
      where("status", "==", "closed"),
      orderBy("date", "desc"),
      limit(limitCount)
    );

    const snapshot = await getDocs(
      query(collectionGroup(db, "dailyReports"), ...constraints)
    );

    return snapshot.docs.map((docSnap) =>
      serializeForRedux({ id: docSnap.id, ...docSnap.data() })
    );
  } catch (error) {
    console.error("[CK-8666] Error fetching reports awaiting review:", {
      errorCode: "CK-8666",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Clients whose measurement schedule has come due.
 *
 * `nextDueAt` is recomputed by the daily job and whenever a measurement is
 * recorded, so "due" here is simply "the date has passed".
 *
 * Error Code: CK-8667
 */
export const getMeasurementsDue = async (clinicId, therapistId, { isAdmin = false } = {}) => {
  try {
    const constraints = [
      where("nutrition.measurementSchedule.enabled", "==", true),
      where("nutrition.measurementSchedule.nextDueAt", "<=", Timestamp.now()),
      orderBy("nutrition.measurementSchedule.nextDueAt", "asc"),
      limit(50),
    ];

    const snapshot = await getDocs(
      query(collection(db, "clinics", clinicId, "patients"), ...constraints)
    );

    return snapshot.docs
      .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
      // A therapist sees their own clients; an admin sees everyone's. Done in
      // memory because patients carry BOTH therapistId and therapistIds[], and
      // a dual read here would cost more than filtering this short list.
      .filter((patient) => {
        if (isAdmin) return true;
        if (patient.therapistId === therapistId) return true;
        return (patient.therapistIds || []).includes(therapistId);
      })
      .filter((patient) => patient.isActive !== false)
      .map((patient) =>
        serializeForRedux({
          id: patient.id,
          firstName: patient.firstName,
          lastName: patient.lastName,
          phone: patient.phone,
          nutrition: patient.nutrition,
        })
      );
  } catch (error) {
    console.error("[CK-8667] Error fetching clients with measurements due:", {
      errorCode: "CK-8667",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Clients created without daily goals (`nutrition.goalsSet === false`) — the
 * third section of the nutrition pending queue. Only clients that went
 * through a create path that stamps the flag are listed; a legacy client
 * with no flag is not nagged retroactively.
 *
 * Error Code: CK-8669
 */
export const getClientsWithoutGoals = async (clinicId, therapistId, { isAdmin = false } = {}) => {
  try {
    const snapshot = await getDocs(
      query(
        collection(db, "clinics", clinicId, "patients"),
        where("nutrition.goalsSet", "==", false),
        limit(50)
      )
    );

    return snapshot.docs
      .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
      // Same in-memory scoping as getMeasurementsDue: therapistId OR therapistIds[]
      .filter((patient) => {
        if (isAdmin) return true;
        if (patient.therapistId === therapistId) return true;
        return (patient.therapistIds || []).includes(therapistId);
      })
      .filter((patient) => patient.isActive !== false && patient.isDraft !== true)
      // Dismissed for good with the ✕ on the pending card (Round 3 / Phase 23)
      .filter((patient) => !patient.nutritionGoalsPromptDismissedAt)
      .map((patient) =>
        serializeForRedux({
          id: patient.id,
          firstName: patient.firstName,
          lastName: patient.lastName,
          createdAt: patient.createdAt,
        })
      );
  } catch (error) {
    console.error("[CK-8669] Error fetching clients without daily goals:", {
      errorCode: "CK-8669",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Send the client the one-shot "time for a measurement" SMS.
 *
 * One per cycle: `lastNotifiedAt` is stamped server-side and the UI disables
 * the button once it is set, so a coach cannot nag a client repeatedly.
 *
 * Error Code: CK-8668
 */
export const sendMeasurementReminder = async (clinicId, patientId) => {
  try {
    const fn = httpsCallable(functions, "sendMeasurementReminderSms");
    const result = await fn({ clinicId, patientId });
    return result.data;
  } catch (error) {
    console.error("[CK-8668] Error sending a measurement reminder:", {
      errorCode: "CK-8668",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

ACTIVITY_ACTIONS.UPDATE_DAILY_REPORT = "update_daily_report";
ACTIVITY_ACTIONS.REVIEW_DAILY_REPORT = "review_daily_report";

ACTION_LABELS.update_daily_report = "עדכון דוח אכילה";
ACTION_LABELS.review_daily_report = "סקירת דוח אכילה";

ENTITY_TYPES.DAILY_REPORT = "daily_report";

// Nutrition menu activity actions
ACTIVITY_ACTIONS.CREATE_MENU = "create_menu";
ACTIVITY_ACTIONS.UPDATE_MENU = "update_menu";
ACTIVITY_ACTIONS.DELETE_MENU = "delete_menu";
ACTIVITY_ACTIONS.SET_ACTIVE_MENU = "set_active_menu";
ACTIVITY_ACTIONS.CREATE_MENU_TEMPLATE = "create_menu_template";
ACTIVITY_ACTIONS.DELETE_MENU_TEMPLATE = "delete_menu_template";

ACTION_LABELS.create_menu = "יצירת תפריט";
ACTION_LABELS.update_menu = "עדכון תפריט";
ACTION_LABELS.delete_menu = "מחיקת תפריט";
ACTION_LABELS.set_active_menu = "הפעלת תפריט";
ACTION_LABELS.create_menu_template = "שמירת תבנית תפריט";
ACTION_LABELS.delete_menu_template = "מחיקת תבנית תפריט";

ENTITY_TYPES.MENU = "menu";
ENTITY_TYPES.MENU_TEMPLATE = "menu_template";

// Nutrition measurement activity actions
ACTIVITY_ACTIONS.CREATE_MEASUREMENT = "create_measurement";
ACTIVITY_ACTIONS.UPDATE_MEASUREMENT = "update_measurement";
ACTIVITY_ACTIONS.DELETE_MEASUREMENT = "delete_measurement";

ACTION_LABELS.create_measurement = "הוספת מדידה";
ACTION_LABELS.update_measurement = "עדכון מדידה";
ACTION_LABELS.delete_measurement = "מחיקת מדידה";

ENTITY_TYPES.MEASUREMENT = "measurement";

// Nutrition food-catalog activity actions (plain assignment — no structural
// change to the maps every other module already reads)
ACTIVITY_ACTIONS.CREATE_CUSTOM_FOOD = "create_custom_food";
ACTIVITY_ACTIONS.UPDATE_CUSTOM_FOOD = "update_custom_food";
ACTIVITY_ACTIONS.DELETE_CUSTOM_FOOD = "delete_custom_food";

ACTION_LABELS.create_custom_food = "הוספת מזון למאגר";
ACTION_LABELS.update_custom_food = "עדכון מזון במאגר";
ACTION_LABELS.delete_custom_food = "מחיקת מזון מהמאגר";

ENTITY_TYPES.FOOD = "food";


// ============================================================================
// MULTI-CLINIC v2 — THERAPIST DOUBLE-BOOKING + ABSENCES (Phase 4)
// ============================================================================

ACTIVITY_ACTIONS.OVERRIDE_THERAPIST_CONFLICT = "override_therapist_conflict";
ACTIVITY_ACTIONS.OVERRIDE_ABSENCE = "override_absence";
ACTIVITY_ACTIONS.CREATE_ABSENCE = "create_absence";
ACTIVITY_ACTIONS.UPDATE_ABSENCE = "update_absence";
ACTIVITY_ACTIONS.DELETE_ABSENCE = "delete_absence";

ACTION_LABELS.override_therapist_conflict = "קביעת תור חופף למטפל (עקיפה)";
ACTION_LABELS.override_absence = "קביעת תור בזמן היעדרות (עקיפה)";
ACTION_LABELS.create_absence = "הוספת היעדרות";
ACTION_LABELS.update_absence = "עדכון היעדרות";
ACTION_LABELS.delete_absence = "מחיקת היעדרות";

ENTITY_TYPES.ABSENCE = "absence";

/**
 * Therapist double-booking check: every non-cancelled, non-deleted
 * appointment of `therapistId` overlapping [startTime, endTime).
 * Blocked-time windows (isBlockedTime) count as conflicts too (flagged).
 * Uses the existing composite index therapistId ASC + startTime ASC.
 *
 * @returns {Promise<{isAvailable: boolean, conflicts: Array<{id, patientName,
 *   patientFirstName, startTime, endTime, roomId, isBlockedTime, therapistId}>}>}
 */
export const checkTherapistAvailability = async (
  clinicId,
  therapistId,
  startTime,
  endTime,
  { excludeAppointmentId = null, excludeGroupId = null } = {}
) => {
  try {
    if (!clinicId || !therapistId || !startTime || !endTime) {
      return { isAvailable: true, conflicts: [] };
    }
    const appointmentsRef = collection(db, "clinics", clinicId, "appointments");
    // Same-day window (appointments never cross midnight).
    const dayStart = new Date(startTime);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(startTime);
    dayEnd.setHours(23, 59, 59, 999);
    const q = query(
      appointmentsRef,
      where("therapistId", "==", therapistId),
      where("startTime", ">=", Timestamp.fromDate(dayStart)),
      where("startTime", "<=", Timestamp.fromDate(dayEnd))
    );
    const snapshot = await getDocs(q);
    const newStart = startTime.getTime();
    const newEnd = endTime.getTime();
    const conflicts = [];
    snapshot.docs.forEach((docSnap) => {
      const apt = docSnap.data();
      if (
        excludeAppointmentId &&
        (docSnap.id === excludeAppointmentId || apt.id === excludeAppointmentId)
      ) {
        return;
      }
      if (excludeGroupId && apt.recurringGroupId === excludeGroupId) return;
      if (apt.isDeleted === true) return;
      if (apt.status === "cancelled") return;
      if (!apt.startTime?.toDate || !apt.endTime?.toDate) return;
      const existingStart = apt.startTime.toDate().getTime();
      const existingEnd = apt.endTime.toDate().getTime();
      if (newStart < existingEnd && newEnd > existingStart) {
        const fullName = apt.patientName || null;
        conflicts.push({
          id: docSnap.id,
          startTime: apt.startTime.toDate().toISOString(),
          endTime: apt.endTime.toDate().toISOString(),
          patientName: fullName,
          patientFirstName: fullName ? fullName.split(" ")[0] : null,
          roomId: apt.roomId || null,
          therapistId: apt.therapistId || null,
          isBlockedTime: apt.isBlockedTime === true,
        });
      }
    });
    conflicts.sort((a, b) => a.startTime.localeCompare(b.startTime));
    return { isAvailable: conflicts.length === 0, conflicts };
  } catch (error) {
    console.error("[CK-8540] Error checking therapist availability:", {
      errorCode: "CK-8540",
      errorMessage: error.message,
      clinicId,
      therapistId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

const ABSENCE_ALLOWED_TYPES = ["vacation", "sick", "other"];

const sanitizeAbsenceInput = (input) => {
  const allDay = input?.allDay !== false;
  return {
    startDate: String(input?.startDate || ""),
    endDate: String(input?.endDate || input?.startDate || ""),
    allDay,
    startTime: allDay ? null : input?.startTime || null,
    endTime: allDay ? null : input?.endTime || null,
    type: ABSENCE_ALLOWED_TYPES.includes(input?.type) ? input.type : "other",
    note: input?.note ? String(input.note).slice(0, 120) : null,
  };
};

/**
 * Absences of a clinic in [from, to] ("YYYY-MM-DD" inclusive), optionally
 * for one therapist. Range query on endDate + in-memory startDate filter
 * (one inequality field ⇒ no exotic index; therapistId+endDate composite).
 */
export const getAbsences = async (clinicId, { from, to, therapistId = null } = {}) => {
  try {
    if (!clinicId || !from || !to) return [];
    const ref = collection(db, "clinics", clinicId, "absences");
    const constraints = [];
    if (therapistId) constraints.push(where("therapistId", "==", therapistId));
    constraints.push(where("endDate", ">=", from));
    constraints.push(orderBy("endDate", "asc"));
    const snapshot = await getDocs(query(ref, ...constraints));
    return snapshot.docs
      .map((d) => serializeForRedux({ id: d.id, ...d.data() }))
      .filter((a) => a.startDate <= to);
  } catch (error) {
    console.error("[CK-8541] Error fetching absences:", {
      errorCode: "CK-8541",
      errorMessage: error.message,
      clinicId,
      therapistId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/** Notify clinic admins (not the actor) about an absence change. Best effort. */
const notifyAdminsAboutAbsence = async (clinicId, actorId, actorName, absence, verb) => {
  try {
    const therapists = await getClinicTherapists(clinicId);
    const admins = therapists.filter(
      (t) =>
        (t.role === "owner" || t.isAdmin === true) &&
        t.isActive !== false &&
        (t.uid || t.id) !== actorId
    );
    const typeLabel =
      { vacation: "חופשה", sick: "מחלה", other: "היעדרות" }[absence.type] || "היעדרות";
    const range =
      absence.startDate === absence.endDate
        ? absence.startDate.split("-").reverse().slice(0, 2).join("/")
        : `${absence.startDate.split("-").reverse().slice(0, 2).join("/")}–${absence.endDate
            .split("-")
            .reverse()
            .slice(0, 2)
            .join("/")}`;
    admins.forEach((adminT) => {
      sendNotification(clinicId, {
        title:
          verb === "deleted"
            ? "היעדרות בוטלה"
            : verb === "updated"
              ? "היעדרות עודכנה"
              : "היעדרות חדשה בצוות",
        message:
          verb === "deleted"
            ? `${absence.therapistName || actorName || "מטפל/ת"} ביטל/ה היעדרות (${typeLabel}) ${range}`
            : verb === "updated"
              ? `${absence.therapistName || actorName || "מטפל/ת"} עדכן/ה היעדרות (${typeLabel}) ${range}`
              : `${absence.therapistName || actorName || "מטפל/ת"} הוסיף/ה היעדרות (${typeLabel}) ${range}`,
        type: NOTIFICATION_TYPES.SYSTEM,
        urgency: NOTIFICATION_URGENCY.LOW,
        therapistId: adminT.uid || adminT.id,
        link: "/dashboard/admin/calendar",
        createdBy: actorId,
        timestamp: new Date().toISOString(),
      });
    });
  } catch (error) {
    console.error("[CK-8545] Error notifying admins about absence:", {
      errorCode: "CK-8545",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
  }
};

/**
 * Create an absence. Self-service (no approval). `actor` = who is writing
 * (may differ from the absent therapist for admins / calendar.manageAbsences).
 */
export const createAbsence = async (
  clinicId,
  { therapistId, therapistName, ...input },
  { actorId, actorName } = {}
) => {
  await guardTrialWrite(clinicId);
  const data = sanitizeAbsenceInput(input);
  try {
    if (!therapistId) throw new Error("therapistId is required");
    if (!data.startDate || !data.endDate) throw new Error("dates are required");
    const ref = collection(db, "clinics", clinicId, "absences");
    const docRef = await addDoc(ref, {
      therapistId,
      therapistName: therapistName || null,
      ...data,
      createdBy: actorId || therapistId,
      createdByName: actorName || therapistName || null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    const absence = { id: docRef.id, therapistId, therapistName: therapistName || null, ...data };
    logActivity(clinicId, {
      therapistId: actorId || therapistId,
      therapistName: actorName || therapistName || null,
      action: ACTIVITY_ACTIONS.CREATE_ABSENCE,
      entityType: ENTITY_TYPES.ABSENCE,
      entityId: docRef.id,
      entityName: therapistName || null,
      details: {
        targetTherapistId: therapistId,
        startDate: data.startDate,
        endDate: data.endDate,
        allDay: data.allDay,
        type: data.type,
      },
      success: true,
    });
    notifyAdminsAboutAbsence(clinicId, actorId || therapistId, actorName, absence, "created");
    return absence;
  } catch (error) {
    console.error("[CK-8542] Error creating absence:", {
      errorCode: "CK-8542",
      errorMessage: error.message,
      clinicId,
      therapistId,
      timestamp: new Date().toISOString(),
    });
    logActivity(clinicId, {
      therapistId: actorId || therapistId,
      therapistName: actorName || therapistName || null,
      action: ACTIVITY_ACTIONS.CREATE_ABSENCE,
      entityType: ENTITY_TYPES.ABSENCE,
      entityId: null,
      details: { targetTherapistId: therapistId, startDate: data.startDate, endDate: data.endDate },
      success: false,
      errorMessage: error.message,
    });
    throw error;
  }
};

export const updateAbsence = async (
  clinicId,
  absenceId,
  input,
  { actorId, actorName } = {}
) => {
  await guardTrialWrite(clinicId);
  const data = sanitizeAbsenceInput(input);
  try {
    const ref = doc(db, "clinics", clinicId, "absences", absenceId);
    await updateDoc(ref, { ...data, updatedAt: serverTimestamp() });
    // One "updated" notification (instead of delete + recreate = two).
    // therapistId/therapistName are read from the input when the panel passes
    // the full absence; absent ⇒ generic name.
    notifyAdminsAboutAbsence(
      clinicId,
      actorId || null,
      actorName,
      { id: absenceId, therapistName: input?.therapistName || null, ...data },
      "updated"
    );
    logActivity(clinicId, {
      therapistId: actorId || null,
      therapistName: actorName || null,
      action: ACTIVITY_ACTIONS.UPDATE_ABSENCE,
      entityType: ENTITY_TYPES.ABSENCE,
      entityId: absenceId,
      details: { startDate: data.startDate, endDate: data.endDate, allDay: data.allDay, type: data.type },
      success: true,
    });
    return { id: absenceId, ...data };
  } catch (error) {
    console.error("[CK-8543] Error updating absence:", {
      errorCode: "CK-8543",
      errorMessage: error.message,
      clinicId,
      absenceId,
      timestamp: new Date().toISOString(),
    });
    logActivity(clinicId, {
      therapistId: actorId || null,
      therapistName: actorName || null,
      action: ACTIVITY_ACTIONS.UPDATE_ABSENCE,
      entityType: ENTITY_TYPES.ABSENCE,
      entityId: absenceId,
      success: false,
      errorMessage: error.message,
    });
    throw error;
  }
};

export const deleteAbsence = async (
  clinicId,
  absenceId,
  { actorId, actorName, absence = null } = {}
) => {
  await guardTrialWrite(clinicId);
  try {
    const ref = doc(db, "clinics", clinicId, "absences", absenceId);
    await deleteDoc(ref);
    logActivity(clinicId, {
      therapistId: actorId || null,
      therapistName: actorName || null,
      action: ACTIVITY_ACTIONS.DELETE_ABSENCE,
      entityType: ENTITY_TYPES.ABSENCE,
      entityId: absenceId,
      entityName: absence?.therapistName || null,
      details: absence
        ? { targetTherapistId: absence.therapistId, startDate: absence.startDate, endDate: absence.endDate }
        : {},
      success: true,
    });
    if (absence) {
      notifyAdminsAboutAbsence(clinicId, actorId, actorName, absence, "deleted");
    }
    return { id: absenceId };
  } catch (error) {
    console.error("[CK-8544] Error deleting absence:", {
      errorCode: "CK-8544",
      errorMessage: error.message,
      clinicId,
      absenceId,
      timestamp: new Date().toISOString(),
    });
    logActivity(clinicId, {
      therapistId: actorId || null,
      therapistName: actorName || null,
      action: ACTIVITY_ACTIONS.DELETE_ABSENCE,
      entityType: ENTITY_TYPES.ABSENCE,
      entityId: absenceId,
      success: false,
      errorMessage: error.message,
    });
    throw error;
  }
};

/**
 * Primary-patient counts per therapist (Team page cards). One aggregation
 * query per therapist — cheap, no documents read. Active patients only.
 * @returns {Promise<Record<string, number>>} therapistId → count
 */
export const getPatientCountsByTherapist = async (clinicId, therapistIds = []) => {
  try {
    const ref = collection(db, "clinics", clinicId, "patients");
    const entries = await Promise.all(
      therapistIds.map(async (tid) => {
        const snap = await getCountFromServer(
          query(ref, where("therapistId", "==", tid))
        );
        return [tid, snap.data().count || 0];
      })
    );
    return Object.fromEntries(entries);
  } catch (error) {
    console.error("[CK-8547] Error counting patients per therapist:", {
      errorCode: "CK-8547",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};


// ============================================================================
// MULTI-CLINIC v2 Phase 3 — TREATMENT READ AUDIT (clinics/{id}/treatmentViews)
// ============================================================================

ACTIVITY_ACTIONS.UPDATE_PATIENT_SHARING_MODE = "update_patient_sharing_mode";
ACTION_LABELS.update_patient_sharing_mode = "שינוי מצב שיתוף מטופלים";
ACTION_LABELS.update_therapist_role = "עדכון תפקיד והרשאות";
ACTION_LABELS.resend_team_invite = "שליחת תזכורת הצטרפות";
ACTION_LABELS.invite_accepted = "כניסה ראשונה למערכת";
ACTION_LABELS.create_therapist = "הוספת איש/אשת צוות";
ACTION_LABELS.update_therapist_status = "שינוי סטטוס איש/אשת צוות";
ACTION_LABELS.clinic_upgraded_multi = "הקליניקה הפכה למרובת מטפלים";
ACTION_LABELS.transfer_patients = "העברת מטופלים בין מטפלים";
ACTION_LABELS.reassign_patient = "שיוך מטופל/ת למטפל/ת אחר/ת";
ENTITY_TYPES.CLINIC = "clinic";

export const TREATMENT_VIEW_CONTEXTS = {
  PROFILE: "profile",
  DOC: "doc",
  REPORT: "report",
  AI: "ai",
};

// One row per open, debounced 5 minutes per viewer+treatment (client-side).
const TREATMENT_VIEW_DEBOUNCE_MS = 5 * 60 * 1000;
const treatmentViewDebounce = new Map();

/**
 * Audit a read of someone else's treatment report. No-op when the viewer is
 * the author. Admins/owners viewing others' reports ARE logged. Never throws.
 * @param {string} clinicId
 * @param {{treatmentId, patientId, ownerTherapistId, context}} view
 * @param {{viewerId, viewerName, viewerRole}} viewer
 */
export const logTreatmentReadAudit = async (clinicId, view, viewer) => {
  try {
    if (!clinicId || !view?.treatmentId || !view?.ownerTherapistId || !viewer?.viewerId) return false;
    if (view.ownerTherapistId === viewer.viewerId) return false;
    const key = `${viewer.viewerId}:${view.treatmentId}`;
    const last = treatmentViewDebounce.get(key) || 0;
    if (Date.now() - last < TREATMENT_VIEW_DEBOUNCE_MS) return false;
    treatmentViewDebounce.set(key, Date.now());
    await addDoc(collection(db, "clinics", clinicId, "treatmentViews"), {
      treatmentId: view.treatmentId,
      patientId: view.patientId || null,
      ownerTherapistId: view.ownerTherapistId,
      viewerId: viewer.viewerId,
      viewerName: viewer.viewerName || null,
      viewerRole: viewer.viewerRole || null,
      context: Object.values(TREATMENT_VIEW_CONTEXTS).includes(view.context) ? view.context : "doc",
      viewedAt: serverTimestamp(),
    });
    return true;
  } catch (error) {
    console.error("[CK-8548] Error logging treatment view:", {
      errorCode: "CK-8548",
      errorMessage: error.message,
      clinicId,
      treatmentId: view?.treatmentId,
      timestamp: new Date().toISOString(),
    });
    return false;
  }
};

/**
 * Admin-only list of treatment views (newest first). Optional filters:
 * viewerId | ownerTherapistId | patientId (one equality at a time keeps the
 * composite-index set small), startDate/endDate ("YYYY-MM-DD"), cursor.
 */
export const getTreatmentViews = async (clinicId, options = {}) => {
  const { viewerId, ownerTherapistId, patientId, startDate, endDate, cursor, pageSize = 50 } = options;
  try {
    const ref = collection(db, "clinics", clinicId, "treatmentViews");
    const constraints = [];
    if (viewerId) constraints.push(where("viewerId", "==", viewerId));
    else if (ownerTherapistId) constraints.push(where("ownerTherapistId", "==", ownerTherapistId));
    else if (patientId) constraints.push(where("patientId", "==", patientId));
    if (startDate) constraints.push(where("viewedAt", ">=", Timestamp.fromDate(new Date(`${startDate}T00:00:00`))));
    if (endDate) constraints.push(where("viewedAt", "<=", Timestamp.fromDate(new Date(`${endDate}T23:59:59.999`))));
    constraints.push(orderBy("viewedAt", "desc"));
    if (cursor) constraints.push(startAfter(Timestamp.fromDate(new Date(cursor))));
    constraints.push(limit(pageSize + 1));
    const snap = await getDocs(query(ref, ...constraints));
    const docs = snap.docs.map((d) => serializeForRedux({ id: d.id, ...d.data() }));
    const hasMore = docs.length > pageSize;
    const views = hasMore ? docs.slice(0, pageSize) : docs;
    const last = views[views.length - 1];
    return { views, hasMore, nextCursor: hasMore && last?.viewedAt ? last.viewedAt : null };
  } catch (error) {
    console.error("[CK-8549] Error fetching treatment views:", {
      errorCode: "CK-8549",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};


/**
 * Multi-clinic v2 Phase 5 — batched weekly availability for several
 * therapists (team coverage grid, room overlays). One read per therapist,
 * all in parallel; a failed therapist read yields [] instead of failing the
 * whole batch (CK-8550). Reads need rules access (admin or calendar.viewClinic).
 * @returns {Promise<Array<{therapistId: string, slots: Array}>>}
 */
export const getTeamAvailability = async (therapistIds = [], startDate, endDate) => {
  const ids = Array.from(new Set((therapistIds || []).filter(Boolean)));
  return Promise.all(
    ids.map(async (therapistId) => {
      try {
        const slots = await getAvailabilityForRange(therapistId, startDate, endDate);
        return { therapistId, slots: slots || [] };
      } catch (error) {
        console.error("[CK-8550] Error fetching team availability for therapist:", {
          errorCode: "CK-8550",
          errorMessage: error.message,
          therapistId,
          timestamp: new Date().toISOString(),
        });
        return { therapistId, slots: [], error: true };
      }
    })
  );
};

export { app, analytics, auth, db, storage, functions };
